/* ============================================================
 * 武者之路 —— 2D 横版修仙路遇战斗
 *
 * 玩法流程：
 *   按住左下角【移动】→ 角色向前走
 *   → 路上遇到拦路者 → 进入战斗
 *   → 使用【攻击 / 法术】击败敌人
 *   → 移动按钮恢复，才能继续前进
 *
 * 素材：Kenney.nl (Creative Commons CC0)
 * 运行：npm run dev（开发热更新） / npm run build（产物在 dist/，交给 nginx 托管）
 * ============================================================ */

import {
  SLOT_DEFS,
  WEAPON_KINDS,
  RARITY_DEFS,
  SHOP_LOCK_COST,
  SAVE_KEY,
  GEM_OPTIONS,
  SKILL_POOL,
  ACTIVE_SKILL_POOL,
  REALM_LIST,
  CULTIVATION_STAGES,
  MAX_STAGE,
  TRIBULATION_STAGE,
  ATTR_DEFS,
  ATTR_POINTS_PER_LEVEL,
  CAVE_DEFS,
  CAVE_TOKEN_COST,
  CAVE_JOIN_CHANCE,
  CAVE_JOIN_MAX,
  SHIELD_AFFIXES,
  GENERIC_AFFIXES,
  ARMOUR_AFFIXES,
  GLOVES_AFFIXES,
  SHOES_AFFIXES,
  JEWELRY_AFFIXES,
  TREASURE_SPECIALS,
  AFFIX_BAND_SIZE,
  CODEX_ITEMS,
  CODEX_TIERS,
  CODEX_LEGACY_IDS,
  STAT_LABELS,
  GACHA_SINGLE_COST,
  GACHA_TEN_COST,
  GACHA_PITY_MAX,
  GACHA_TEN_MIN_TIER,
  GACHA_TEN_CAPS,
  WORLD_BOSSES
} from './data/defs.js';
// 网络层：服务器地址解析 + 账号/存档 API + 聊天 WebSocket（服务器不可用时自动回退单机）
import { createApiClient, createChatClient, normalizeServerUrl, wsUrlFrom, ApiError } from './net.js';
// 资源基址（挂第三方平台 iframe 时素材仍从自有域名取，见 assetBase.js）
import { ASSET_BASE, assetUrl, fixStaticAssetSrcs, guardDrawImage } from './assetBase.js';

(function () {
  'use strict';

  /* ============================================================
   * 素材地址修正（必须先跑，越早越好）
   *   把游戏挂到 VibeHub 之类的平台 iframe 里时，页面文档地址会变成平台域名，
   *   index.html / JS 里的 "assets/img/..." 会被拼到平台站点上 → 全部 404。
   *   这里统一补成绝对地址（自有域名或正式域名），并给 drawImage 加一层兜底。
   * ============================================================ */
  fixStaticAssetSrcs();
  guardDrawImage();
  window.__assetBase = ASSET_BASE;      // 调试用：确认素材实际从哪个基址取（本地/线上排查 404 用）
  window.__assetUrl = assetUrl;         // 调试用：传入任意素材路径，看最终会请求成什么地址

  // ---------- 画布 ----------
  const W = 1280;
  const H = 720;
  const canvas = document.getElementById('game');
  const ctx = canvas.getContext('2d');

  // ---------- 游戏常量 ----------
  const GROUND_Y = 650;
  const START_X = 360;
  const SUN_X = 995;
  const SUN_Y = 128;
  const KM_UNIT = 2400;
  const WALK_SPEED = 250;
  const FIRST_ENEMY_DIST = 1700;   // 起点到第一个敌人
  const ENEMY_GAP = 3000;          // 敌人之间的间距
  const ENEMY_GAP_PX = 170;        // 玩家停在敌人面前的距离
  const ENEMY_LEVEL_KM = 10;       // 每 10km 敌人等级 +1

  // ---------- 测试环境便利设置 ----------
  // 本地 / 内网 / file:// 访问时，每次进入游戏自动发放测试资源：
  //   10000 金币（买装备 / 抽卡测试）+ 100 活力（自动冒险 / 活力兑换测试）
  // 加 ?debug=0 关闭，加 ?debug=1 可在任意环境强制开启。
  const TEST_GOLD = 10000;
  const TEST_VITALITY = 100;
  let testGoldGranted = false;

  function isTestEnvironment() {
    if (/[?&](debug|test)=0/.test(location.search)) return false;
    if (/[?&](debug|test)=1/.test(location.search) || location.hash === '#debug') return true;
    const host = location.hostname;
    return location.protocol === 'file:' ||
      host === 'localhost' || host === '127.0.0.1' || host === '::1' ||
      /^192\.168\./.test(host) || /^10\./.test(host) ||
      /^172\.(1[6-9]|2\d|3[01])\./.test(host);
  }

  // “劈砍”特效节奏：刀光从敌人上方劈下，落到敌人身上时结算伤害
  const SLASH_DUR = 0.78;
  const SLASH_IMPACT = 0.42;
  const HURT_TIME = 0.32; // 受击震动 / 血色划痕持续时长

  // “防御”护盾特效：pipo-btleffect208（192×192，20 帧，见 drawDefendFx）
  // 表现：护盾从角色身上撑开 → 防御期间把角色整个包裹住（护盾维持）→ 防御结束收盾
  const DEFEND_FX_FRAMES = 20;
  // ① 撑开：1→12 帧，0.42 秒
  const DEFEND_FX_IN_DUR = 0.42;
  const DEFEND_FX_IN_FROM = 1, DEFEND_FX_IN_TO = 12;
  // ② 维持：12~17 帧循环（护盾裹住角色，轻微呼吸感）
  const DEFEND_FX_HOLD_FROM = 12, DEFEND_FX_HOLD_TO = 17;
  const DEFEND_FX_HOLD_FPS = 10;
  // ③ 收盾：17→20 帧，0.28 秒
  const DEFEND_FX_OUT_DUR = 0.28;
  const DEFEND_FX_OUT_FROM = 17, DEFEND_FX_OUT_TO = 20;
  const DEFEND_FX_SIZE = 252;                        // 屏幕上的绘制尺寸：正好罩住整个角色
  // 贴图里护盾是从左侧一点往右撑开的：这样会看成"从背后往前糊"，方向反了。
  // 水平镜像之后，护盾改为从角色前方（敌人在的那一侧）撑开、再回裹到身上。
  const DEFEND_FX_FLIP = true;
  // 源图里"盾面"的重心（192×192 画布约 86,98）：把这一点钉在角色身上，护盾才会包裹角色，
  // 而不是从角色身前一路铺到敌人脚下。
  // 镜像后重心同步翻到 1-86/192，保证翻面以后护盾依旧罩在角色身上。
  const DEFEND_FX_ORIGIN = { x: (DEFEND_FX_FLIP ? 1 - 86 / 192 : 86 / 192), y: 98 / 192 };
  const DEFEND_FX_LIFT = 94;    // 角色身体中心离地高度（护盾圆心）

  // 「破空斩」剑气：用户素材 HolySlash_B（64×64，4 帧，红色弧形剑气）
  // 表现：连放 2 道，从角色身前向前扩散，一直飞到屏幕最右侧边界才消失
  const AIRSLASH_FX_FRAMES = 4;
  const AIRSLASH_FX_SIZE = 220;        // 屏幕上的绘制尺寸（像素风放大）
  const AIRSLASH_FX_LIFT = 104;        // 剑气中心离地高度（横过敌人胸腹）
  const AIRSLASH_FX_SPEED = 1500;      // px/秒：约 0.7 秒穿过整屏
  const AIRSLASH_FX_FPS = 16;          // 4 帧循环
  const AIRSLASH_FX_WAVES = 2;         // 连续 2 道
  const AIRSLASH_FX_GAP = 0.26;        // 第二道落后的时间
  const AIRSLASH_FX_START = 52;        // 出招位置：角色身前

  // ---------- 玩家角色素材（生成素材 → 统一帧） ----------
  // tools/make_player_assets 把 434×724 的原始帧按"脚底基线 + 腿部重心"对齐，
  // 输出成尺寸一致、脚底基线固定在画布底部上方 basePad 的小画布，游戏里整帧贴即可。
  const PLAYER_FRAME = { w: 199, h: 207, basePad: 12 };
  const PLAYER_SPRITE_FLIP = false; // 素材方向反了就改成 true（一行切换）
  const PLAYER_IDLE_FPS = 6;        // 待机呼吸
  const PLAYER_WALK_FPS = 7;        // 赶路（复用这一套帧 + 轻微上下起伏）
  const PLAYER_ATK_FPS = 16;        // 出手动作
  const PLAYER_ATK_FRAMES = 5;
  const PLAYER_ATK_DUR = PLAYER_ATK_FRAMES / PLAYER_ATK_FPS; // ≈0.31s
  // 休息动作（路上停着不动、按钮显示「休息」时）：5 张不同姿态随机挑一张当当前休息状态
  const PLAYER_REST_FRAMES = 5;
  const PLAYER_REST_REROLL_MIN = 6;   // 秒：随机换下一个休息姿态的最短间隔
  const PLAYER_REST_REROLL_MAX = 11;  // 秒：最长间隔

  // ---------- 敌人美术（9 种，各只有移动帧；见 tools/make_enemy_assets.cjs）----------
  // 统一画布 199×199、脚底基线距底 12px、锚点 x=96 —— 和玩家素材同一套对齐思路
  // 说明：素材默认朝右（ENEMY_ART_FACES_RIGHT）。若某个模型的原图天生朝左，
  // 只需给那一项加 faceRight: false，游戏会单独把它翻过来。
  const ENEMY_SPRITES = [
    { key: 'bingfei', name: '兵匪', frames: 2 },
    { key: 'qigai', name: '乞丐', frames: 4 },
    { key: 'qiangdao', name: '强盗', frames: 5 },
    { key: 'shanzhu', name: '山猪', frames: 6 },
    { key: 'shuifei', name: '水匪', frames: 5 },
    { key: 'yaoshi', name: '药士', frames: 5 },
    { key: 'youmin', name: '游民', frames: 5 },
    { key: 'nongfu', name: '农夫', frames: 5 },
    { key: 'kuaiji', name: '会计', frames: 5 }
  ];
  const ENEMY_FRAME = { w: 199, h: 199, basePad: 12, anchorX: 96 };
  // 世界BOSS 专用立绘（森特欧斯萨满）：见 tools/make_boss_asset.cjs
  // 导出图是「游戏内显示尺寸的 2 倍」，所以绘制时用 scaleAdj 折半（超采样更清晰）
  const WB_BOSS_ART_KEY = 'wbBossStos';
  // scaleAdj 0.68：显示尺寸 ≈ 665×608（比敌人立绘大得多，衬托"庞然大物"）
  const WB_BOSS_ART = { w: 376, h: 344, basePad: 6, scaleAdj: 0.68 };
  const ENEMY_ART_FACES_RIGHT = true; // 素材朝向：和玩家素材一样默认朝右（反了就改这里）
  const ENEMY_WALK_FPS = 8;           // 赶路时的帧率
  const ENEMY_STAND_FPS = 2.2;        // 战斗站定：只有移动帧，就慢慢循环，别完全定格

  // ---------- 武器美术图标（用户提供的 2 组 ×30 张 32×32 像素图）----------
  const WEAPON_ICON_COUNT = 60; // 每件武器随机分一个，存在物品上，渲染时不会变
  // ---------- 防具美术图标（用户提供的 服装 素材：头盔 / 衣服 / 鞋子 / 手套 各 10 张）----------
  // 生成脚本：tools/make_gear_icons.cjs（512×512 源图缩到 128×128）
  const GEAR_ICON_COUNT = 10;
  // 装备槽 → 图标集合（集合名同时也是文件名前缀：helmet_1.png / clothes_3.png …）
  const GEAR_ICON_SET_BY_SLOT = {
    helmet: 'helmet',
    clothes: 'clothes',
    shoes: 'shoes',
    gloves: 'gloves',
    jewelry: 'jewelry'   // 首饰（戒指素材）
  };

  function gearIconUrl(setName, idx) {
    if (!setName || !idx) return null;
    if (setName === 'weapon') return assetUrl('assets/img/weapon/wicon_' + idx + '.png');
    return assetUrl('assets/img/gear/' + setName + '_' + idx + '.png');
  }

  // 给装备随机分配一张美术图标（存在物品上，之后渲染都用它，不会变）
  // 老存档里没有 iconSet 的装备也会在这里补齐
  function ensureItemIcon(item) {
    if (!item || item.type !== 'equipment') return item;
    let set = item.iconSet;
    if (!set) {
      set = item.slot === 'weapon' ? 'weapon' : (GEAR_ICON_SET_BY_SLOT[item.slot] || null);
      if (set) item.iconSet = set;
    }
    if (!item.iconImg && set) {
      item.iconImg = set === 'weapon' ? randInt(1, WEAPON_ICON_COUNT) : randInt(1, GEAR_ICON_COUNT);
    }
    return item;
  }

  const ASSETS = [
    // 玩家角色：生成素材处理后的统一帧（待机 4 帧 / 攻击 5 帧）
    ['player_idle_1', 'assets/img/player/player_idle_1.png'],
    ['player_idle_2', 'assets/img/player/player_idle_2.png'],
    ['player_idle_3', 'assets/img/player/player_idle_3.png'],
    ['player_idle_4', 'assets/img/player/player_idle_4.png'],
    ['player_atk_1', 'assets/img/player/player_atk_1.png'],
    ['player_atk_2', 'assets/img/player/player_atk_2.png'],
    ['player_atk_3', 'assets/img/player/player_atk_3.png'],
    ['player_atk_4', 'assets/img/player/player_atk_4.png'],
    ['player_atk_5', 'assets/img/player/player_atk_5.png'],
    ['cactus',    'assets/img/cactus.png'],
    ['rock',      'assets/img/rock.png'],
    ['enemyIdle', 'assets/img/enemy_idle.png'],
    ['enemyWalkA', 'assets/img/enemy_walk_a.png'],
    ['enemyWalkB', 'assets/img/enemy_walk_b.png'],
    // 洞府入口美术（用户提供的最新 Build1.png，处理脚本 tools/make_cave_asset.cjs）
    // 注意：nginx 对 /assets/ 做了 immutable 长缓存，换图必须换文件名，否则浏览器会一直用旧图
    ['caveEntrance', 'assets/img/cave/cave_entrance3.png'],
    // 世界BOSS「森特欧斯萨满」立绘（用户素材 Boss_stos.png，处理脚本 tools/make_boss_asset.cjs）
    [WB_BOSS_ART_KEY, 'assets/img/boss/stos.png'],
    // 世界BOSS：战场宝箱（击杀后走到屏幕中央亲手开启）+ 奖励宝箱外观 + 掉在地上的战利品展示
    ['wbChestBig', 'assets/img/boss/wb_chest_big.png'],
    ['wbChestBox', 'assets/img/boss/wb_chest_box.png'],
    ['wbGift1', 'assets/img/boss/wb_gift1.png'],
    ['wbGift2', 'assets/img/boss/wb_gift2.png'],
    ['wbGift3', 'assets/img/boss/wb_gift3.png'],
    ['wbGift4', 'assets/img/boss/wb_gift4.png'],
    // 「劈砍」刀光：itch.io「Pixel Art Animations - Slashes」64/128 像素刀光（9 帧，红色）
    ['slash1_1', 'assets/img/slash1/slash1_1.png'],
    ['slash1_2', 'assets/img/slash1/slash1_2.png'],
    ['slash1_3', 'assets/img/slash1/slash1_3.png'],
    ['slash1_4', 'assets/img/slash1/slash1_4.png'],
    ['slash1_5', 'assets/img/slash1/slash1_5.png'],
    ['slash1_6', 'assets/img/slash1/slash1_6.png'],
    ['slash1_7', 'assets/img/slash1/slash1_7.png'],
    ['slash1_8', 'assets/img/slash1/slash1_8.png'],
    ['slash1_9', 'assets/img/slash1/slash1_9.png'],
    // 普通攻击刀光：同一资源包的 Slash 3（64×64，9 帧）
    ['slash3_1', 'assets/img/slash3/slash3_1.png'],
    ['slash3_2', 'assets/img/slash3/slash3_2.png'],
    ['slash3_3', 'assets/img/slash3/slash3_3.png'],
    ['slash3_4', 'assets/img/slash3/slash3_4.png'],
    ['slash3_5', 'assets/img/slash3/slash3_5.png'],
    ['slash3_6', 'assets/img/slash3/slash3_6.png'],
    ['slash3_7', 'assets/img/slash3/slash3_7.png'],
    ['slash3_8', 'assets/img/slash3/slash3_8.png'],
    ['slash3_9', 'assets/img/slash3/slash3_9.png'],
    // 召唤宠物素材：沙漠敌人包（48×48/帧，idle / walk / attack / hurt 四种动作）
    ['pet_scorpio_idle', 'assets/img/pets/scorpio_idle.png'],
    ['pet_scorpio_walk', 'assets/img/pets/scorpio_walk.png'],
    ['pet_scorpio_attack', 'assets/img/pets/scorpio_attack.png'],
    ['pet_scorpio_hurt', 'assets/img/pets/scorpio_hurt.png'],
    ['pet_hyena_idle', 'assets/img/pets/hyena_idle.png'],
    ['pet_hyena_walk', 'assets/img/pets/hyena_walk.png'],
    ['pet_hyena_attack', 'assets/img/pets/hyena_attack.png'],
    ['pet_hyena_hurt', 'assets/img/pets/hyena_hurt.png'],
    ['pet_snake_idle', 'assets/img/pets/snake_idle.png'],
    ['pet_snake_walk', 'assets/img/pets/snake_walk.png'],
    ['pet_snake_attack', 'assets/img/pets/snake_attack.png'],
    ['pet_snake_hurt', 'assets/img/pets/snake_hurt.png'],
    ['pet_vulture_idle', 'assets/img/pets/vulture_idle.png'],
    ['pet_vulture_walk', 'assets/img/pets/vulture_walk.png'],
    ['pet_vulture_attack', 'assets/img/pets/vulture_attack.png'],
    ['pet_vulture_hurt', 'assets/img/pets/vulture_hurt.png']
  ];

  // 敌人的 32 张帧按类型批量挂进来（加载进度条会一起算进去）
  ENEMY_SPRITES.forEach(function (s) {
    for (let i = 1; i <= s.frames; i++) {
      ASSETS.push([s.key + '_' + i, 'assets/img/enemy/' + s.key + '_' + i + '.png']);
    }
  });

  // 「防御」护盾特效：pipo-btleffect208（192×192，20 帧，淡蓝能量冲击）
  // 导入脚本：tools/make_defend_fx.cjs
  for (let dfi = 1; dfi <= DEFEND_FX_FRAMES; dfi++) {
    ASSETS.push(['defend_' + dfi, 'assets/img/defend/defend_' + dfi + '.png']);
  }

  // 「破空斩」剑气：HolySlash_B（64×64，4 帧）
  // 导入脚本：tools/make_holyslash_fx.cjs
  for (let hsi = 1; hsi <= AIRSLASH_FX_FRAMES; hsi++) {
    ASSETS.push(['holyslash_' + hsi, 'assets/img/holyslash/holyslash_' + hsi + '.png']);
  }

  // 武器图标（60 张，很小，一起进加载进度条）
  for (let wi = 1; wi <= WEAPON_ICON_COUNT; wi++) {
    ASSETS.push(['wicon_' + wi, 'assets/img/weapon/wicon_' + wi + '.png']);
  }

  // 防具图标（头盔 / 衣服 / 鞋子 / 手套 各 10 张，共 40 张，也会进加载进度条）
  Object.keys(GEAR_ICON_SET_BY_SLOT).forEach(function (slotKey) {
    const setName = GEAR_ICON_SET_BY_SLOT[slotKey];
    for (let gi = 1; gi <= GEAR_ICON_COUNT; gi++) {
      ASSETS.push(['gear_' + setName + '_' + gi, 'assets/img/gear/' + setName + '_' + gi + '.png']);
    }
  });

  // 清单里的路径统一补成绝对地址：这样 loadAssets() / 软加载 / 装备图标
  // 都自动带上资源基址，挂到第三方平台也不会再去请求平台域名。
  ASSETS.forEach(function (a) { a[1] = assetUrl(a[1]); });

  // ---------- DOM ----------
  const ui = {
    title: document.getElementById('titleScreen'),
    start: document.getElementById('btnStart'),
    loginScreen: document.getElementById('loginScreen'),
    loginUser: document.getElementById('loginUser'),
    loginPwd: document.getElementById('loginPwd'),
    loginIp: document.getElementById('loginIp'),
    loginError: document.getElementById('loginError'),
    btnLogin: document.getElementById('btnLogin'),
    btnWipe: document.getElementById('btnWipe'),
    introDialog: document.getElementById('introDialog'),
    introDialogBtn: document.getElementById('introDialogBtn'),
    questPopup: document.getElementById('questPopup'),
    questNowStage: document.getElementById('questNowStage'),
    questAccept: document.getElementById('questAccept'),
    wipePopup: document.getElementById('wipePopup'),
    wipeConfirm: document.getElementById('wipeConfirm'),
    wipeCancel: document.getElementById('wipeCancel'),
    titleUser: document.getElementById('titleUser'),
    hud: document.getElementById('hud'),
    wrap: document.getElementById('wrap'),
    menuGrow: document.getElementById('menuGrow'),
    menuTrade: document.getElementById('menuTrade'),
    subGrow: document.getElementById('subGrow'),
    subTrade: document.getElementById('subTrade'),
    dist: document.getElementById('distDisplay'),
    flaskWrap: document.getElementById('flaskWrap'),
    btnFlask: document.getElementById('btnFlask'),
    flaskCount: document.getElementById('flaskCount'),
    goldValue: document.getElementById('goldValue'),
    vitalityValue: document.getElementById('vitalityValue'),
    playerLv: document.getElementById('playerLv'),
    xpFill: document.getElementById('xpFill'),
    xpText: document.getElementById('xpText'),
    onlineTimer: document.getElementById('onlineTimer'),
    btnBreak: document.getElementById('btnBreak'),
    btnOnlineClaim: document.getElementById('btnOnlineClaim'),
    btnGear: document.getElementById('btnGear'),
    btnShop: document.getElementById('btnShop'),
    btnSign: document.getElementById('btnSign'),
    btnAuto: document.getElementById('btnAuto'),
    btnExchange: document.getElementById('btnExchange'),
    legendBar: document.getElementById('legendBar'),
    legendWave: document.getElementById('legendWave'),
    legendKm: document.getElementById('legendKm'),

    controls: document.getElementById('controls'),
    btnMove: document.getElementById('btnMove'),
    btnAttack: document.getElementById('btnAttack'),
    btnSpell: document.getElementById('btnSpell'),
    btnDefend: document.getElementById('btnDefend'),

    battleTint: document.getElementById('battleTint'),
    battleHud: document.getElementById('battleHud'),
    playerHpBar: document.getElementById('playerHpBar'),
    playerMpBar: document.getElementById('playerMpBar'),
    playerMpReserve: document.getElementById('playerMpReserve'),
    playerHpText: document.getElementById('playerHpText'),
    mpText: document.getElementById('mpText'),
    battleLog: document.getElementById('battleLog'),

    spellPopup: document.getElementById('spellPopup'),
    spellList: document.getElementById('spellList'),
    spellClose: document.getElementById('spellClose'),
    spellHint: document.getElementById('spellHint'),
    spellActiveHint: document.getElementById('spellActiveHint'),
    skillTip: document.getElementById('skillTip'),
    itemTip: document.getElementById('itemTip'),
    wbBossTip: document.getElementById('wbBossTip'),
    wbTipName: document.getElementById('wbTipName'),
    wbTipPlace: document.getElementById('wbTipPlace'),
    wbTipImg: document.getElementById('wbTipImg'),
    wbTipDetail: document.getElementById('wbTipDetail'),
    btnDonate: document.getElementById('btnDonate'),
    beianBar: document.getElementById('beianBar'),
    donatePopup: document.getElementById('donatePopup'),
    donateClose: document.getElementById('donateClose'),
    donateAlipay: document.getElementById('donateAlipay'),
    donateWechat: document.getElementById('donateWechat'),
    btnWorldBoss: document.getElementById('btnWorldBoss'),
    wbPopup: document.getElementById('worldBossPopup'),
    wbList: document.getElementById('wbList'),
    wbNote: document.getElementById('wbNote'),
    wbClose: document.getElementById('wbClose'),
    wbClockNow: document.getElementById('wbClockNow'),
    wbClockSrc: document.getElementById('wbClockSrc'),
    wbRewardState: document.getElementById('wbRewardState'),
    wbSchedule: document.getElementById('wbSchedule'),
    wbHelpBtn: document.getElementById('wbHelpBtn'),
    wbHelp: document.getElementById('wbHelp'),
    wbHelpClose: document.getElementById('wbHelpClose'),
    wbRewardPopup: document.getElementById('wbRewardPopup'),
    wbRewardChests: document.getElementById('wbRewardChests'),
    wbRewardNote: document.getElementById('wbRewardNote'),
    wbRewardProgressText: document.getElementById('wbRewardProgressText'),
    wbRewardDots: document.getElementById('wbRewardDots'),
    wbRewardClose: document.getElementById('wbRewardClose'),
    wbBar: document.getElementById('wbBar'),
    wbBarName: document.getElementById('wbBarName'),
    wbBarLv: document.getElementById('wbBarLv'),
    wbBarReq: document.getElementById('wbBarReq'),
    wbHpFill: document.getElementById('wbHpFill'),
    wbHpText: document.getElementById('wbHpText'),
    wbBarTime: document.getElementById('wbBarTime'),
    wbLeave: document.getElementById('wbLeave'),

    lootPopup: document.getElementById('lootPopup'),
    lootList: document.getElementById('lootList'),
    lootClose: document.getElementById('lootClose'),

    gearPopup: document.getElementById('gearPopup'),
    equipSlots: document.getElementById('equipSlots'),
    bagList: document.getElementById('bagList'),
    gearClose: document.getElementById('gearClose'),
    reforgePopup: document.getElementById('reforgePopup'),
    reforgeIcon: document.getElementById('reforgeIcon'),
    reforgeModelName: document.getElementById('reforgeModelName'),
    reforgeModelMeta: document.getElementById('reforgeModelMeta'),
    reforgeList: document.getElementById('reforgeList'),
    reforgeHint: document.getElementById('reforgeHint'),
    btnReforge: document.getElementById('btnReforge'),
    reforgeClose: document.getElementById('reforgeClose'),
    derivedText: document.getElementById('derivedText'),

    btnChar: document.getElementById('btnChar'),
    btnSkill: document.getElementById('btnSkill'),
    btnPet: document.getElementById('btnPet'),
    petPopup: document.getElementById('petPopup'),
    petList: document.getElementById('petList'),
    petNote: document.getElementById('petNote'),
    petClose: document.getElementById('petClose'),
    skillPopup: document.getElementById('skillPopup'),
    skillList: document.getElementById('skillList'),
    skillNote: document.getElementById('skillNote'),
    skillClose: document.getElementById('skillClose'),
    charPopup: document.getElementById('charPopup'),
    charClose: document.getElementById('charClose'),
    charStage: document.getElementById('charStage'),
    charPoints: document.getElementById('charPoints'),
    charPointNote: document.getElementById('charPointNote'),
    charAttrs: document.getElementById('charAttrs'),
    charStats: document.getElementById('charStats'),
    charSlots: document.getElementById('charSlots'),
    charFigureImg: document.getElementById('charFigureImg'),

    shopPopup: document.getElementById('shopPopup'),
    shopList: document.getElementById('shopList'),
    shopGold: document.getElementById('shopGold'),
    shopClose: document.getElementById('shopClose'),

    chestPopup: document.getElementById('chestPopup'),
    chestWeapons: document.getElementById('chestWeapons'),
    chestGem: document.getElementById('chestGem'),
    chestStone: document.getElementById('chestStone'),
    chestClose: document.getElementById('chestClose'),
    deathPopup: document.getElementById('deathPopup'),
    deathInfo: document.getElementById('deathInfo'),
    deathNote: document.getElementById('deathNote'),
    deathLoss: document.getElementById('deathLoss'),
    reviveBtn: document.getElementById('reviveBtn'),

    gemPopup: document.getElementById('gemPopup'),
    gemOptions: document.getElementById('gemOptions'),
    gemClose: document.getElementById('gemClose'),

    socketPopup: document.getElementById('socketPopup'),
    socketOptions: document.getElementById('socketOptions'),
    socketClose: document.getElementById('socketClose'),

    exchangePopup: document.getElementById('exchangePopup'),
    exchangeList: document.getElementById('exchangeList'),
    exchangeNote: document.getElementById('exchangeNote'),
    exchangeClose: document.getElementById('exchangeClose'),

    btnLog: document.getElementById('btnLog'),
    logPopup: document.getElementById('logPopup'),
    logList: document.getElementById('logList'),
    logClose: document.getElementById('logClose'),

    chatPanel: document.getElementById('chatPanel'),
    loadPanel: document.getElementById('loadPanel'),
    loadFill: document.getElementById('loadFill'),
    loadPct: document.getElementById('loadPct'),
    loadTip: document.getElementById('loadTip'),
    chatList: document.getElementById('chatList'),
    chatForm: document.getElementById('chatForm'),
    chatStatus: document.getElementById('chatStatus'),
    identityPopup: document.getElementById('identityPopup'),
    idRealName: document.getElementById('idRealName'),
    idCardNo: document.getElementById('idCardNo'),
    identityError: document.getElementById('identityError'),
    identitySubmit: document.getElementById('identitySubmit'),
    identityCancel: document.getElementById('identityCancel'),
    nickPopup: document.getElementById('nickPopup'),
    nickInput: document.getElementById('nickInput'),
    nickError: document.getElementById('nickError'),
    nickSubmit: document.getElementById('nickSubmit'),
    nickCancel: document.getElementById('nickCancel'),
    nickNow: document.getElementById('nickNow'),
    btnEditNick: document.getElementById('btnEditNick'),
    chatInput: document.getElementById('chatInput'),
    chatToggle: document.getElementById('chatToggle'),
    chatStatus: document.getElementById('chatStatus'),

    btnCodex: document.getElementById('btnCodex'),
    btnGacha: document.getElementById('btnGacha'),
    codexPopup: document.getElementById('codexPopup'),
    codexTiers: document.getElementById('codexTiers'),
    codexGrid: document.getElementById('codexGrid'),
    codexTip: document.getElementById('codexTip'),
    codexProgress: document.getElementById('codexProgress'),
    codexBonusText: document.getElementById('codexBonusText'),
    gachaPopup: document.getElementById('gachaPopup'),
    gachaGold: document.getElementById('gachaGold'),
    gachaPity: document.getElementById('gachaPity'),
    gachaCount: document.getElementById('gachaCount'),
    gachaResults: document.getElementById('gachaResults'),
    gachaRates: document.getElementById('gachaRates'),
    gachaStage: document.getElementById('gachaStage'),
    gachaStageCards: document.getElementById('gachaStageCards'),
    gachaStageTip: document.getElementById('gachaStageTip'),
    gachaHalo: document.getElementById('gachaHalo'),
    gachaOne: document.getElementById('gachaOne'),
    gachaTen: document.getElementById('gachaTen'),
    gachaCostNote: document.getElementById('gachaCostNote'),
    codexClose: document.getElementById('codexClose'),
    gachaClose: document.getElementById('gachaClose'),

    btnElite: document.getElementById('btnElite'),
    eliteCount: document.getElementById('eliteCount'),

    caveBar: document.getElementById('caveBar'),
    caveName: document.getElementById('caveName'),
    caveLeft: document.getElementById('caveLeft'),
    caveKilled: document.getElementById('caveKilled'),
    caveLeave: document.getElementById('caveLeave')
  };

  // ---------- 状态 ----------
  let gameState = 'loading'; // loading | title | walk | battle
  let images = {};
  let loaded = 0;
  let lastTime = performance.now();
  let hudTimer = 0;
  let slashFx = null; // 当前“劈砍”动画：{ t, enemyWx, hit, particles, ... }
  let damagePopups = []; // 飘出的伤害数字
  let pendingVictory = 0; // 击败演出的小延迟，让受击特效可见
  let battleTurn = 'player'; // player | enemy（回合制先后手）
  let enemyTurnTimer = 0;
  let playerIdleTimer = 0; // 玩家先手但 10 秒不行动时敌人抢攻

  /* ---------- 洞府敌人增幅作用在玩家身上的状态 ---------- */
  let playerStunTurns = 0;    // 被「震慑」击晕：跳过 N 个玩家回合
  let playerFrozenTurns = 0;  // 被「寒冰」冰冻：跳过 N 个玩家回合
  let playerBurnStacks = 0;   // 被「点燃」：每层每回合掉 1% 最大生命
  let caveAuraClock = 0;      // 「烈焰光环」每秒结算的计时器
  let corpse = null; // 可搜索的尸体 { worldX, level, searched }
  // 出战宠物列表（可以有多只；按等级从高到低排在玩家身后）
  const pets = [];
  let petSeq = 0;
  let petAttackCount = 0; // 宠物出手次数（调试 / 统计用）
  let gold = 0;
  let flaskCharges = 5; // 左下角血瓶充能（默认 5 层，靠击杀进度补充）
  // 血瓶充能进度：击败 10 个普通敌人 + 1 个精英/传奇敌人 → 充能 +1
  const FLASK_COMMON_NEED = 10;
  const FLASK_ELITE_NEED = 1;
  let flaskKillCommon = 0;
  let flaskKillElite = 0;
  let inventory = [];
  let shopItems = [];
  let lockedShopItems = []; // 被锁定、下次开店仍保留的商品
  let chest = null;         // 首次游戏的宝箱
  let newbieChestUsed = false;   // 新手宝箱每个档案只出一次（随存档保存）

  /* ---------- 任务系统（目前只有"第一个任务"） ----------
   * first：提升至武者大圆满（level 达到 TRIBULATION_STAGE = 武者十境·后期/大圆满） */
  let quests = { first: { accepted: false, done: false } };
  const QUEST_FIRST_STAGE = TRIBULATION_STAGE;
  let identifyingGem = null; // 正在选择效果的空白技能石
  let socketingGem = null;   // 正在镶嵌的技能石
  let usingStone = null;     // 正在使用的主动技能石
  let vitality = 0;          // 活力值：签到获得，自动冒险消耗
  let lastSignDate = '';
  let autoAdventure = false; // 自动冒险开关
  let autoKmLeft = 0;        // 当前 1 点活力剩余的可冒险公里数
  let autoLastX = 0;
  let autoSkillId = null;    // 法术窗口里「激活」的技能：自动冒险时优先使用它
  let restHealTimer = 0;     // 连续休息计时（满 60 秒回一次血）
  let cans = [];             // 马路上滚动的易拉罐
  let canTimer = 0;
  let playerDirtyT = 0;      // 被易拉罐脏水溅到的持续时间
  let playerAtkT = 0;        // >0 表示正在播放玩家出手动作
  let lastPlayerFrameKey = null; // 最近一次真正画出去的玩家帧（调试/测试用）
  let debugHidePlayer = false;   // 调试用：临时隐藏玩家，便于量出角色在屏幕上的位置
  let debugHideEnemies = false;  // 调试用：临时隐藏敌人
  // ---------- 玩家休息姿态（软加载：图不存在就自动回落待机呼吸） ----------
  const playerRestFrames = [];
  let playerResting = false;  // 当前是否处于"休息"状态
  let restPoseIdx = -1;       // 当前随到的休息姿态（0..PLAYER_REST_FRAMES-1）
  let restPoseTimer = 0;      // 距离下次随机换姿态还有多久
  let enemyAnimClock = 0;     // 敌人动画用的自由时钟（每秒 += dt）
  // ---------- 路上的人影：海市蜃楼错觉 ----------
  let mirage = null;         // 当前的虚影 { t, life, worldX, mode, enemyWx, walkT, phase }
  let mirageTimer = rand(8, 16); // 下一次"可能"出现的时间
  const dirtyDrops = [];     // 脏水飞溅粒子
  // 赶路扬尘：跑步时身后扬起的沙尘粒子
  const runDust = [];
  let runDustTimer = 0;
  let wasRunning = false;
  let dustSprite = null;
  let dustDrawn = 0;        // 已绘制过的尘粒数（调试用：确认真的画出来了）
  let eliteCharges = 0;      // 精英挑战次数
  let ally = null;           // “召唤普通敌人”的临时召唤物
  let enemyMinionTurns = 0;  // 精英“召唤普通敌人”的协助攻击次数
  let bloodRebound = false;  // 气血反弹：持续生效的开关状态
  // 气血反弹的血色波纹：{ t, life, maxR, strength }
  const bloodWaves = [];
  let bloodWaveTimer = 0;
  let enemyQuake = 0;        // 传奇BOSS落步/出手的地面余震
  let legendMap = null;      // 传奇BOSS地图：{ wave, startX, km }
  // ---------- 遗迹洞府 ----------
  const caveEntrances = [];  // 马路上的洞府入口：{ def, worldX, used }
  let cave = null;           // 当前所在的洞府：{ def, remaining, total, killed }
  let caveNextX = 0;         // 下一个洞府入口生成的位置
  let caveToken = 0;         // 金色洞府令牌数量（活力兑换获得）
  const battleGroup = [];    // 一场战斗里的敌人（[0] 为当前目标 activeEnemy）
  let onlineSeconds = 0;     // 在线时长（每满 1 小时获得 1 点活力）
  let onlineUiTimer = 0;
  const battleLogHistory = []; // 战斗日志（本次会话）
  let battleLogClock = 0;
  let equipment = {
    helmet: null,
    weapon: null,
    clothes: null,
    shoes: null,
    gloves: null,
    jewelry: null,
    treasure: null   // 灵宝：金色洞府终点掉落
  };
  let itemSeq = 0;
  let weatherTime = 0;
  const sandFar = [];
  const sandNear = [];
  const leaves = [];
  const derived = {
    maxHp: 100,
    maxMp: 30,
    armor: 0,
    flatAtk: 0,
    speedMult: 1,
    res: 0,
    thorns: 0,
    blockPct: 0,
    strength: 0,
    chaosRes: 0,
    critReduce: 0,
    armorPct: 0,
    lifeRegen: 0,
    atkSpeed: 0,
    critChance: 0,
    critDamage: 0,
    // 自由分配的属性点（角色窗口）
    attrStr: 0,
    attrAgi: 0,
    attrInt: 0,
    strengthTotal: 0,
    spellDmgPct: 0,
    // 持续 / 召唤类技能占用的魔力上限
    mpReservePct: 0,
    mpUsable: 30,
    mpReserved: 0
  };

  const input = { moving: false };

 const player = {
   worldX: START_X,
   vx: 0,
   walkT: 0,
   animT: 0,                  // 待机呼吸用的累计时间
   hurtT: 0,
    level: 1,
    xp: 0,
    bottleneck: false,
    attrs: { str: 0, agi: 0, int: 0 }, // 玩家自由分配的三维
    attrPoints: 0                      // 未分配的属性点
  };

  // 玩家在战斗中的属性
  const combat = {
    hp: 100,
    maxHp: 100,
    mp: 30,
    maxMp: 30
  };

  // ---------- 装备槽 / 品级 ----------
  // 活力兑换（vitality 兑换物品）
  const EXCHANGE_ITEMS = [
    { cost: 30, label: '传奇BOSS挑战卷', note: '开启 10km 传奇BOSS地图', make: function () { return makeLegendTicket(); } },
    { cost: 30, label: '主动技能石', note: '升级/学习主动技能', make: function () { return makeActiveSkillStone(); } },
    { cost: 20, label: '随机辅助技能石', note: '镶嵌到主动技能', make: function () { return gemOptionToItem(GEM_OPTIONS[randInt(0, GEM_OPTIONS.length - 1)]); } },
    { cost: 30, label: '宠物技能书', note: '有几率提升宠物品级', make: function () { return makePetSkillBook(); } },
    {
      cost: CAVE_TOKEN_COST,
      label: '金色洞府令牌',
      note: '进入金色洞府所需（每次消耗 1 枚）',
      grant: function () { caveToken += 1; },
      owned: function () { return '已持有 ' + caveToken + ' 枚'; }
    }
  ];

  // 玩家已学会的技能/法术。技能书只能通过击败 BOSS 掉落获得。
 const knownSkills = [
   {
     id: 'slash',
       name: '劈砍',
       desc: '挥动武器劈向敌人，造成 34~48 点伤害。',
       dmg: [34, 48],
       cost: 10, // 施放需要消耗魔力（打空时可以用不计魔力的普通攻击）
       type: 'melee',
       level: 1,
     sockets: []
   }
 ];

  // 初始技能（新档案 / 读档时没有技能数据时用）
  function starterSkill() {
    return {
      id: 'slash', name: '劈砍', desc: '挥动武器劈向敌人，造成 34~48 点伤害。',
      dmg: [34, 48], cost: 10, type: 'melee', level: 1, sockets: []
    };
  }
  // 已经移除的技能（召唤宠物 / 横扫 / 强攻）：老存档里如果留着，读档时直接丢掉
  // 注：法杖灵宠本身还在（装备法杖宠物依旧自动现身），只是不再占用一个技能格
  const REMOVED_SKILL_IDS = ['summonPet', 'sweep', 'powerStrike'];
  function isRemovedSkillId(id) { return REMOVED_SKILL_IDS.indexOf(id) >= 0; }

  // 读档续玩的起点：玩家上次的里程（除非阵亡 / 新建档案，否则接着往前走）
  let resumeWorldX = null;

  let defeatedEnemies = 0;
  let activeEnemy = null; // 当前路上的敌人（击败后生成下一个）

  // ---------- 小工具 ----------
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
  function rand(a, b) { return a + Math.random() * (b - a); }
  function randInt(a, b) { return Math.floor(rand(a, b + 1)); }
  function wrapInt(v, n) { return ((v % n) + n) % n; }
  function wrapX(v, span) { return ((v % span) + span) % span; }
  function hash01(v) {
    const s = Math.sin(v * 127.1 + 311.7) * 43758.5453;
    return s - Math.floor(s);
  }
  function roundRect(x, y, w, h, r) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
  }

  // 在角色身侧弹出伤害数字（屏幕坐标，随画面自然缩放）
  function addDamagePopup(sx, sy, dmg, color, positive) {
    damagePopups.push({
      x: sx + rand(-6, 8),
      y: sy + rand(-8, 6),
      t: 0,
      life: 0.9,
      dmg: Math.max(1, Math.ceil(dmg)),
      color: color || '#ff5940',
      positive: !!positive
    });
  }

  // 纯文字飘字（例如“格挡”）
  function addTextPopup(sx, sy, text, color) {
    damagePopups.push({
      x: sx + rand(-6, 8),
      y: sy + rand(-8, 6),
      t: 0,
      life: 0.9,
      text: text,
      color: color || '#ffd9a8'
    });
  }

  function rollRarity() {
    const r = Math.random();
    if (r < 0.2) return 'rare';   // 黄色：3~6 条
    if (r < 0.55) return 'magic'; // 蓝色：1~2 条
    return 'common';
  }

  // 掉落品级与敌人品级绑定：普通敌人绝不掉黄色
  // 装备掉落品级：永远不超过敌人自己的品级
  //   tier     敌人品级        允许掉出的最高品级
  //   common   普通            白色
  //   magic    蓝色            蓝色
  //   elite    精英            黄色
  //   legend   传奇            黄色（暗金/灵宝只能从洞府终点等特殊途径拿）
  // force=true 时"必掉装备"（洞府内的怪走这条路），此时按品级加权抽一件，而不是有概率空手
  function rollGearForTier(tier, force) {
    const r = Math.random();
    if (tier === 'magic') {
      if (r < 0.3) return 'common';
      if (r < 0.4) return 'magic';
      return force ? (Math.random() < 0.68 ? 'common' : 'magic') : null;
    }
    if (tier === 'elite') {
      if (r < 0.3) return 'common';
      if (r < 0.4) return 'magic';
      if (r < 0.45) return 'rare';
      return force ? (Math.random() < 0.5 ? 'common' : (Math.random() < 0.6 ? 'magic' : 'rare')) : null;
    }
    if (tier === 'legend') {
      if (r < 0.3) return 'common';
      if (r < 0.5) return 'magic';
      if (r < 0.65) return 'rare';
      return force ? (Math.random() < 0.4 ? 'common' : (Math.random() < 0.58 ? 'magic' : 'rare')) : null;
    }
    // 普通敌人：只掉白色装备
    if (r < 0.3) return 'common';
    return force ? 'common' : null;   // 洞府里必掉，且只能是白色
  }

  // 一次击杀掉几件装备、各是什么品级（返回数组，可能为空）
  //   野外：按 rollGearForTier 概率掉 0~1 件
  //   洞府：必掉；其中精英怪是「黄色必掉 + 额外再掉一件（白/蓝）」
  function rollGearRarities(tier, force) {
    const t = tier || 'common';
    if (!force) {
      const r = rollGearForTier(t, false);
      return r ? [r] : [];
    }
    if (t === 'elite') {
      // 黄色装备必然掉落，且不受概率影响
      return ['rare', Math.random() < 0.68 ? 'common' : 'magic'];
    }
    return [rollGearForTier(t, true)];
  }

  // 装备词缀涉及的所有属性字段（洗练前先清零，避免旧值累加）
  const GEAR_STAT_KEYS = [
    'hp', 'armor', 'atk', 'speedPct', 'mp', 'res', 'thorns', 'blockPct', 'strength',
    'agility', 'intelligence',
    'chaosRes', 'critReduce', 'armorPct', 'lifeRegen', 'atkSpeed', 'critChance', 'critDamage'
  ];

  const lastAffixRolls = [];   // 调试用：最近一次词缀随机的明细（key / 档位 / 数值）

  // 装备价格：只按品级在区间内浮动（不再按词缀强度算，避免首饰因为词缀强而普遍更贵）
  const GEAR_PRICE_BANDS = {
    common: [100, 300],   // 白色
    magic: [300, 500],    // 蓝色
    rare: [500, 1000]     // 黄色稀有
  };

  function rollGearPrice(rarity) {
    const band = GEAR_PRICE_BANDS[rarity] || GEAR_PRICE_BANDS.common;
    return randInt(band[0], band[1]);
  }

  // 词缀库分配：盾牌 / 头盔衣服 / 手套 / 首饰 / 其它（生成与洗练共用）
  function affixPoolFor(slot, weaponType) {
    if (weaponType === '盾牌') return SHIELD_AFFIXES;
    if (slot === 'helmet' || slot === 'clothes') return ARMOUR_AFFIXES;
    if (slot === 'gloves') return GLOVES_AFFIXES;
    if (slot === 'shoes') return SHOES_AFFIXES;   // 鞋子专用词缀库：分档位、不含攻击伤害
    if (slot === 'jewelry') return JEWELRY_AFFIXES;
    if (slot === 'treasure') return JEWELRY_AFFIXES; // 灵宝的常规词缀沿用首饰（特殊属性另算）
    // 其余（长弓/短剑/爪/法杖/鞋子）用通用词缀，武器不出荆棘
    return GENERIC_AFFIXES.filter(function (mod) {
      if (mod.key !== 'thorns') return true;
      return slot !== 'weapon';
    });
  }

  // 按装备自己的等级 + 品级重新随机全部词缀。
  // 「生成装备」与「洗练」共用这一套规则：等级只决定"能抽到哪一档"，
  // 所以 1 级装备洗练多少次也只会落在 1-10 级的档位区间里。
// 词缀 → 中文描述
  const AFFIX_TEXT = {
    hp: ['最大生命 +', ''],
    armor: ['护甲 +', ''],
    atk: ['攻击伤害 +', ''],
    speedPct: ['移动速度提高 ', '%'],
    mp: ['最大魔力 +', ''],
    res: ['全元素抗性 +', '%'],
    thorns: ['荆棘伤害 +', ''],
    blockPct: ['格挡几率 +', '%'],
    strength: ['力量 +', ''],
    agility: ['敏捷 +', ''],
    intelligence: ['智力 +', ''],
    chaosRes: ['混沌抗性 +', '%'],
    critReduce: ['暴击伤害降低 ', '%'],
    lifeRegen: ['生命每回合再生 +', ''],
    atkSpeed: ['攻击速度提高 ', '%'],
    critChance: ['暴击率 +', '%'],
    critDamage: ['暴击伤害 +', '%']
  };

  function modToText(mod) {
    if (mod.key === 'hybridArmor') {
      return '护甲提高 ' + mod.value + '%，生命上限 +' + (mod.value2 || 0);
    }
    const t = AFFIX_TEXT[mod.key];
    return t ? t[0] + mod.value + t[1] : (mod.key + ' +' + mod.value);
  }

  // 反向解析：老存档里只有 detail 文本、没有逐条词缀数据时，按文本还原成词缀（数值不变）
  function affixTextToMod(text) {
    const hy = text.match(/^护甲提高 (\d+)%，生命上限 \+(\d+)$/);
    if (hy) return { key: 'hybridArmor', value: Number(hy[1]), value2: Number(hy[2]), bandIdx: 0, locked: false };
    for (const key in AFFIX_TEXT) {
      if (!Object.prototype.hasOwnProperty.call(AFFIX_TEXT, key)) continue;
      const pre = AFFIX_TEXT[key][0];
      const suf = AFFIX_TEXT[key][1];
      if (text.indexOf(pre) !== 0) continue;
      if (suf && text.slice(-suf.length) !== suf) continue;
      const num = parseInt(text.slice(pre.length).replace('%', ''), 10);
      if (!isNaN(num)) return { key: key, value: num, value2: 0, bandIdx: 0, locked: false };
    }
    return null;
  }

  // 随机一条词缀。等级只决定"最高能到第几档"：1 级装备永远只落在第 1 档（1-10 级区间）
  function rollOneMod(slot, weaponType, level, rarity, pool) {
    const rar = RARITY_DEFS[rarity] || RARITY_DEFS.common;
    const tier = rar.tier;
    const statMult = 1 + (level - 1) * 0.08 + (tier - 1) * 0.22;
    const mod = pool[randInt(0, pool.length - 1)];
    let value = 1;
    let value2 = 0;
    let usedBand = -1;
    if (mod.bands) {
      const maxBand = Math.min(mod.bands.length - 1, Math.floor((level - 1) / AFFIX_BAND_SIZE));
      const bandIdx = randInt(0, maxBand);
      usedBand = bandIdx;
      const range = mod.bands[bandIdx];
      value = randInt(range[0], range[1]);
      if (mod.bands2) {
        const range2 = mod.bands2[Math.min(bandIdx, mod.bands2.length - 1)];
        value2 = randInt(range2[0], range2[1]);
      }
    } else {
      let base;
      if (mod.key === 'atk') {
        if (slot !== 'weapon') base = 2 + level * 0.35;
        else if (weaponType === '长弓') base = 5 + level * 0.95;
        else if (weaponType === '短剑') base = 4 + level * 0.8;
        else if (weaponType === '爪') base = 3 + level * 0.72;
        else base = 2 + level * 0.6;
      } else if (mod.key === 'hp') {
        base = (slot === 'helmet' || slot === 'clothes' ? 5 : 3) + level * 0.5;
      } else if (mod.key === 'armor') {
        base = (slot === 'clothes' ? 6 : slot === 'helmet' ? 4 : 3) + level * 0.45;
      } else if (mod.key === 'speedPct') {
        base = (slot === 'shoes' ? 4 : weaponType === '爪' ? 3 : 2) + level * 0.18;
      } else if (mod.key === 'mp') {
        base = (weaponType === '法杖' ? 5 : 4) + level * 0.55;
      } else if (mod.key === 'thorns') {
        base = 2 + level * 0.6;
      } else {
        base = (weaponType === '法杖' ? 3 : 2) + level * 0.2;
      }
      value = Math.max(1, Math.round((base + rand(0, base * 0.9)) * statMult));
    }
    return { key: mod.key, value: value, value2: value2, bandIdx: usedBand, locked: false };
  }

  // 把逐条词缀汇总成：属性数值 / 中文描述 / 价格（生成、洗练、老存档迁移都走这里）
  function applyItemMods(item) {
    const rar = RARITY_DEFS[item.rarity] || RARITY_DEFS.common;
    const rarity = RARITY_DEFS[item.rarity] ? item.rarity : 'common';
    GEAR_STAT_KEYS.forEach(function (k) { item[k] = 0; });
    const texts = [];
    (item.mods || []).forEach(function (mod) {
      if (mod.key === 'speedPct') item.speedPct = Math.min(15, mod.value);
      else if (mod.key === 'hybridArmor') {
        item.armorPct = (item.armorPct || 0) + mod.value;
        item.hp = (item.hp || 0) + (mod.value2 || 0);
      } else {
        item[mod.key] = (item[mod.key] || 0) + mod.value;
      }
      texts.push(modToText(mod));
    });
    item.detail = texts.join('　');
    item.rarityName = rar.name;
    item.rarityColor = rar.color;
  // 价格只在生成时随品级 roll 一次，洗练不会改价（稀有度没变）
    if (!item.price) item.price = rollGearPrice(rarity);
    return item;
  }

  // ---------- 灵宝（金色洞府终点掉落的传奇装备）----------
  // 除了常规词缀，还会带一条"特殊属性"：改机制 / 给特殊收益
  function makeTreasureItem(level) {
    const it = makeEquipment(Math.max(1, Math.round(level || 1)), 'legend', 'treasure');
    return attachTreasureSpecial(it, pickTreasureSpecial());
  }

  // 灵宝特殊属性的抽取：普通灵宝等概率，带 rare 标记的（破境符诏）出现率极低
  const TREASURE_RARE_WEIGHT = 0.14;   // ≈ 7 件普通灵宝里才见 1 件的量级（约 2%）
  function pickTreasureSpecial() {
    const total = TREASURE_SPECIALS.reduce(function (s, sp) {
      return s + (sp.rare ? TREASURE_RARE_WEIGHT : 1);
    }, 0);
    let r = Math.random() * total;
    for (let i = 0; i < TREASURE_SPECIALS.length; i++) {
      const w = TREASURE_SPECIALS[i].rare ? TREASURE_RARE_WEIGHT : 1;
      r -= w;
      if (r <= 0) return TREASURE_SPECIALS[i];
    }
    return TREASURE_SPECIALS[0];
  }

  // 给一件灵宝挂上特殊属性（掉落与调试共用同一套逻辑）
  function attachTreasureSpecial(it, sp) {
    it.special = { key: sp.key, name: sp.name, kind: sp.kind, desc: sp.desc };
    // 「蓝宝灵韵」：增幅比例 50%~150%，每件灵宝随机 roll 一次
    if (sp.key === 'magicAmp') {
      it.special.roll = randInt(50, 150);
      it.special.desc = '已装备的蓝色（魔法）装备属性增幅 +' + it.special.roll + '%';
    }
    if (sp.kind === 'skill') {
      const pool = ACTIVE_SKILL_POOL;
      const def = pool[randInt(0, pool.length - 1)];
      it.special.skillId = def.id;
      it.special.skillName = def.name;
      it.special.desc = '附带主动技能「' + def.name + '」（装备后自动学会）';
    }
    it.detail = it.detail + '　【灵宝·' + it.special.name + '】' + it.special.desc;
    it.name = '灵宝 · ' + it.special.name;
    return it;
  }

  // 当前穿戴的灵宝是否带某条特殊属性
  function treasureSpecial(key) {
    const it = equipment.treasure;
    return !!(it && it.special && it.special.key === key);
  }

  // 老存档兼容：把只有文本的装备反解成逐条词缀（数值保持一致）
  function ensureItemMods(item) {
    if (!item || item.type !== 'equipment') return item;
    ensureItemIcon(item);   // 老存档的装备顺便补一张美术图标
    if (Array.isArray(item.mods) && item.mods.length) return item;
    const mods = [];
    String(item.detail || '').split('　').filter(Boolean).forEach(function (t) {
      const m = affixTextToMod(t);
      if (m) mods.push(m);
    });
    if (mods.length) {
      item.mods = mods;
      item.modCount = mods.length;
      applyItemMods(item);
    }
    return item;
  }

  /* 重新随机装备词缀。
   * 规则：
   *   · 词缀数量不变（原有 3 条就还是 3 条，不会洗成 6 条）
   *   · 被锁定的词缀整条保留（key / 数值 / 档位都不动）
   *   · 其余词缀按「装备等级」对应的档位重掷 —— 1 级装备永远洗不出高等级档位
   */
  function rollItemAffixes(item, opts) {
    const options = opts || {};
    const slot = item.slot;
    const level = Math.max(1, Math.round(item.level || 1));
    const rarity = RARITY_DEFS[item.rarity] ? item.rarity : 'common';
    const weaponType = item.weaponType || null;
    const pool = affixPoolFor(slot, weaponType);
    const old = Array.isArray(item.mods) ? item.mods : [];

    let modCount = old.length;
    if (!modCount) {
      modCount = rarity === 'common' ? 1 : rarity === 'magic' ? randInt(2, 3) : randInt(3, 6);
    }
    item.modCount = modCount;

    lastAffixRolls.length = 0;
    const mods = [];
    for (let i = 0; i < modCount; i++) {
      const prev = old[i];
      if (prev && prev.locked && options.ignoreLocks !== true) {
        mods.push(prev);           // 锁定的原样保留
        continue;
      }
      const fresh = rollOneMod(slot, weaponType, level, rarity, pool);
      mods.push(fresh);
      lastAffixRolls.push({ key: fresh.key, level: level, bandIdx: fresh.bandIdx, value: fresh.value, value2: fresh.value2 });
    }
    item.mods = mods;
    applyItemMods(item);
    return item;
  }

  // 随机生成一件装备，词缀方向参考流放之路：防御、生命、攻击、移速等
  function makeEquipment(level, forcedRarity, forcedSlot, forcedWeaponKind) {
    // 灵宝槽不参与随机：只能由金色洞府终点掉落（makeTreasureItem）
    const slotNames = Object.keys(SLOT_DEFS).filter(function (s) { return s !== 'treasure'; });
    const slot = forcedSlot && SLOT_DEFS[forcedSlot]
      ? forcedSlot
      : slotNames[randInt(0, slotNames.length - 1)];
    const def = SLOT_DEFS[slot];
    const rarity = forcedRarity && RARITY_DEFS[forcedRarity]
      ? forcedRarity
      : rollRarity();
    const rar = RARITY_DEFS[rarity];
    const tier = rar.tier;
    level = Math.max(1, Math.round(level));
    let baseName;
    let icon = def.icon;
    let weaponType = null;
    if (slot === 'weapon') {
      let kind = null;
      if (forcedWeaponKind) {
        for (let i = 0; i < WEAPON_KINDS.length; i++) {
          if (WEAPON_KINDS[i].name === forcedWeaponKind) kind = WEAPON_KINDS[i];
        }
      }
      if (!kind) kind = WEAPON_KINDS[randInt(0, WEAPON_KINDS.length - 1)];
      baseName = kind.name;
      icon = kind.icon;
      weaponType = kind.name;
    } else {
      baseName = def.base[Math.min(def.base.length - 1, Math.floor(level / 2))];
    }
    const item = {
      id: 'eq' + (++itemSeq),
      type: 'equipment',
      slot: slot,
      rarity: rarity,
      name: rar.name + ' ' + baseName,
      icon: icon,
      // 随机分一张美术图标：武器用武器图，头盔/衣服/鞋子/手套用服装图
      iconSet: slot === 'weapon' ? 'weapon' : (GEAR_ICON_SET_BY_SLOT[slot] || null),
      iconImg: slot === 'weapon' ? randInt(1, WEAPON_ICON_COUNT)
        : (GEAR_ICON_SET_BY_SLOT[slot] ? randInt(1, GEAR_ICON_COUNT) : 0),
      weaponType: weaponType,
      level: level,
      hp: 0,
      armor: 0,
      atk: 0,
      speedPct: 0,
      mp: 0,
      res: 0,
      thorns: 0,
      blockPct: 0,
      strength: 0,
      agility: 0,
      intelligence: 0,
      chaosRes: 0,
      critReduce: 0,
      armorPct: 0,
      lifeRegen: 0,
      atkSpeed: 0,
      critChance: 0,
      critDamage: 0
    };

    // 词缀按等级档位随机（生成装备 / 洗练共用同一套规则）
    rollItemAffixes(item);
    return item;
  }

  // ---------- 物品图标渲染 ----------
  // 武器 / 防具用美术图标（<img>），其它物品/素材没图就退回原来的文字图标
  function itemIconUrl(item) {
    if (!item) return null;
    ensureItemIcon(item);
    if (!item.iconImg) return null;
    return gearIconUrl(item.iconSet, item.iconImg);
  }

  function fillItemIcon(el, item, glyph) {
    if (!el) return;
    const url = itemIconUrl(item);
    el.textContent = '';
    if (url) {
      const img = document.createElement('img');
      // 武器是 32×32 像素图（锐利），服装素材是高清插画（平滑缩放）
      img.className = 'itemIconImg' + (item && item.iconSet && item.iconSet !== 'weapon' ? ' gearArt' : '');
      img.alt = '';
      img.src = url;
      el.appendChild(img);
      el.classList.add('hasImg');
    } else {
      el.classList.remove('hasImg');
      el.textContent = glyph || (item && item.icon) || '?';
    }
    return el;
  }

  // ---------- 世界BOSS 独特圣物（森特欧斯）----------
  // 只要带在背包里就生效（不需要装备），是打世界BOSS的极小概率掉落
  const SANTOS_RELIC_CHANCE = 0.001;    // 0.001%（十万分之一），每次领取世界BOSS奖励时判定
  const SANTOS_RELICS = {
    skull: {
      key: 'skull', name: '森特欧斯的头骨', icon: '颅', intBonus: 200,
      detail: '免疫中毒 · 智力 +200 · 附带主动技能「森特欧斯的囚笼」'
    },
    totem: {
      key: 'totem', name: '森特欧斯的图腾', icon: '柱', intBonus: 50,
      detail: '免疫控制 · 智力 +50 · 每回合随机召唤陨石 / 树木 / 有毒的孢子攻击敌人'
    }
  };

  function makeSantosRelic(key) {
    const def = SANTOS_RELICS[key] || SANTOS_RELICS.skull;
    return {
      id: 'relic' + (++itemSeq),
      type: 'santosRelic',
      relic: def.key,
      name: def.name,
      icon: def.icon,
      detail: def.detail,
      rarityName: '圣物',
      rarityColor: '#ff8ad4'
    };
  }

  function hasSantosRelic(key) {
    return inventory.some(function (it) {
      return it && it.type === 'santosRelic' && it.relic === key;
    });
  }

  // 圣物提供的智力总和（头骨 +200 / 图腾 +50）
  function santosRelicIntBonus() {
    let n = 0;
    Object.keys(SANTOS_RELICS).forEach(function (k) {
      if (hasSantosRelic(k)) n += SANTOS_RELICS[k].intBonus;
    });
    return n;
  }

  function makeMaterial(level) {
    const names = ['粗布', '皮革', '铁矿石', '精木料', '魔化尘'];
    return {
      id: 'mat' + (++itemSeq),
      type: 'material',
      name: names[randInt(0, names.length - 1)],
      icon: '料',
      detail: '制作装备的材料',
      rarityName: '材料',
      rarityColor: '#a8d1a8'
    };
  }

  function makePotion(level) {
    const heal = Math.round(24 + level * 2 + rand(0, 8));
    return {
      id: 'pot' + (++itemSeq),
      type: 'potion',
      name: '回血药瓶',
      icon: '药',
      detail: '恢复 ' + heal + ' 点生命',
      heal: heal,
      rarityName: '道具',
      rarityColor: '#ff9d86'
    };
  }

  // ---------- 被动技能石 ----------
  function makeEmptyGem() {
    return {
      id: 'gem' + (++itemSeq),
      type: 'gem',
      identified: false,
      name: '空白被动技能石',
      icon: '石',
      detail: '右键点击挑选被动效果',
      rarityColor: '#d9d3c4'
    };
  }

  function gemOptionToItem(opt) {
    const value = opt.support ? 0 : randInt(opt.min, opt.max);
    return {
      id: 'gem' + (++itemSeq),
      type: 'gem',
      identified: true,
      effect: opt.id,
      value: value,
      onlySkill: opt.onlySkill || null,
      projectileOnly: !!opt.projectileOnly,
      support: !!opt.support,
      name: (opt.support ? '辅助技能石：' : '被动技能石：') + opt.name,
      icon: '石',
      detail: opt.support ? (opt.short || opt.desc || opt.name) : (opt.name + ' +' + value + opt.unit),
      desc: opt.desc || '',
      rarityColor: '#8fd7ff'
    };
  }

  function socketedGems() {
    const list = [];
    knownSkills.forEach(function (skill) {
      (skill.sockets || []).forEach(function (gem) { list.push(gem); });
    });
    return list;
  }

  // ---------- 技能的镶嵌槽：默认 3 个，最多 6 个，每开一个槽 500 金币 ----------
  const SOCKET_SLOTS_DEFAULT = 3;
  const SOCKET_SLOTS_MAX = 6;
  const SOCKET_SLOT_COST = 500;

  function socketSlotsOf(skill) {
    const n = Math.floor(Number(skill && skill.socketSlots) || SOCKET_SLOTS_DEFAULT);
    return clamp(n, SOCKET_SLOTS_DEFAULT, SOCKET_SLOTS_MAX);
  }

  // 花 500 金币开一个槽（存到技能上，随存档保留）
  function buySocketSlot(skill) {
    if (!skill) return false;
    const slots = socketSlotsOf(skill);
    if (slots >= SOCKET_SLOTS_MAX) {
      showToast('「' + skill.name + '」的镶嵌槽已经开到上限（' + SOCKET_SLOTS_MAX + ' 个）。');
      return false;
    }
    if (gold < SOCKET_SLOT_COST) {
      showToast('金币不足：开一个镶嵌槽需要 ' + SOCKET_SLOT_COST + ' 金币（当前 ' + gold + '）。');
      return false;
    }
    gold -= SOCKET_SLOT_COST;
    skill.socketSlots = slots + 1;
    saveGame();
    updateGold();
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    if (!ui.socketPopup.classList.contains('hidden')) renderSocketOptions();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('「' + skill.name + '」镶嵌槽 +1（' + (slots + 1) + ' / ' + SOCKET_SLOTS_MAX +
      '），花费 ' + SOCKET_SLOT_COST + ' 金币。');
    return true;
  }

  // 汇总某几类被动技能石的百分比 / 数值
  function gemSum(effectIds) {
    let sum = 0;
    socketedGems().forEach(function (gem) {
      if (effectIds.indexOf(gem.effect) >= 0) sum += gem.value || 0;
    });
    return sum;
  }

  function gemDamageMult(kind) {
    if (kind === 'pet') {
      return 1 + skillGemSum('summonPet', ['summonDamage']) / 100;
    }
    // 普通攻击与劈砍共用「劈砍」技能上的辅助宝石
    return 1 + skillGemSum('slash', ['weaponDamage', 'meleeDamage', 'spellDamage']) / 100;
  }

  function skillGemSum(skillId, effectIds) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return 0;
    let sum = 0;
    (skill.sockets || []).forEach(function (gem) {
      if (effectIds.indexOf(gem.effect) >= 0) sum += gem.value || 0;
    });
    return sum;
  }

  /* ============================================================
   * 投射物 / 通用辅助技能石（参考流放之路的辅助宝石）
   *   多重投射物：伤害总降 25%、技能速度总降 20%、多发射 2 个投射物
   *   投射物加速：投射物速度总增 40%
   *   处决：你低血 或 敌人低血（≤ 最大生命 50%）时，击中伤害总增 30%
   * 说明：目前游戏里"真的会发射投射物"的主动技能只有「破空斩」（剑气），
   *       以后给火球术 / 闪电箭做了飞行物，把它们加进 PROJECTILE_SKILLS 即可。
   * ============================================================ */
  const PROJECTILE_SKILLS = ['airSlash'];
  const MULTI_PROJ_EXTRA = 2;        // 额外投射物数量
  const MULTI_PROJ_DMG_PCT = -25;    // 被辅助技能伤害总降
  const MULTI_PROJ_SPEED_PCT = -20;  // 被辅助技能速度总降
  const PROJ_SPEED_PCT = 40;         // 投射物速度总增
  const LOW_LIFE_PCT = 50;           // 低血门槛：≤ 最大生命 50%
  const EXECUTE_DMG_PCT = 30;        // 处决：每一条 +30%

  function skillById(id) {
    return knownSkills.filter(function (s) { return s.id === id; })[0] || null;
  }

  function skillHasGem(skillId, effect) {
    const skill = skillById(skillId);
    return !!(skill && (skill.sockets || []).some(function (g) { return g.effect === effect; }));
  }

  function isProjectileSkill(skillId) {
    return PROJECTILE_SKILLS.indexOf(skillId) >= 0;
  }

  // 旧档案里存下来的技能石可能没有 projectileOnly 字段，按 effect 回查一遍
  function isProjectileOnlyGem(gem) {
    if (!gem) return false;
    if (gem.projectileOnly) return true;
    const opt = GEM_OPTIONS.filter(function (o) { return o.id === gem.effect; })[0];
    return !!(opt && opt.projectileOnly);
  }

  // 低血：≤ 最大生命 50%（玩家血量见战斗状态，敌人血量见 maxHp）
  function playerLowLife() {
    return combat.maxHp > 0 && combat.hp <= combat.maxHp * LOW_LIFE_PCT / 100;
  }

  function enemyLowLife(e) {
    return !!(e && e.maxHp > 0 && e.hp <= e.maxHp * LOW_LIFE_PCT / 100);
  }

  // 被辅助技能的伤害总增/总降（同一层相加，不做叠乘）
  function supportDamagePct(skillId, enemy) {
    let pct = 0;
    if (skillHasGem(skillId, 'multipleProjectiles')) pct += MULTI_PROJ_DMG_PCT;
    if (skillHasGem(skillId, 'execute')) {
      if (playerLowLife()) pct += EXECUTE_DMG_PCT;
      if (enemyLowLife(enemy)) pct += EXECUTE_DMG_PCT;
    }
    return pct;
  }

  function supportDamageMult(skillId, enemy) {
    return 1 + supportDamagePct(skillId, enemy) / 100;
  }

  // 投射物数量：基础数量 + 多重投射物的额外数量
  function projectileCountOf(skillId, base) {
    return base + (skillHasGem(skillId, 'multipleProjectiles') ? MULTI_PROJ_EXTRA : 0);
  }

  // 投射物速度倍率（投射物加速）
  function projectileSpeedMult(skillId) {
    return 1 + (skillHasGem(skillId, 'projectileSpeed') ? PROJ_SPEED_PCT : 0) / 100;
  }

  // 技能速度倍率（多重投射物：总降 20% → 出手间隔 ×1.25）
  function skillSpeedMult(skillId) {
    return Math.max(0.2, 1 + (skillHasGem(skillId, 'multipleProjectiles') ? MULTI_PROJ_SPEED_PCT : 0) / 100);
  }

  // 主动技能每提升 1 级，效果提高 12%
  // 「阳爪」改成 6 次连击：每次 6~10 点基础伤害（逐次独立结算/显示）
  const YANG_CLAW_HITS = 6;
  const YANG_CLAW_MIN = 6;
  const YANG_CLAW_MAX = 10;
 function skillLevelMult(skill) {
   const lv = (skill && skill.level) || 1;
   return 1 + 0.12 * (lv - 1);
 }

  // 主动伤害技能（劈砍 / 阳爪）：每提升 1 级，基础伤害提高 10%
  const SKILL_DAMAGE_LV_STEP = 0.10;
  function skillDamageLevelMult(skill) {
    const lv = (skill && skill.level) || 1;
    const step = (skill && (skill.id === 'slash' || skill.id === 'yangClaw'))
      ? SKILL_DAMAGE_LV_STEP
      : 0.12;
    return 1 + step * (lv - 1);
  }

  // 技能格 / 悬停提示里显示的「等级加成 +N%」（伤害技能按伤害加成算）
  function levelBonusPct(skill) {
    const mult = (skill && skill.dmg) ? skillDamageLevelMult(skill) : skillLevelMult(skill);
    return Math.round((mult - 1) * 100);
  }

  // 玩家暴击：暴击率来自手套等装备，暴击伤害 = 1.5 倍 + 暴击伤害加成
  function rollPlayerDamage(base) {
    const isCrit = derived.critChance > 0 && Math.random() * 100 < derived.critChance;
    const dmg = isCrit
      ? Math.max(1, Math.round(base * (1.5 + derived.critDamage / 100)))
      : Math.max(1, Math.round(base));
    return { dmg: dmg, crit: isCrit };
  }

  // ---------- 休息回血：站在路上不动（休息）满 1 分钟，恢复 3% 最大生命 ----------
  const REST_HEAL_INTERVAL = 60;   // 秒
  const REST_HEAL_PCT = 0.03;      // 3% 最大生命

  function restHealOnce() {
    const heal = Math.max(1, Math.round(combat.maxHp * REST_HEAL_PCT));
    if (combat.hp >= combat.maxHp) return 0;
    const before = combat.hp;
    combat.hp = Math.min(combat.maxHp, combat.hp + heal);
    const real = Math.round(combat.hp - before);
    addDamagePopup(player.worldX - cameraX() - 82, GROUND_Y - 172, real, '#6dff8d', true);
    updateBattleHud();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return real;
  }

  function updateRestHeal(dt) {
    // 只在路上、没有移动、也没有出手动作时算「休息」
    const resting = gameState === 'walk' && !deathActive && !input.moving &&
      player.vx <= 0 && playerAtkT <= 0;
    if (!resting) {
      restHealTimer = 0;
      return;
    }
    restHealTimer += dt;
    while (restHealTimer >= REST_HEAL_INTERVAL) {
      restHealTimer -= REST_HEAL_INTERVAL;
      const healed = restHealOnce();
      if (healed > 0) {
        showToast('休息恢复：生命 +' + healed + '（' + Math.round(REST_HEAL_PCT * 100) + '% 最大生命）');
        pushBattleLog('休息 1 分钟：恢复 ' + healed + ' 点生命', 'reward');
      }
    }
  }

  // 生命每回合再生
  let regenUiTimer = 0;
  function applyLifeRegen(dt) {
    // 「生命再生」现在是按回合结算的，不再按秒持续回血（见 applyTurnRegen）
    return;
  }

  // 生命再生：每个战斗回合开始时结算一次
  function applyTurnRegen() {
    if (derived.lifeRegen <= 0 || combat.hp >= combat.maxHp) return;
    const heal = Math.min(derived.lifeRegen, combat.maxHp - combat.hp);
    if (heal <= 0) return;
    combat.hp = Math.min(combat.maxHp, combat.hp + heal);
    addDamagePopup(
      player.worldX - cameraX() - 82,
      GROUND_Y - 168,
      Math.round(heal),
      '#6dff8d',
      true
    );
    pushBattleLog('生命再生：本回合恢复 ' + Math.round(heal) + ' 点生命', 'reward');
    updateBattleHud();
  }

  // ---------- 气血反弹（持续生效） ----------
  // 必须真的学过「气血反弹」才允许生效（特效与反噬都以此为前提）
  function hasBloodReboundSkill() {
    return knownSkills.some(function (s) { return s.id === 'bloodRebound'; });
  }

  function bloodReboundValue() {
    const sk = knownSkills.filter(function (s) { return s.id === 'bloodRebound'; })[0];
    return Math.max(1, Math.round(
      (combat.maxHp * 0.10 + derived.strengthTotal) * skillLevelMult(sk)
    ));
  }

  // 每次被敌人攻击时反噬敌人；返回本次反噬伤害（未激活返回 0）
  function applyBloodRebound() {
    if (!bloodRebound || !hasBloodReboundSkill() || !activeEnemy) return 0;
    const dmg = bloodReboundValue();
    // 反噬成功的瞬间，向外炸开两圈更浓的血色波纹
    spawnBloodWave(1.6, 0);
    spawnBloodWave(1.1, 0.12);
    activeEnemy.hp -= dmg;
    activeEnemy.hurtT = HURT_TIME;
    addDamagePopup(
      activeEnemy.x - cameraX() + 88,
      GROUND_Y - 218,
      dmg,
      '#e05cff'
    );
    if (activeEnemy.hp <= 0) {
      activeEnemy.hp = 0;
      setBattleLog('「气血反弹」反噬 ' + dmg + ' 点，敌人被反噬击败！');
      updateBattleHud();
      if (!resolveEnemyDown()) return dmg;
      pendingVictory = 0.5;
      battleTurn = 'player';
      resetPlayerIdleTimer();
      setControls('battle');
      return dmg;
    }
    return dmg;
  }

  // 尸体必定掉 5~10 金币；道具 / 材料 / 装备各自独立 5% 掉落
  // 结算尸体战利品。
  // commit=false 时只掷结果、先摆在原地（敌人一倒下就要能看出"这堆东西值不值得搜"），
  // 等玩家搜索尸体时再真正进背包 / 加金币。
  function rollCorpseLoot(level, tier, commit, forceGear) {
    const take = commit !== false;
    const drops = [];
    // 金币随敌人等级提高：Lv1 约 5~10，之后每级上下限都会提升
    const coins = randInt(4 + level, 8 + level * 2);
    if (take) gold += coins;
    drops.push({
      kind: 'gold',
      name: '金币',
      icon: '金',
      detail: '+' + coins,
      coins: coins,
      rarityColor: '#ffd970'
    });
    if (Math.random() < 0.05) {
      const mat = makeMaterial(level);
      if (take) inventory.push(mat);
      drops.push(mat);
    }
    if (Math.random() < 0.05) {
      const pot = makePotion(level);
      if (take) inventory.push(pot);
      drops.push(pot);
    }
    // 装备掉落品级受敌人品级限制；forceGear=true（洞府内）时必掉（精英怪还会多掉一件）
    rollGearRarities(tier || 'common', !!forceGear).forEach(function (gearRarity) {
      const gear = makeEquipment(level, gearRarity);
      gear.fromCave = !!forceGear;
      if (take) inventory.push(gear);
      drops.push(gear);
    });
    if (take) updateGold();
    return drops;
  }

  // 把"掉在原地"的战利品收进背包（搜索尸体时调用）
  function collectCorpseDrops(drops) {
    (drops || []).forEach(function (d) {
      if (!d) return;
      if (d.kind === 'gold') gold += d.coins || 0;
      else inventory.push(d);
    });
    updateGold();
  }

  function recalcPlayerStats() {
    syncStaffPet();
    syncSantosSkill();
    let hp = 100 + (player.level - 1) * 10;
    let mp = 30;
    let armor = 0;
    let flatAtk = (player.level - 1) * 2;
    let speedPct = 0;
    let res = 0;
    let thorns = 0;
    let blockPct = 0;
    let strength = 0;
    let agility = 0;         // 装备/图鉴提供的敏捷（三维之一）
    let intelligence = 0;    // 装备/图鉴提供的智力（三维之一）
    let chaosRes = 0;
    let critReduce = 0;
    let armorPct = 0;
    let lifeRegen = 0;
    let atkSpeed = 0;
    let critChance = 0;
    let critDamage = 0;
    // 灵宝「蓝宝灵韵」：已装备的蓝色（魔法）装备属性按比例增幅
    const magicAmp = treasureSpecial('magicAmp')
      ? 1 + ((equipment.treasure.special && equipment.treasure.special.roll) || 100) / 100
      : 1;
    Object.keys(equipment).forEach(function (slot) {
      const g = equipment[slot];
      if (!g) return;
      const m = (magicAmp !== 1 && g.rarity === 'magic') ? magicAmp : 1;
      const mp_ = function (v) { return Math.round((v || 0) * m); };
      hp += mp_(g.hp);
      armor += mp_(g.armor);
      flatAtk += mp_(g.atk);
      speedPct += mp_(g.speedPct);
      mp += mp_(g.mp);
      res += mp_(g.res);
      thorns += mp_(g.thorns);
      blockPct += mp_(g.blockPct);
      strength += mp_(g.strength);
      agility += mp_(g.agility);
      intelligence += mp_(g.intelligence);
      chaosRes += mp_(g.chaosRes);
      critReduce += mp_(g.critReduce);
      armorPct += mp_(g.armorPct);
      lifeRegen += mp_(g.lifeRegen);
      atkSpeed += mp_(g.atkSpeed);
      critChance += mp_(g.critChance);
      critDamage += mp_(g.critDamage);
    });
    // 图鉴增幅：已激活的图鉴提供永久属性
    const codexBonus = codexTotalBonus();
    hp += codexBonus.hp || 0;
    armor += codexBonus.armor || 0;
    flatAtk += codexBonus.atk || 0;
    speedPct += codexBonus.speedPct || 0;
    mp += codexBonus.mp || 0;
    res += codexBonus.res || 0;
    thorns += codexBonus.thorns || 0;
    blockPct += codexBonus.blockPct || 0;
    strength += codexBonus.strength || 0;
    agility += codexBonus.agility || 0;
    intelligence += codexBonus.intelligence || 0;
    chaosRes += codexBonus.chaosRes || 0;
    critReduce += codexBonus.critReduce || 0;
    armorPct += codexBonus.armorPct || 0;
    lifeRegen += codexBonus.lifeRegen || 0;
    atkSpeed += codexBonus.atkSpeed || 0;
    critChance += codexBonus.critChance || 0;
    critDamage += codexBonus.critDamage || 0;
    // 力量：每点 +5 生命上限（装备/图鉴提供的力量同样算），
    // 灵宝「蛮力之心」会把这 5 点提升到 10 点；力量不再提供攻击伤害
    const strHpPer = treasureSpecial('strToHpDouble') ? 10 : 5;
    hp += strength * strHpPer;
    // 自由分配的属性点：力量 / 敏捷 / 智力
    const attrStr = Math.max(0, player.attrs.str | 0);
    const attrAgi = Math.max(0, player.attrs.agi | 0);
    const attrInt = Math.max(0, player.attrs.int | 0);
    // 加点力量与装备力量同一套换算
    hp += attrStr * strHpPer;
    // 记录"力量换算出来的生命"（面板/调试用）
    derived.strengthHp = (strength + attrStr) * strHpPer;
    // 敏捷：每点 +1% 攻击速度、+0.6% 移动速度
    atkSpeed += attrAgi;
    speedPct += attrAgi * 0.6;
    // 装备/图鉴提供的敏捷同样按「每点 +1% 攻速、+0.6% 移速」换算
    atkSpeed += agility;
    speedPct += agility * 0.6;
  // 智力：每点 +2 魔力上限（法术消耗魔力；魔力同时提高宠物伤害）
    // 圣物（森特欧斯的头骨 / 图腾）也提供智力，同样按 +2 魔力换算
    const relicInt = santosRelicIntBonus();
    // 装备/图鉴提供的智力同样按 +2 魔力换算
    mp += (attrInt + relicInt + intelligence) * 2;
    // 护甲提高%：只作用于装备/力量提供的护甲总量
    armor = Math.round(armor * (1 + armorPct / 100));
    hp += gemSum(['life']);
    mp += gemSum(['energy']);
    armor += gemSum(['armorUp']);
    speedPct += gemSum(['moveSpeed']);
    // 灵宝「不坏金身」：生命上限额外 +1000
    if (treasureSpecial('hpFlat1000')) hp += 1000;
    derived.maxHp = Math.max(1, hp);
    derived.maxMp = Math.max(1, mp);
    // 持续类 / 召唤类技能会占用（保留）一部分魔力上限：
    //   持续类（气血反弹等）= 35%，召唤类第一只 = 50%，每多一只 +20%（封顶 85%）
    let mpReserve = 0;
    if (bloodRebound && knownSkills.some(function (s) { return s.type === 'toggle'; })) mpReserve += 35;
    const alivePetCount = alivePets().length;
    if (alivePetCount > 0) mpReserve += 50 + (alivePetCount - 1) * 20;
    mpReserve = Math.min(85, mpReserve);
    derived.mpReservePct = mpReserve;
    derived.mpUsable = Math.max(1, Math.round(derived.maxMp * (1 - mpReserve / 100)));
    derived.mpReserved = Math.max(0, derived.maxMp - derived.mpUsable);
    derived.armor = armor;
    derived.flatAtk = flatAtk;
    derived.speedMult = Math.max(1, 1 + speedPct / 100);
    derived.res = res;
    derived.thorns = thorns;
    derived.blockPct = Math.min(75, blockPct);
    derived.strength = strength;
    derived.agility = agility;
    derived.intelligence = intelligence;
    derived.chaosRes = chaosRes;
    derived.critReduce = critReduce;
    derived.armorPct = armorPct;
    derived.lifeRegen = lifeRegen;
    derived.atkSpeed = atkSpeed;
    derived.critChance = Math.min(100, critChance);
    derived.critDamage = critDamage;
    derived.attrStr = attrStr;
    derived.attrAgi = attrAgi;
    derived.attrInt = attrInt;
    derived.relicInt = relicInt;
    derived.agilityTotal = agility + attrAgi;
    // 「100% 智力伤害」用的就是它：加点 + 圣物 + 装备/图鉴
    derived.intTotal = attrInt + relicInt + intelligence;
    // 圣物效果：头骨免疫中毒、图腾免疫控制
    derived.poisonImmune = hasSantosRelic('skull');
    derived.controlImmune = hasSantosRelic('totem');
    derived.strengthTotal = strength + attrStr;
    derived.spellDmgPct = 0; // 智力不再提高法术伤害
    combat.maxHp = derived.maxHp;
    combat.maxMp = derived.maxMp;
    combat.hp = Math.min(combat.hp, combat.maxHp);
    combat.mp = Math.min(combat.mp, derived.mpUsable);
    refreshPetStats();
    if (ui.petPopup && !ui.petPopup.classList.contains('hidden')) renderPetPanel();
    updateBattleHud();
  }

  function isStaffEquipped() {
    return equipment.weapon && equipment.weapon.weaponType === '法杖';
  }

  // 法杖被动：装备后宠物自动出现，卸下法杖宠物消失
  // 召唤宠物素材：itch「free-desert-enemy-sprite-sheets-pixel-art」（每帧 48×48，横向排列）
  const PET_SPRITES = {
    scorpio: { name: '蝎子', idle: 4, walk: 4, attack: 4, hurt: 2 },
    hyena: { name: '鬣狗', idle: 4, walk: 6, attack: 6, hurt: 2 },
    snake: { name: '毒蛇', idle: 4, walk: 4, attack: 6, hurt: 2 },
    vulture: { name: '猎鹰', idle: 4, walk: 4, attack: 4, hurt: 2 }
  };
  const PET_SPRITE_KEYS = Object.keys(PET_SPRITES);
  const PET_FRAME_SIZE = 48;
  const PET_DRAW_SIZE = 66;   // 屏幕上的绘制尺寸
  const PET_SPRITE_FLIP = true; // 素材默认朝左，翻转让宠物面向玩家前进方向（朝右）

  function activatePet() {
    const maxHp = Math.max(1, Math.round(derived.maxHp * 0.35));
    const p = {
      hp: maxHp,
      maxHp: maxHp,
      name: '法杖灵宠',
      tier: 'common',
      level: 1,
      sprite: 'snake',
      source: '法杖被动（装备法杖自动出现）',
      sourceKind: 'staff',
      atkMult: 1,
      hpMult: 1,
      hurtT: 0,
      animT: 0,
      attackT: 0,
      walkT: rand(0, 10),
      seq: ++petSeq
    };
    addPet(p);
    return p;
  }

  function syncStaffPet() {
    if (isStaffEquipped()) {
      if (!pets.some(function (p) { return p.sourceKind === 'staff' && p.hp > 0; })) activatePet();
    } else {
      // 只清理由法杖提供的灵宠；召唤技能 / 宠物蛋得到的宠物保留
      for (let i = pets.length - 1; i >= 0; i--) {
        if (pets[i].sourceKind === 'staff') pets.splice(i, 1);
      }
    }
  }

  // ---------- 森特欧斯的囚笼：头骨圣物附带的主动技能 ----------
  const WB_CAGE_SECONDS = 2;   // 「囚笼」控制时长（秒）
  const SANTOS_CAGE_SKILL = {
    id: 'santosCage', name: '森特欧斯的囚笼', type: 'control', cost: 8, level: 1,
    desc: '强行囚禁敌人 2 秒：期间敌人无法移动与攻击，并立刻造成 100% 智力（按当前智力结算）的伤害。'
  };

  // 带着头骨就会自动学会「囚笼」，丢掉/卖掉则撤回技能
  function syncSantosSkill() {
    const has = hasSantosRelic('skull');
    const idx = knownSkills.findIndex(function (s) { return s.id === 'santosCage'; });
    if (has && idx < 0) {
      knownSkills.push({
        id: SANTOS_CAGE_SKILL.id, name: SANTOS_CAGE_SKILL.name, desc: SANTOS_CAGE_SKILL.desc,
        cost: SANTOS_CAGE_SKILL.cost, type: SANTOS_CAGE_SKILL.type, level: 1, sockets: []
      });
      if (ui.spellPopup && !ui.spellPopup.classList.contains('hidden')) renderSpellList();
    } else if (!has && idx >= 0) {
      const sk = knownSkills[idx];
      (sk.sockets || []).forEach(function (g) {
        if (g) { g.socketedTo = null; inventory.push(g); }
      });
      knownSkills.splice(idx, 1);
      if (ui.spellPopup && !ui.spellPopup.classList.contains('hidden')) renderSpellList();
    }
  }

  // ---------- 宠物列表（多只共存，按等级从高到低排在玩家身后）----------
  function petLevelOf(tier) {
    return tier === 'rare' ? 3 : tier === 'magic' ? 2 : 1;
  }

  function sortPets() {
    pets.sort(function (a, b) {
      return (b.level || 1) - (a.level || 1) || (a.seq || 0) - (b.seq || 0);
    });
  }

  function addPet(p) {
    p.seq = p.seq || (++petSeq);
    p.level = p.level || petLevelOf(p.tier);
    pets.push(p);
    sortPets();
    return p;
  }

  function alivePets() {
    return pets.filter(function (p) { return p.hp > 0; });
  }

  // 出战宠物数量上限（避免无限堆叠）
  const PET_MAX = 4;

  function petAlive() {
    return alivePets().length > 0;
  }

  function leadPet() {
    return alivePets()[0] || null;
  }

  // 宠物排在玩家身后（玩家朝右，所以往后 = 屏幕左侧），按列表顺序依次向后
  function petScreenX(index) {
    return player.worldX - cameraX() - 76 - (index || 0) * 58;
  }

  // 换装备后宠物上限跟随玩家 35% 生命同步
  function refreshPetStats() {
    pets.forEach(function (p) {
      const newMax = Math.max(1, Math.round(derived.maxHp * 0.35 * (p.hpMult || 1)));
      const ratio = p.maxHp > 0 ? p.hp / p.maxHp : 1;
      p.maxHp = newMax;
      p.hp = Math.max(1, Math.min(newMax, Math.round(newMax * ratio)));
    });
  }

  function updateGold() {
    if (ui.goldValue) ui.goldValue.textContent = String(gold);
    // 金币是随时变动的（卖装备、世界BOSS奖励、搜尸体…），这里统一触发一次"防抖存档"，
    // 避免只改了内存数值、刷新后金币回退（之前线上就踩过这个坑）
    markSaveDirty();
  }

  // ---------- 玩家等级 / 经验 ----------
  // 升到下一级所需经验：Lv1→2 为 100，之后每级约 ×1.35 并取整到十位
  function stageInfo(stage) {
    return CULTIVATION_STAGES[clamp(stage, 1, MAX_STAGE) - 1];
  }

  function stageName(stage) {
    const s = stageInfo(stage);
    return s.sub ? s.realm + '·' + s.sub : s.realm;
  }

  // 每次小境界突破所需修为：首个阶段 100，之后每阶段 +25 并取整到十位
  function xpNeeded(stage) {
    if (stage >= MAX_STAGE) return Infinity;
    return Math.round((100 + 25 * (stage - 1)) / 10) * 10;
  }

  function hasSpiritItem() {
    return inventory.some(function (it) { return it.type === 'spiritItem'; });
  }

  function makeSpiritItem() {
    return {
      id: 'sp' + (++itemSeq),
      type: 'spiritItem',
      name: '天地灵物',
      icon: '灵',
      detail: '武者十境大圆满，服下可突破至炼气期',
      rarityColor: '#8fffd0'
    };
  }

  function updateLevelUI() {
    if (!ui.playerLv) return;
    ui.playerLv.textContent = stageName(player.level);
    const need = xpNeeded(player.level);
    if (player.bottleneck) {
      ui.xpFill.style.width = '100%';
      ui.xpText.textContent = '大圆满 · 需天地灵物';
    } else {
      const pct = need === Infinity ? 100 : clamp(player.xp / need, 0, 1) * 100;
      ui.xpFill.style.width = pct.toFixed(1) + '%';
      ui.xpText.textContent = need === Infinity
        ? '已至道祖'
        : Math.floor(player.xp) + '/' + need;
    }
    if (ui.btnBreak) {
      ui.btnBreak.classList.toggle('hidden', !(player.bottleneck && hasSpiritItem()));
    }
  }

  function gainXp(amount) {
    if (!amount || amount <= 0 || player.level >= MAX_STAGE) return;
    player.xp += amount;
    let advanced = false;
    let levelsGained = 0;
    while (player.level < MAX_STAGE && player.xp >= xpNeeded(player.level)) {
      // 武者十境大圆满：必须服下天地灵物才能蜕凡入炼气
      if (player.level === TRIBULATION_STAGE) {
        player.xp = xpNeeded(player.level);
        if (!player.bottleneck) {
          player.bottleneck = true;
          showToast('修为已至武者十境大圆满，需要「天地灵物」才能继续蜕变！');
        }
        break;
      }
      player.xp -= xpNeeded(player.level);
      player.level += 1;
      levelsGained += 1;
      advanced = true;
    }
    updateLevelUI();
    if (advanced) {
      // 每提升一个等级（境界）获得 ATTR_POINTS_PER_LEVEL 点可自由分配的属性点
      const gainedPoints = levelsGained * ATTR_POINTS_PER_LEVEL;
      player.attrPoints += gainedPoints;
      updateCharButton();
      if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
      const before = combat.maxHp;
      recalcPlayerStats();
      const gained = Math.max(0, combat.maxHp - before);
      combat.hp = Math.min(combat.maxHp, combat.hp + gained);
      updateBattleHud();
      pushBattleLog(
        '境界突破：' + stageName(player.level) + '（生命 +' + gained + '，属性点 +' + gainedPoints + '）',
        'reward'
      );
      showToast(
        '境界突破：' + stageName(player.level) +
        '（生命 +' + gained + '，获得 ' + gainedPoints + ' 点属性点，可在「角色」窗口分配）'
      );
    }
    checkFirstQuestDone();   // 升到武者十境·大圆满就完成第一个任务
    saveGame();
  }

  // 服下天地灵物，从武者十境大圆满突破到炼气初期
  function breakthrough() {
    if (!player.bottleneck) return;
    const item = inventory.filter(function (it) { return it.type === 'spiritItem'; })[0];
    if (!item) {
      showToast('需要「天地灵物」才能突破当前瓶颈。');
      return;
    }
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    player.bottleneck = false;
    player.xp = 0;
    player.level = Math.min(MAX_STAGE, TRIBULATION_STAGE + 1);
    player.attrPoints += ATTR_POINTS_PER_LEVEL; // 蜕凡入仙同样视为提升一个境界
    updateCharButton();
    recalcPlayerStats();
    combat.hp = combat.maxHp;
    updateBattleHud();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    updateLevelUI();
    saveGame();
    showToast(
      '服下「天地灵物」，成功蜕凡入仙：' + stageName(player.level) +
      '！（属性点 +' + ATTR_POINTS_PER_LEVEL + '）'
    );
  }

  /* ============================================================
   * 任务：第一个任务「提升至武者大圆满」
   *   正式进入游戏时弹出一次；修为升到 武者十境·大圆满 即完成。
   * ============================================================ */
  function refreshQuestPanel() {
    if (ui.questNowStage) ui.questNowStage.textContent = stageName(player.level);
  }

  // 进入游戏时调用：新档案接任务弹窗；老档案只补一次完成判定
  function maybeStartFirstQuest() {
    if (quests.first.accepted) {
      checkFirstQuestDone();
      return;
    }
    // 已经超过这个境界的存档（老档 / 测试档）不再弹窗，直接记为已完成
    if (player.level >= QUEST_FIRST_STAGE) {
      quests.first.accepted = true;
      quests.first.done = true;
      markSaveDirty();
      return;
    }
    quests.first.accepted = true;
    refreshQuestPanel();
    if (ui.questPopup) ui.questPopup.classList.remove('hidden');
    pushBattleLog('接取任务：提升至武者大圆满（当前 ' + stageName(player.level) + '）', 'system');
    markSaveDirty();
  }

  function checkFirstQuestDone() {
    if (!quests.first.accepted || quests.first.done) return false;
    if (player.level < QUEST_FIRST_STAGE) return false;
    quests.first.done = true;
    showToast('★ 任务完成：提升至武者大圆满！接下来需要「天地灵物」才能蜕凡入仙。');
    pushBattleLog('★ 任务完成：提升至武者大圆满 · 下一步要拿到「天地灵物」（只在世界BOSS死亡时掉落，0.01%）', 'reward');
    markSaveDirty();
    return true;
  }

  function closeQuestPopup() {
    if (ui.questPopup) ui.questPopup.classList.add('hidden');
    saveGame();
  }

  // 血瓶充能进度：击败 10 个普通敌人 或 1 个精英/传奇敌人 → 充能 +1（上限 5 层）
  // ---------- 震撼大地：脚下荡开的冲击波（辅助「阳爪」用） ----------
  const shockWaves = [];

  function spawnShockWave(delay) {
    shockWaves.push({ t: -(delay || 0), life: 0.7, maxR: rand(150, 240) });
    if (shockWaves.length > 12) shockWaves.shift();
  }

  function updateShockWaves(dt) {
    for (let i = shockWaves.length - 1; i >= 0; i--) {
      shockWaves[i].t += dt;
      if (shockWaves[i].t >= shockWaves[i].life) shockWaves.splice(i, 1);
    }
  }

  function drawShockWavesGround() {
    if (!shockWaves.length) return;
    const cx = player.worldX - cameraX() - 6;
    const gy = GROUND_Y + 6;
    shockWaves.forEach(function (w) {
      if (w.t < 0) return;
      const p = clamp(w.t / w.life, 0, 1);
      const r = w.maxR * (1 - Math.pow(1 - p, 2.4));
      const a = (1 - p) * (1 - p);
      if (a <= 0.01 || r <= 1) return;
      ctx.save();
      ctx.translate(cx, gy);
      ctx.scale(1, 0.36);
      const g = ctx.createRadialGradient(0, 0, r * 0.45, 0, 0, r);
      g.addColorStop(0, 'rgba(120, 90, 50, 0)');
      g.addColorStop(0.72, 'rgba(198, 152, 88, ' + (0.34 * a).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(255, 226, 164, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(255, 228, 176, ' + (0.42 * a).toFixed(3) + ')';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.96, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    });
  }

  // ---------- 「阳爪」特效：2 秒内多个白色虚影分身突进前方敌人 ----------
  const YANGCLAW_FX_DUR = 2.0;          // 整套演出 2 秒内播完（分身放慢，残影看得清）
  const YANGCLAW_FX_MAX_CLONES = 5;
  let yangClawFx = null;
  let lastSkillCalc = null;             // 最近一次技能伤害计算（调试用：核对等级加成）

  function startYangClawFx() {
    if (!activeEnemy) return;
    const n = randInt(3, YANGCLAW_FX_MAX_CLONES);   // 随机 3~5 个分身
    const clones = [];
    for (let i = 0; i < n; i++) {
      clones.push({
        delay: (i / n) * 0.7,         // 前后错开出发，保证 2 秒内全部冲完
        dur: 1.15,                    // 单次突进拉长，白色残影才留得住
        offsetY: rand(-8, 8),
        scale: rand(0.9, 1.08),
        phase: rand(0, Math.PI * 2)
      });
    }
    // 终点不是第一个敌人，而是战场最右侧（穿透沿途所有敌人）
    const farRight = cameraX() + W + 160;
    yangClawFx = { t: 0, dur: YANGCLAW_FX_DUR, fromX: player.worldX, toX: farRight, clones: clones };
  }

  function updateYangClawFx(dt) {
    if (!yangClawFx) return;
    yangClawFx.t += dt;
    if (yangClawFx.t >= yangClawFx.dur) yangClawFx = null;
  }

  function drawYangClawFx() {
    if (!yangClawFx || !images.player_idle_1) return;
    const fx = yangClawFx;
    const fromX = fx.fromX - cameraX();
    const toX = fx.toX - cameraX();
    const scale = playerFx.scale * caveScale();

    fx.clones.forEach(function (c) {
      const local = fx.t - c.delay;
      if (local < 0 || local > c.dur) return;
      const p = clamp(local / c.dur, 0, 1);
      const ease = 1 - Math.pow(1 - p, 3);          // 突进：起步快、末尾收住
      const x = fromX + (toX - fromX) * ease;
      const alpha = (p < 0.18 ? p / 0.18 : 1 - (p - 0.18) / 0.82) * 0.62;
      if (alpha <= 0.02) return;

      ctx.save();
      // 冲刺拖影（白色横条）
      const trailW = Math.max(0, x - (fromX + (toX - fromX) * Math.max(0, ease - 0.22)));
      if (trailW > 4) {
        ctx.globalAlpha = alpha * 0.5;
        const tg = ctx.createLinearGradient(x - trailW, 0, x, 0);
        tg.addColorStop(0, 'rgba(255,255,255,0)');
        tg.addColorStop(1, 'rgba(255,255,255,0.75)');
        ctx.fillStyle = tg;
        ctx.fillRect(x - trailW, GROUND_Y - 118 + c.offsetY, trailW, 10);
      }
      // 白色虚幻分身（同一套玩家贴图，去色 + 提亮）
      ctx.globalAlpha = alpha;
      try { ctx.filter = 'saturate(0) brightness(2.6)'; } catch (e) { /* 浏览器不支持就算了 */ }
      ctx.imageSmoothingEnabled = false;
      drawPlayerFrame(images.player_idle_1, x, GROUND_Y + c.offsetY, scale * c.scale, PLAYER_SPRITE_FLIP, 0);
      ctx.restore();
    });
  }

  // ---------- 流血：敌方回合开始前结算一次 ----------
  /* ---------- 炎军（辅助）：燃烧层数 ---------- */
  const BURN_CHANCE = 0.5;        // 每次造成伤害 50% 几率叠 1 层
  const BURN_STACK_MAX = 20;      // 层数上限
  const BURN_PCT = 0.01;          // 每层每回合 = 玩家最大生命 1% 的真实伤害

  // 造成伤害时调用：命中就给敌人叠 1 层燃烧（「炎军」镶嵌在被辅助技能上才生效）
  function maybeApplyBurn(enemy, skillId) {
    if (!enemy || enemy.hp <= 0) return false;
    if (!skillId || !skillHasGem(skillId, 'flameLegion')) return false;
    if (Math.random() >= BURN_CHANCE) return false;
    const before = enemy.burnStacks || 0;
    enemy.burnStacks = Math.min(BURN_STACK_MAX, before + 1);
    if (enemy.burnStacks === before) return false;    // 已经满层
    addTextPopup(enemy.x - cameraX() + 88, GROUND_Y - 196, '燃烧 +1（' + enemy.burnStacks + ' 层）', '#ff9a3c');
    return true;
  }

  // 燃烧结算：每个敌人回合，每层造成玩家最大生命 1% 的真实伤害（无视护甲/抗性）
  function applyBurnTick() {
    const burning = battleAlive().filter(function (e) { return (e.burnStacks || 0) > 0; });
    if (!burning.length) return false;
    const per = Math.max(1, Math.round(combat.maxHp * BURN_PCT));
    const parts = [];
    burning.forEach(function (e) {
      const dmg = per * e.burnStacks;
      e.hp = Math.max(0, e.hp - dmg);
      e.hurtT = HURT_TIME;
      addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 84, dmg, '#ff7a2f');
      parts.push(enemyLabel(e, false) + ' −' + dmg + '（燃烧 ' + e.burnStacks + ' 层）');
    });
    pushBattleLog('燃烧结算（每层 ' + per + ' 点真实伤害）：' + parts.join('，'), 'player');
    setBattleLog('燃烧结算：' + parts.join('，'));
    updateBattleHud();
    return true;
  }

  function applyBleedTick() {
    let any = false;
    const list = battleAlive().filter(function (e) { return e.bleed && e.bleed.turns > 0; });
    if (list.length) {
      const parts = [];
      list.forEach(function (e) {
        const dmg = Math.max(1, Math.round(e.bleed.dmg));
        e.hp = Math.max(0, e.hp - dmg);
        e.hurtT = HURT_TIME;
        addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 126, dmg, '#ff2b2b');
        e.bleed.turns -= 1;
        if (e.bleed.turns <= 0) e.bleed = null;
        parts.push(enemyLabel(e, false) + ' −' + dmg);
      });
      pushBattleLog('流血结算（每回合持续掉血）：' + parts.join('，'), 'enemy');
      setBattleLog('流血结算：' + parts.join('，'));
      updateBattleHud();
      any = true;
    }
    // 中毒结算（「有毒的孢子」造成，圣物「森特欧斯的头骨」免疫）
    const poisoned = battleAlive().filter(function (e) { return e.poison && e.poison.turns > 0; });
    if (poisoned.length) {
      const parts = [];
      poisoned.forEach(function (e) {
        const dmg = Math.max(1, Math.round(e.poison.dmg));
        e.hp = Math.max(0, e.hp - dmg);
        e.hurtT = HURT_TIME;
        addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 100, dmg, '#b6e05a');
        e.poison.turns -= 1;
        if (e.poison.turns <= 0) e.poison = null;
        parts.push(enemyLabel(e, false) + ' −' + dmg);
      });
      pushBattleLog('中毒结算（每回合持续掉血）：' + parts.join('，'), 'enemy');
      setBattleLog('中毒结算：' + parts.join('，'));
      updateBattleHud();
      any = true;
    }
    return any;
  }

  // ---------- 阵亡与复活：生命归零 → 回到 0km 重新出发 ----------
  let deathActive = false;

  // ---------- 「防御」姿态：本轮受到的敌人伤害 -35%，另有 5% 几率完全格挡 ----------
  const DEFEND_REDUCE = 0.35;   // 减伤 35%
  const DEFEND_BLOCK_PCT = 5;   // 完全格挡几率 5%
  let defending = false;        // 本回合是否处于防御姿态（敌方回合结束后清除）

  function triggerDeath() {
    if (deathActive) return;
    deathActive = true;
    // 洞府阵亡惩罚：按洞府类型丢掉身上的装备（灵宝槽不参与，避免一次性毁掉极稀有物）
    const lostGear = dropEquippedOnDeath();

    // 收干净当前战斗
    pendingVictory = 0;
    slashFx = null;
    defendFx = null;
    airSlashFx = null;
    damagePopups = [];
    ally = null;
    battleGroup.length = 0;
    activeEnemy = null;
    corpse = null;
    input.moving = false;
    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    gameState = 'dead';
    if (ui.deathInfo) {
      ui.deathInfo.textContent = '你走到了 ' + (Math.max(0, player.worldX - START_X) / KM_UNIT).toFixed(1) +
        ' km，击败了 ' + defeatedEnemies + ' 个敌人。';
    }
    renderDeathLoss(lostGear);
    ui.deathPopup.classList.remove('hidden');
    addBattleLogSafe('你被击倒，回到了 0km 起点。' +
      (lostGear.length ? '（洞府陨落：丢失 ' + lostGear.length + ' 件装备）' : ''), 'enemy');
    if (autoAdventure) stopAutoAdventure('你倒下了，自动冒险结束。');
  }

  /* ============================================================
   * 洞府阵亡掉落：绿色丢 1 件、蓝色丢 2 件、金色丢全身
   *   从"身上已装备的槽位"里随机挑（灵宝槽除外），挑中即卸下丢失
   * ============================================================ */
  const CAVE_DEATH_LOSS = { green: 1, blue: 2, gold: Infinity };

  function dropEquippedOnDeath() {
    if (!cave) return [];
    const def = cave.def || {};
    const limit = CAVE_DEATH_LOSS[def.id];
    if (!limit) return [];
    const slots = Object.keys(equipment).filter(function (s) {
      return s !== 'treasure' && equipment[s];
    });
    if (!slots.length) return [];
    // 洗牌后按数量取
    for (let i = slots.length - 1; i > 0; i--) {
      const j = randInt(0, i);
      const tmp = slots[i]; slots[i] = slots[j]; slots[j] = tmp;
    }
    const take = limit === Infinity ? slots.length : Math.min(limit, slots.length);
    const lost = [];
    for (let i = 0; i < take; i++) {
      const slot = slots[i];
      lost.push(equipment[slot]);
      equipment[slot] = null;
    }
    if (lost.length) {
      recalcPlayerStats();
      pushBattleLog('洞府陨落：' + (def.name || '洞府') + '中丢失了 ' + lost.length + ' 件装备（' +
        lost.map(function (g) { return g.name; }).join('、') + '）', 'enemy');
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
      showToast('洞府陨落：丢失 ' + lost.length + ' 件装备！' +
        (limit === Infinity ? '（金色洞府会丢光全身装备）' : ''));
      saveGame();
    }
    return lost;
  }

  function renderDeathLoss(lost) {
    if (!ui.deathLoss) return;
    ui.deathLoss.textContent = '';
    if (ui.deathNote) {
      ui.deathNote.textContent = lost.length
        ? '洞府陨落：这次丢掉了 ' + lost.length + ' 件装备（绿色洞府丢 1 件 / 蓝色洞府丢 2 件 / 金色洞府丢全身）。'
        : '回到 0km 起点，生命与魔力恢复满值，装备与收获都会保留。';
    }
    if (!lost.length) { ui.deathLoss.classList.add('hidden'); return; }
    lost.forEach(function (g) {
      const row = document.createElement('div');
      row.className = 'lootRow';
      const icon = document.createElement('div');
      icon.className = 'lootIcon';
      fillItemIcon(icon, g, g.icon || '?');
      icon.style.borderColor = g.rarityColor || 'rgba(255,255,255,.2)';
      const info = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'lootName';
      name.textContent = g.name;
      name.style.color = g.rarityColor || '#ffe9c0';
      const meta = document.createElement('div');
      meta.className = 'lootMeta';
      meta.textContent = (SLOT_DEFS[g.slot] ? SLOT_DEFS[g.slot].label : '装备') + ' · 已丢失';
      info.appendChild(name);
      info.appendChild(meta);
      row.appendChild(icon);
      row.appendChild(info);
      ui.deathLoss.appendChild(row);
    });
    ui.deathLoss.classList.remove('hidden');
  }

  // 复活：回到起点重新上路（装备 / 金币 / 境界都保留）
  function reviveAtStart() {
    ui.deathPopup.classList.add('hidden');
    deathActive = false;
    player.worldX = START_X;
    player.vx = 0;
    input.moving = false;
    defeatedEnemies = 0;
    resumeWorldX = null;   // 阵亡后里程清零：重开/读档都从起点重新出发
    activeEnemy = null;
    battleGroup.length = 0;
    corpse = null;
    chest = null;
    cans = [];
    eliteCharges = 0;
    cave = null;
    caveEntrances.length = 0;
    caveNextX = player.worldX + rand(4200, 6200);
    legendMap = null;
    mirage = null;
    recalcPlayerStats();
    combat.hp = combat.maxHp;
    combat.mp = derived.mpUsable;
    flaskCharges = 5;
    updateFlaskUI();
    gameState = 'walk';
    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    updateLegendBar();
    updateCaveBar();
    updateEliteButton();
    updateDist();
    updateBattleHud();
    spawnNextEnemy();
    saveGame();
    showToast('你回到了 0km 起点，重新踏上武者之路。');
  }

  function addFlaskKill(isElite, silent) {
    // 满充能时不累计也不消耗进度：先用掉一层，进度继续有效
    if (flaskCharges >= 5) {
      if (!silent) showToast('血瓶已是满充能（5/5），先用掉一层再来积累进度。');
      updateFlaskUI();
      return;
    }
    if (isElite) {
      // 精英 / 传奇：每 FLASK_ELITE_NEED 个就加一层
      flaskKillElite += 1;
      if (flaskKillElite >= FLASK_ELITE_NEED) {
        flaskKillElite -= FLASK_ELITE_NEED;
        flaskCharges += 1;
        if (!silent) showToast('击败精英敌人，血瓶充能 +1（当前 ' + flaskCharges + '/5 层）');
      }
    } else {
      // 普通 / 蓝色：每 FLASK_COMMON_NEED 个加一层
      flaskKillCommon += 1;
      if (flaskKillCommon >= FLASK_COMMON_NEED) {
        flaskKillCommon -= FLASK_COMMON_NEED;
        flaskCharges += 1;
        if (!silent) {
          showToast('累计击败 ' + FLASK_COMMON_NEED + ' 个普通敌人，血瓶充能 +1（当前 ' + flaskCharges + '/5 层）');
        }
      }
    }
    updateFlaskUI();
  }

  function updateFlaskUI() {
    if (!ui.flaskCount) return;
    ui.flaskCount.textContent = String(flaskCharges);
    ui.btnFlask.disabled = flaskCharges <= 0;
    if (ui.btnFlask) {
      ui.btnFlask.title = '血瓶充能 ' + flaskCharges + ' / 5' +
        '\n点击恢复 30% 生命（消耗 1 层）' +
        '\n充能进度：普通敌人 ' + flaskKillCommon + '/' + FLASK_COMMON_NEED + ' 个（满 ' + FLASK_COMMON_NEED + ' 个 +1 层）' +
        ' · 精英敌人 ' + flaskKillElite + '/' + FLASK_ELITE_NEED + ' 个（每 ' + FLASK_ELITE_NEED + ' 个 +1 层）';
    }
  }

  // 左下角血瓶：恢复 30% 最大生命并消耗一层充能
  function useFlask() {
    if (flaskCharges <= 0) {
      showToast('血瓶没有充能了，击败敌人可以补充。');
      return;
    }
    if (combat.hp >= combat.maxHp) {
      showToast('生命已满，暂时不需要喝血瓶。');
      return;
    }
    const heal = Math.max(1, Math.round(combat.maxHp * 0.3));
    combat.hp = Math.min(combat.maxHp, combat.hp + heal);
    flaskCharges--;
    updateFlaskUI();
    updateBattleHud();
    if (gameState === 'battle') {
      setBattleLog('你喝下血瓶，恢复 ' + heal + ' 点生命（剩余充能 ' + flaskCharges + '）。');
    } else {
      showToast('你喝下血瓶，恢复 ' + heal + ' 点生命。');
    }
  }

  // ---------- 签到 / 活力 / 自动冒险 ----------
  function todayKey() {
    const d = new Date();
    return d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();
  }

  function loadSave() {
    // 先按"新档"归零，再套用存档数据 —— 避免上一条档案 / 旧版存档的脏数据残留
    resetProgressState();
    try {
      const raw = window.localStorage.getItem(activeSaveKey());
      if (!raw) { clampAttrPoints(); return; }
      const data = JSON.parse(raw);
      vitality = Math.max(0, Math.floor(data.vitality || 0));
      // 金币（新版才存；老存档没有就是 0）
      gold = Math.max(0, Math.floor(Number(data.gold) || 0));
      // 血瓶充能与进度
      if (typeof data.flaskCharges === 'number') flaskCharges = clamp(Math.round(data.flaskCharges), 0, 5);
      if (typeof data.flaskKillCommon === 'number') flaskKillCommon = Math.max(0, Math.floor(data.flaskKillCommon));
      if (typeof data.flaskKillElite === 'number') flaskKillElite = Math.max(0, Math.floor(data.flaskKillElite));
      newbieChestUsed = !!data.newbieChestUsed;
      // 已学技能 + 技能上的镶嵌石（老存档没有该字段时保留初始「劈砍」）
      if (Array.isArray(data.skills) && data.skills.length) {
        knownSkills.length = 0;
        data.skills.forEach(function (s) {
          if (!s || !s.id || !s.name) return;
          if (isRemovedSkillId(s.id)) return;   // 已移除的技能不再恢复
          const sk = {
            id: s.id,
            name: s.name,
            desc: s.desc || '',
            cost: Math.max(0, Math.floor(s.cost || 0)),
            type: s.type || 'melee',
            level: Math.max(1, Math.floor(s.level || 1)),
            sockets: Array.isArray(s.sockets)
              ? s.sockets.filter(function (g) { return g && g.effect; })
              : []
          };
          // 镶嵌槽数量（默认 3，最多 6）；老存档没有该字段就按默认给
          sk.socketSlots = clamp(Math.floor(Number(s.socketSlots) || SOCKET_SLOTS_DEFAULT),
            SOCKET_SLOTS_DEFAULT, SOCKET_SLOTS_MAX);
          if (Array.isArray(s.dmg) && s.dmg.length === 2) sk.dmg = [s.dmg[0], s.dmg[1]];
        sk.sockets.forEach(function (g) { g.socketedTo = s.id; });
        knownSkills.push(sk);
      });
      // 技能改名 / 调整基础数值后，老存档也跟着最新定义走（等级与镶嵌石保留）
      knownSkills.forEach(function (sk) {
        const def = skillDefById(sk.id);
        if (!def) return;
        sk.name = def.name;
        if (def.desc) sk.desc = def.desc;
        if (def.type) sk.type = def.type;
        if (def.cost != null) sk.cost = def.cost;
        if (Array.isArray(def.dmg) && def.dmg.length === 2) sk.dmg = [def.dmg[0], def.dmg[1]];
      });
        // 兜底：万一把技能都清了，至少留一个劈砍
        if (!knownSkills.length) knownSkills.push(starterSkill());
      }
      // 自动冒险的优先技能：存档里有、且该技能还在，才恢复
      autoSkillId = (data.autoSkillId && knownSkills.some(function (s) { return s.id === data.autoSkillId; }))
        ? data.autoSkillId : null;
      // 世界BOSS：刷新时刻由上海时间实时推演，存档只恢复本轮血量 / 今日奖励 / 击杀数
      if (data.worldBoss && typeof data.worldBoss === 'object') {
        const wd = data.worldBoss;
        wb.killed = Math.max(0, Math.floor(wd.killed || 0));
        wb.hp = Math.max(0, Math.floor(wd.hp || 0));
        wb.spawnedAt = Math.max(0, Number(wd.spawnedAt) || 0);
        wb.lastRewardDay = typeof wd.lastRewardDay === 'string' ? wd.lastRewardDay : '';
      }
      // 冒险进度：接着上次的里程继续走（除非是本档阵亡 / 新建档案）
      if (typeof data.distance === 'number' && isFinite(data.distance)) {
        const d = Math.max(START_X, Math.round(data.distance));
        resumeWorldX = d > START_X + 10 ? d : null;
      }
      if (typeof data.defeatedEnemies === 'number' && data.defeatedEnemies >= 0) {
        defeatedEnemies = Math.floor(data.defeatedEnemies);
      }
      // 任务进度（老存档没有这个字段：留空 → 进游戏时补弹一次「第一个任务」）
      if (data.quests && data.quests.first && typeof data.quests.first === 'object') {
        quests.first.accepted = !!data.quests.first.accepted;
        quests.first.done = !!data.quests.first.done;
      }
      lastSignDate = data.lastSignDate || '';
      if (data.level) player.level = Math.max(1, Math.floor(data.level));
      if (data.xp) player.xp = Math.max(0, Math.floor(data.xp));
      // 等级兜底：0 / 空 / 非法值一律按 1 级处理
      player.level = clamp(Math.max(1, Math.floor(Number(data.level) || 1)), 1, MAX_STAGE);
      player.bottleneck = !!data.bottleneck;
      if (data.onlineSeconds) onlineSeconds = Math.max(0, Math.floor(data.onlineSeconds));
      if (typeof data.onlineRewardIndex === 'number') onlineRewardIndex = Math.max(0, Math.floor(data.onlineRewardIndex));
      if (typeof data.onlinePending === 'number') onlinePending = Math.max(0, Math.floor(data.onlinePending));
      // 「气血反弹」的开关跟随技能本身：技能是会话状态（不写入存档），
      // 所以这里也不再从存档恢复开关，避免“没有技能却自带反噬/血色波纹”
      if (Array.isArray(data.codex)) {
        // 老存档里原「神级」的 id 映射到新的「暗金」id
        data.codex.forEach(function (id) { codexUnlocked[CODEX_LEGACY_IDS[id] || id] = true; });
      }
      if (data.gachaPity) gachaPity = Math.max(0, Math.floor(data.gachaPity));
      if (data.gachaCount) gachaCount = Math.max(0, Math.floor(data.gachaCount));
      if (data.caveToken) caveToken = Math.max(0, Math.floor(data.caveToken));
      // 属性点：老存档没有该字段时，按“每提升一个境界 +ATTR_POINTS_PER_LEVEL 点”补发
      if (data.attrs && typeof data.attrs === 'object') {
        ATTR_DEFS.forEach(function (def) {
          player.attrs[def.id] = Math.max(0, Math.floor(data.attrs[def.id] || 0));
        });
      }
      if (typeof data.attrPoints === 'number') {
        player.attrPoints = Math.max(0, Math.floor(data.attrPoints));
      } else {
        const spent = ATTR_DEFS.reduce(function (sum, def) {
          return sum + (player.attrs[def.id] || 0);
        }, 0);
        player.attrPoints = Math.max(0, (player.level - 1) * ATTR_POINTS_PER_LEVEL - spent);
      }
      // 装备与背包
      if (data.equipment && typeof data.equipment === 'object') {
        Object.keys(equipment).forEach(function (slot) {
          const it = data.equipment[slot];
          equipment[slot] = (it && typeof it === 'object') ? it : null;
        });
      }
      if (Array.isArray(data.inventory)) {
        inventory = data.inventory.filter(function (it) { return it && typeof it === 'object'; });
      }
      // 物品 id 计数器推到已有最大值，避免新建物品撞 id
      let maxItemId = 0;
      const scanId = function (it) {
        if (!it || !it.id) return;
        const n = parseInt(String(it.id).replace(/\D/g, ''), 10);
        if (!isNaN(n) && n > maxItemId) maxItemId = n;
      };
      Object.keys(equipment).forEach(function (slot) { scanId(equipment[slot]); });
      inventory.forEach(scanId);
      if (maxItemId > itemSeq) itemSeq = maxItemId;
      // 老物品补齐词缀数据（洗练/展示都要用）
      Object.keys(equipment).forEach(function (slot) {
        if (equipment[slot] && equipment[slot].type === 'equipment') ensureItemMods(equipment[slot]);
      });
      inventory.forEach(function (it) { if (it && it.type === 'equipment') ensureItemMods(it); });
      // 老存档的装备补美术图标（武器 / 头盔 / 衣服 / 鞋子 / 手套）
      Object.keys(equipment).forEach(function (slot) { ensureItemIcon(equipment[slot]); });
      inventory.forEach(function (it) { ensureItemIcon(it); });
      recalcPlayerStats();
      updateGold();
      updateFlaskUI();
    } catch (e) { /* file:// 下可能禁用 localStorage，忽略即可 */ }
    clampAttrPoints();
  }

  // 属性点不得超过"当前境界一共能拿到多少"（旧存档可能留下超量垃圾数据）
  function clampAttrPoints() {
    const spent = ATTR_DEFS.reduce(function (sum, def) {
      return sum + (player.attrs[def.id] || 0);
    }, 0);
    const maxAvailable = Math.max(0, (player.level - 1) * ATTR_POINTS_PER_LEVEL - spent);
    if (!(player.attrPoints >= 0)) player.attrPoints = 0;
    if (player.attrPoints > maxAvailable) player.attrPoints = maxAvailable;
    return player.attrPoints;
  }

  // 存档内容：同一份数据既写浏览器本地（离线兜底），也上传服务器
  function buildSavePayload() {
    return {
        schemaVersion: 1,
        savedAt: Date.now(),
        vitality: vitality,
        gold: gold,
        lastSignDate: lastSignDate,
        level: player.level,
        xp: Math.floor(player.xp),
        bottleneck: !!player.bottleneck,
        onlineSeconds: Math.floor(onlineSeconds),
        onlineRewardIndex: onlineRewardIndex,
        onlinePending: onlinePending,
        codex: Object.keys(codexUnlocked),
        gachaPity: gachaPity,
        gachaCount: gachaCount,
        caveToken: caveToken,
        attrs: { str: player.attrs.str, agi: player.attrs.agi, int: player.attrs.int },
        attrPoints: player.attrPoints,
        // 血瓶
        flaskCharges: flaskCharges,
        flaskKillCommon: flaskKillCommon,
        flaskKillElite: flaskKillElite,
        // 新手宝箱是否已经用过（每个档案只出一次）
        newbieChestUsed: newbieChestUsed,
        // 世界BOSS：刷新时刻由上海时间推演（不入档），存档只记本轮血量 / 今日奖励 / 击杀数
        worldBoss: {
          spawnedAt: wb.spawnedAt,
          hp: wb.hp,
          killed: wb.killed,
          lastRewardDay: wb.lastRewardDay
        },
        // 已学会的技能（含镶嵌的辅助/被动技能石）。召唤宠物是法杖被动，按装备实时推导，不入档
        skills: knownSkills.filter(function (s) { return s.id !== 'summonPet'; }),
        // 自动冒险优先使用的技能（法术窗口里「激活」的那个）
        autoSkillId: autoSkillId,
        // 装备与背包（物品都是纯数据，可以直接序列化）
        // 冒险进度：移动距离（里程）与已击败敌人数，读档时接着走
        distance: Math.round(player.worldX),
        defeatedEnemies: defeatedEnemies,
        // 任务进度
        quests: { first: { accepted: !!quests.first.accepted, done: !!quests.first.done } },
        equipment: (function () {
          const out = {};
          Object.keys(equipment).forEach(function (slot) {
            if (equipment[slot]) out[slot] = equipment[slot];
          });
          return out;
        })(),
        inventory: inventory
    };
  }

  function saveGame() {
    let payload = null;
    try {
      payload = buildSavePayload();
      window.localStorage.setItem(activeSaveKey(), JSON.stringify(payload));
      // 档案索引里记一下最近玩的等级 / 时间
      if (account.hash) touchAccount(account.hash, player.level);
    } catch (e) { /* 忽略写入失败 */ }
    queueRemoteSave(payload);   // 联网时顺带同步到服务器
  }

  /* ---------- 云端存档同步 ---------- */
  // 节流：2.5 秒内的连续存档合并成一次上传，避免频繁刷服务器
  const REMOTE_SAVE_DELAY_MS = 1500;

  /* ---------- 自动存档（关键数值变动后落盘 + 定期兜底）----------
   * 背景：金币 / 活力 / 物品这些是"随时"变的，但以前只有少数事件才 saveGame()，
   *       结果云端刷新后金币回退。现在改成一有变动就标记 dirty，几秒后落盘。 */
  let saveDirty = false;
  let dirtySaveTimer = null;
  const DIRTY_SAVE_DELAY_MS = 3000;    // 变动后 3 秒落盘（合并短时间内的连续变动）
  const PERIODIC_SAVE_SEC = 30;        // 另外每 30 秒兜底存一次
  let periodicSaveClock = 0;

  function markSaveDirty() {
    saveDirty = true;
    if (dirtySaveTimer) return;
    dirtySaveTimer = setTimeout(function () {
      dirtySaveTimer = null;
      if (!saveDirty) return;
      saveDirty = false;
      saveGame();
    }, DIRTY_SAVE_DELAY_MS);
  }

  function updatePeriodicSave(dt) {
    if (gameState !== 'walk' && gameState !== 'battle') return;
    periodicSaveClock += dt;
    if (periodicSaveClock >= PERIODIC_SAVE_SEC) {
      periodicSaveClock = 0;
      saveGame();          // 兜底：即便某些改动没触发 markSaveDirty，也会定时落盘
    }
  }

  function queueRemoteSave(payload) {
    if (!net.accountOnline || !net.api || !net.token || !payload) return;
    net.pendingSave = payload;
    if (net.saveTimer) return;
    net.saveTimer = setTimeout(function () {
      net.saveTimer = null;
      flushRemoteSave();
    }, REMOTE_SAVE_DELAY_MS);
  }

  function flushRemoteSave() {
    if (!net.accountOnline || !net.api || !net.token) return Promise.resolve(false);
    const payload = net.pendingSave;
    net.pendingSave = null;
    if (!payload) return Promise.resolve(false);
    return net.api.pushSave(net.token, payload, payload.savedAt).then(function (r) {
      net.lastPushAt = Date.now();
      net.pushCount += 1;
      return r;
    }).catch(function (err) {
      // 上传失败：把内容放回队列，等下一次存档再试（不打扰玩家）
      net.pendingSave = net.pendingSave || payload;
      net.lastError = err && err.message ? err.message : '存档上传失败';
      if (err && err.code === 'unauthorized') {
        net.accountOnline = false;
        showToast('登录状态已失效，本局改为本地存档（重新登录可恢复云端同步）。');
      }
      return false;
    });
  }

  // 关页面 / 切后台时把还没上传的存档立刻推上去
  function flushRemoteSaveNow() {
    if (net.saveTimer) { clearTimeout(net.saveTimer); net.saveTimer = null; }
    return flushRemoteSave();
  }

  /* 页面要关掉/刷新时：不再用 fetch（可能来不及发出去就被浏览器取消），
   * 改用 sendBeacon 把"当前最新状态"直接投递到服务器。 */
  function flushOnPageHide() {
    let payload = null;
    try {
      payload = buildSavePayload();
      window.localStorage.setItem(activeSaveKey(), JSON.stringify(payload));
    } catch (e) { /* 忽略 */ }
    if (!payload || !net.accountOnline || !net.token || !net.serverUrl) return;
    try {
      const url = net.serverUrl + '/api/save?token=' + encodeURIComponent(net.token);
      const body = JSON.stringify({ save: payload, savedAt: payload.savedAt });
      if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } catch (e) { /* 忽略 */ }
  }

  // 改 HUD 按钮文字时只动 <span> 标签，别把图标（<img>）冲掉
  function setHudBtnLabel(btn, text) {
    if (!btn) return;
    const span = btn.querySelector('span');
    if (span) span.textContent = text;
    else btn.textContent = text;
  }

  function updateVitalityUI() {
    if (ui.vitalityValue) ui.vitalityValue.textContent = String(vitality);
    if (ui.btnSign) {
      setHudBtnLabel(ui.btnSign, lastSignDate === todayKey() ? '今日已签到' : '签到');
    }
    if (ui.btnAuto) {
      ui.btnAuto.classList.toggle('active', autoAdventure);
      ui.btnAuto.textContent = autoAdventure ? '托管中' : '托管';
      ui.btnAuto.title = autoAdventure
        ? '托管进行中（点击停止）'
        : '托管：自动打怪 / 搜尸体 / 喝药，每 1 点活力可托管 10km';
    }
  }

  // 在线奖励阶梯：第 1 次 3 分钟，之后 5 / 10 / 15 / 30 / 60 分钟，第 6 次以后固定 60 分钟
  const ONLINE_REWARD_STEPS = [180, 300, 600, 900, 1800, 3600];
  const ONLINE_REWARD_LAST = ONLINE_REWARD_STEPS[ONLINE_REWARD_STEPS.length - 1];
  let onlineRewardIndex = 0;   // 已经领过几次（第几次奖励 = 索引 + 1），随存档保存
  let onlinePending = 0;       // 已达门槛但还没点击领取的次数（在线奖励必须手动领）

  function onlineNextStep() {
    return ONLINE_REWARD_STEPS[Math.min(onlineRewardIndex, ONLINE_REWARD_STEPS.length - 1)];
  }

  function fmtMMSS(sec) {
    const t = Math.max(0, Math.floor(sec));
    return String(Math.floor(t / 60)).padStart(2, '0') + ':' + String(t % 60).padStart(2, '0');
  }

  function onlineStepText(sec) {
    return sec >= 60 ? (sec / 60) + ' 分钟' : sec + ' 秒';
  }

  function updateOnlineUI() {
    if (!ui.onlineTimer) return;
    const step = onlineNextStep();
    const total = ONLINE_REWARD_STEPS.length;
    const idx = Math.min(onlineRewardIndex + 1, total);
    if (onlinePending > 0) {
      ui.onlineTimer.textContent = '在线奖励 可领取 ×' + onlinePending;
      ui.onlineTimer.classList.add('claimable');
    } else {
      ui.onlineTimer.textContent = '在线奖励 ' + fmtMMSS(onlineSeconds) + ' / ' + fmtMMSS(step) +
        '（第 ' + idx + ' 次）';
      ui.onlineTimer.classList.remove('claimable');
    }
    ui.onlineTimer.title = '累计在线时长达到门槛后，需要手动点击「领取」才会获得 1 点活力：\n' +
      '第 1~6 次分别是 3 / 5 / 10 / 15 / 30 / 60 分钟，第 6 次之后每 60 分钟一次\n' +
      (onlinePending > 0
        ? '当前有 ' + onlinePending + ' 次还没领，点右边按钮领取'
        : '本次是第 ' + idx + ' 次，还差 ' + Math.ceil(Math.max(0, step - onlineSeconds) / 60) + ' 分钟');
    if (ui.btnOnlineClaim) {
      ui.btnOnlineClaim.classList.toggle('hidden', onlinePending <= 0);
      ui.btnOnlineClaim.textContent = onlinePending > 1 ? ('领取 ×' + onlinePending) : '领取';
      ui.btnOnlineClaim.title = '点击领取在线奖励：活力 +' + Math.max(1, onlinePending);
    }
  }

  function updateOnline(dt) {
    if (gameState !== 'walk' && gameState !== 'battle') return;
    onlineSeconds += dt;
    let reached = false;
    while (onlineSeconds >= onlineNextStep()) {
      const step = onlineNextStep();
      onlineSeconds -= step;
      onlineRewardIndex += 1;
      onlinePending += 1;      // 只记"可领取"，要玩家自己点
      reached = true;
    }
    if (reached) {
      saveGame();   // 阶梯进度也一起落盘
      showToast('在线奖励已达标：点右上角「领取」按钮领取活力 +' + onlinePending + '。');
      updateOnlineUI();
    }
    onlineUiTimer += dt;
    if (onlineUiTimer >= 0.25) {
      onlineUiTimer = 0;
      updateOnlineUI();
    }
  }

  // 手动领取在线奖励（活力不会自动到账）
  function claimOnlineReward() {
    if (onlinePending <= 0) {
      showToast('在线奖励还没到门槛，继续在线累计时长吧。');
      return 0;
    }
    const n = onlinePending;
    onlinePending = 0;
    vitality += n;
    saveGame();
    updateVitalityUI();
    updateOnlineUI();
    showToast('在线奖励已领取：活力 +' + n + '（当前活力 ' + vitality + '）');
    return n;
  }

  function signIn() {
    const today = todayKey();
    if (lastSignDate === today) {
      showToast('今天已经签到过了，明天再来。');
      return;
    }
    lastSignDate = today;
    vitality += 1;
    saveGame();
    updateVitalityUI();
    showToast('签到成功！活力 +1（当前活力 ' + vitality + '）。');
  }

  function stopAutoAdventure(message) {
    autoAdventure = false;
    autoKmLeft = 0;
    if (gameState === 'walk') {
      input.moving = false;
      syncMoveButton();
    }
    updateVitalityUI();
    if (message) showToast(message);
  }

  function startAutoAdventure() {
    if (autoAdventure) return;
    if (cave) {
      showToast('洞府内需要手动探索，无法开启自动冒险。');
      return;
    }
    if (vitality <= 0) {
      showToast('活力不足，请先签到获取活力。');
      return;
    }
    // 前方有未开的宝箱也允许直接自动冒险（不再拦截，宝箱留着以后再回来点也行）
    // 点击即扣除 1 点活力，换取 10km 的自动冒险里程
    vitality -= 1;
    saveGame();
    autoAdventure = true;
    autoKmLeft = ENEMY_LEVEL_KM;
    autoLastX = player.worldX;
    updateVitalityUI();
    showToast('消耗 1 点活力，自动冒险开始（本次可冒险 10km）。');
    if (gameState === 'walk') {
      input.moving = true;
      syncMoveButton();
    }
  }

  function toggleAutoAdventure() {
    if (autoAdventure) stopAutoAdventure('已停止自动冒险。');
    else startAutoAdventure();
  }

  // 自动喝药：优先血瓶，其次背包里的回血药瓶
  function autoDrinkPotion() {
    if (combat.hp > combat.maxHp * 0.5) return;
    if (flaskCharges > 0) {
      useFlask();
      return;
    }
    const pot = inventory.filter(function (it) { return it.type === 'potion'; })[0];
    if (pot) usePotion(pot);
  }

  function updateAutoAdventure(dt) {
    if (!autoAdventure) return;
    if (gameState !== 'walk' && gameState !== 'battle') return;

    // 有任何弹窗打开时暂停自动行为，避免误操作
    const popupOpen = document.querySelector('.popupOverlay:not(.hidden):not(#logPopup)');
    const passed = player.worldX - autoLastX;
    autoLastX = player.worldX;
    if (popupOpen) return;

    if (gameState === 'walk' && passed > 0) {
      autoKmLeft -= passed / KM_UNIT;
      while (autoKmLeft <= 0) {
        if (vitality >= 1) {
          vitality -= 1;
          autoKmLeft += ENEMY_LEVEL_KM;
          saveGame();
          updateVitalityUI();
          showToast('自动冒险继续：消耗 1 点活力，剩余活力 ' + vitality + '。');
        } else {
          stopAutoAdventure('10km 冒险里程已用完，活力不足，自动冒险结束。');
          return;
        }
      }
    }

    autoDrinkPotion();
    if (player.bottleneck && hasSpiritItem()) breakthrough();

    if (gameState === 'walk') {
      if (corpse && !corpse.searched) searchCorpse(true);
      if (!input.moving) {
        input.moving = true;
        syncMoveButton();
      }
      return;
    }

    // 战斗中：自动出手 / 自动施法
    if (pendingVictory > 0 || slashFx || battleTurn !== 'player') return;
    // 法术窗口里「激活」的技能优先；用不了（魔力不够 / 宠物已满）再退回劈砍 / 普通攻击
    if (autoSkillId) {
      const fav = knownSkills.filter(function (s) { return s.id === autoSkillId; })[0];
      if (fav && autoSkillUsable(fav)) {
        castSkill(fav);
        return;
      }
    }
    const slash = knownSkills.filter(function (s) { return s.id === 'slash'; })[0];
    if (slash && combat.mp >= slash.cost) castSkill(slash);
    else doPlayerAction('attack');
  }

  // 自动冒险时某个技能能不能用（避免空放浪费回合）
  function autoSkillUsable(skill) {
    if (!skill || skill.id === 'summonPet') return false;   // 法杖被动，不主动施放
    if (combat.mp < skill.cost) return false;
    if (skill.type === 'summon') return alivePets().length < PET_MAX;
    if (skill.type === 'toggle') return !bloodRebound;      // 持续技能只负责"打开"
    return true;
  }

  function equipItem(item) {
    if (!item || item.type !== 'equipment') return;
    if (!(item.slot in equipment)) return;
    const old = equipment[item.slot];
    if (old) inventory.push(old);
    equipment[item.slot] = item;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    // 灵宝附带主动技能：装备后自动学会
    if (item.special && item.special.kind === 'skill' && item.special.skillId) {
      const learned = learnSkillById(item.special.skillId);
      if (learned) {
        showToast('灵宝传承：自动学会主动技能「' + learned.name + '」！');
        pushBattleLog('灵宝「' + item.special.name + '」传承：学会「' + learned.name + '」', 'reward');
      }
    }
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast(
      '已装备「' + item.name + '」' +
      (item.weaponType === '法杖' ? '，宠物自动现身跟随。' : '')
    );
  }

  function unequipSlot(slot) {
    const old = equipment[slot];
    if (!old) return;
    equipment[slot] = null;
    inventory.push(old);
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast(
      '已卸下「' + old.name + '」' +
      (old.weaponType === '法杖' ? '，宠物消失了。' : '')
    );
  }

  function usePotion(item) {
    if (item.type !== 'potion') return;
    if (combat.hp >= combat.maxHp) {
      showToast('生命已满，暂时不需要药水。');
      return;
    }
    combat.hp = Math.min(combat.maxHp, combat.hp + (item.heal || 25));
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    updateBattleHud();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('使用「' + item.name + '」，恢复 ' + (item.heal || 25) + ' 点生命。');
  }

  // 出售装备：价格随等阶与词缀强度提升
  function sellPrice(item) {
    const ratio = { common: 0.34, magic: 0.52, rare: 0.72 }[item.rarity] || 0.3;
    return Math.max(1, Math.round(item.price * ratio));
  }

  function sellItem(item) {
    if (!item || item.type !== 'equipment') return;
    const value = sellPrice(item);
    gold += value;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    updateGold();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('售出「' + item.name + '」，获得 ' + value + ' 金币。');
  }

  // ---------- 素材加载 ----------
  function loadAssets() {
    ASSETS.forEach(function (item) {
      const img = new Image();
      img.onload = img.onerror = function () {
        loaded++;
        if (loadTask.active) {
          loadTask.real = loaded / ASSETS.length;
          loadTask.tip = '已载入 ' + loaded + ' / ' + ASSETS.length + ' 项资源';
        }
      };
      img.src = item[1];
      images[item[0]] = img;
    });
  }

  // ---------- 开始游戏前的资源加载进度条（左下角）----------
  const LOAD_MIN_TIME = 0.9;   // 进度条最短展示时间，避免一闪而过
  const loadTask = { active: false, real: 0, shown: 0, t: 0, tip: '', onDone: null };

  // ---------- 账号与存档 ----------
  // 存档按「账号 + 密码」分开：同样的账号密码 = 继续原档案；否则新建一个档案。
  // 数据都存在浏览器本地（localStorage），仅用于本地档案区分，不是安全校验。
  const ACCOUNT_KEY = 'homeward.accounts.v1';
  const LEGACY_SAVE_KEY = SAVE_KEY;      // 旧版单存档（已废弃，首次运行会清掉）
  const account = { name: '', ip: '', hash: '', isNew: false, nickname: '' };
  let loginConfirmNew = false;           // 兼容旧逻辑的复位标记（现在账号名唯一，不再允许同名新建）

  /* ============================================================
   * 联网状态（聊天 + 云端存档）
   *   服务器可用 → 存档以服务器为准、聊天走 WebSocket
   *   服务器不可用 → 自动回退到本地 localStorage + 本地聊天（单机也能玩）
   * ============================================================ */
  const NET_SERVER_KEY = 'homeward.net.server.v1';
  const NET_DEVICE_KEY = 'homeward.net.device.v1';

  // 本机设备标识：同一个浏览器（同一设备）刷新/重进不会被"单设备登录"挡住
  function deviceId() {
    try {
      let id = window.localStorage.getItem(NET_DEVICE_KEY);
      if (!id) {
        id = 'd' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
        window.localStorage.setItem(NET_DEVICE_KEY, id);
      }
      return id;
    } catch (e) {
      return 'd-unknown';
    }
  }
  const net = {
    api: null,          // createApiClient 的实例
    chat: null,         // createChatClient 的实例
    token: '',
    serverUrl: '',      // 归一化后的服务器地址
    accountOnline: false, // 账号已连上服务器（能读写云端存档）
    chatState: 'offline', // 'connecting' | 'online' | 'offline'
    onlineCount: 0,
    saveTimer: null,
    lastPushAt: 0,
    pushCount: 0,
    lastError: '',
    identityBound: false,   // 是否已绑定实名（服务器账号级信息）
    profile: null,
    pendingChatText: ''     // 因为没实名被拦下的那条消息
  };

  function netServerFromInput(raw) {
    // 登录页那个 IP 输入框：空 = 当前页面同源（nginx 反代 /api、/ws）
    return normalizeServerUrl(raw);
  }

  function netRememberServer(url) {
    net.serverUrl = url;
    try { window.localStorage.setItem(NET_SERVER_KEY, url); } catch (e) { /* 忽略 */ }
  }

  function accountHash(name, pwd) {
    let h = 0x811c9dc5;
    const s = String(name) + '\u0000' + String(pwd);
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return ('00000000' + h.toString(16)).slice(-8);
  }

  function saveKeyFor(hash) { return 'homeward.save.' + hash; }
  // 没有登录（例如测试直连 / 自动化）时继续用旧的本地存档 key，行为跟以前一致
  function activeSaveKey() { return account.hash ? saveKeyFor(account.hash) : LEGACY_SAVE_KEY; }

  // 新档案：把可存档的进度全部归零
  function resetProgressState() {
    vitality = 0;
    lastSignDate = '';
    player.level = 1;
    player.xp = 0;
    player.bottleneck = false;
    onlineSeconds = 0;
    onlineRewardIndex = 0;   // 在线奖励阶梯也从头开始
    onlinePending = 0;
    Object.keys(codexUnlocked).forEach(function (k) { delete codexUnlocked[k]; });
    gachaPity = 0;
    gachaCount = 0;
    caveToken = 0;
    player.attrs = { str: 0, agi: 0, int: 0 };
    player.attrPoints = 0;
    // 血瓶：新档案给满 5 层充能，进度清零
    flaskCharges = 5;
    flaskKillCommon = 0;
    flaskKillElite = 0;
    newbieChestUsed = false;   // 新档案：新手宝箱重新给一次
    quests.first = { accepted: false, done: false };   // 新档案：任务也从头开始
    // 技能：新档案只保留初始「劈砍」
    knownSkills.length = 0;
    knownSkills.push(starterSkill());
    autoSkillId = null;   // 新档案没有「自动冒险优先技能」
    resumeWorldX = null;  // 新档案从起点开始
    defeatedEnemies = 0;
    // 世界BOSS：刷新时刻跟着上海时间走，新档案只需要清空本轮状态
    wb.phase = 'wait';
    wb.spawnedAt = 0;
    wb.expiresAt = 0;
    wb.maxHp = 0;
    wb.hp = 0;
    wb.killed = 0;
    wb.claimed = false;
    wb.lastRewardDay = '';
    wbOverride = null;
    updateVitalityUI();
    updateLevelUI();
    updateOnlineUI();
  }

  function readAccounts() {
    try {
      const data = JSON.parse(window.localStorage.getItem(ACCOUNT_KEY) || 'null');
      if (data && Array.isArray(data.accounts)) return data;
    } catch (e) { /* 忽略 */ }
    return { accounts: [] };
  }

  function writeAccounts(store) {
    try { window.localStorage.setItem(ACCOUNT_KEY, JSON.stringify(store)); } catch (e) { /* 忽略 */ }
  }

  function findAccountByName(name) {
    const store = readAccounts();
    // 账号名唯一：按"归一化 + 忽略大小写"比较（AAA / aaa / ＡＡＡ / "AAA " 视为同一个）
    const key = normalizeAccountName(name).toLowerCase();
    return store.accounts.filter(function (a) {
      return normalizeAccountName(a.name).toLowerCase() === key;
    });
  }

  // 账号名归一化：全角转半角、去零宽字符、折叠空格、首尾去空格
  function normalizeAccountName(name) {
    let s = String(name == null ? '' : name);
    try { s = s.normalize('NFKC'); } catch (e) { /* 忽略 */ }
    return s.replace(/[\u200b-\u200f\u2028\u2029\ufeff]/g, '').replace(/\s+/g, ' ').trim();
  }

  // 登记新档案（同名不同密码会各占一个档案，互不覆盖）
  function registerAccount(name, hash, extra) {
    const store = readAccounts();
    const exists = store.accounts.some(function (a) { return a.hash === hash; });
    if (!exists) store.accounts.push({ name: name, hash: hash, createdAt: Date.now(), lastPlayed: Date.now() });
    writeAccounts(store);
    return { name: name, hash: hash, ip: (extra && extra.ip) || '' };
  }

  function touchAccount(hash, level) {
    const store = readAccounts();
    store.accounts.forEach(function (a) {
      if (a.hash === hash) { a.lastPlayed = Date.now(); if (level) a.level = level; }
    });
    writeAccounts(store);
  }

  // 抹掉本机所有档案（含旧版单存档）——首次运行清一次，保证"从零开始"
  function resetAllSaves() {
    try {
      const store = readAccounts();
      store.accounts.forEach(function (a) { window.localStorage.removeItem(saveKeyFor(a.hash)); });
      window.localStorage.removeItem(ACCOUNT_KEY);
      window.localStorage.removeItem(LEGACY_SAVE_KEY);
    } catch (e) { /* 忽略 */ }
    Object.keys(window.localStorage).forEach(function (k) {
      if (k.indexOf('homeward.save.') === 0) window.localStorage.removeItem(k);
    });
  }

  // 自动化测试用：地址后面加 ?skiplogin=1 直接跳过登录页（正常打开不受影响）
  function skipLoginRequested() {
    try {
      const p = new URLSearchParams(location.search);
      const v = p.get('skiplogin');
      return v === '1' || v === 'true';
    } catch (e) {
      return false;
    }
  }

  function showLoginError(msg) {
    if (!ui.loginError) return;
    ui.loginError.textContent = msg;
    ui.loginError.classList.remove('hidden');
  }

  function resetLoginConfirm() {
    if (!loginConfirmNew) return;
    loginConfirmNew = false;
    if (ui.btnLogin) ui.btnLogin.textContent = '登录';
  }

  let loginBusy = false;

  /* ============================================================
   * 赞助 / 自愿捐赠
   *   收款码图片固定放这两个路径（没有图就显示占位提示，不影响其它功能）：
   *     public/assets/img/donate/alipay.png
   *     public/assets/img/donate/wechat.png
   * ============================================================ */
  const DONATE_CODES = [
    { el: 'donateAlipay', src: assetUrl('assets/img/donate/alipay.png') },
    { el: 'donateWechat', src: assetUrl('assets/img/donate/wechat.png') }
  ];
  let donateLoaded = false;

  function loadDonateCodes() {
    DONATE_CODES.forEach(function (c) {
      const img = ui[c.el];
      if (!img) return;
      const box = img.parentNode;
      img.onload = function () {
        if (img.naturalWidth > 0) box.classList.add('hasImg');
      };
      img.onerror = function () {
        box.classList.remove('hasImg');
      };
      if (!img.getAttribute('src')) img.setAttribute('src', c.src + '?v=1');
      else if (img.complete && img.naturalWidth > 0) box.classList.add('hasImg');
    });
    donateLoaded = true;
  }

  function openDonate() {
    if (!ui.donatePopup) return;
    if (!donateLoaded) loadDonateCodes();
    ui.donatePopup.classList.remove('hidden');
    hideItemTip();
  }

  function closeDonate() {
    if (ui.donatePopup) ui.donatePopup.classList.add('hidden');
  }

  /* 赞助入口开关：先暂时隐藏（按钮、弹窗、收款码都留在工程里，改回 true 就恢复） */
  const DONATE_ENABLED = false;

  // 首页（路上休息场景 / 标题页）显示「赞助」按钮；进游戏后隐藏，免得挡住命令轮盘
  function updateDonateButton() {
    if (!ui.btnDonate) return;
    const loginOpen = ui.loginScreen && !ui.loginScreen.classList.contains('hidden');
    const popupOpen = ui.donatePopup && !ui.donatePopup.classList.contains('hidden');
    const show = DONATE_ENABLED && (gameState === 'intro' || gameState === 'title') && !loginOpen && !popupOpen;
    ui.btnDonate.classList.toggle('hidden', !show);
    updateBeianBar();
  }

  /* 备案信息条：只挂在首页（点角色登录前）和标题页，进游戏后收起，
     免得压住右下角命令轮盘和左下角聊天窗 */
  function updateBeianBar() {
    if (!ui.beianBar) return;
    const show = gameState === 'intro' || gameState === 'title';
    ui.beianBar.classList.toggle('hidden', !show);
    // 同时给舞台底部留出备案条的高度：备案信息就落在游戏画面下方，
    // 不会贴着路上的角色；进游戏后把高度收回去，画面恢复满屏
    const stageEl = document.getElementById('stage');
    if (stageEl && stageEl.classList.contains('beianOn') !== show) {
      stageEl.classList.toggle('beianOn', show);
      window.dispatchEvent(new Event('resize'));   // 舞台变矮了，HUD 重新排一次
    }
  }

  /* ============================================================
   * 首页状态（intro）：打开网页先看到"角色在路上休息"，
   * 点角色才弹出登录窗口（而不是一进来就糊玩家一脸登录框）
   * ============================================================ */
  function enterIntroIdle() {
    input.moving = false;
    player.vx = 0;
    playerAtkT = 0;
    playerResting = true;
    restPoseIdx = INTRO_REST_POSE;   // 固定用 player_rest_3.png 这张
    restPoseTimer = 0;
    preloadIntroPlayerFrames();      // 首页也要能看到角色（否则路上只有背景）
    updateDonateButton();
    hideIntroDialog();
    scheduleIntroDialog();           // 停在首页 10 秒还没登录 → 引路人开口
  }

  /* ============================================================
   * 首页引导剧情：停在首页 10 秒，且这 10 秒内没有被弹出账号窗口，
   * 就出现引路人对白（点它可以进登录窗口）。
   * ============================================================ */
  const INTRO_DIALOG_DELAY_MS = 10000;
  let introDialogTimer = null;

  function cancelIntroDialogTimer() {
    if (introDialogTimer) {
      clearTimeout(introDialogTimer);
      introDialogTimer = null;
    }
  }

  function introDialogVisible() {
    return !!ui.introDialog && !ui.introDialog.classList.contains('hidden');
  }

  // 收起对白并取消计时（已经登录 / 离开首页时用）
  function hideIntroDialog() {
    cancelIntroDialogTimer();
    if (ui.introDialog) ui.introDialog.classList.add('hidden');
  }

  // 重新计时：只有在"还停在首页、账号窗口也没开"的时候才会真的弹出来
  function scheduleIntroDialog() {
    cancelIntroDialogTimer();
    if (!ui.introDialog) return;
    introDialogTimer = setTimeout(function () {
      introDialogTimer = null;
      if (gameState !== 'intro') return;
      if (ui.loginScreen && !ui.loginScreen.classList.contains('hidden')) return;
      ui.introDialog.classList.remove('hidden');
    }, INTRO_DIALOG_DELAY_MS);
  }

  // 点到角色身上了吗（用角色贴图的实际大小做判定，留一点余量）
  function hitPlayerAt(sx, sy) {
    const scale = playerFx.scale * caveScale();
    const px = player.worldX - cameraX();
    const halfW = (PLAYER_FRAME.w * scale) * 0.42;
    return Math.abs(sx - px) <= halfW &&
      sy >= GROUND_Y - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * scale - 12 &&
      sy <= GROUND_Y + 26 * scale;
  }

  function openLoginFromIntro() {
    if (ui.loginScreen && !ui.loginScreen.classList.contains('hidden')) return;
    hideIntroDialog();                 // 登录窗口一开，引路人就让位
    ui.title.classList.add('hidden');
    ui.loginScreen.classList.remove('hidden');
    updateDonateButton();
    if (ui.loginUser) {
      ui.loginUser.focus();
      if (!ui.loginUser.value) ui.loginUser.value = '';
    }
  }

  // 点登录窗口之外的空白处：收起登录窗口，回到首页那个"角色在路上休息"的场景
  function closeLoginFromIntro() {
    if (!ui.loginScreen || ui.loginScreen.classList.contains('hidden')) return;
    if (loginBusy) return;                 // 正在连服务器，别把窗口中途收掉
    ui.loginScreen.classList.add('hidden');
    if (ui.loginError) ui.loginError.classList.add('hidden');
    updateDonateButton();                  // 「赞助」按钮和底部备案条重新出现
    scheduleIntroDialog();                 // 又回到首页了，10 秒后引路人会再开口
  }

  // 登录：优先连服务器（账号 + 云端存档 + 聊天），连不上再回退到本地单机档案
  async function doLogin() {
    if (ui.loginScreen.classList.contains('hidden')) return; // 已经登录过了
    if (loginBusy) return;
    const name = (ui.loginUser.value || '').trim();
    const pwd = (ui.loginPwd.value || '').trim();
    const ip = (ui.loginIp.value || '').trim();
    if (!name) { showLoginError('请填写账号'); ui.loginUser.focus(); return; }
    if (!pwd) { showLoginError('请填写密码'); ui.loginPwd.focus(); return; }
    if (!ip) { showLoginError('请填写服务器 IP'); ui.loginIp.focus(); return; }

    const serverUrl = netServerFromInput(ip);
    loginBusy = true;
    setLoginButton('连接服务器…', true);
    let loginResult = null;
    let serverErr = null;
    try {
      const api = createApiClient({ baseUrl: serverUrl });
      try {
        loginResult = await api.loginWithDevice(name, pwd, deviceId());
      } catch (err) {
        if (err.code === 'no_user') {
          // 服务器上还没有这个账号 → 注册后用同样的账号密码登录（等于新建档案）
          await api.register(name, pwd);
          loginResult = await api.loginWithDevice(name, pwd, deviceId());
        } else {
          throw err;
        }
      }
      net.api = api;
      net.token = loginResult.token;
      net.accountOnline = true;
      net.lastError = '';
      netRememberServer(serverUrl);
    } catch (err) {
      serverErr = err;
      net.accountOnline = false;
      net.token = '';
      // 服务器明确说"账号被别人占了 / 密码不对 / 被限流" → 这是真错误，不能静默当单机
      if (err && (err.code === 'user_exists' || err.code === 'bad_pwd' || err.code === 'locked' ||
        err.code === 'already_online')) {
        loginBusy = false;
        setLoginButton('登录', false);
        showLoginError('服务器拒绝：' + err.message);
        return;
      }
      net.lastError = err && err.message ? err.message : '连接失败';
    }
    loginBusy = false;
    setLoginButton('登录', false);
    if (!loginResult) {
      // 连不上服务器 → 走本地单机档案（和以前完全一样）
      showToast('连不上服务器（' + net.lastError + '），已切换到本地单机档案。');
      doLocalLogin(name, pwd, ip);
      return;
    }
    finishServerLogin(name, pwd, ip, loginResult);
  }

  function setLoginButton(text, disabled) {
    if (!ui.btnLogin) return;
    ui.btnLogin.textContent = text;
    ui.btnLogin.disabled = !!disabled;
  }

  /* ---------- 全服重置纪元 ----------
   * 服务器执行 tools/reset-all.cjs 会把 data/meta.json 的 epoch +1，登录接口会带回来。
   * 客户端发现服务器纪元比自己记的大 → 说明服务器清过档，于是把本机所有账号与存档也清掉，
   * 避免"服务器没存档 → 用本地档推上去"把旧进度复活。
   * 老服务器不带这个字段时 epoch=0，行为跟以前完全一致。 */
  const WIPE_EPOCH_KEY = 'homeward.wipeEpoch.v1';

  function localWipeEpoch() {
    try { return Math.max(0, Math.floor(Number(window.localStorage.getItem(WIPE_EPOCH_KEY)) || 0)); }
    catch (e) { return 0; }
  }

  function applyServerWipeEpoch(rawEpoch) {
    const epoch = Math.max(0, Math.floor(Number(rawEpoch) || 0));
    if (!epoch) return false;                 // 老服务器 / 没重置过：不动本地
    const local = localWipeEpoch();
    if (epoch <= local) {
      if (epoch < local) {                    // 换了服务器（纪元反而更小）→ 以服务器的为准
        try { window.localStorage.setItem(WIPE_EPOCH_KEY, String(epoch)); } catch (e) { /* 忽略 */ }
      }
      return false;
    }
    resetAllSaves();                          // 本机所有档案 + 存档一起清
    try { window.localStorage.setItem(WIPE_EPOCH_KEY, String(epoch)); } catch (e) { /* 忽略 */ }
    return true;
  }

  // 服务器登录成功：决定用云端存档还是本地较新的存档
  function finishServerLogin(name, pwd, ip, loginResult) {
    const hash = accountHash(name, pwd);
    const key = saveKeyFor(hash);
    // 服务器做过"全服重置"（纪元变大）→ 先把本机所有旧档作废，
    // 否则下面会走 local-first，把浏览器里那份旧存档又推回云端
    const wipedByServer = applyServerWipeEpoch(loginResult && loginResult.saveEpoch);
    let localSave = null;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) localSave = JSON.parse(raw);
    } catch (e) { /* 忽略 */ }

    const serverSave = loginResult.save || null;
    const serverSavedAt = Number(loginResult.savedAt) || Number(serverSave && serverSave.savedAt) || 0;
    const localSavedAt = Number(localSave && localSave.savedAt) || 0;
    let source = 'new';
    let localIgnored = false;   // 本机那份被忽略（云端没有存档，或本机那份更旧/更新）

    /* 以云端为权威副本：登录后一律用服务器的存档。
       以前这里还有"本地更新 → 推给服务器"的分支，结果老客户端会把浏览器里那份旧存档
       覆盖到云端（服务器清过档也会被推回来）。那个行为已经去掉，客户端只读不写。 */
    if (serverSave) {
      try { window.localStorage.setItem(key, JSON.stringify(serverSave)); } catch (e) { /* 忽略 */ }
      source = 'server';
      // 本机那份比云端还新：只提示，绝不上传（避免覆盖云端）
      localIgnored = !!(localSave && localSavedAt > serverSavedAt);
    } else if (localSave) {
      // 云端没有存档 → 同样以云端为准：把本机那份丢掉，从零开始。
      // （只"不上传"是不够的——之后第一次自动存档照样会把它写进云端）
      try { window.localStorage.removeItem(key); } catch (e) { /* 忽略 */ }
      localSave = null;
      localIgnored = true;
    }

    account.name = name;
    account.ip = ip;
    account.hash = hash;
    account.isNew = source === 'new';
    // 昵称：服务器上有就用服务器的；服务器没有则用本机缓存（并尝试同步上去）
    const serverNick = (loginResult.user && loginResult.user.nickname) || '';
    const localNick = loadLocalNickname();
    account.nickname = serverNick || localNick || '';
    if (serverNick) {
      saveLocalNickname(serverNick);
    } else if (localNick && net.accountOnline && net.api && net.token) {
      net.api.setNickname(net.token, localNick).catch(function () { /* 重名或含屏蔽词就算了 */ });
    }
    updateNickUI();
    registerAccount(name, hash, { ip: ip });
    loadSave();
    enterTitleScreen(ip);

    const level = player.level || 1;
    if (wipedByServer) {
      showToast('服务器已重置全服数据，本机旧存档作废：' + name + ' 从零开始。');
    } else if (source === 'server') {
      showToast(localIgnored
        ? ('欢迎回来，' + name + '（以云端存档为准 · 境界 ' + level + '；本机那份旧存档已忽略，不会再上传覆盖）')
        : ('欢迎回来，' + name + '（已从服务器读取存档 · 境界 ' + level + '）'));
    } else {
      showToast('已创建云端档案：' + name + '（从零开始）');
    }
  }

  // 服务器不可用时的本地档案登录（原逻辑）
  function doLocalLogin(name, pwd, ip) {

    const hash = accountHash(name, pwd);
    const sameName = findAccountByName(name);
    const sameAccount = sameName.filter(function (a) { return a.hash === hash; })[0];

    // ① 账号 + 密码都对得上 → 继续这个档案
    if (sameAccount) {
    account.name = name;
    account.ip = ip;
    account.hash = hash;
    account.isNew = false;
    account.nickname = loadLocalNickname();
    updateNickUI();
    registerAccount(name, hash, { ip: ip });   // 顺便刷新 lastPlayed
      loadSave();                                 // 读这个账号的存档
      enterTitleScreen(ip);
      showToast('欢迎回来，' + name + '（继续原档案，境界 ' + (player.level || 1) + '）');
      return;
    }

    // ② 账号名已被占用（唯一性）：不允许用同名新建档案，直接提示
    if (sameName.length) {
      showLoginError('账号名「' + sameName[0].name + '」已被占用（账号名唯一，不能重复创建）。' +
        '如果这是你的账号，请填写正确密码后登录；想换账号请换一个名字。');
      loginConfirmNew = false;
      return;
    }

    // ③ 全新账号 → 新档案，从零开始
    account.name = name;
    account.ip = ip;
    account.hash = hash;
    account.isNew = true;
    account.nickname = loadLocalNickname();
    updateNickUI();
    // 新档案：先把旧版单存档彻底删掉，再把进度归零，保证真的从零开始
    try { window.localStorage.removeItem(LEGACY_SAVE_KEY); } catch (e) { /* 忽略 */ }
    resetProgressState();
    registerAccount(name, hash, { ip: ip });
    loadSave();                                   // 新档案没有数据 → 走默认值
    enterTitleScreen(ip);
    showToast('已创建新档案：' + name + '（从零开始）');
  }

  // 登录成功 → 露出标题页（开始游戏）
  // 删档：先弹确认，确认后才真的清
  function askWipeSaves() {
    if (ui.wipePopup) ui.wipePopup.classList.remove('hidden');
  }

  function closeWipeConfirm() {
    if (ui.wipePopup) ui.wipePopup.classList.add('hidden');
  }

  function doWipeSaves() {
    resetAllSaves();
    account.name = '';
    account.ip = '';
    account.hash = '';
    account.isNew = false;
    resetProgressState();
    closeWipeConfirm();
    if (ui.loginUser) ui.loginUser.value = '';
    if (ui.loginPwd) ui.loginPwd.value = '';
    if (ui.loginUser) ui.loginUser.focus();
    showLoginError('已删除本机所有档案，请重新输入账号创建新档案。');
    showToast('已删除本机所有档案。');
  }

  function enterTitleScreen(ip) {
    hideIntroDialog();
    ui.loginError.classList.add('hidden');
    ui.loginScreen.classList.add('hidden');
    ui.title.classList.remove('hidden');
    gameState = 'title';        // 离开首页（角色休息）状态
    updateDonateButton();
    // 账号确定后再连聊天（昵称要用账号名）；单机模式保持本地聊天
    if (net.accountOnline) connectChat();
    updateNickUI();
    if (ui.btnLogin) ui.btnLogin.textContent = '登录';
    loginConfirmNew = false;
    if (ui.titleUser) {
      ui.titleUser.textContent = '当前账号：' + account.name +
        '　·　服务器：' + (ip || account.ip) +
        '　·　' + (account.isNew ? '新档案（从零开始）' : '继续原档案 · 境界 ' + (player.level || 1));
    }
    if (ui.start) ui.start.focus();
  }

  function updateLoadUi() {
    if (!ui.loadPanel) return;
    const pct = Math.round(loadTask.shown * 100);
    ui.loadFill.style.width = pct + '%';
    ui.loadPct.textContent = pct + '%';
    ui.loadTip.textContent = loadTask.tip || '正在准备场景…';
  }

  function beginStartGame() {
    if (loadTask.active) return;
    if (ui.loginScreen && !ui.loginScreen.classList.contains('hidden')) return; // 必须先登录
    loadTask.active = true;
    loadTask.real = loaded / ASSETS.length;
    loadTask.shown = 0;
    loadTask.t = 0;
    loadTask.tip = '正在载入场景贴图…';
    loadTask.onDone = startScene;
    ui.title.classList.add('hidden');
    ui.loadPanel.classList.remove('hidden');
    updateLoadUi();
    if (loaded < ASSETS.length) loadAssets();
    else loadTask.real = 1;
  }

  function updateLoading(dt) {
    if (!loadTask.active) return;
    loadTask.t += dt;
    // 进度按「最短展示时间」匀速推进，且不会超过真实加载进度：
    // 资源秒载完时也能看到进度条从头走到尾；资源大时则真实等待
    const ramp = Math.min(1, loadTask.t / LOAD_MIN_TIME);
    const target = Math.min(loadTask.real, ramp);
    loadTask.shown += (target - loadTask.shown) * Math.min(1, dt * 10);
    if (loadTask.shown > target) loadTask.shown = target;
    if (loadTask.real >= 1 && ramp >= 1 && loadTask.shown > 0.985) loadTask.shown = 1;
    updateLoadUi();
    if (loadTask.shown >= 0.999 && loadTask.real >= 1 && loadTask.t >= LOAD_MIN_TIME) {
      loadTask.active = false;
      ui.loadPanel.classList.add('hidden');
      const done = loadTask.onDone;
      loadTask.onDone = null;
      if (done) done();
    }
  }

  // ---------- 敌人生成 ----------
  function spawnNextEnemy() {
    if (cave) return; // 洞府里用 spawnCaveEnemy
    // 敌人等级由所处的路程决定：0~10km 为 Lv.1，之后每 10km 提升一级
    let x = START_X + FIRST_ENEMY_DIST + defeatedEnemies * ENEMY_GAP;
    // 读档续玩 / 瞬移后：敌人必须刷在玩家前方，否则会一路遇不到人
    if (x < player.worldX + 300) x = player.worldX + rand(700, 1100);
    const km = Math.max(0, (x - START_X) / KM_UNIT);
    const level = 1 + Math.floor(km / ENEMY_LEVEL_KM);
    const maxHp = 62 + level * 16;
    const atk = 8 + level * 3;
    // 战斗用移速：围绕 100% 随机浮动，随等级略增
    const combatSpeed = clamp(1 + level * 0.02 + rand(-0.06, 0.05), 0.88, 1.8);
    const kind = ENEMY_SPRITES[randInt(0, ENEMY_SPRITES.length - 1)];
    activeEnemy = {
      sprite: kind.key,
      kindName: kind.name,
      name: kind.name,
      elite: false,
      tier: 'common',
      level: level,
      baseX: x,             // 敌人巡逻区域的中心
      x: x,                 // 敌人当前世界坐标（随巡逻变化）
      offset: 0,
      dir: Math.random() < 0.5 ? -1 : 1,
      speed: rand(20, 42),
      combatSpeed: combatSpeed,
      openingAttack: false,
      range: rand(55, 95),
      walkT: rand(0, 10),
      animPhase: rand(0, 8),
      hurtT: 0,
      maxHp: maxHp,
      hp: maxHp,
      atk: atk
    };
  }

  // ---------- 精英挑战 ----------
  function playerLevel() {
    return player.level || 1;
  }

  function spawnEliteEnemy() {
    const level = playerLevel();
    const x = player.worldX + 900;
    const maxHp = Math.round((62 + level * 16) * 2.4);
    const atk = Math.round((8 + level * 3) * 1.6);
    const skillDef = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
    const kindE = ENEMY_SPRITES[randInt(0, ENEMY_SPRITES.length - 1)];
    activeEnemy = {
      sprite: kindE.key,
      kindName: kindE.name,
      name: '精英' + kindE.name,
      elite: true,
      tier: 'elite',
      skill: { id: skillDef.id, name: skillDef.name, type: skillDef.type },
      healUsed: false,
      level: level,
      baseX: x,
      x: x,
      offset: 0,
      dir: -1,
      speed: rand(10, 24),
      combatSpeed: clamp(1 + level * 0.03 + rand(0, 0.08), 0.9, 2),
      openingAttack: false,
      range: rand(40, 70),
      walkT: rand(0, 10),
      animPhase: rand(0, 8),
      hurtT: 0,
      maxHp: maxHp,
      hp: maxHp,
      atk: atk
    };
  }

  // 传奇敌人：比精英更强，携带两个技能，掉落奖励更好
  function spawnLegendEnemy() {
    const level = playerLevel() + 2;
    const x = player.worldX + 900;
    const maxHp = Math.round((62 + level * 16) * 3.6);
    const atk = Math.round((8 + level * 3) * 2.3);
    const a = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
    const b = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
    const skillA = { id: a.id, name: a.name, type: a.type };
    const skillB = { id: b.id, name: b.name, type: b.type };
    const kindL = ENEMY_SPRITES[randInt(0, ENEMY_SPRITES.length - 1)];
    activeEnemy = {
      sprite: kindL.key,
      kindName: kindL.name,
      name: '传奇' + kindL.name,
      elite: true,
      legend: true,
      tier: 'legend',
      skill: skillA,
      skills: [skillA, skillB],
      healUsed: false,
      level: level,
      baseX: x,
      x: x,
      offset: 0,
      dir: -1,
      speed: rand(8, 18),
      combatSpeed: clamp(1 + level * 0.03 + rand(0, 0.1), 1, 2.2),
      openingAttack: false,
      range: rand(30, 60),
      walkT: rand(0, 10),
      animPhase: rand(0, 8),
      hurtT: 0,
      maxHp: maxHp,
      hp: maxHp,
      atk: atk
    };
  }

  function tierName(tier) {
    return tier === 'legend' ? '传奇' : tier === 'elite' ? '精英' : tier === 'magic' ? '蓝色' : '普通';
  }

  // 敌人的显示名：优先用类型名（兵匪 / 乞丐 …），没有就退回品级名
  function enemyLabel(e, withLevel) {
    if (!e) return '';
    const base = e.name || tierName(e.tier);
    return withLevel === false ? base : base + '（Lv.' + e.level + '）';
  }

  // ---------- 敌人美术：取帧 & 按锚点贴图 ----------
  function enemySpriteDef(enemy) {
    const key = enemy && enemy.sprite;
    for (let i = 0; i < ENEMY_SPRITES.length; i++) if (ENEMY_SPRITES[i].key === key) return ENEMY_SPRITES[i];
    return ENEMY_SPRITES[0];
  }

  // 只有移动帧：赶路按 ENEMY_WALK_FPS 播，战斗站定时慢慢循环
  function enemyFrameImage(enemy, fps) {
    const def = enemySpriteDef(enemy);
    const t = enemyAnimClock + (enemy && enemy.animPhase ? enemy.animPhase : 0);
    const idx = Math.abs(Math.floor(t * fps)) % def.frames;
    const im = images[def.key + '_' + (idx + 1)];
    return im && im.complete && im.naturalWidth > 0 ? im : null;
  }

  // 世界BOSS 立绘：锚点在水平中心、脚底压在落地线上；带呼吸起伏与缓慢摇摆
  function drawWorldBossArt(img, sx, groundY, scale) {
    if (!img) return false;
    const k = scale * WB_BOSS_ART.scaleAdj;      // 导出图是显示尺寸的 2 倍
    const w = WB_BOSS_ART.w * k;
    const h = WB_BOSS_ART.h * k;
    const breathe = Math.sin(enemyAnimClock * 1.45) * 3.2;      // 呼吸起伏
    const sway = Math.sin(enemyAnimClock * 0.7) * 2.2;          // 左右微摆
    const top = groundY + 4 - (WB_BOSS_ART.h - WB_BOSS_ART.basePad) * k + breathe;
    // 脚下多一层厚重的暗影，压住这个庞然大物
    ctx.save();
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = 'rgba(48, 16, 8, .55)';
    ctx.beginPath();
    ctx.ellipse(sx - 10 * k, groundY + 6, 92 * k, 11 * k, -0.04, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    ctx.drawImage(img, sx - w / 2 + sway, top, w, h);
    lastBossArtRect = { x: Math.round(sx - w / 2 + sway), y: Math.round(top), w: Math.round(w), h: Math.round(h),
      centerX: Math.round(sx), bottom: Math.round(top + h) };
    return true;
  }

  // 整帧贴图：锚点（腿脚重心）对齐到 sx，脚底基线固定在 groundY + 4
  // def 可选：传入该模型定义后，可用 def.faceRight 单独覆盖素材朝向
  function drawEnemySprite(img, sx, groundY, scale, faceRight, bob, def) {
    if (!img) return false;
    const artFacesRight = def && def.faceRight !== undefined ? def.faceRight : ENEMY_ART_FACES_RIGHT;
    const flip = artFacesRight ? !faceRight : !!faceRight;
    const w = ENEMY_FRAME.w * scale;
    const h = ENEMY_FRAME.h * scale;
    const top = groundY + 4 - (ENEMY_FRAME.h - ENEMY_FRAME.basePad) * scale + (bob || 0) * scale;
    const ax = ENEMY_FRAME.anchorX * scale;
    if (flip) {
      ctx.save();
      ctx.translate(sx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -ax, top, w, h);
      ctx.restore();
    } else {
      ctx.drawImage(img, sx - ax, top, w, h);
    }
    return true;
  }

  // 传奇BOSS地图第 N 波敌人
  function spawnWaveEnemy(tier) {
    const level = playerLevel() + (tier === 'legend' ? 2 : 0);
    const x = player.worldX + KM_UNIT;
    activeEnemy = buildEnemy(level, tier, { worldX: x });
  }

  // 按品级造一个敌人（洞府 / 地图波次共用）
  function buildEnemy(level, tier, opts) {
    const options = opts || {};
    const x = options.worldX !== undefined ? options.worldX : player.worldX + 900;
    const hpMult = tier === 'magic' ? 1.45 : tier === 'elite' ? 2.4 : tier === 'legend' ? 3.6 : 1;
    const atkMult = tier === 'magic' ? 1.25 : tier === 'elite' ? 1.6 : tier === 'legend' ? 2.3 : 1;
    // 随机挑一种敌人模型 / 类型（兵匪、乞丐、强盗、山猪、水匪、药士、游民、农夫、会计）
    const kind = ENEMY_SPRITES[randInt(0, ENEMY_SPRITES.length - 1)];
    const prefix = tier === 'elite' ? '精英' : tier === 'legend' ? '传奇' : '';
    const enemy = {
      sprite: kind.key,          // 用哪套美术
      kindName: kind.name,       // 类型名（兵匪 / 乞丐 …）
      name: prefix + kind.name,  // 显示名：普通怪就是类型名，精英/传奇会带前缀
      tier: tier,
      elite: tier === 'elite' || tier === 'legend',
      legend: tier === 'legend',
      level: level,
      baseX: x,
      x: x,
      offset: 0,
      dir: -1,
      speed: rand(12, 26),
      combatSpeed: clamp(1 + level * 0.02 + rand(0, 0.06), 0.9, 2.1),
      openingAttack: false,
      range: rand(45, 80),
      walkT: rand(0, 10),
      animPhase: rand(0, 8),   // 让同类敌人的帧错开，不要整齐划一
      hurtT: 0,
      maxHp: Math.round((62 + level * 16) * hpMult),
      atk: Math.round((8 + level * 3) * atkMult)
    };
    enemy.hp = enemy.maxHp;
    // 洞府里的敌人随机带一种特殊增幅
    if (options.cave) enemy.caveBuff = CAVE_BUFFS[randInt(0, CAVE_BUFFS.length - 1)];
    if (tier === 'elite' || tier === 'legend') {
      const a = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
      const skillA = { id: a.id, name: a.name, type: a.type };
      enemy.skill = skillA;
      if (tier === 'legend') {
        const b = SKILL_POOL[randInt(0, SKILL_POOL.length - 1)];
        const skillB = { id: b.id, name: b.name, type: b.type };
        enemy.skills = [skillA, skillB];
      }
      enemy.healUsed = false;
    }
    return enemy;
  }

  function updateLegendBar() {
    if (!ui.legendBar) return;
    if (!legendMap) {
      ui.legendBar.classList.add('hidden');
      return;
    }
    ui.legendBar.classList.remove('hidden');
    ui.legendWave.textContent = legendMap.wave + ' / 10 波';
    ui.legendKm.textContent = (legendMap.km || 0).toFixed(1) + ' / 10 km';
  }

  function spawnNextLegendWave() {
    if (!legendMap) return;
    legendMap.wave += 1;
    const wave = legendMap.wave;
    const tier = wave >= 10 ? 'legend' : (wave % 3 === 1 ? 'common' : wave % 3 === 2 ? 'magic' : 'elite');
    spawnWaveEnemy(tier);
    updateLegendBar();
    pushBattleLog('传奇BOSS地图 第 ' + wave + ' / 10 波：' + tierName(tier) + '敌人出现', 'system');
    showToast('第 ' + wave + ' / 10 波：' + tierName(tier) + '敌人出现！');
  }

  function useLegendTicket(item) {
    if (!item || item.type !== 'legendTicket') return;
    if (gameState !== 'walk') {
      showToast('只能在上路时使用传奇BOSS挑战卷。');
      return;
    }
    if (legendMap) {
      showToast('你已经在传奇BOSS地图中了。');
      return;
    }
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    legendMap = { wave: 0, startX: player.worldX, km: 0 };
    updateEliteButton();
    spawnNextLegendWave();
    input.moving = true;
    syncMoveButton();
    showToast('传奇BOSS挑战开始：10km 路程，共 10 波敌人！');
  }

  // ---------- 精英挑战按钮：超时平移到右侧边缘 ----------
  const ELITE_DOCK_WAIT = 10;    // 秒：在屏幕中央等待点击的时间
  const ELITE_DOCK_MARGIN = 14;  // 贴到右边缘时留的边距
  const ELITE_DOCK_SCALE = 0.62; // 停靠后缩小的比例（和 CSS 里的 scale 保持一致）
  let eliteDockWait = ELITE_DOCK_WAIT; // 调试可改
  let eliteDockTimer = null;
  let eliteDocked = false;
  let eliteDockPending = false;  // 计时到了但按钮当时被藏起来（战斗中），等重新出现再靠边

  // 平移量：让按钮右边缘贴住舞台右侧（Y 轴不动）
  function eliteDockShift() {
    if (!ui.wrap || !ui.btnElite) return 0;
    const wrapRect = ui.wrap.getBoundingClientRect();
    // 按钮水平居中 + 停靠时会缩小，所以右边缘按缩放后的宽度算
    const halfW = (ui.btnElite.offsetWidth || 122) / 2;
    const centeredRight = wrapRect.left + wrapRect.width / 2 + halfW * ELITE_DOCK_SCALE;
    return Math.round((wrapRect.right - ELITE_DOCK_MARGIN) - centeredRight);
  }

  function armEliteDockTimer() {
    if (eliteDockTimer) clearTimeout(eliteDockTimer);
    eliteDockTimer = setTimeout(function () {
      eliteDockTimer = null;
      dockEliteButton();
    }, Math.max(0, eliteDockWait) * 1000);
  }

  function dockEliteButton() {
    if (!ui.btnElite) return;
    if (ui.btnElite.classList.contains('hidden')) {
      // 战斗中按钮被临时藏起来：记下来，等它重新出现时直接靠边
      eliteDockPending = true;
      return;
    }
    const shift = eliteDockShift();
    ui.btnElite.style.setProperty('--eliteShift', shift + 'px');
    eliteDocked = true;
    eliteDockPending = false;
    ui.btnElite.classList.add('docked');
  }

  // 复原到屏幕中央（下次出现时重新计时）
  function resetEliteDock() {
    if (eliteDockTimer) {
      clearTimeout(eliteDockTimer);
      eliteDockTimer = null;
    }
    eliteDocked = false;
    eliteDockPending = false;
    if (!ui.btnElite) return;
    ui.btnElite.classList.remove('docked');
    ui.btnElite.style.removeProperty('--eliteShift');
  }

  // 窗口尺寸变化（4399 全屏 / 手机旋转）：贴边位置跟着重算
  window.addEventListener('resize', function () {
    layoutHud();
    if (!eliteDocked || !ui.btnElite) return;
    const shift = eliteDockShift();
    if (shift > 0) ui.btnElite.style.setProperty('--eliteShift', shift + 'px');
  });

  // ---------- HUD 自适应：舞台（#wrap）变矮时，左侧按钮列收紧、聊天窗变矮 ----------
  // 否则小窗口下聊天窗会盖住「世界BOSS / 抽卡」等按钮，点不到。
  let hudLayoutClock = 0;
  function layoutHud() {
    const wrapEl = ui.wrap || document.getElementById('wrap');
    if (!wrapEl) return;
    if (ui.hud && ui.hud.classList.contains('hidden')) return;   // 还没进游戏就不用量了
    const wrapH = wrapEl.clientHeight || 720;
    const k = clamp(wrapH / 720, 0.5, 1);
    const tight = k < 0.88;
    const veryTight = k < 0.75;
    if (ui.hud) {
      ui.hud.classList.toggle('hudTight', tight);
      ui.hud.classList.toggle('hudVeryTight', veryTight);
    }
    // 左侧功能列：分类标签 + 一排按钮，按宽度给一个统一缩放
    const colScale = tight ? 0.9 : 1;
    if (ui.hud) ui.hud.style.setProperty('--hudColScale', colScale.toFixed(2));
    // 左侧两个分类（养成 / 交易）依次往下排；子按钮直接跟在分类标题下方，
    // 所以按各分类的实际高度顺排即可（世界BOSS 固定顶部居中、战斗日志固定右上角）
    // 从「金币 / 活力」那一行下面留出间距再开始排功能按钮（钱包固定在 76~98px）
    let top = 126;
    const groups = document.querySelectorAll('#hud > .hudMenu');
    for (let i = 0; i < groups.length; i++) {
      groups[i].style.top = Math.round(top) + 'px';
      const h = (groups[i].offsetHeight || 180) * colScale;
      // 两组之间留出稍大的间距（没有分组标题时靠这个区分上下两组）
      top += h + Math.round((tight ? 10 : 14) * k);
    }
    // 聊天窗：窗口越矮越扁（文字大小不变，只减行数）
    const chatH = Math.max(70, Math.round(148 * k * k));
    wrapEl.style.setProperty('--chatListH', chatH + 'px');
    // 战斗条也跟着舞台高度微调，避免小窗口压住按钮列
    if (ui.wbBar) ui.wbBar.style.top = Math.round(66 * Math.max(0.7, k)) + 'px';
  }

  function updateEliteButton() {
    if (!ui.btnElite) return;
    const show = eliteCharges > 0 && gameState === 'walk' && !legendMap && !cave;
    ui.btnElite.classList.toggle('hidden', !show);
    ui.eliteCount.textContent = String(Math.max(1, eliteCharges));
    // 中央出现后 10 秒内没人点，就平移到右侧边缘继续等着（Y 轴不变）
    if (show) {
      if (eliteDocked) {
        // 已经靠边了：战斗中藏起来再出现也保持靠边缩小，不再回到屏幕中央
        eliteDockPending = false;
      } else if (eliteDockPending) {
        dockEliteButton();
      } else if (!eliteDockTimer) {
        armEliteDockTimer();
      }
    }
    // 隐藏（战斗 / 洞府 / 传奇地图）时**不重置**停靠状态：
    // 计时器照常走，到点会记成 eliteDockPending，重新出现时直接靠边。
  }

  function learnSkillFromPool(pool) {
    const available = pool.filter(function (s) {
      return (!s.minStage || player.level >= s.minStage) &&
        !knownSkills.some(function (k) { return k.id === s.id; });
    });
    if (!available.length) return null;
    return learnSkillFromPoolById(available[randInt(0, available.length - 1)]);
  }

  // 按 id 学一个技能（灵宝传承用）
  function learnSkillById(id) {
    if (!id) return null;
    const all = ACTIVE_SKILL_POOL.concat(SKILL_POOL);
    for (let i = 0; i < all.length; i++) {
      if (all[i].id === id) return learnSkillFromPoolById(all[i]);
    }
    return null;
  }

  function makeActiveSkillStone() {
    return {
      id: 'stone' + (++itemSeq),
      type: 'skillStone',
      name: '主动技能石',
      icon: '技',
      detail: '使用后随机习得一个主动技能',
      rarityColor: '#ffd265'
    };
  }

  function makeLegendStone() {
    return {
      id: 'lg' + (++itemSeq),
      type: 'legendStone',
      name: '传奇Boss召唤石',
      icon: '召',
      detail: '使用后在身前召唤传奇敌人Boss（更强、奖励更好）',
      rarityColor: '#ff9be0'
    };
  }

  function useLegendStone(item) {
    if (!item || item.type !== 'legendStone') return;
    if (gameState !== 'walk') {
      showToast('只能在路上使用传奇Boss召唤石。');
      return;
    }
    if (activeEnemy && activeEnemy.legend) {
      showToast('传奇敌人已经在前方了。');
      return;
    }
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    spawnLegendEnemy();
    input.moving = true;
    syncMoveButton();
    showToast('传奇Boss召唤石生效：传奇敌人出现在前方！');
  }

  function makeLegendTicket() {
    return {
      id: 'tk' + (++itemSeq),
      type: 'legendTicket',
      name: '传奇BOSS挑战卷',
      icon: '卷',
      detail: '使用后开启 10km 传奇BOSS地图（共 10 波敌人）',
      rarityColor: '#ffb3f0'
    };
  }

  function makePetEgg() {
    return {
      id: 'egg' + (++itemSeq),
      type: 'petEgg',
      name: '宠物蛋',
      icon: '蛋',
      detail: '使用后孵化随机品级宠物（蝎子 / 鬣狗 / 毒蛇 / 猎鹰）',
      rarityColor: '#c8ffd9'
    };
  }

  function makePetSkillBook() {
    return {
      id: 'book' + (++itemSeq),
      type: 'petBook',
      name: '宠物技能书',
      icon: '书',
      detail: '对宠物使用：20% 几率提升品级与属性',
      rarityColor: '#ffe9a8'
    };
  }

  function petTierName(tier) {
    return tier === 'rare' ? '稀有' : tier === 'magic' ? '蓝色' : '普通';
  }

  function usePetEgg(item) {
    if (!item || item.type !== 'petEgg') return;
    // 宠物蛋孵化的也是沙漠生物（与召唤技能同一套素材）
    const pick = PET_SPRITE_KEYS[randInt(0, PET_SPRITE_KEYS.length - 1)];
    const name = PET_SPRITES[pick].name;
    const roll = Math.random();
    const tier = roll < 0.5 ? 'common' : roll < 0.85 ? 'magic' : 'rare';
    const mult = tier === 'rare' ? 1.5 : tier === 'magic' ? 1.15 : 0.85;
    const p = {
      name: name,
      tier: tier,
      level: petLevelOf(tier),
      sprite: pick,
      source: '宠物蛋孵化',
      sourceKind: 'egg',
      atkMult: mult,
      hpMult: mult,
      hp: 1,
      maxHp: 1,
      hurtT: 0,
      animT: 0,
      attackT: 0,
      walkT: rand(0, 10)
    };
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    addPet(p);
    refreshPetStats();
    p.hp = p.maxHp;
    recalcPlayerStats(); // 宠物在场 → 占用 50% 魔力上限
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
    showToast('孵化出' + petTierName(tier) + '宠物「' + name + '」（等级 ' + p.level + '）！');
  }

  function usePetSkillBook(item) {
    if (!item || item.type !== 'petBook') return;
    if (!petAlive()) {
      showToast('还没有宠物。装备法杖会出现灵宠，学会召唤技能也能召唤宠物。');
      return;
    }
    // 随机挑一只出战宠物来强化
    const target = alivePets()[randInt(0, alivePets().length - 1)];
    const success = Math.random() < 0.2;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    if (success) {
      target.tier = target.tier === 'common' ? 'magic' : 'rare';
      target.level = petLevelOf(target.tier);
      target.atkMult = (target.atkMult || 1) * 1.15;
      target.hpMult = (target.hpMult || 1) * 1.15;
      sortPets();
      refreshPetStats();
      target.hp = target.maxHp;
      recalcPlayerStats(); // 召唤物在场 → 占用 50% 魔力上限
      if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
      showToast('技能书融入成功！「' + target.name + '」提升为' + petTierName(target.tier) + '宠物（等级 ' + target.level + '）。');
    } else {
      showToast('技能书没有融入，宠物似乎不太感兴趣……');
    }
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
  }

  function useActiveSkillStone(item) {
    if (!item || item.type !== 'skillStone') return;
    usingStone = item;
    // 不再单独弹窗：直接跳到「技能」窗口，在里面点击技能学习 / 升级
    closeGearPanel();
    openSkillPanel();
    showToast('正在使用主动技能石：点击想学习或升级的技能。');
  }

  // 消耗一颗技能石，并把模式切换到下一颗（没有就退出技能石模式）
  function consumeStone(message) {
    if (!usingStone) return;
    inventory = inventory.filter(function (it) { return it.id !== usingStone.id; });
    const next = inventory.filter(function (it) { return it.type === 'skillStone'; })[0];
    usingStone = next || null;
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    if (usingStone) {
      renderSkillPanel();
      showToast('主动技能石生效：' + message + '　还有 ' + countSkillStones() + ' 颗技能石可继续使用。');
    } else {
      closeSkillPanel();
      showToast('主动技能石生效：' + message);
    }
    saveGame();
  }

  function countSkillStones() {
    return inventory.filter(function (it) { return it.type === 'skillStone'; }).length;
  }

  // 技能石模式下点击技能：未学会 → 学习；已学会 → 升 1 级
  function useStoneOnSkill(defId, learned) {
    if (!usingStone) {
      showToast(learned ? '需要主动技能石才能升级技能。' : '需要主动技能石才能学习这个技能。');
      return;
    }
    const known = knownSkills.filter(function (k) { return k.id === defId; })[0];
    if (known) {
      const lv = (known.level || 1) + 1;
      known.level = lv;
      consumeStone('「' + known.name + '」升级到 Lv.' + lv);
      return;
    }
    const def = visibleSkillDefs().filter(function (e) { return e.def.id === defId; })[0];
    if (!def) return;
    const skill = learnSkillFromPoolById(def.def);
    if (!skill) return;
    consumeStone('学会新技能「' + skill.name + '」');
  }

  // 按技能定义直接学习（技能石自选）
  function learnSkillFromPoolById(def) {
    if (!def) return null;
    if (knownSkills.some(function (k) { return k.id === def.id; })) return null;
    const skill = {
      id: def.id,
      name: def.name,
      desc: def.desc,
      cost: def.cost,
      type: def.type,
      level: 1,
      sockets: []
    };
    if (def.dmg) skill.dmg = [def.dmg[0], def.dmg[1]];
    knownSkills.push(skill);
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    return skill;
  }

  function tryEliteChallenge() {
    if (gameState !== 'walk' || eliteCharges <= 0) return;
    if (cave) {
      showToast('洞府里不能发起精英挑战，先离开洞府。');
      return;
    }
    eliteCharges -= 1;
    // 用掉一次机会：如果后面还有次数，按钮保持原样；用完则复位（下次出现重新居中计时）
    if (eliteCharges <= 0) resetEliteDock();
    updateEliteButton();
    // 精英挑战不再打断自动冒险：自动冒险会继续自动应战（含精英 / 传奇）
    spawnEliteEnemy();
    input.moving = true;
    syncMoveButton();
    showToast('精英拦路者出现（自带技能：' + activeEnemy.skill.name + '），准备迎战！');
  }

  // ---------- 马路上的易拉罐 ----------
  function spawnCan() {
    cans.push({
      worldX: player.worldX + rand(900, 1500),
      y: GROUND_Y + 30 + rand(0, 8),
      vx: -rand(90, 170),
      rot: rand(0, Math.PI * 2),
      spin: -rand(2, 4),
      kicked: false,
      vy: 0,
      kvx: 0
    });
  }

  function updateCans(dt) {
    if (gameState !== 'walk') return;
    canTimer -= dt;
    if (canTimer <= 0) {
      canTimer = rand(7, 13);
      if (cans.length < 3) spawnCan();
    }
    for (let i = cans.length - 1; i >= 0; i--) {
      const c = cans[i];
      if (c.kicked) {
        c.worldX += c.kvx * dt;
        c.y += c.vy * dt;
        c.vy += 980 * dt;
        c.rot += c.spin * dt;
        if (c.y > H + 140 || c.worldX - cameraX() > W + 260) cans.splice(i, 1);
      } else {
        c.worldX += c.vx * dt;
        c.rot += c.spin * dt;
        if (c.worldX < player.worldX - 340) cans.splice(i, 1);
      }
    }
  }

  // ---------- 马路上的遗迹洞府 ----------
  function pickCaveDef() {
    const total = CAVE_DEFS.reduce(function (s, d) { return s + d.weight; }, 0);
    let r = Math.random() * total;
    for (let i = 0; i < CAVE_DEFS.length; i++) {
      r -= CAVE_DEFS[i].weight;
      if (r <= 0) return CAVE_DEFS[i];
    }
    return CAVE_DEFS[0];
  }

  // 每隔一段路在玩家前方生成一个洞府入口
  function updateCaveSpawn() {
    if (legendMap || cave || caveTransition) return;
    // 角色只能前进：已经落在身后的入口直接清掉，不再占着名额
    for (let i = caveEntrances.length - 1; i >= 0; i--) {
      if (caveEntrances[i].worldX < player.worldX - 2600) caveEntrances.splice(i, 1);
    }
    if (caveNextX <= 0) {
      caveNextX = player.worldX + rand(4200, 6200);
      return;
    }
    if (player.worldX < caveNextX) return;
    // 前方不远处已经有入口就顺延，避免挤在一起（不会再永久卡住）
    if (caveEntrances.some(function (c) {
      return !c.used && c.worldX > player.worldX - 1500;
    })) {
      caveNextX = player.worldX + rand(2600, 4200);
      return;
    }
    caveEntrances.push({
      def: pickCaveDef(),
      worldX: player.worldX + rand(1600, 2800),
      used: false
    });
    caveNextX = player.worldX + rand(5200, 7600);
  }

  function caveEntranceAt(sx, sy) {
    for (let i = 0; i < caveEntrances.length; i++) {
      const c = caveEntrances[i];
      const cxs = c.worldX - cameraX();
      // 命中范围跟着美术资源走（宽度按素材实际比例，底座压在路面下沿）
      if (Math.abs(sx - cxs) < CAVE_IMG_H * caveImgRatio() * 0.5 + 10 &&
          sy > GROUND_Y - CAVE_IMG_H - 40 && sy < H) return c;
    }
    return null;
  }

  // 点击洞府：金色洞府要消耗令牌，进洞后手动探索
  function clickCaveEntrance(entrance) {
    if (cave) {
      showToast('已经在「' + cave.def.name + '」里了，先离开再进别的洞府。');
      return;
    }
    if (gameState !== 'walk') return;
    if (entrance.def.token && caveToken <= 0) {
      showToast('「' + entrance.def.name + '」需要金色洞府令牌，去活力兑换里换一枚（' +
        CAVE_TOKEN_COST + ' 活力）。');
      return;
    }
    if (entrance.def.token) {
      caveToken -= 1;
      saveGame();
      showToast('消耗 1 枚金色洞府令牌，进入「' + entrance.def.name + '」。');
    }
    // 绿色 / 蓝色洞府可以随时进出：入口不消耗，通关后才标记为已探索
    beginCaveEnter(entrance.def, entrance);
  }

  // ---------- 进入洞府的过场动画：走近洞口 → 缩小没入 → 进入洞府 ----------
  const CAVE_ENTER_DUR = 1.25;
  let caveTransition = null;
  const playerFx = { scale: 1, alpha: 1 };

  function beginCaveEnter(def, entrance) {
    if (caveTransition || cave) return;
    input.moving = false;
    syncMoveButton();
    const dust = [];
    for (let i = 0; i < 30; i++) {
      dust.push({
        t: 0,
        life: rand(0.5, 1.15),
        ang: rand(0, Math.PI * 2),
        r: rand(8, 46),
        vr: rand(60, 190),
        size: rand(1.5, 4.5),
        vy: rand(-30, -6)
      });
    }
    caveTransition = {
      t: 0,
      dur: CAVE_ENTER_DUR,
      fromX: player.worldX,
      toX: entrance.worldX - 26,
      def: def,
      entrance: entrance,
      dust: dust
    };
    showToast('正在进入「' + def.name + '」……');
  }

  function updateCaveTransition(dt) {
    if (!caveTransition) return;
    const ct = caveTransition;
    ct.t += dt;
    ct.dust.forEach(function (d) { d.t += dt; });
    const p = clamp(ct.t / ct.dur, 0, 1);
    const walkEnd = 0.55;

    if (p <= walkEnd) {
      // 阶段一：走向洞口（播放走路动画）
      const k = p / walkEnd;
      const e = k * k * (3 - 2 * k);
      player.worldX = ct.fromX + (ct.toX - ct.fromX) * e;
      player.vx = 1;
      player.walkT += dt * 8;
      playerFx.scale = 1;
      playerFx.alpha = 1;
    } else {
      // 阶段二：缩小、变淡，被洞口吞没
      const k = (p - walkEnd) / (1 - walkEnd);
      player.worldX = ct.toX;
      player.vx = 0;
      playerFx.scale = Math.max(0.12, 1 - k * 0.88);
      playerFx.alpha = Math.max(0, 1 - k * 1.1);
    }

    if (p >= 1) {
      const def = ct.def;
      const entrance = ct.entrance;
      caveTransition = null;
      playerFx.scale = 1;
      playerFx.alpha = 1;
      enterCave(def, entrance);
    }
  }

  // 过场特效：洞口的光尘与扩散光环 + 收尾时的颜色白闪
  function drawCaveEnterFx() {
    if (!caveTransition) return;
    const ct = caveTransition;
    const p = clamp(ct.t / ct.dur, 0, 1);
    const def = ct.def;
    const sx = ct.entrance.worldX - cameraX();
    const baseY = GROUND_Y - 90;

    // 光环
    const rings = 3;
    for (let i = 0; i < rings; i++) {
      const rp = clamp((p - i * 0.08) / 0.55, 0, 1);
      if (rp <= 0 || rp >= 1) continue;
      ctx.strokeStyle = def.glow.replace(/[\d.]+\)$/, (0.5 * (1 - rp)).toFixed(3) + ')');
      ctx.lineWidth = 3 - rp * 2;
      ctx.beginPath();
      ctx.ellipse(sx, baseY, 40 + rp * 170, 26 + rp * 110, 0, 0, Math.PI * 2);
      ctx.stroke();
    }

    // 光尘
    ct.dust.forEach(function (d) {
      if (d.t >= d.life) return;
      const k = d.t / d.life;
      const r = d.r + d.vr * d.t;
      const x = sx + Math.cos(d.ang) * r;
      const y = baseY + Math.sin(d.ang) * r * 0.6 + d.vy * d.t * k;
      ctx.fillStyle = def.glow.replace(/[\d.]+\)$/, (0.7 * (1 - k)).toFixed(3) + ')');
      ctx.beginPath();
      ctx.arc(x, y, d.size, 0, Math.PI * 2);
      ctx.fill();
    });

    // 结尾：颜色闪光覆盖，衔接洞内画面
    if (p > 0.6) {
      const k = (p - 0.6) / 0.4;
      ctx.fillStyle = def.glow.replace(/[\d.]+\)$/, (0.55 * k).toFixed(3) + ')');
      ctx.fillRect(0, 0, W, H);
    }
  }

  function enterCave(def, entrance) {
    if (autoAdventure) stopAutoAdventure('洞府内需要手动探索，已退出自动冒险。');
    input.moving = false;
    syncMoveButton();
    cave = { def: def, remaining: def.enemies, total: def.enemies, killed: 0, entrance: entrance || null };
    activeEnemy = null;
    battleGroup.length = 0;
    addBattleLogSafe('进入「' + def.name + '」：里面共有 ' + def.enemies + ' 个敌人，需要手动探索。', 'reward');
    updateCaveBar();
    showToast('进入「' + def.name + '」（敌人 ' + def.enemies + ' 个）· 手动点击「移动」前进，点击「离开洞府」可随时出去。');
    spawnCaveEnemy();
  }

  function leaveCave() {
    if (!cave) return;
    const killed = cave.killed;
    const name = cave.def.name;
    // 如果在战斗中离开，必须把战斗状态一起收干净，否则战斗色调 / 战斗 UI 会一直留着
    if (gameState === 'battle') {
      pendingVictory = 0;
      battleTurn = 'player';
      enemyTurnTimer = 0;
      playerIdleTimer = 0;
      slashFx = null;
      defendFx = null;
      airSlashFx = null;
      damagePopups = [];
      ally = null;
      enemyMinionTurns = 0;
      battleGroup.length = 0;
      showBattleUi(false);
      setControls('walk');
      syncMoveButton();
      gameState = 'walk';
    }
    cave = null;
    battleGroup.length = 0;
    activeEnemy = null;
    updateCaveBar();
    addBattleLogSafe('离开「' + name + '」（本次击败 ' + killed + ' 个敌人）。', 'system');
    showToast('已离开「' + name + '」，回到马路上。');
    spawnNextEnemy();
  }

  // 洞府里敌人品级按洞府类型抽取
  /* ============================================================
   * 洞府敌人的特殊增幅
   *   每个洞府里的敌人随机带一种（野外敌人没有）：
   *     震慑    首次攻击把你击晕 1 回合
   *     烈焰光环 每秒灼烧你 10% 最大生命（真实伤害），脚下有火焰光环
   *     点燃    每次攻击点燃你（每层每回合掉 1% 生命），手上有火焰
   *     寒冰    攻击时 30% 几率冰冻你 1 回合，脚下有寒冰光环
   *     反伤    被你攻击时反伤 50% 物理伤害
   * ============================================================ */
  const CAVE_BUFFS = [
    { id: 'stunFirst', name: '震慑', short: '震', color: '#ffd76a', desc: '首次攻击把你击晕 1 回合' },
    { id: 'flameAura', name: '烈焰光环', short: '焰', color: '#ff8a3c', desc: '每秒灼烧你 10% 最大生命（真实伤害）' },
    { id: 'ignite', name: '点燃', short: '燃', color: '#ff6a3c', desc: '每次攻击点燃你（每层每回合 1% 生命）' },
    { id: 'freeze', name: '寒冰', short: '冰', color: '#8fd7ff', desc: '攻击时 30% 几率把你冰冻 1 回合' },
    { id: 'reflect', name: '反伤', short: '反', color: '#c0a0ff', desc: '被你攻击时反伤 50% 物理伤害' }
  ];
  const CAVE_FREEZE_PCT = 30;      // 寒冰：攻击时冰冻几率（%）
  const CAVE_BURN_MAX = 5;         // 点燃：玩家身上最多叠几层
  const CAVE_AURA_PCT = 10;        // 烈焰光环：每秒灼烧 % 最大生命
  const CAVE_REFLECT_PCT = 50;     // 反伤：反弹 % 物理伤害
  const PLAYER_SKIP_TURN_SEC = 0.9;// 被控住跳过回合后，隔多久敌人再动

  // 敌人一次攻击打中玩家后，结算它带的增幅（震慑 / 点燃 / 寒冰）
  function caveEnemyOnHit(enemy) {
    if (!enemy || !enemy.caveBuff) return;
    const b = enemy.caveBuff;
    const px = player.worldX - cameraX();
    if (b.id === 'stunFirst') {
      if (enemy.stunUsed) return;
      enemy.stunUsed = true;
      playerStunTurns = Math.max(playerStunTurns, 1);
      addTextPopup(px - 88, GROUND_Y - 214, '被击晕！', '#ffd76a');
      pushBattleLog('「' + enemyLabel(enemy, false) + '」的首次攻击把你击晕了：下一回合无法行动', 'enemy');
      return;
    }
    if (b.id === 'ignite') {
      playerBurnStacks = Math.min(CAVE_BURN_MAX, playerBurnStacks + 1);
      addTextPopup(px - 88, GROUND_Y - 244, '点燃 ×' + playerBurnStacks, '#ff8a3c');
      pushBattleLog('你被「' + enemyLabel(enemy, false) + '」点燃了（' + playerBurnStacks +
        ' 层，每回合损失 ' + (playerBurnStacks * Math.max(1, Math.round(combat.maxHp * 0.01))) + ' 点真实伤害）', 'enemy');
      return;
    }
    if (b.id === 'freeze' && Math.random() * 100 < CAVE_FREEZE_PCT) {
      playerFrozenTurns = Math.max(playerFrozenTurns, 1);
      addTextPopup(px - 88, GROUND_Y - 214, '被冰冻！', '#8fd7ff');
      pushBattleLog('「' + enemyLabel(enemy, false) + '」的寒冰把你冻住了：下一回合无法行动（' +
        CAVE_FREEZE_PCT + '% 几率）', 'enemy');
    }
  }

  // 玩家身上的「点燃」：每次敌方回合结算一次，每层 1% 最大生命真实伤害
  function applyPlayerBurnTick() {
    if (playerBurnStacks <= 0) return 0;
    const dmg = Math.max(1, Math.round(combat.maxHp * 0.01)) * playerBurnStacks;
    combat.hp = Math.max(0, combat.hp - dmg);
    addDamagePopup(player.worldX - cameraX() - 88, GROUND_Y - 226, dmg, '#ff8a3c');
    pushBattleLog('你身上的燃烧（' + playerBurnStacks + ' 层）造成 ' + dmg + ' 点真实伤害', 'enemy');
    updateBattleHud();
    return dmg;
  }

  // 被洞府敌人反伤：反弹本次物理伤害的 50%（按护甲/抗性折算后打在玩家身上）
  function caveReflectOnPlayerHit(enemy, dmg) {
    if (!enemy || !enemy.caveBuff || enemy.caveBuff.id !== 'reflect') return 0;
    if (!dmg || dmg <= 0 || enemy.hp <= 0) return 0;
    const reduction = clamp(
      derived.armor / (derived.armor + 120) + derived.res / 250 + derived.chaosRes / 300,
      0, 0.85
    );
    const back = Math.max(1, Math.round(dmg * (CAVE_REFLECT_PCT / 100) * (1 - reduction)));
    combat.hp = Math.max(0, combat.hp - back);
    player.hurtT = HURT_TIME;
    addDamagePopup(player.worldX - cameraX() - 88, GROUND_Y - 168, back, '#c0a0ff');
    pushBattleLog('「' + enemyLabel(enemy, false) + '」反伤 ' + back + ' 点（你造成 ' + dmg +
      ' 的 ' + CAVE_REFLECT_PCT + '%，已按护甲/抗性折算）', 'enemy');
    updateBattleHud();
    return back;
  }

  // 玩家被击晕 / 冰冻：跳过这一回合（敌人接着动一次）
  function consumePlayerSkipTurn() {
    if (playerStunTurns <= 0 && playerFrozenTurns <= 0) return false;
    let why;
    if (playerFrozenTurns > 0) { playerFrozenTurns -= 1; why = '被寒冰冻住'; }
    else { playerStunTurns -= 1; why = '被击晕'; }
    addTextPopup(player.worldX - cameraX() - 88, GROUND_Y - 190, '无法行动', '#9fd4ff');
    setBattleLog('你' + why + '，这一回合无法行动！');
    pushBattleLog('你' + why + '，跳过本回合（敌人继续行动）', 'enemy');
    battleTurn = 'enemy';
    enemyTurnTimer = PLAYER_SKIP_TURN_SEC;
    setControls('battle');
    updateBattleHud();
    return true;
  }

  // 「烈焰光环」：每秒灼烧 10% 最大生命（真实伤害），敌人脚下有火焰光环
  function updateCaveEnemyBuffs(dt) {
    if (gameState !== 'battle') { caveAuraClock = 0; return; }
    const aura = battleAlive().filter(function (e) {
      return e.caveBuff && e.caveBuff.id === 'flameAura';
    });
    if (!aura.length) { caveAuraClock = 0; return; }
    caveAuraClock += dt;
    if (caveAuraClock < 1) return;
    caveAuraClock -= 1;
    const dmg = Math.max(1, Math.round(combat.maxHp * (CAVE_AURA_PCT / 100)));
    combat.hp = Math.max(0, combat.hp - dmg);
    player.hurtT = Math.max(player.hurtT, 0.12);
    addDamagePopup(player.worldX - cameraX() - 88, GROUND_Y - 202, dmg, '#ff8a3c');
    pushBattleLog('「烈焰光环」灼烧：' + dmg + ' 点真实伤害（每秒 ' + CAVE_AURA_PCT + '% 最大生命）', 'enemy');
    updateBattleHud();
  }

  function spawnCaveEnemy() {
    if (!cave || cave.remaining <= 0) return;
    const def = cave.def;
    const tier = def.tiers[randInt(0, def.tiers.length - 1)];
    const enemy = buildEnemy(playerLevel() + (def.id === 'gold' ? 2 : def.id === 'blue' ? 1 : 0), tier, {
      worldX: player.worldX + rand(520, 760),
      cave: true
    });
    battleGroup.length = 0;
    battleGroup.push(enemy);
    activeEnemy = enemy;
    battleTurn = 'player';
    resetPlayerIdleTimer();
    enemyTurnTimer = 0;
    gameState = 'walk';
    updateCaveBar();
  }

  // 战斗中附近的敌人被吸引过来，变成多对一
  function tryCaveJoinBattle() {
    if (!cave || cave.remaining <= 1) return false;
    if (battleGroup.length >= CAVE_JOIN_MAX) return false;
    if (Math.random() >= CAVE_JOIN_CHANCE) return false;
    const def = cave.def;
    const tier = def.tiers[randInt(0, def.tiers.length - 1)];
    addCaveHelper(tier, def);
    addBattleLogSafe('附近的' + tierName(tier) + '敌人被战斗声引来，加入了战斗！（多对一）', 'enemy');
    // 追加在最新的战报后面，别把这一回合的围攻伤害提示顶掉
    const prevLog = ui.battleLog.textContent || '';
    setBattleLog((prevLog ? prevLog + ' ' : '') +
      '又有敌人加入围攻！现在同时面对 ' + battleGroup.filter(aliveEnemy).length + ' 个敌人。');
    updateBattleHud();
    updateCaveBar();
    return true;
  }

  // 新加入的敌人不会站得很远：从后方小跑过来，补到"当前敌人身后"的位置
  function addCaveHelper(tier, def) {
    const level = playerLevel() + (def.id === 'gold' ? 2 : def.id === 'blue' ? 1 : 0);
    const anchor = activeEnemy ? activeEnemy.x : player.worldX + 600;
    const slot = battleGroup.length;                 // 第几个（0 是当前目标）
    const targetX = anchor + 118 + Math.max(0, slot - 1) * 96; // 当前敌人身后，间距 96
    const extra = buildEnemy(level, tier, { worldX: targetX + 260, cave: true }); // 先从更远处出现
    extra.joined = true;
    extra.moveToX = targetX;                          // 再走到阵位
    battleGroup.push(extra);
    return extra;
  }

  function aliveEnemy(e) { return e && e.hp > 0; }

  // 当前这场战斗里还站着的敌人
  function battleAlive() {
    return battleGroup.filter(aliveEnemy);
  }

  // 目标被击倒：同伙还在就继续打（多对一），全清才结算胜利。
  // 返回 true 表示整场战斗结束（可以走原有胜利流程）
  function resolveEnemyDown() {
    if (cave) {
      cave.killed += 1;
      cave.remaining = Math.max(0, cave.remaining - 1);
      updateCaveBar();
    }
    const rest = battleAlive();
    if (rest.length) {
      activeEnemy = rest[0];
      activeEnemy.hp = Math.max(1, activeEnemy.hp);
      setBattleLog('你击倒了一个敌人，但附近还有 ' + rest.length + ' 个敌人围了上来！');
      pushBattleLog('击倒一名敌人，仍有 ' + rest.length + ' 名敌人（多对一）', 'enemy');
      updateBattleHud();
      battleTurn = 'player';
      resetPlayerIdleTimer();
      setControls('battle');
      return false;
    }
    return true;
  }

  function addBattleLogSafe(text, kind) {
    pushBattleLog(text, kind);
  }

  // 洞府状态条
  function updateCaveBar() {
    if (!ui.caveBar) return;
    const show = !!cave;
    ui.caveBar.classList.toggle('hidden', !show);
    if (!show) return;
    ui.caveName.textContent = cave.def.name;
    ui.caveName.style.color = cave.def.color;
    ui.caveLeft.textContent = '剩余敌人 ' + cave.remaining + ' / ' + cave.total;
    ui.caveKilled.textContent = '已击败 ' + cave.killed;
  }

  // 清空洞府：发放通关奖励并回到马路
  function finishCave() {
    if (!cave) return;
    const def = cave.def;
    const killed = cave.killed;
    const goldGain = def.id === 'gold' ? randInt(320, 520)
      : def.id === 'blue' ? randInt(140, 220) : randInt(60, 110);
    gold += goldGain;
    updateGold();
    const drops = [];
    if (def.id === 'green') {
      drops.push(makeEquipment(playerLevel(), 'magic'));
    } else if (def.id === 'blue') {
      drops.push(makeEquipment(playerLevel(), 'rare'));
    } else {
      drops.push(makeEquipment(playerLevel(), 'rare'));
      drops.push(makeEquipment(playerLevel(), 'rare'));
      drops.push(makeActiveSkillStone());
      // 金色洞府终点：额外掉落一件「灵宝」传奇装备（自带特殊属性 / 主动技能）
      drops.push(makeTreasureItem(playerLevel() + 2));
    }
    drops.forEach(function (it) { inventory.push(it); });
    if (cave.entrance) cave.entrance.used = true; // 通关后该入口不再可用
    cave = null;
    battleGroup.length = 0;
    updateCaveBar();
    addBattleLogSafe('通关「' + def.name + '」！共击败 ' + killed + ' 个敌人，获得 ' + goldGain +
      ' 金币与 ' + drops.length + ' 件战利品。' +
      (def.id === 'gold' ? '（其中包含灵宝：' + drops[drops.length - 1].name + '）' : ''), 'reward');
    showToast('通关「' + def.name + '」！金币 +' + goldGain + '，战利品 ' + drops.length + ' 件已放入背包。' +
      (def.id === 'gold' ? ' 灵宝「' + drops[drops.length - 1].name + '」到手！' : ''));
    spawnNextEnemy();
    updateEliteButton();
    updateDist();
  }

  function kickCan(c) {
    if (c.kicked) return;
    c.kicked = true;
    c.kvx = rand(340, 620);
    c.vy = -rand(330, 470);
    c.spin = rand(9, 16);
    showToast('你一脚把易拉罐踢飞了！');
    // 有 1% 概率从易拉罐里掉出一颗辅助技能石
    if (Math.random() < 0.01) {
      const opt = GEM_OPTIONS[randInt(0, GEM_OPTIONS.length - 1)];
      const gem = gemOptionToItem(opt);
      inventory.push(gem);
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      setTimeout(function () {
        showToast('易拉罐里滚出一颗辅助技能石：' + gem.detail);
      }, 900);
    }
    // 罐里可能还有脏水，较大概率溅玩家一身
    if (Math.random() < 0.7) splashDirtyWater();
  }

  function splashDirtyWater() {
    playerDirtyT = 7;
    const sx = player.worldX - cameraX();
    for (let i = 0; i < 16; i++) {
      dirtyDrops.push({
        x: sx + rand(-16, 16),
        y: GROUND_Y - rand(70, 150),
        vx: rand(-150, 150),
        vy: -rand(60, 230),
        t: 0,
        life: rand(0.5, 1),
        size: rand(2, 5)
      });
    }
    showToast('易拉罐里的脏水溅了你一身！');
  }

  // ---------- 海市蜃楼：路上偶尔出现的"其他玩家"虚影 ----------
  const MIRAGE_CHANCE = 0.3;      // 每次检查时出现的概率（较低）
  const MIRAGE_CHECK_MIN = 20;    // 检查间隔（秒）
  const MIRAGE_CHECK_MAX = 40;
  const MIRAGE_FIRST_KM = 1;      // 第一个 1km 处必然出现一次
  let mirageFirstSpawned = false;
  const MIRAGE_MODES = ['walk', 'walk', 'walk', 'run', 'idle', 'battle'];

  function spawnMirage(forceMode, ahead) {
    // 出现在玩家身侧（身前一 / 身后），不会离太远
    const side = ahead ? 1 : (Math.random() < 0.5 ? -1 : 1);
    const dist = ahead ? rand(180, 300) : rand(140, 340);
    const mode = forceMode || MIRAGE_MODES[randInt(0, MIRAGE_MODES.length - 1)];
    mirage = {
      t: 0,
      life: rand(1.7, 2.7),      // 很快消失：1.7~2.7 秒
      worldX: player.worldX + side * dist,
      mode: mode,
      facing: side > 0 ? 1 : -1, // 面向自己前进的方向
      phase: rand(0, Math.PI * 2),
      walkT: rand(0, 10),
      clashT: 0,
    };
    if (mode === 'battle') {
      mirage.enemyWx = mirage.worldX + 150;
      mirage.enemyHurtT = 0;
      mirage.enemySprite = ENEMY_SPRITES[randInt(0, ENEMY_SPRITES.length - 1)].key;
    }
    return mirage;
  }

  function updateMirage(dt) {
    if (mirage) {
      mirage.t += dt;
      mirage.walkT += dt * (mirage.mode === 'run' ? 16 : 8);
      if (mirage.clashT > 0) mirage.clashT = Math.max(0, mirage.clashT - dt);
      if (mirage.enemyHurtT > 0) mirage.enemyHurtT = Math.max(0, mirage.enemyHurtT - dt);
      // 战斗类虚影每隔一会儿"交手"一次
      if (mirage.mode === 'battle' && mirage.clashT <= 0) {
        mirage.clashT = rand(0.45, 0.8);
        mirage.enemyHurtT = 0.18;
      }
      if (mirage.t >= mirage.life) mirage = null;
      return;
    }
    // 只在野外马路上出现（洞府 / 传奇地图里不出）
    if (gameState !== 'walk' || cave || legendMap || caveTransition) {
      mirageTimer = Math.max(mirageTimer, 6);
      return;
    }
    // 第一个 1km 处必然出现一次（脚本化的"偶遇"，出现在前方更容易看到）
    if (!mirageFirstSpawned && (player.worldX - START_X) / KM_UNIT >= MIRAGE_FIRST_KM) {
      mirageFirstSpawned = true;
      spawnMirage(Math.random() < 0.5 ? 'walk' : 'run', true);
      return;
    }
    mirageTimer -= dt;
    if (mirageTimer > 0) return;
    mirageTimer = rand(MIRAGE_CHECK_MIN, MIRAGE_CHECK_MAX); // 下次检查间隔
    if (Math.random() < MIRAGE_CHANCE) spawnMirage();
  }

  function drawMirageGhost(img, worldX, groundY, scale, alpha, t, phase, flip, frameKind) {
    if (!img) return;
    const sx = worldX - cameraX();
    // frameKind: 'player' | 'enemy' = 整帧统一画布的新素材；其它 = 旧的 256×256 精灵图裁剪
    const whole = frameKind === 'player' || frameKind === 'enemy';
    const geom = frameKind === 'player' ? PLAYER_FRAME : ENEMY_FRAME;
    const src = whole
      ? { x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight }
      : { x: 42, y: 48, w: 176, h: 208 };
    const dw = (whole ? geom.w : 148) * scale;
    const dh = (whole ? geom.h : 178) * scale;
    const top = whole
      ? groundY + 4 - (geom.h - geom.basePad) * scale
      : groundY - dh + 4;
    const slices = 10;
    ctx.save();
    ctx.globalAlpha = alpha;
    // 幽灵感：压低饱和度、提高亮度
    try { ctx.filter = 'saturate(0.15) brightness(1.55)'; } catch (e) { /* 不支持就算了 */ }
    for (let i = 0; i < slices; i++) {
      const srcY = src.y + (src.h / slices) * i;
      const srcH = src.h / slices;
      // 热浪扭曲：越靠上抖动越明显
      const shimmer = Math.sin(t * 7 + phase + i * 0.85) * 3.2 * (1 - i / slices);
      ctx.save();
      if (flip < 0) {
        ctx.translate(sx, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(img, src.x, srcY, src.w, srcH, -dw / 2 + shimmer, top + (dh / slices) * i, dw, dh / slices);
      } else {
        ctx.drawImage(img, src.x, srcY, src.w, srcH, sx - dw / 2 + shimmer, top + (dh / slices) * i, dw, dh / slices);
      }
      ctx.restore();
    }
    ctx.restore();
  }

  function drawMirage() {
    if (!mirage || !images.player_idle_1) return;
    const m = mirage;
    // 淡入很快、淡出也快
    const alpha = Math.min(1, m.t / 0.22) * Math.min(1, (m.life - m.t) / 0.5) * 0.62;
    if (alpha <= 0.01) return;
    const sx = m.worldX - cameraX();
    if (sx < -320 || sx > W + 320) return;

    const groundY = GROUND_Y + 4;

    // ① 沙尘热浪：一层朦胧的光晕
    const haze = ctx.createRadialGradient(sx, groundY - 80, 10, sx, groundY - 80, 170);
    haze.addColorStop(0, 'rgba(255, 246, 220, ' + (0.3 * alpha).toFixed(3) + ')');
    haze.addColorStop(0.45, 'rgba(255, 226, 176, ' + (0.16 * alpha).toFixed(3) + ')');
    haze.addColorStop(1, 'rgba(255, 210, 150, 0)');
    ctx.fillStyle = haze;
    ctx.beginPath();
    ctx.ellipse(sx, groundY - 80, 170, 130, 0, 0, Math.PI * 2);
    ctx.fill();

    // ② 脚下淡淡的影子
    ctx.fillStyle = 'rgba(120, 90, 60, ' + (0.18 * alpha).toFixed(3) + ')';
    ctx.beginPath();
    ctx.ellipse(sx - 10, groundY + 6, 54, 8, 0, 0, Math.PI * 2);
    ctx.fill();

    // ③ 虚影本体：走路 / 奔跑 / 站立 / 战斗（与玩家同一套素材）
    let frame = images.player_idle_1;
    if (m.mode === 'walk' || m.mode === 'run') {
      const step = Math.floor(m.walkT * (m.mode === 'run' ? 0.9 : 0.6)) % 4;
      frame = images['player_idle_' + (step + 1)] || frame;
    }
    drawMirageGhost(frame, m.worldX, groundY, 1, alpha, m.t, m.phase, m.facing, 'player');

    // ④ 战斗虚影：对面还有一个敌人，间隔闪出交手火花
    if (m.mode === 'battle' && images.enemyIdle) {
      const ex = m.enemyWx - cameraX();
      const ehaze = ctx.createRadialGradient(ex, groundY - 80, 10, ex, groundY - 80, 140);
      ehaze.addColorStop(0, 'rgba(255, 236, 210, ' + (0.24 * alpha).toFixed(3) + ')');
      ehaze.addColorStop(1, 'rgba(255, 210, 150, 0)');
      ctx.fillStyle = ehaze;
      ctx.beginPath();
      ctx.ellipse(ex, groundY - 80, 140, 110, 0, 0, Math.PI * 2);
      ctx.fill();
      const recoil = m.enemyHurtT > 0 ? 6 : 0;
      const ghostDef = enemySpriteDef({ sprite: m.enemySprite });
      const ghostImg = images[ghostDef.key + '_1'];
      drawMirageGhost(
        ghostImg && ghostImg.complete && ghostImg.naturalWidth > 0 ? ghostImg : images.enemyIdle,
        m.enemyWx + recoil, groundY, 1, alpha * 0.9, m.t + 1.3, m.phase + 2, -1,
        ghostImg && ghostImg.complete && ghostImg.naturalWidth > 0 ? 'enemy' : undefined
      );
      // 交手火花
      if (m.clashT > 0.12) {
        const k = Math.max(0, (m.clashT - 0.12) / 0.6);
        const fx = (sx + ex) / 2;
        const fy = groundY - 120;
        const g = ctx.createRadialGradient(fx, fy, 0, fx, fy, 46);
        g.addColorStop(0, 'rgba(255, 252, 240, ' + (0.75 * k * alpha).toFixed(3) + ')');
        g.addColorStop(0.4, 'rgba(255, 180, 120, ' + (0.45 * k * alpha).toFixed(3) + ')');
        g.addColorStop(1, 'rgba(255, 140, 80, 0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(fx, fy, 46, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ⑤ 顶部再压一层薄雾，强化"海市蜃楼"的边界
    ctx.save();
    ctx.globalAlpha = alpha * 0.5;
    ctx.strokeStyle = 'rgba(255, 240, 210, .5)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 3; i++) {
      const yy = groundY - 40 - i * 42 + Math.sin(m.t * 4 + i) * 2;
      ctx.beginPath();
      ctx.moveTo(sx - 70 + i * 6, yy);
      ctx.quadraticCurveTo(sx, yy - 6, sx + 70 - i * 6, yy + 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  // ---------- 赶路扬尘：沙漠里跑起来身后扬起沙尘 ----------
  function getDustSprite() {
    if (dustSprite) return dustSprite;
    const c = document.createElement('canvas');
    c.width = 64;
    c.height = 48;
    const g = c.getContext('2d');
    const rg = g.createRadialGradient(32, 25, 1, 32, 25, 30);
    rg.addColorStop(0, 'rgba(240, 219, 180, 0.95)');
    rg.addColorStop(0.5, 'rgba(216, 184, 138, 0.5)');
    rg.addColorStop(1, 'rgba(198, 162, 112, 0)');
    g.fillStyle = rg;
    g.beginPath();
    g.ellipse(32, 25, 30, 21, 0, 0, Math.PI * 2);
    g.fill();
    dustSprite = c;
    return c;
  }

  // 在玩家脚后方撒一把尘土（back 用来让"刚停下"的那一团再靠后一点）
  function spawnRunDust(n, back) {
    const wx = player.worldX - cameraX();
    for (let i = 0; i < n; i++) {
      if (runDust.length >= 48) return;
      runDust.push({
        x: wx - rand(20, 46 + (back || 0)),   // 屏幕坐标：脚后跟偏后
        y: GROUND_Y - rand(2, 14),            // 脚踝高度（贴着地面往上一点）
        vx: -rand(18, 62),
        vy: -rand(3, 22),
        r: rand(4, 8.5),
        grow: rand(8, 20),
        t: 0,
        life: rand(0.42, 0.8)
      });
    }
  }

  function updateRunDust(dt) {
    const running = gameState === 'walk' && !deathActive && player.vx > 0 && playerAtkT <= 0;
    if (running) {
      runDustTimer -= dt;
      if (runDustTimer <= 0) {
        // 跑得越快扬尘越密
        runDustTimer = rand(0.07, 0.13);
        spawnRunDust(player.vx > 210 ? 2 : 1, 0);
      }
    } else {
      runDustTimer = 0;
      if (wasRunning) spawnRunDust(2, 14);   // 刚停下：补一小团收尾的尘土
    }
    wasRunning = running;

    for (let i = runDust.length - 1; i >= 0; i--) {
      const d = runDust[i];
      d.t += dt;
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      d.vx *= (1 - Math.min(1, dt * 1.8));
      d.vy -= 16 * dt;      // 慢慢往上飘
      d.r += d.grow * dt;   // 越飘越散
      if (d.t >= d.life) runDust.splice(i, 1);
    }
  }

  function drawRunDust() {
    if (!runDust.length) return;
    const sprite = getDustSprite();
    ctx.save();
    runDust.forEach(function (d) {
      if (d.x < -90 || d.x > W + 90) return;
      const p = clamp(d.t / d.life, 0, 1);
      const a = (p < 0.22 ? p / 0.22 : 1 - (p - 0.22) / 0.78) * 0.55;
      if (a <= 0.02) return;
      ctx.globalAlpha = a;
      ctx.drawImage(sprite, d.x - d.r * 1.5, d.y - d.r * 1.1, d.r * 3, d.r * 2.2);
      dustDrawn += 1;
    });
    ctx.restore();
  }

  function updateDirty(dt) {
    if (playerDirtyT > 0) playerDirtyT = Math.max(0, playerDirtyT - dt);
    for (let i = dirtyDrops.length - 1; i >= 0; i--) {
      const d = dirtyDrops[i];
      d.t += dt;
      d.vy += 900 * dt;
      d.x += d.vx * dt;
      d.y += d.vy * dt;
      if (d.t >= d.life) dirtyDrops.splice(i, 1);
    }
  }

  // 玩家身上的脏水污渍
  function drawDirtyOnPlayer() {
    if (playerDirtyT <= 0) return;
    const sx = player.worldX - cameraX();
    const a = Math.min(1, playerDirtyT / 1.6) * 0.55;
    const stains = [
      [-18, -120, 16, 7], [14, -96, 12, 6], [-6, -64, 18, 8],
      [22, -140, 9, 5], [-30, -78, 10, 5]
    ];
    ctx.save();
    ctx.beginPath();
    ctx.rect(sx - 80, GROUND_Y - 182, 160, 184);
    ctx.clip();
    ctx.fillStyle = 'rgba(84, 74, 42, ' + a.toFixed(3) + ')';
    stains.forEach(function (s, idx) {
      ctx.beginPath();
      ctx.ellipse(sx + s[0], GROUND_Y + s[1], s[2], s[3], (idx - 2) * 0.24, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  function drawDirtyDrops() {
    ctx.save();
    for (let i = 0; i < dirtyDrops.length; i++) {
      const d = dirtyDrops[i];
      const a = Math.max(0, 1 - d.t / d.life);
      ctx.globalAlpha = a * 0.85;
      ctx.fillStyle = '#6f6330';
      ctx.beginPath();
      ctx.arc(d.x, d.y, d.size * (0.6 + a * 0.4), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  function drawCans() {
    const cam = cameraX();
    for (let i = 0; i < cans.length; i++) {
      const c = cans[i];
      const sx = c.worldX - cam;
      if (sx < -80 || sx > W + 80) continue;
      ctx.save();
      ctx.translate(sx, c.y);
      ctx.rotate(c.rot);
      ctx.fillStyle = '#c6ccd4';
      roundRect(-9, -15, 18, 30, 4);
      ctx.fill();
      ctx.fillStyle = '#eef2f6';
      ctx.fillRect(-8, -14, 5, 28);
      ctx.fillStyle = '#98a0a8';
      ctx.beginPath();
      ctx.ellipse(0, -15, 9, 3.6, 0, 0, Math.PI * 2);
      ctx.ellipse(0, 15, 9, 3.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#818a92';
      ctx.lineWidth = 1.2;
      roundRect(-9, -15, 18, 30, 4);
      ctx.stroke();
      ctx.restore();
    }
  }

  // ---------- 界面 ----------
  function updateDist() {
    const px = Math.max(0, player.worldX - START_X);
    const km = px / KM_UNIT;
    ui.dist.innerHTML = km.toFixed(1) + ' <small>km</small>';
  }

  // mode: 'walk' 走路可用；'battle' 战斗按钮可用
  function setControls(mode) {
    const inBattle = mode === 'battle';
    const playerCanAct = inBattle && battleTurn === 'player' && pendingVictory <= 0;
    ui.btnMove.disabled = inBattle;
    ui.btnAttack.disabled = !playerCanAct;
    // 法术按钮：非战斗时也能点开技能窗口看看（只是不能施法），战斗轮到自己出手时才真正能放
    ui.btnSpell.disabled = inBattle && !playerCanAct;
    ui.btnDefend.disabled = !playerCanAct;
  }

  function syncMoveButton() {
    const moving = (gameState === 'walk' || worldBossLootActive()) && input.moving;
    ui.btnMove.classList.toggle('active', moving);
    // 按钮文字表示“点击后会做什么”：赶路中显示「休息」，休息中显示「移动」
    const label = moving ? '休息' : '移动';
    if (ui.btnMove.textContent !== label) ui.btnMove.textContent = label;
  }

  function formatLogTime() {
    const t = Math.max(0, Math.floor(battleLogClock));
    const m = Math.floor(t / 60);
    const s = t % 60;
    return String(m).padStart(2, '0') + ':' + String(s).padStart(2, '0');
  }

  function guessLogKind(text, kind) {
    if (kind) return kind;
    if (/你损失|敌人|拦路者|精英|传奇|反击|抢先/.test(text)) return 'enemy';
    if (/^你|你(发起|使出|施展|喝下|召唤|装备)/.test(text)) return 'player';
    return 'system';
  }

  function pushBattleLog(text, kind) {
    if (!text) return;
    battleLogHistory.push({
      time: formatLogTime(),
      text: text,
      kind: guessLogKind(text, kind)
    });
    if (battleLogHistory.length > 200) battleLogHistory.shift();
    if (!ui.logPopup.classList.contains('hidden')) renderBattleLogPanel();
  }

  function renderBattleLogPanel() {
    ui.logList.textContent = '';
    if (!battleLogHistory.length) {
      const empty = document.createElement('div');
      empty.className = 'logRow system';
      const span = document.createElement('span');
      span.className = 'logText';
      span.textContent = '暂无战斗记录，去路上遭遇敌人吧。';
      empty.appendChild(span);
      ui.logList.appendChild(empty);
      return;
    }
    battleLogHistory.forEach(function (entry) {
      const row = document.createElement('div');
      row.className = 'logRow ' + entry.kind;
      const time = document.createElement('span');
      time.className = 'logTime';
      time.textContent = '[' + entry.time + ']';
      const text = document.createElement('span');
      text.className = 'logText';
      text.textContent = entry.text;
      row.appendChild(time);
      row.appendChild(text);
      ui.logList.appendChild(row);
    });
    ui.logList.scrollTop = ui.logList.scrollHeight;
  }

  function openBattleLog() {
    renderBattleLogPanel();
    ui.logPopup.classList.remove('hidden');
    document.getElementById('wrap').classList.add('log-open');
  }

  function closeBattleLog() {
    // 战斗日志只在玩家手动关闭窗口时清空（战斗结束 / 搜索尸体都不再清空）
    battleLogHistory.length = 0;
    renderBattleLogPanel();
    ui.logPopup.classList.add('hidden');
    document.getElementById('wrap').classList.remove('log-open');
  }

  // 清空日志（只在开始新一局、或玩家关闭日志窗口时调用）
  function clearBattleLog() {
    battleLogHistory.length = 0;
    if (!ui.logPopup.classList.contains('hidden')) renderBattleLogPanel();
  }

  // ---------- 左下角聊天窗口（本地模式：暂未联网，仅本地显示） ----------
  const CHAT_MAX_ROWS = 60;
  const CHAT_NICK = '旅人';
  const CHAT_CHANNEL = 'world';
  let chatInited = false;
  let chatLocalHintShown = false;
  const chatSeenKeys = new Set();   // 去重（服务器会把历史 + 自己的发言都广播回来）

  // 聊天昵称：优先用玩家自定的昵称，其次账号名，最后默认"旅人"
  function chatNick() { return account.nickname || account.name || CHAT_NICK; }
  function chatKey(m) { return String(m.ts || 0) + '|' + String(m.text || ''); }

  function updateChatStatus() {
    if (!ui.chatStatus) return;
    if (net.chatState === 'online') {
      // 不显示在线人数：人少时反而让玩家觉得"没人"，也让"新玩家/机器人"的加入无从对照
      ui.chatStatus.textContent = '世界频道' + (net.identityBound ? '' : ' · 未实名');
      ui.chatStatus.title = net.identityBound
        ? ('已实名：' + (net.profile && net.profile.realName ? net.profile.realName : '') +
           (net.profile && net.profile.idCardMasked ? '（' + net.profile.idCardMasked + '）' : ''))
        : '按实名制要求：首次发言前需要绑定姓名与身份证号（点击聊天输入框发送即可触发）';
      ui.chatStatus.classList.add('online');
      ui.chatStatus.classList.remove('offline');
    } else if (net.chatState === 'connecting') {
      ui.chatStatus.textContent = '世界频道连接中…';
      ui.chatStatus.classList.remove('online');
      ui.chatStatus.classList.add('offline');
    } else if (net.accountOnline) {
      // 账号连上服务器了，但聊天长连接还没通（多半是 nginx 没配 /ws 反代）
      ui.chatStatus.textContent = '聊天未连接（点击重试）';
      ui.chatStatus.classList.remove('online');
      ui.chatStatus.classList.add('offline');
      ui.chatStatus.title = '聊天长连接没建立：请确认 nginx 里 /ws 的反代配置（Upgrade / Connection 两行）后重载 nginx，' +
        '然后点这里重试。后端地址：' + net.serverUrl;
    } else {
      ui.chatStatus.textContent = '本地模式（未联网）';
      ui.chatStatus.classList.remove('online');
      ui.chatStatus.classList.add('offline');
    }
  }

  // 手动重连聊天（点聊天窗右上角状态即可触发）
  function reconnectChat() {
    if (!net.accountOnline) {
      showToast('当前是本地单机模式（没连上服务器账号），无法连接世界频道。');
      return;
    }
    if (net.chat) { try { net.chat.close(); } catch (e) { /* 忽略 */ } net.chat = null; }
    net.chatState = 'connecting';
    updateChatStatus();
    connectChat();
    showToast('正在重新连接世界频道…');
  }

  // 连服务器聊天（登录成功后调用；连不上会自动重连，不影响单机玩法）
  function connectChat() {
    if (net.chat) return net.chat;
    // 单机模式（没连上服务器账号）不连聊天：避免对着不存在的 /ws 反复重连刷报错
    if (!net.accountOnline) {
      net.chatState = 'offline';
      updateChatStatus();
      return null;
    }
    if (!net.serverUrl) net.serverUrl = netServerFromInput(ui.loginIp ? ui.loginIp.value : '');
    net.chat = createChatClient({
      // token 走查询串（服务器据此把昵称绑定为账号名，防冒充）
      url: wsUrlFrom(net.serverUrl, net.token ? { token: net.token } : {}),
      nick: chatNick(),
      token: net.token,
      onStatus: function (st) {
        net.chatState = st;
        updateChatStatus();
        if (st === 'online') {
          pushChatMessage('', '已连接世界频道（' + net.serverUrl + '）', 'system');
        }
      },
      onMessage: onChatServerMessage
    });
    refreshProfile();
    return net.chat;
  }

  /* ---------- 实名信息（服务器账号级）---------- */
  function refreshProfile() {
    if (!net.api || !net.token) { net.identityBound = false; net.profile = null; return Promise.resolve(null); }
    return net.api.profile(net.token).then(function (r) {
      net.profile = r.profile || null;
      net.identityBound = !!(r.profile && r.profile.bound);
      updateChatStatus();
      return net.profile;
    }).catch(function () { return null; });
  }

  function showIdentityError(text) {
    if (!ui.identityError) return;
    if (!text) { ui.identityError.classList.add('hidden'); return; }
    ui.identityError.textContent = text;
    ui.identityError.classList.remove('hidden');
  }

  // 打开实名绑定窗口（pendingText：绑定成功后自动补发的那条消息）
  function openIdentityPanel(pendingText) {
    if (!ui.identityPopup) return;
    net.pendingChatText = pendingText || '';
    showIdentityError('');
    ui.identityPopup.classList.remove('hidden');
    if (ui.idRealName) { ui.idRealName.focus(); }
  }

  function closeIdentityPanel() {
    if (!ui.identityPopup) return;
    ui.identityPopup.classList.add('hidden');
    showIdentityError('');
    net.pendingChatText = '';
  }

  /* ============================================================
   * 昵称：默认用账号名，玩家可在「角色」窗口改成自己的昵称
   *   联网：存服务器账号（唯一、过屏蔽词）；单机：存本机 localStorage
   *   改完聊天会用新昵称重新连接，战斗信息里的名字也一起换
   * ============================================================ */
  const NICK_KEY_PREFIX = 'homeward.nick.';

  function nickStorageKey() { return NICK_KEY_PREFIX + (account.hash || account.name || 'default'); }

  function loadLocalNickname() {
    try { return window.localStorage.getItem(nickStorageKey()) || ''; } catch (e) { return ''; }
  }

  function saveLocalNickname(nick) {
    try { window.localStorage.setItem(nickStorageKey(), nick || ''); } catch (e) { /* 忽略 */ }
  }

  // 昵称变了：刷新界面（战斗信息里的名字）+ 聊天用新昵称重连
  function applyNicknameChange(nick) {
    account.nickname = nick || '';
    const who = document.querySelector('#battleHud .who');
    if (who) who.textContent = chatNick();
    updateNickUI();
    if (net.accountOnline) reconnectChat();
  }

  function updateNickUI() {
    if (ui.nickNow) ui.nickNow.textContent = chatNick();
    if (ui.btnEditNick) ui.btnEditNick.title = '当前昵称：' + chatNick();
  }

  function showNickError(text) {
    if (!ui.nickError) return;
    if (!text) { ui.nickError.classList.add('hidden'); return; }
    ui.nickError.textContent = text;
    ui.nickError.classList.remove('hidden');
  }

  function openNickPanel() {
    if (!ui.nickPopup) return;
    updateNickUI();
    showNickError('');
    if (ui.nickInput) {
      ui.nickInput.value = account.nickname || account.name || '';
      ui.nickInput.focus();
      ui.nickInput.select();
    }
    ui.nickPopup.classList.remove('hidden');
  }

  function closeNickPanel() {
    if (!ui.nickPopup) return;
    ui.nickPopup.classList.add('hidden');
    showNickError('');
  }

  function submitNickname() {
    const nick = (ui.nickInput && ui.nickInput.value ? ui.nickInput.value : '').trim();
    if (nick.length < 2 || nick.length > 12) {
      showNickError('昵称需要 2~12 个字符');
      return Promise.resolve(false);
    }
    if (/[\u0000-\u001f]/.test(nick)) { showNickError('昵称不能包含特殊字符'); return Promise.resolve(false); }

    // 联网：交给服务器校验（唯一性 + 屏蔽词），成功后本地也记一份
    if (net.accountOnline && net.api && net.token) {
      if (ui.nickSubmit) { ui.nickSubmit.disabled = true; ui.nickSubmit.textContent = '保存中…'; }
      return net.api.setNickname(net.token, nick).then(function (r) {
        const finalNick = (r.profile && r.profile.nickname) || nick;
        saveLocalNickname(finalNick);
        applyNicknameChange(finalNick);
        closeNickPanel();
        showToast('昵称已改为「' + finalNick + '」，世界频道会用这个昵称。');
        return true;
      }).catch(function (err) {
        showNickError((err && err.message) || '保存失败，请稍后再试');
        return false;
      }).then(function (r) {
        if (ui.nickSubmit) { ui.nickSubmit.disabled = false; ui.nickSubmit.textContent = '保存'; }
        return r;
      });
    }
    // 单机：只存本机
    saveLocalNickname(nick);
    applyNicknameChange(nick);
    closeNickPanel();
    showToast('昵称已改为「' + nick + '」（本机存档，联网后可在账号里同步）。');
    return Promise.resolve(true);
  }

  function submitIdentity() {
    if (!ui.identityPopup || ui.identityPopup.classList.contains('hidden')) return Promise.resolve(false);
    const realName = (ui.idRealName.value || '').trim();
    const idCard = (ui.idCardNo.value || '').trim().toUpperCase();
    if (!realName) { showIdentityError('请填写姓名'); ui.idRealName.focus(); return Promise.resolve(false); }
    if (!idCard) { showIdentityError('请填写身份证号'); ui.idCardNo.focus(); return Promise.resolve(false); }
    if (!/^\d{17}[\dX]$/.test(idCard)) { showIdentityError('身份证号需要 18 位（末位可能是 X）'); ui.idCardNo.focus(); return Promise.resolve(false); }
    if (!net.api || !net.token) { showIdentityError('当前未连服务器账号，无法绑定实名'); return Promise.resolve(false); }

    if (ui.identitySubmit) { ui.identitySubmit.disabled = true; ui.identitySubmit.textContent = '提交中…'; }
    return net.api.bindIdentity(net.token, realName, idCard).then(function (r) {
      net.profile = r.profile || null;
      net.identityBound = !!(r.profile && r.profile.bound);
      updateChatStatus();
      const text = net.pendingChatText;
      if (ui.identityPopup) ui.identityPopup.classList.add('hidden');
      showIdentityError('');
      showToast('实名信息已绑定：' + (net.profile.realName || realName) +
        '（' + (net.profile.idCardMasked || '') + '）');
      if (text) {
        net.pendingChatText = '';
        sendChatText(text);     // 自动补发刚才那条
      }
      return true;
    }).catch(function (err) {
      showIdentityError((err && err.message) || '绑定失败，请稍后再试');
      return false;
    }).then(function (r) {
      if (ui.identitySubmit) { ui.identitySubmit.disabled = false; ui.identitySubmit.textContent = '提交绑定'; }
      return r;
    });
  }

  function onChatServerMessage(msg) {
    if (!msg || !msg.type) return;
    if (msg.type === 'hello') return;
    if (msg.type === 'welcome') {
      net.onlineCount = msg.online || 1;
      updateChatStatus();
      (msg.history || []).forEach(function (m) {
        const key = chatKey(m);
        if (chatSeenKeys.has(key)) return;
        chatSeenKeys.add(key);
        pushChatMessage(m.nick || '', m.text || '', m.nick === chatNick() ? 'me' : 'other');
      });
      return;
    }
    if (msg.type === 'online') {
      // 服务器已经不再下发在线人数；这里保留兼容处理但不显示
      return;
    }
    if (msg.type === 'chat') {
      const key = chatKey(msg);
      if (chatSeenKeys.has(key)) return;
      chatSeenKeys.add(key);
      if (msg.kind === 'system') pushChatMessage('', msg.text || '', 'system');
      else {
        pushChatMessage(msg.nick || '', msg.text || '', msg.nick === chatNick() ? 'me' : 'other');
        if (msg.nick === chatNick()) net.pendingChatText = '';   // 自己的消息已经成功发出
      }
      return;
    }
    if (msg.type === 'error') {
      if (msg.code === 'need_identity') {
        // 服务器要求实名：弹出绑定窗口，绑定成功后会补发刚才那条
        const pending = net.pendingChatText || '';
        net.pendingChatText = '';
        pushChatMessage('', '（需要先绑定实名信息才能在世界频道发言）', 'system');
        openIdentityPanel(pending);
        return;
      }
      if (msg.code === 'need_login') {
        pushChatMessage('', '（请先用账号登录后再发言）', 'system');
        return;
      }
      pushChatMessage('', '（' + (msg.message || '发送失败') + '）', 'system');
    }
  }

  function chatTime() {
    const d = new Date();
    return String(d.getHours()).padStart(2, '0') + ':' +
      String(d.getMinutes()).padStart(2, '0');
  }

  function pushChatMessage(name, text, kind) {
    if (!ui.chatList) return;
    const row = document.createElement('div');
    row.className = 'chatRow' + (kind ? ' ' + kind : '');

    const time = document.createElement('span');
    time.className = 'chatTime';
    time.textContent = '[' + chatTime() + ']';
    row.appendChild(time);

    if (name) {
      const who = document.createElement('span');
      who.className = 'chatName';
      who.textContent = name + '：';
      row.appendChild(who);
    }

    const content = document.createElement('span');
    content.className = 'chatText';
    content.textContent = text;   // 统一用 textContent，避免 XSS
    row.appendChild(content);

    ui.chatList.appendChild(row);
    while (ui.chatList.children.length > CHAT_MAX_ROWS) {
      ui.chatList.removeChild(ui.chatList.firstChild);
    }
    ui.chatList.scrollTop = ui.chatList.scrollHeight;
  }

  // 发送：联网时发给服务器（服务器会广播回来，含自己）；离线时只在本机回显
  function sendChatMessage() {
    if (!ui.chatInput) return;
    const text = (ui.chatInput.value || '').trim().slice(0, 100);
    if (!text) return;
    ui.chatInput.value = '';
    ui.chatInput.focus();
    sendChatText(text);
  }

  function sendChatText(text) {
    if (!text) return;
    if (net.chat && net.chatState === 'online') {
      net.pendingChatText = text;   // 万一被"未实名"拦下，绑定后要补发这条
      net.chat.send({ type: 'chat', channel: CHAT_CHANNEL, text: text });
      return;   // 不本地回显：等服务器广播回来，避免出现两条
    }
    // 账号已经登录服务器，只是聊天长连接断了：消息进队列，连上后自动补发（不要当成单机）
    if (net.accountOnline && net.chat) {
      net.pendingChatText = text;
      net.chat.send({ type: 'chat', channel: CHAT_CHANNEL, text: text });
      pushChatMessage('', '（聊天连接已断开，正在重连：消息会在连上后自动发出）', 'system');
      if (net.chatState !== 'connecting') reconnectChat();
      return;
    }
    pushChatMessage(chatNick(), text, 'me');
    if (!chatLocalHintShown) {
      chatLocalHintShown = true;
      pushChatMessage('', '（本地单机模式：消息只显示在本机，其它玩家看不到）', 'system');
    }
    maybeBotReply();
  }

  function setupChat() {
    if (chatInited) return;
    chatInited = true;
    updateChatStatus();
    pushChatMessage('', '欢迎来到世界频道。', 'system');
    if (!net.chat) {
      pushChatMessage('', '当前未连接服务器（本地模式）：消息只显示在本机。', 'system');
    }
    pushChatMessage('', '输入消息后按回车发送，右上角可「收起」。', 'system');
    // 启动机器人随机发言（间隔 30~120 秒随机抖动）
    scheduleChatBot();
  }

  function toggleChatCollapse() {
    if (!ui.chatPanel || !ui.chatToggle) return;
    const collapsed = ui.chatPanel.classList.toggle('collapsed');
    ui.chatToggle.textContent = collapsed ? '展开' : '收起';
    if (!collapsed) ui.chatInput.focus();
  }

  // ---------- 聊天机器人（本地模拟在线玩家，非联网） ----------
  const CHAT_BOTS = [
    { name: '夜雨行舟', lines: ['有人组队吗？', '刚打完精英，掉了一件黄装。', '这词缀也太看运气了……', '气血反弹配荆棘好用吗？'] },
    { name: '一剑霜寒', lines: ['求带，我在半路被拦路者堵住了。', '刚突破到炼气期，感觉强了不少。', '商店刷新了一件好装备。'] },
    { name: '沙丘拾荒者', lines: ['易拉罐里居然能开出技能石，笑死。', '今天运气不错，捡了两块灵物。', '谁有多余的宠物蛋？'] },
    { name: '青竹小道友', lines: ['手动玩了两小时，才到武者三境。', '自动冒险真省心，挂机去了。', '传奇BOSS太猛了，被秒了。'] },
    { name: '剑胆琴心', lines: ['组队刷传奇地图，还差一个人。', '有人在吗？', '刚才被精英反伤打死了。'] }
  ];
  const CHAT_BOT_MIN_DELAY = 30;   // 最短 30 秒
  const CHAT_BOT_MAX_DELAY = 120;  // 最长 120 秒，且每次都会抖动，避免固定节奏
  const chatBotDelays = [];        // 调试用：最近几次机器人发言间隔（秒）
  let chatBotTimer = null;
  let chatBotLastLine = '';

  function randomChatBotDelay() {
    const base = CHAT_BOT_MIN_DELAY + Math.random() * (CHAT_BOT_MAX_DELAY - CHAT_BOT_MIN_DELAY);
    const jitter = (Math.random() - 0.5) * 12;
    return Math.max(15, base + jitter);
  }

  function pickBotLine(bot) {
    let line = bot.lines[randInt(0, bot.lines.length - 1)];
    let guard = 0;
    // 避免连续两次说同一句
    while (line === chatBotLastLine && guard < 6) {
      line = bot.lines[randInt(0, bot.lines.length - 1)];
      guard += 1;
    }
    return line;
  }

  function scheduleChatBot(delaySec) {
    if (chatBotTimer) clearTimeout(chatBotTimer);
    const delay = (delaySec || randomChatBotDelay()) * 1000;
    chatBotDelays.push(Math.round(delay / 1000));
    if (chatBotDelays.length > 20) chatBotDelays.shift();
    window.__chatBotDelays = chatBotDelays;   // 调试用：可查看实际随机间隔
    chatBotTimer = setTimeout(function () {
      chatBotTimer = null;
      if (document.hidden) {           // 页面切到后台就不冒泡
        scheduleChatBot();
        return;
      }
      // 已经连上真实世界频道了就不再插机器人发言（免得和真人聊天混在一起）
      if (net.chatState === 'online') {
        scheduleChatBot();
        return;
      }
      const bot = CHAT_BOTS[randInt(0, CHAT_BOTS.length - 1)];
      const line = pickBotLine(bot);
      chatBotLastLine = line;
      pushChatMessage(bot.name, line, 'bot');
      scheduleChatBot();
    }, delay);
  }

  // 玩家发言后有小概率被机器人接话（延迟也是随机的）
  function maybeBotReply() {
    if (Math.random() > 0.15) return;
    const bot = CHAT_BOTS[randInt(0, CHAT_BOTS.length - 1)];
    const delay = (2 + Math.random() * 7) * 1000;
    setTimeout(function () {
      const line = pickBotLine(bot);
      chatBotLastLine = line;
      pushChatMessage(bot.name, line, 'bot');
    }, delay);
  }

  // ---------- 图鉴（普通 / 魔法 / 稀有 / 传奇 / 暗金）& 抽卡 ----------
  const codexUnlocked = {};   // 图鉴 id -> true（激活后永久增幅）
  let gachaPity = 0;          // 距上次传奇 / 暗金的抽数
  let gachaCount = 0;         // 累计抽卡次数
  let gachaResultList = [];
  let codexTierFilter = 'all'; // 图鉴窗口当前查看的阶级

  function codexItemBonusText(item) {
    return Object.keys(item.bonus).map(function (k) {
      return (STAT_LABELS[k] || k) + ' +' + item.bonus[k];
    }).join('　');
  }

  function codexTotalBonus() {
    const total = {};
    CODEX_ITEMS.forEach(function (it) {
      if (!codexUnlocked[it.id]) return;
      Object.keys(it.bonus).forEach(function (k) {
        total[k] = (total[k] || 0) + it.bonus[k];
      });
    });
    return total;
  }

  function codexTotalBonusText() {
    const total = codexTotalBonus();
    const keys = Object.keys(total);
    if (!keys.length) return '尚未激活任何图鉴';
    return keys.map(function (k) {
      return (STAT_LABELS[k] || k) + ' +' + total[k];
    }).join('　');
  }

  function unlockCodex(id) {
    if (codexUnlocked[id]) return false;
    codexUnlocked[id] = true;
    recalcPlayerStats();
    return true;
  }

  function codexTierInfo(tierId) {
    return CODEX_TIERS.filter(function (t) { return t.id === tierId; })[0] || CODEX_TIERS[0];
  }

  // ---------- 图鉴悬停提示（显示该藏品的属性增幅）----------
  function hideCodexTip() {
    if (ui.codexTip) ui.codexTip.classList.add('hidden');
  }

  function showCodexTip(cell, item) {
    if (!ui.codexTip) return;
    const unlocked = !!codexUnlocked[item.id];
    const info = codexTierInfo(item.tier);
    const tip = ui.codexTip;

    tip.textContent = '';
    tip.style.borderColor = info.color;

    const head = document.createElement('div');
    head.className = 'tipHead';
    const dot = document.createElement('i');
    dot.style.background = info.color;
    const name = document.createElement('b');
    name.textContent = unlocked ? item.name : '未收集';
    const tier = document.createElement('span');
    tier.textContent = info.name;
    tier.style.color = info.color;
    head.appendChild(dot);
    head.appendChild(name);
    head.appendChild(tier);
    tip.appendChild(head);

    if (unlocked) {
      const mods = document.createElement('div');
      mods.className = 'tipMods';
      const title = document.createElement('em');
      title.textContent = '属性增幅';
      mods.appendChild(title);
      Object.keys(item.bonus).forEach(function (k) {
        const line = document.createElement('b');
        line.textContent = (STAT_LABELS[k] || k) + ' +' + item.bonus[k];
        mods.appendChild(line);
      });
      tip.appendChild(mods);

      const desc = document.createElement('div');
      desc.className = 'tipDesc';
      desc.textContent = item.type + ' · ' + item.desc;
      tip.appendChild(desc);
    } else {
      const mods = document.createElement('div');
      mods.className = 'tipMods locked';
      const line = document.createElement('b');
      line.textContent = info.gacha === false ? info.source : '遗迹探索 / 抽卡获得';
      mods.appendChild(line);
      tip.appendChild(mods);

      const desc = document.createElement('div');
      desc.className = 'tipDesc';
      desc.textContent = '收集后永久增幅角色属性';
      tip.appendChild(desc);
    }

    // 先显示再量尺寸，位置以 #wrap 为基准（保证不被窗口裁掉）
    tip.classList.remove('hidden');
    const wrapRect = (ui.wrap || document.body).getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let left = cellRect.left - wrapRect.left + cellRect.width / 2 - tw / 2;
    let top = cellRect.top - wrapRect.top - th - 8;
    if (top < 6) top = cellRect.bottom - wrapRect.top + 8; // 上方放不下 → 放到下方
    left = Math.max(6, Math.min(left, wrapRect.width - tw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - th - 6));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
  }

  // 每个阶级的收集进度：{ common: { got, total }, ... }
  function codexTierCounts() {
    const counts = {};
    CODEX_TIERS.forEach(function (t) { counts[t.id] = { got: 0, total: 0 }; });
    CODEX_ITEMS.forEach(function (it) {
      const c = counts[it.tier];
      if (!c) return;
      c.total += 1;
      if (codexUnlocked[it.id]) c.got += 1;
    });
    return counts;
  }

  // 图鉴窗口的「阶级入口」：普通 / 魔法 / 稀有 / 传奇 / 暗金（外加「全部」）
  function renderCodexTiers() {
    if (!ui.codexTiers) return;
    ui.codexTiers.textContent = '';
    const counts = codexTierCounts();
    const totalGot = CODEX_ITEMS.filter(function (it) { return codexUnlocked[it.id]; }).length;
    const entries = [{ id: 'all', name: '全部', color: '#ffd76a', note: '所有阶级的藏品' }]
      .concat(CODEX_TIERS);

    entries.forEach(function (t) {
      const isAll = t.id === 'all';
      const c = isAll ? { got: totalGot, total: CODEX_ITEMS.length } : counts[t.id];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'codexTab' + (codexTierFilter === t.id ? ' active' : '');
      btn.setAttribute('data-tier', t.id);
      btn.style.setProperty('--tierColor', t.color);

      const name = document.createElement('b');
      name.textContent = t.name;
      btn.appendChild(name);

      const prog = document.createElement('small');
      prog.textContent = c.got + ' / ' + c.total;
      btn.appendChild(prog);

      const rate = document.createElement('em');
      rate.textContent = isAll
        ? ('共 ' + CODEX_ITEMS.length + ' 件藏品')
        : (t.gacha === false
          ? t.source
          : ('单抽 ' + t.rate + '% · 十连 ' + t.rateTen + '%'));
      btn.appendChild(rate);

      btn.title = isAll ? '显示全部阶级' : (t.name + '：' + t.note);
      btn.addEventListener('click', function () {
        if (codexTierFilter === t.id) return;
        codexTierFilter = t.id;
        renderCodexGrid();
      });
      ui.codexTiers.appendChild(btn);
    });
  }

  function renderCodexGrid() {
    renderCodexTiers();
    ui.codexGrid.textContent = '';
    const list = CODEX_ITEMS.filter(function (it) {
      return codexTierFilter === 'all' || it.tier === codexTierFilter;
    });

    list.forEach(function (it) {
      const unlocked = !!codexUnlocked[it.id];
      const info = codexTierInfo(it.tier);
      const cell = document.createElement('div');
      cell.className = 'codexCell tier-' + it.tier + (unlocked ? ' unlocked' : ' locked');

      const icon = document.createElement('div');
      icon.className = 'codexIcon';
      icon.textContent = unlocked ? it.icon : '?';
      cell.appendChild(icon);

      const name = document.createElement('div');
      name.className = 'codexName';
      name.textContent = unlocked ? it.name : '未收集';
      cell.appendChild(name);

      const tier = document.createElement('div');
      tier.className = 'gcTier';
      tier.style.color = info.color;
      tier.textContent = info.name;
      cell.appendChild(tier);

      // 用自定义悬停窗口展示增幅，这里只留 aria-label（避免原生 tooltip 叠在上面）
      cell.setAttribute('aria-label', unlocked
        ? (it.name + '：' + codexItemBonusText(it) + '（' + it.desc + '）')
        : ('尚未收集 · ' + info.name + '（' +
          (info.gacha === false ? info.source : '遗迹探索 / 抽卡获得') + '）'));
      // 鼠标移上去显示属性增幅窗口，移开就关掉
      cell.addEventListener('mouseenter', function () { showCodexTip(cell, it); });
      cell.addEventListener('mouseleave', hideCodexTip);
      ui.codexGrid.appendChild(cell);
    });

    const got = CODEX_ITEMS.filter(function (it) { return codexUnlocked[it.id]; }).length;
    const counts = codexTierCounts();
    const view = codexTierFilter === 'all'
      ? ('全部 ' + got + ' / ' + CODEX_ITEMS.length)
      : (codexTierInfo(codexTierFilter).name + ' ' + counts[codexTierFilter].got +
        ' / ' + counts[codexTierFilter].total);
    ui.codexProgress.textContent = '已收集 ' + got + ' / ' + CODEX_ITEMS.length +
      '　·　当前查看：' + view + '（激活后永久增幅角色属性）';
    ui.codexBonusText.textContent = codexTotalBonusText();
  }

  function renderGacha() {
    ui.gachaGold.textContent = String(gold);
    ui.gachaPity.textContent = gachaPity + ' / ' + GACHA_PITY_MAX;
    ui.gachaCount.textContent = String(gachaCount);
    // 价格文案跟着常量走，改价不用再改 HTML
    if (ui.gachaOne) ui.gachaOne.textContent = '单抽 · ' + GACHA_SINGLE_COST;
    if (ui.gachaTen) ui.gachaTen.textContent = '十连 · ' + GACHA_TEN_COST;
    if (ui.gachaCostNote) {
      ui.gachaCostNote.textContent = '单抽 ' + GACHA_SINGLE_COST + ' 金币 · 十连 ' + GACHA_TEN_COST +
        ' 金币（十连限流：最多 ' + GACHA_TEN_CAPS.rare + ' 件稀有、' + GACHA_TEN_CAPS.legend +
        ' 件传奇，并保底一件稀有及以上；暗金不参与抽卡）';
    }
    renderGachaRates();
  }

  // 各阶级图鉴的获取几率一览（单抽 / 十连两套概率）
  function renderGachaRates() {
    if (!ui.gachaRates) return;
    ui.gachaRates.textContent = '';

    const head = document.createElement('div');
    head.className = 'rateRow rateHead';
    head.innerHTML = '<span>图鉴阶级</span><span>单抽</span><span>十连</span>';
    ui.gachaRates.appendChild(head);

    CODEX_TIERS.forEach(function (t) {
      const row = document.createElement('div');
      row.className = 'rateRow';
      const noGacha = t.gacha === false;

      const name = document.createElement('span');
      name.className = 'rateName';
      const dot = document.createElement('i');
      dot.style.background = t.color;
      const text = document.createElement('b');
      text.textContent = t.name;
      text.style.color = t.color;
      name.appendChild(dot);
      name.appendChild(text);
      row.appendChild(name);

      // 不参与抽卡与保底的阶级（暗金）：两列合并为一格说明
      if (noGacha) {
        const ev = document.createElement('span');
        ev.className = 'rateEvent';
        ev.textContent = t.source;
        row.appendChild(ev);
        row.title = t.name + '：' + t.note + '（不参与抽卡与保底）';
        ui.gachaRates.appendChild(row);
        return;
      }

      const one = document.createElement('span');
      one.className = 'rateVal';
      one.textContent = t.rate + '%';

      const ten = document.createElement('span');
      ten.className = 'rateVal' +
        (t.rateTen > t.rate ? ' up' : t.rateTen < t.rate ? ' down' : '');
      ten.textContent = t.rateTen + '%';

      row.appendChild(one);
      row.appendChild(ten);
      row.title = t.name + '：' + t.note +
        '（重复获得补偿 ' + t.comp + ' 金币）';
      ui.gachaRates.appendChild(row);
    });

    const foot = document.createElement('p');
    foot.className = 'rateFoot';
    foot.textContent =
      '十连每一抽的稀有以上几率更低（红色数值），但有数量上限：每次十连最多 ' +
      GACHA_TEN_CAPS.rare + ' 件稀有、最多 ' + GACHA_TEN_CAPS.legend + ' 件传奇及以上；' +
      '十连必得一件稀有及以上；累计 ' + GACHA_PITY_MAX + ' 抽未出传奇时，下一抽必出传奇；' +
      '暗金图鉴不参与抽卡与保底，只能通过活动等方式获得（暂未开放）。';
    ui.gachaRates.appendChild(foot);
  }

  function renderGachaResults(withRevealAnim) {
    ui.gachaResults.textContent = '';
    // 单抽只有 1 张卡时，让卡片在结果区里上下左右居中
    ui.gachaResults.classList.toggle('single', gachaResultList.length === 1);
    gachaResultList.forEach(function (r, idx) {
      const card = document.createElement('div');
      card.className = 'gachaCard';
      card.style.borderColor = r.color;
      if (r.tier === 'god') card.style.boxShadow = '0 0 14px rgba(255,200,80,.55)';
      // 抽卡动画播完后揭晓：卡片逐张翻出来
      if (withRevealAnim) {
        card.classList.add('reveal');
        card.style.animationDelay = (idx * 55) + 'ms';
      }

      const tier = document.createElement('div');
      tier.className = 'gcTier';
      tier.style.color = r.color;
      tier.textContent = r.tierName;
      card.appendChild(tier);

      const icon = document.createElement('div');
      icon.className = 'codexIcon';
      icon.textContent = r.icon;
      card.appendChild(icon);

      const name = document.createElement('div');
      name.className = 'gcName';
      name.textContent = r.name;
      card.appendChild(name);

      if (r.isNew) {
        const nw = document.createElement('div');
        nw.className = 'gcNew';
        nw.textContent = 'NEW · 图鉴激活';
        card.appendChild(nw);
      } else if (r.note) {
        const nt = document.createElement('div');
        nt.className = 'gcNew';
        nt.textContent = r.note;
        card.appendChild(nt);
      }
      card.title = r.detail || '';
      ui.gachaResults.appendChild(card);
    });
  }

  // 抽到某个阶级的图鉴（优先给还没收集的）；tierId 由调用方决定
  function rollGachaOnce(tierId) {
    if (!tierId) tierId = rollGachaTier(false);
    const info = codexTierInfo(tierId);
    const pool = CODEX_ITEMS.filter(function (it) { return it.tier === tierId; });
    const locked = pool.filter(function (it) { return !codexUnlocked[it.id]; });
    const list = locked.length ? locked : pool;
    const pick = list[randInt(0, list.length - 1)];
    const isNew = unlockCodex(pick.id);
    let note = '';
    if (!isNew) {
      gold += info.comp;
      updateGold();
      note = '重复 +' + info.comp + ' 金币';
    }
    return {
      tier: tierId, tierName: info.name, color: info.color,
      name: pick.name, icon: pick.icon, isNew: isNew, note: note,
      detail: isNew ? ('图鉴激活：' + codexItemBonusText(pick)) : note
    };
  }

  // 抽卡阶级：只从参与抽卡的阶级里摇（暗金 gacha:false，永远不会被摇到）
  function rollGachaTier(tenPull) {
    const r = Math.random() * 100;
    let acc = 0;
    for (let i = 0; i < CODEX_TIERS.length; i++) {
      const t = CODEX_TIERS[i];
      if (t.gacha === false || (!tenPull ? t.rate <= 0 : t.rateTen <= 0)) continue;
      acc += tenPull ? t.rateTen : t.rate;
      if (r < acc) return t.id;
    }
    return CODEX_TIERS.filter(function (t) { return t.gacha !== false; })[0].id;
  }

  // 30 抽保底：必出传奇（暗金不参与保底，只能靠活动等其它方式获得）
  function rollPityTier() {
    return 'legend';
  }

  // 十连限流：稀有最多 GACHA_TEN_CAPS.rare 件，传奇 + 暗金 合计最多 GACHA_TEN_CAPS.legend 件
  function clampTierForTen(tierId, used) {
    if (tierRank(tierId) >= tierRank('legend')) {
      if (used.legend < GACHA_TEN_CAPS.legend) return tierId;
      tierId = 'rare'; // 传奇 / 暗金名额用完 → 降级为稀有
    }
    if (tierId === 'rare') {
      if (used.rare < GACHA_TEN_CAPS.rare) return 'rare';
      return 'magic'; // 稀有也满了 → 降级为魔法
    }
    return tierId;
  }

  function countTierUse(tierId, used) {
    if (tierRank(tierId) >= tierRank('legend')) used.legend += 1;
    else if (tierId === 'rare') used.rare += 1;
  }

  function tierRank(tierId) {
    for (let i = 0; i < CODEX_TIERS.length; i++) {
      if (CODEX_TIERS[i].id === tierId) return i;
    }
    return 0;
  }

  /* ============================================================
   * 抽卡动画
   *   点单抽 / 十连后：先把整个面板盖住播一段动画（卡背浮动 + 光晕 + 转光），
   *   动画播完才把抽到的图鉴卡片揭晓出来（逐张翻出）。
   *   动画期间锁定两个抽卡按钮，避免连点重复扣钱。
   * ============================================================ */
  const GACHA_ANIM_MS_SINGLE = 1200;
  const GACHA_ANIM_MS_TEN = 1600;
  let gachaAnim = null;          // { timer, playing }

  function gachaAnimPlaying() {
    return !!(gachaAnim && gachaAnim.playing);
  }

  function playGachaAnimation(results, onDone) {
    if (!ui.gachaStage || !ui.gachaStageCards) {
      if (onDone) onDone();      // 没有动画容器就直接出结果，别把东西吞了
      return;
    }
    // 光晕颜色取本次最高品级
    let best = results[0];
    results.forEach(function (r) { if (tierRank(r.tier) > tierRank(best.tier)) best = r; });
    if (ui.gachaHalo) ui.gachaHalo.style.color = best ? best.color : '#ffd08a';

    ui.gachaStageCards.textContent = '';
    const ten = results.length > 1;
    results.forEach(function (r, i) {
      const back = document.createElement('div');
      back.className = 'gsCardBack' + (ten ? '' : ' single');
      back.textContent = '鉴';
      back.style.animationDelay = (i * 90) + 'ms';
      ui.gachaStageCards.appendChild(back);
    });
    if (ui.gachaStageTip) {
      ui.gachaStageTip.textContent = ten ? '正在抽取 · 十连…' : '正在抽取…';
    }
    ui.gachaStage.classList.remove('hidden');
    ui.gachaResults.textContent = '';      // 动画期间结果区保持空白
    if (ui.gachaOne) ui.gachaOne.disabled = true;
    if (ui.gachaTen) ui.gachaTen.disabled = true;

    gachaAnim = { playing: true, timer: null };
    const ms = ten ? GACHA_ANIM_MS_TEN : GACHA_ANIM_MS_SINGLE;
    gachaAnim.timer = setTimeout(function () {
      finishGachaAnimation(onDone);
    }, ms);
  }

  // 收尾：收起动画、解锁按钮、揭晓图鉴（可以在动画中途被关闭窗口调用）
  function finishGachaAnimation(onDone) {
    if (!gachaAnim) return;
    if (gachaAnim.timer) clearTimeout(gachaAnim.timer);
    gachaAnim = null;
    if (ui.gachaStage) ui.gachaStage.classList.add('hidden');
    if (ui.gachaStageCards) ui.gachaStageCards.textContent = '';
    if (ui.gachaOne) ui.gachaOne.disabled = false;
    if (ui.gachaTen) ui.gachaTen.disabled = false;
    if (onDone) onDone();
  }

  function doGacha(times) {
    const ten = times >= 10;
    const cost = ten ? GACHA_TEN_COST : GACHA_SINGLE_COST;
    if (gold < cost) {
      showToast('金币不足，抽卡需要 ' + cost + ' 金币。');
      return;
    }
    gold -= cost;
    updateGold();

    const results = [];
    let rarePlus = 0;
    const usedTiers = { rare: 0, legend: 0 }; // 十连限流计数
    for (let i = 0; i < times; i++) {
      gachaCount += 1;
      gachaPity += 1;
      const forced = gachaPity >= GACHA_PITY_MAX ? rollPityTier() : null;
      let tierId = forced || rollGachaTier(ten);
      // 十连限流只作用于自然抽取，保底（30 抽必出传奇/暗金）不受限制
      if (ten && !forced) tierId = clampTierForTen(tierId, usedTiers);
      countTierUse(tierId, usedTiers);
      const res = rollGachaOnce(tierId);
      if (tierRank(res.tier) >= tierRank('legend')) gachaPity = 0;
      if (tierRank(res.tier) >= tierRank(GACHA_TEN_MIN_TIER)) rarePlus += 1;
      results.push(res);
    }

    // 十连保底：至少一件稀有及以上
    if (ten && rarePlus === 0) {
      const res = rollGachaOnce('rare');
      usedTiers.rare += 1;
      res.tierName = res.tierName + '（保底）';
      results[results.length - 1] = {
        tier: res.tier, tierName: res.tierName, color: res.color,
        name: res.name, icon: res.icon, isNew: res.isNew, note: res.note,
        detail: res.detail
      };
    }

    gachaResultList = results;
    renderGacha();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.charPopup.classList.contains('hidden')) renderCharPanel();
    saveGame();
    const newCount = results.filter(function (r) { return r.isNew; }).length;
    // 先播动画，动画播完才揭晓图鉴；提示也放到揭晓之后
    playGachaAnimation(results, function () {
      renderGachaResults(true);
      showToast(newCount
        ? ('抽卡完成：新激活 ' + newCount + ' 件图鉴！')
        : '抽卡完成（都是重复藏品，已折算金币）。');
    });
  }

  // 图鉴窗口：只显示图鉴内容
  function openCodex() {
    closeGacha(); // 两个模块互不共存：打开图鉴时收起抽卡窗口
    renderCodexGrid();
    ui.codexPopup.classList.remove('hidden');
  }

  function closeCodex() {
    hideCodexTip();
    ui.codexPopup.classList.add('hidden');
  }

  // 抽卡窗口：只显示抽卡内容
  function openGacha() {
    closeCodex(); // 两个模块互不共存：打开抽卡时收起图鉴窗口
    renderGacha();
    renderGachaResults();
    ui.gachaPopup.classList.remove('hidden');
  }

  function closeGacha() {
    // 动画播到一半就关窗口：直接跳到结果，别把已经抽到的图鉴吞了
    if (gachaAnimPlaying()) finishGachaAnimation(function () { renderGachaResults(false); });
    ui.gachaPopup.classList.add('hidden');
  }

  function setBattleLog(text) {
    ui.battleLog.textContent = text;
    pushBattleLog(text);
  }

  function updateBattleHud() {
    layoutBattleLogBtn();   // 战斗日志按钮跟着战斗信息面板走
    const pp = clamp(combat.hp / combat.maxHp, 0, 1) * 100;
    ui.playerHpBar.style.width = pp.toFixed(1) + '%';
    ui.playerHpText.textContent =
      Math.max(0, Math.ceil(combat.hp)) + ' / ' + combat.maxHp;
    // 魔力条：可用的部分 + 被持续 / 召唤技能占用的保留段
    const usablePct = clamp(derived.mpUsable / Math.max(1, combat.maxMp), 0, 1) * 100;
    if (ui.playerMpBar) ui.playerMpBar.style.width = usablePct.toFixed(1) + '%';
    if (ui.playerMpReserve) {
      ui.playerMpReserve.style.width = (100 - usablePct).toFixed(1) + '%';
    }
    // 显示「当前魔力 / 最大魔力」，被占用的部分另外标注（不再只显示扣掉占用后的值）
    ui.mpText.textContent = '魔力 ' + Math.ceil(combat.mp) + ' / ' + combat.maxMp +
      (derived.mpReservePct > 0
        ? '　（已占用 ' + derived.mpReservePct + '%，当前可用上限 ' + derived.mpUsable + '）'
        : '');
  }

  function showBattleUi(show) {
    ui.battleTint.classList.toggle('hidden', !show);
    ui.battleHud.classList.toggle('hidden', !show);
    // 战斗日志按钮：默认不显示，开战后出现在右上角战斗信息下方
    if (ui.btnLog) {
      ui.btnLog.classList.toggle('hidden', !show);
      if (show) layoutBattleLogBtn();
    }
  }

  // 「战斗日志」按钮贴在右上角战斗信息面板的正下方（面板高度会随文案变化，所以动态量）
  let logBtnClock = 0;
  function layoutBattleLogBtn() {
    if (!ui.btnLog || !ui.battleHud) return;
    const wrapEl = ui.wrap || document.body;
    if (ui.battleHud.classList.contains('hidden')) return;
    const wr = wrapEl.getBoundingClientRect();
    const hr = ui.battleHud.getBoundingClientRect();
    ui.btnLog.style.top = Math.round(hr.bottom - wr.top + 8) + 'px';
    ui.btnLog.style.right = Math.max(8, Math.round(wr.right - hr.right)) + 'px';
  }

  // ---------- 状态切换 ----------
 function startScene() {
    // 读档续玩：接着上次的里程继续（新档案 / 阵亡后回到起点时 resumeWorldX 为 null）
    player.worldX = resumeWorldX && resumeWorldX > START_X ? resumeWorldX : START_X;
    player.vx = 0;
    // 联网校时改到「打开世界BOSS窗口」时再做（避免开局多一次外部请求，失败也无影响）
    layoutHud();          // HUD 按舞台高度自适应（小窗口收窄按钮列 / 聊天窗）
   player.walkT = 0;
   player.animT = 0;
   playerAtkT = 0;
   // 休息姿态不重置：加载页 → 进入场景是同一个姿势，别在这一下突然换一个
   input.moving = false;
    player.hurtT = 0;
    battleTurn = 'player';
    enemyTurnTimer = 0;
    playerIdleTimer = 0;
    ally = null;
    corpse = null;
    // 新手宝箱：每个档案只给一次（已经用过就不再出现，随存档记录）
    if (!newbieChestUsed) {
      newbieChestUsed = true;
      chest = {
        worldX: START_X + 520,
        opened: false,
        weaponTaken: false,
        gemTaken: false,
        weapons: WEAPON_KINDS.map(function (kind) {
          const item = makeEquipment(1, 'common', 'weapon', kind.name);
          if (kind.name === '盾牌') {
            // 宝箱盾牌固定为 1 条"荆棘伤害 +4"，清掉随机到的那条词缀，避免名不符实
            item.hp = 0;
            item.armor = 0;
            item.atk = 0;
            item.speedPct = 0;
            item.mp = 0;
            item.res = 0;
            item.blockPct = 0;
            item.strength = 0;
            item.chaosRes = 0;
            item.critReduce = 0;
            item.thorns = 4;
            item.detail = '荆棘伤害 +4';
            item.price = rollGearPrice(item.rarity);
          }
          return item;
        }),
        chosenWeapon: null,
        gemItem: null,
        stoneItem: null,
        stoneTaken: false
      };
      saveGame();   // 立刻落盘，之后重开游戏不会再刷出这个宝箱
    } else {
      chest = null;
    }
    pets.length = 0; // 新一局清空出战宠物
    mirageFirstSpawned = false; // 新一局：第一个 1km 还会必出一次虚影
    pendingVictory = 0;
    recalcPlayerStats();
    combat.hp = combat.maxHp;
    combat.mp = derived.mpUsable;
    defeatedEnemies = 0;
    damagePopups = [];
    bloodWaves.length = 0;
    bloodWaveTimer = 0;
    spawnNextEnemy();
    // 只有在"自动化测试直连"模式（?skiplogin=1）下才发放测试资源，
    // 正常玩（哪怕在本机 127.0.0.1 上）不再有金币/活力金手指。
    if (!testGoldGranted && isTestEnvironment() && skipLoginRequested()) {
      testGoldGranted = true;
      gold = Math.max(gold, TEST_GOLD);
      vitality += TEST_VITALITY;
      saveGame(); // 立刻落盘，刷新/再登录时继续累加
    }
    updateGold();
    autoAdventure = false;
    autoKmLeft = 0;
    updateVitalityUI();
    updateLevelUI();
    updateOnlineUI();
    cans = [];
    canTimer = rand(6, 10);
    eliteCharges = 0;
    ally = null;
    legendMap = null;
    // 洞府：新一局清空入口与当前洞府状态（令牌数量保留在存档里）
    cave = null;
    caveEntrances.length = 0;
    caveNextX = player.worldX + rand(4200, 6200);
    battleGroup.length = 0;
    updateCaveBar();
    // 血瓶充能沿用存档里的值（新档案在 resetProgressState 里给满 5 层）
    updateFlaskUI();

    gameState = 'walk';
    ui.title.classList.add('hidden');
    ui.hud.classList.remove('hidden');
    ui.controls.classList.remove('hidden');
    ui.flaskWrap.classList.remove('hidden');
    ui.chatPanel.classList.remove('hidden');
    updateDonateButton();   // 进入游戏后隐藏首页的「赞助」按钮
    setupChat();
    // 战斗信息里的名字用玩家昵称（默认账号名）
    const whoEl = document.querySelector('#battleHud .who');
    if (whoEl) whoEl.textContent = chatNick();
    updateNickUI();
    updateEliteButton();
    updateLegendBar();
    clearBattleLog();
    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    updateDist();
    setBattleLog('');
    if (testGoldGranted) {
      showToast('【测试环境】已发放 ' + TEST_GOLD + ' 金币 · ' + TEST_VITALITY + ' 活力');
    }
    maybeStartFirstQuest();   // 正式进入游戏：弹出第一个任务「提升至武者大圆满」
  }

  function startBattle() {
    if (gameState !== 'walk' || !activeEnemy) return;
    gameState = 'battle';
    input.moving = false;
    player.vx = 0;
    player.worldX = activeEnemy.x - ENEMY_GAP_PX;
    // 路边遭遇也要把当前目标放进战斗队列（battleGroup[0] = activeEnemy）：
    // 群攻（破空斩）/ 阳爪流血 / 震撼大地的冲击波都是按 battleGroup 取目标，
    // 之前野外战斗没进队列，会导致这些效果在野外"打了但没伤害"。
    battleGroup.length = 0;
    battleGroup.push(activeEnemy);

    // 先手判定：比较双方移动速度，快者先行动
    const playerSpd = derived.speedMult;
    const enemySpd = activeEnemy.combatSpeed || 1;
    // 灵宝「先手符诏」：永远先手，不看移动速度
    const firstByTreasure = treasureSpecial('alwaysFirst');
    battleTurn = (firstByTreasure || playerSpd >= enemySpd) ? 'player' : 'enemy';
    if (firstByTreasure && playerSpd < enemySpd) {
      setBattleLog('灵宝「先手符诏」生效：你抢在敌人之前出手！');
    }
    enemyTurnTimer = battleTurn === 'enemy' ? 0.85 : 0;
    playerIdleTimer = battleTurn === 'player' ? 10 : 0;
    defending = false;          // 新战斗：清掉上一场残留的防御姿态
    defendFx = null;            // 新战斗：清掉上一场残留的护盾特效
    airSlashFx = null;          // 新战斗：清掉上一场残留的剑气
    activeEnemy.openingAttack = battleTurn === 'enemy';
    ally = null;
    enemyMinionTurns = 0;
    updateEliteButton();

    showBattleUi(true);
    setControls('battle');
    syncMoveButton();
    updateBattleHud();
    if (isStaffEquipped() && !petAlive()) activatePet();
    const petNote = isStaffEquipped() ? ' 法杖宠物已自动现身助战。' : '';
    if (battleTurn === 'player') {
      setBattleLog(
        '你更快（移速 ' + Math.round(playerSpd * 100) + '% vs ' +
        Math.round(enemySpd * 100) + '%），获得先手！' + petNote
      );
    } else {
      setBattleLog(
        '敌人更快（移速 ' + Math.round(enemySpd * 100) + '% vs ' +
        Math.round(playerSpd * 100) + '%），抢先发起攻击！' + petNote
      );
    }
    pushBattleLog('遭遇 ' + (activeEnemy.name || '拦路者') + ' Lv.' + activeEnemy.level, 'system');
    // 新一场战斗：清掉上一场留下的控制 / 点燃状态
    playerStunTurns = 0;
    playerFrozenTurns = 0;
    playerBurnStacks = 0;
    caveAuraClock = 0;
    // 洞府敌人：进场就把它的特殊增幅说清楚，免得玩家以为是 bug
    if (activeEnemy.caveBuff) {
      const b = activeEnemy.caveBuff;
      pushBattleLog('⚠ 洞府增幅：' + (activeEnemy.name || '敌人') + ' 带「' + b.name + '」——' + b.desc, 'enemy');
      addTextPopup(activeEnemy.x - cameraX() + 88, GROUND_Y - 226, b.name, b.color);
    }
    // 先手是玩家时，第一回合也算一个"回合"（图腾圣物同样会出手）
    if (battleTurn === 'player') totemTurnSpell();
  }

  function winBattle() {
    // 世界BOSS：走单独的结算（不发尸体、不刷路上的敌人）
    if (worldBossRun) { finishWorldBossFight(); return; }
    const level = activeEnemy.level;
    const corpseX = activeEnemy.x;
    const wasElite = !!activeEnemy.elite;
    const wasLegend = !!activeEnemy.legend;
    const enemyTier = activeEnemy.tier || 'common';
    const enemySprite = activeEnemy.sprite;
    activeEnemy = null;
    battleGroup.length = 0;
    defeatedEnemies++;
    // 击杀经验随敌人等级提升：Lv1 敌人 10 点
    gainXp(10 * Math.max(1, level));
    pendingVictory = 0;
    damagePopups = [];
    player.hurtT = 0;
    battleTurn = 'player';
    enemyTurnTimer = 0;
    playerIdleTimer = 0;
    ally = null;
    enemyMinionTurns = 0;

    // 简易战利品：恢复部分生命与魔力
    combat.hp = Math.min(combat.maxHp, combat.hp + 22);
    combat.mp = derived.mpUsable;
    // 灵宝「聚宝灵纹」：每场战斗胜利必然额外掉 10 金币
    if (treasureSpecial('goldPerWin')) {
      gold += 10;
      updateGold();
      pushBattleLog('灵宝「聚宝灵纹」：本场胜利额外获得 10 金币', 'reward');
    }
    // 血瓶充能：不再"杀一个就 +1"，而是累计【10 个普通敌人 + 1 个精英/传奇敌人】
    addFlaskKill(wasElite || wasLegend);
    // 留下可搜索的尸体（记住类型，尸体用同一套美术）
    // 掉落物在这里就掷好"摆在原地"：有装备就按品级亮光柱，只有金币就是默认样式
    // 洞府内的怪：必掉一件装备（品级仍然按敌人品级封顶）
    const caveGear = !!cave;
    corpse = {
      worldX: corpseX, level: level, tier: enemyTier, sprite: enemySprite, searched: false,
      forceGear: caveGear,
      drops: rollCorpseLoot(level, enemyTier, false, caveGear)
    };

    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    gameState = 'walk';
    if (cave) {
      // 洞府：清掉这一波后，还有敌人就继续下一波，清空则结算洞府奖励
      if (cave.remaining > 0) {
        setTimeout(function () { if (cave) spawnCaveEnemy(); }, 700);
      } else {
        finishCave();
      }
    } else if (legendMap) {
      if (legendMap.wave >= 10) {
        legendMap = null;
        updateLegendBar();
        spawnNextEnemy();
        setTimeout(function () { showToast('传奇BOSS地图通关！'); }, 1300);
      } else {
        spawnNextLegendWave();
      }
    } else {
      spawnNextEnemy();
    }
    updateEliteButton();
    updateDist();
    updateBattleHud();

    const km = (player.worldX - START_X) / KM_UNIT;
    let msg = '击败 Lv.' + level + ' 拦路者！点击地上的尸体搜索战利品（' + km.toFixed(1) + ' km）';
    if (defeatedEnemies % 10 === 0) {
      eliteCharges += 1;
      msg = '累计击败 10 名敌人，获得 1 次精英挑战机会！';
    }
    if (wasLegend) {
      // 传奇敌人：奖励显著更好
      const rareA = makeEquipment(level, 'rare');
      const rareB = makeEquipment(level, 'rare');
      const stoneA = makeActiveSkillStone();
      const stoneB = makeActiveSkillStone();
      const gem = gemOptionToItem(GEM_OPTIONS[randInt(0, GEM_OPTIONS.length - 1)]);
      const bonusGold = randInt(100, 200);
      gold += bonusGold;
      updateGold();
      inventory.push(rareA);
      inventory.push(rareB);
      inventory.push(stoneA);
      inventory.push(stoneB);
      inventory.push(gem);
      if (Math.random() < 0.001) inventory.push(makeLegendStone());
      if (Math.random() < 0.2) inventory.push(makePetSkillBook());
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      pushBattleLog('传奇战利品：黄色装备 ×2、主动技能石 ×2、辅助技能石 ×1、金币 +' + bonusGold, 'reward');
      setTimeout(function () {
        showToast('传奇战利品：黄色装备 ×2、主动技能石 ×2、辅助技能石 ×1、金币 +' + bonusGold);
      }, 1200);
    } else if (wasElite) {
      // 精英：必掉黄色装备 + 主动技能石
      const rareGear = makeEquipment(level, 'rare');
      const stone = makeActiveSkillStone();
      inventory.push(rareGear);
      inventory.push(stone);
      let extra = '';
      // 小概率掉落：传奇Boss召唤石 0.1%
      if (Math.random() < 0.001) {
        inventory.push(makeLegendStone());
        extra += ' + 传奇Boss召唤石 ×1';
      }
      // 宠物技能书：精英 5%
      if (Math.random() < 0.05) {
        inventory.push(makePetSkillBook());
        extra += ' + 宠物技能书 ×1';
      }
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      pushBattleLog('精英战利品：黄色装备「' + rareGear.name + '」+ 主动技能石 ×1' + extra, 'reward');
      setTimeout(function () {
        showToast('精英战利品：黄色装备「' + rareGear.name + '」+ 主动技能石 ×1' + extra);
      }, 1200);
    }
    updateEliteButton();
    pushBattleLog(msg, 'reward');
    showToast(msg);
  }

  function showToast(text) {
    const el = document.createElement('div');
    el.id = 'toast';
    el.textContent = text;
    // 挂在 #wrap 上并抬高 z-index，保证弹窗（例如角色窗口）打开时提示依然可见
    (ui.wrap || ui.hud).appendChild(el);
    setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 2300);
  }

  // ---------- 战斗行动 ----------
  // 玩家行动结束后切换到敌人回合，敌人稍后再反击，避免双方同时出手
  function beginEnemyTurn(message) {
    if (gameState !== 'battle' || !activeEnemy || pendingVictory > 0) return;
    battleTurn = 'enemy';
    enemyTurnTimer = 0.9;
    setBattleLog(message || '拦路者正在逼近，准备反击……');
    setControls('battle');
  }

  function resetPlayerIdleTimer() {
    playerIdleTimer = 10;
  }

  // 玩家先手但持续 10 秒不行动：敌人会抢先进攻
  function updatePlayerIdleTimer(dt) {
    if (gameState !== 'battle' || battleTurn !== 'player' || pendingVictory > 0) return;
    if (slashFx) return;
    playerIdleTimer -= dt;
    if (playerIdleTimer <= 0) {
      playerIdleTimer = 10;
      enemyCounterAttack(false, true);
    }
  }

  // 爪：普通攻击命中后吸取 3 点生命
  function clawLeech() {
    let heal = 0;
    if (equipment.weapon && equipment.weapon.weaponType === '爪') heal += 3;
    // 灵宝「血饮魔纹」：每次命中额外回复 5% 最大生命
    if (treasureSpecial('lifesteal10')) heal += Math.round(combat.maxHp * 0.05);
    if (heal <= 0) return;
    if (combat.hp >= combat.maxHp) return;
    heal = Math.min(heal, combat.maxHp - combat.hp);
    combat.hp += heal;
    addDamagePopup(
      player.worldX - cameraX() - 82,
      GROUND_Y - 160,
      heal,
      '#6dff8d',
      true
    );
    updateBattleHud();
  }

  // 宠物的技能来源：法杖灵宠用「召唤宠物」，技能召唤出来的用对应召唤技能
  function petSkillDef(p) {
    const id = (p && p.skillId) || 'summonPet';
    return knownSkills.filter(function (s) { return s.id === id; })[0];
  }

  // 宠物攻击数值：玩家本次普攻的 35%
  function petAttackValue(p) {
    if (!p) return 0;
    const lvl = activeEnemy ? activeEnemy.level : Math.max(1, defeatedEnemies + 1);
    const base = randInt(23 + derived.flatAtk, 34 + derived.flatAtk) + Math.floor(lvl / 3);
    // 魔力属性加成：每 1 点魔力提高 1 点宠物伤害
    const energyBonus = Math.max(0, Math.round(combat.maxMp));
    const petSkill = petSkillDef(p);
    const gemMult = 1 + skillGemSum(p.skillId || 'summonPet', ['summonDamage']) / 100;
    // 「处决」这类通用辅助对被辅助的召唤技能同样生效
    const supMult = supportDamageMult(p.skillId || 'summonPet', activeEnemy);
    return Math.max(1, Math.round(
      base * 0.35 * (p.atkMult || 1) * gemMult * supMult * skillLevelMult(petSkill)
    ) + energyBonus);
  }

  // 所有宠物一起出手（列表里每只都打一次），返回 true 表示敌人已被击败
  let lastPetParts = []; // 最近一次宠物出手的逐只明细（供战报逐条列出）
  function petFollowUp() {
    if (!petAlive() || gameState !== 'battle' || !activeEnemy) return false;
    const list = alivePets();
    // 每只宠物各自的伤害单独飘字（带名字），方便对比各宠物的输出效率
    const PET_HIT_COLORS = {
      scorpio: '#ffd070', hyena: '#ff9a5a', snake: '#8fe0a0', vulture: '#9fd4ff'
    };
    const parts = [];
    lastPetParts = parts;
    const enemyPopX = activeEnemy.x - cameraX() + 88;
    let total = 0;
    for (let i = 0; i < list.length; i++) {
      const p = list[i];
      p.attackT = 0.45; // 播放攻击动作
      p.animT = 0;
      petAttackCount += 1;
      // 世界BOSS：境界不够，宠物同样打不动（只显示 MISS）
      if (worldBossRun && !worldBossLevelOk()) {
        addTextPopup(enemyPopX, GROUND_Y - 150 - i * 34, 'MISS', '#9fb6d4');
        parts.push('「' + p.name + '」MISS');
        pushBattleLog('宠物「' + p.name + '」攻击世界BOSS：境界不足，MISS', 'system');
        continue;
      }
      const dmg = petAttackValue(p);
      total += dmg;
      const color = PET_HIT_COLORS[p.sprite] || '#ffe08a';
      activeEnemy.hp -= dmg;
      activeEnemy.hurtT = HURT_TIME;
      // 飘在敌人身上：第 1 只最上，之后依次往下错开，避免重叠
      addTextPopup(enemyPopX, GROUND_Y - 150 - i * 34, p.name + ' ' + dmg, color);
      parts.push('「' + p.name + '」' + dmg);
      pushBattleLog('宠物「' + p.name + '」造成 ' + dmg + ' 点伤害', 'reward');
      if (activeEnemy.hp <= 0) {
        activeEnemy.hp = 0;
        setBattleLog('「' + p.name + '」扑向敌人，造成 ' + dmg + ' 点伤害并终结战斗！');
        updateBattleHud();
        if (!resolveEnemyDown()) return false;
        pendingVictory = 0.5;
        return true;
      }
    }
    // 战报逐只列出，不再合并成一个总数
    setBattleLog('宠物出手：' + parts.join('，'));
    updateBattleHud();
    return total;
  }

  // 召唤普通敌人（临时召唤物，攻击 3 次）
  function allyFollowUp() {
    if (!ally || ally.turns <= 0 || gameState !== 'battle' || !activeEnemy) return false;
    const allySkill = knownSkills.filter(function (s) { return s.id === ally.skillId; })[0];
    const mult = (1 + skillGemSum(ally.skillId, ['summonDamage']) / 100) * skillLevelMult(allySkill);
    const dmg = Math.max(1, Math.round(randInt(ally.dmg[0], ally.dmg[1]) * mult));
    activeEnemy.hp -= dmg;
    activeEnemy.hurtT = HURT_TIME;
    addDamagePopup(
      activeEnemy.x - cameraX() + 104,
      GROUND_Y - 196,
      dmg,
      '#b28cff'
    );
      ally.turns -= 1;
    if (activeEnemy.hp <= 0) {
      activeEnemy.hp = 0;
      ally = null;
      setBattleLog('你的召唤物造成 ' + dmg + ' 点伤害并终结了敌人！');
      updateBattleHud();
      if (!resolveEnemyDown()) return false;
      pendingVictory = 0.5;
      return true;
    }
    if (ally.turns <= 0) ally = null;
    updateBattleHud();
    return dmg;
  }

  // 玩家出手后：宠物 → 召唤物 → 敌人回合
  function afterPlayerOffense() {
    const parts = [];
    const petOut = petFollowUp();
    if (petOut === true) return true;
    if (typeof petOut === 'number' && petOut > 0) {
      // 逐只列出宠物伤害（lastPetParts 由 petFollowUp 填好）
      parts.push('宠物出手 ' + (lastPetParts.length
        ? lastPetParts.join('、')
        : (petOut + ' 点伤害')));
    }
    const allyOut = allyFollowUp();
    if (allyOut === true) return true;
    if (typeof allyOut === 'number') parts.push('召唤物造成 ' + allyOut + ' 点伤害');
    beginEnemyTurn(parts.length ? parts.join('，') + '，敌人准备反击……' : '');
    return false;
  }

  function doPlayerAction(type) {
    // 法术按钮：非战斗场景也能打开技能窗口（只看不施法），所以放在战斗判定之前
    if (type === 'spell' && gameState === 'walk') {
      openSpellPanel();
      return;
    }
    if (gameState !== 'battle' || !activeEnemy) return;
    if (pendingVictory > 0) return;
    if (battleTurn !== 'player') return;
    if (slashFx) {
      setBattleLog('血刀还在劈落途中，先等这一击落地……');
      return;
    }

    if (type === 'attack') {
      resetPlayerIdleTimer();
      playPlayerAttack(); // 角色出手动作
      startAttackFx(activeEnemy.x); // 普通攻击也挥一刀（纯视觉，不影响结算）
      // 世界BOSS：境界不够 → 这一击直接 MISS（只挨打、打不动）
      if (worldBossRun && !worldBossLevelOk()) {
        worldBossMiss('你挥出一击，却从「' + worldBossRun.def.name + '」身上穿了过去——境界不足，MISS');
        afterPlayerOffense();
        updateBattleHud();
        return;
      }
      const baseDmg = Math.max(1, Math.round(
        (randInt(23 + derived.flatAtk, 34 + derived.flatAtk) + Math.floor(activeEnemy.level / 3)) *
        gemDamageMult('attack')
      ));
      const hit = rollPlayerDamage(baseDmg);
      const dmg = hit.dmg;
      // 攻击速度：按概率追加一次 60% 伤害的追击
      const extraHit = derived.atkSpeed > 0 && Math.random() * 100 < derived.atkSpeed;
      const extraDmg = extraHit ? Math.max(1, Math.round(dmg * 0.6)) : 0;
      activeEnemy.hp -= dmg + extraDmg;
      activeEnemy.hurtT = HURT_TIME;
      caveReflectOnPlayerHit(activeEnemy, dmg + extraDmg);   // 洞府「反伤」增幅
      addDamagePopup(
        activeEnemy.x - cameraX() + 88,
        GROUND_Y - 148,
        dmg,
        hit.crit ? '#ff4d4d' : '#ff5b40'
      );
      maybeApplyBurn(activeEnemy, 'slash');   // 炎军：普通攻击（共用「劈砍」的宝石）
      if (extraDmg) maybeApplyBurn(activeEnemy, 'slash');   // 攻速追击也算一次伤害
      setBattleLog(
        '你发起攻击，造成 ' + dmg + ' 点伤害' +
        (hit.crit ? '（暴击）' : '') +
        (extraDmg ? '，攻速追加 ' + extraDmg + ' 点' : '') + '。'
      );
      combat.mp = Math.min(derived.mpUsable, combat.mp + 5);
      clawLeech();
      if (activeEnemy.hp <= 0) {
        activeEnemy.hp = 0;
        setBattleLog('你击败了一个敌人！');
        updateBattleHud();
        if (!resolveEnemyDown()) {
          // 还有同伙：这一回合他们照样会还手（不能白打一轮）
          afterPlayerOffense();
          updateBattleHud();
          return;
        }
        pendingVictory = 0.4;
        return;
      }
      afterPlayerOffense();
    } else if (type === 'spell') {
      // 打开已学习技能窗口，由玩家选择要使用的技能
      openSpellPanel();
      return;
    } else if (type === 'defend') {
      // 防御：本回合受到的敌人伤害 -35%，另有 5% 几率完全格挡（原「逃跑」已移除）
      resetPlayerIdleTimer();
      defending = true;
      startDefendFx();   // 角色右侧激活护盾特效（pipo-btleffect208）
      addTextPopup(player.worldX - cameraX() - 86, GROUND_Y - 150, '防御', '#9fd4ff');
      const msg = '你举起盾牌进入防御姿态：受到的伤害 -' + Math.round(DEFEND_REDUCE * 100) +
        '%，并有 ' + DEFEND_BLOCK_PCT + '% 几率完全格挡。';
      setBattleLog(msg);
      pushBattleLog(msg, 'player');
      updateBattleHud();
      afterPlayerOffense();
      return;
    }
    updateBattleHud();
  }

  function openSpellPanel() {
    // 非战斗场景也允许打开（查看技能 / 镶嵌宝石），只是点技能格不会施法
    if (gameState !== 'battle' && gameState !== 'walk') return;
    renderSpellList();
    ui.spellPopup.classList.remove('hidden');
  }

  function closeSpellPanel() {
    hideSkillTip();
    ui.spellPopup.classList.add('hidden');
  }

  // 激活 / 取消激活：自动冒险时优先使用的技能（全局只有一个）
  function toggleAutoSkill(skill) {
    if (!skill) return false;
    if (skill.id === 'summonPet') {
      showToast('「召唤宠物」是法杖带来的被动技能，不需要激活。');
      return false;
    }
    if (autoSkillId === skill.id) {
      autoSkillId = null;
      showToast('已取消「' + skill.name + '」的自动冒险优先。');
    } else {
      autoSkillId = skill.id;
      showToast('已激活「' + skill.name + '」：自动冒险时会优先使用它。');
    }
    saveGame();
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    return autoSkillId === skill.id;
  }

  // ---------- 尸体搜索 ----------
  function searchCorpse(silent) {
    if (!corpse || corpse.searched) return;
    // 掉落物在敌人倒下时就已经摆在原地了，这里把它真正收进背包
    const fromCave = !!corpse.forceGear;   // 洞府内的怪：必掉装备
    const drops = (corpse.drops && corpse.drops.length)
      ? corpse.drops
      : rollCorpseLoot(corpse.level, corpse.tier || 'common', false, fromCave);
    collectCorpseDrops(drops);
    corpse = null;
    ui.lootList.textContent = '';
    drops.forEach(function (d) {
      const row = document.createElement('div');
      row.className = 'lootRow';

      const icon = document.createElement('div');
      icon.className = 'lootIcon';
      fillItemIcon(icon, d, d.icon || '?');
      icon.style.borderColor = d.rarityColor || 'rgba(255,255,255,.2)';
      row.appendChild(icon);

      const info = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'lootName';
      name.textContent = d.name || '';
      name.style.color = d.rarityColor || '#ffe9c0';
      const meta = document.createElement('div');
      meta.className = 'lootMeta';
      meta.textContent = d.detail || '';
      info.appendChild(name);
      info.appendChild(meta);
      row.appendChild(info);

      const kind = document.createElement('span');
      kind.className = 'lootMeta';
      kind.textContent = d.type === 'equipment' ? '装备' : d.type === 'potion' ? '道具' : d.type === 'material' ? '材料' : '金币';
      row.appendChild(kind);
      ui.lootList.appendChild(row);
    });
    // （以前这里会在掉落列表最下面塞一条说明文字，每次搜尸体都出现一次，太吵，已经移除。
    //   规则本身没变：洞府里的怪必掉装备，品级由怪物品级决定。）
    if (silent) {
      const summary = drops.map(function (d) {
        return d.kind === 'gold' ? '金币' + d.detail : d.name;
      }).join('、');
      showToast('自动搜索尸体：' + summary);
    } else {
      ui.lootPopup.classList.remove('hidden');
    }
    // 搜索尸体不再清空日志：日志会保留到玩家手动关闭日志窗口为止
  }

  function closeLootPanel() {
    ui.lootPopup.classList.add('hidden');
  }

  // ---------- 装备 / 背包 ----------
  function openGearPanel() {
    renderGearPanel();
    ui.gearPopup.classList.remove('hidden');
  }

  function closeGearPanel() {
    hideItemTip();
    ui.gearPopup.classList.add('hidden');
  }

  // ---------- 装备洗练 ----------
  let reforgeItem = null;      // 当前在洗练窗口里的装备
  // ============================================================
  // 世界BOSS
  // · 刷新后 3 分钟内可挑战；没被击杀就自己消失，进入冷却后再刷新。
  // · 传送后是一场特殊战斗：BOSS 用敌人美术放大，场上还有一堆"其他玩家"的虚影
  //   （本地机器人）一起打，制造"很多人在打同一个BOSS"的感觉。
  // · 只要玩家对 BOSS 造成过伤害 → 发参与奖励（金币 + 黄色稀有装备 + 世界BOSS宝箱）。
  // · 后期联网：把这里的血量/伤害换成服务端数据 + 排行榜即可。
  // ============================================================
  const WB_WINDOW_SEC = 180;          // 刷新后存在 3 分钟（没被击杀就自己消失）
  // 刷新时刻：东八区（上海）每天 12 次 —— 00:15 / 02:15 / 04:15 … 22:15
  const WB_SCHEDULE_HOURS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22];
  const WB_SPAWN_MINUTE = 15;
  const WB_TZ_OFFSET_MS = 8 * 60 * 60 * 1000;   // 东八区 UTC+8
  const WB_REWARD_GOLD = 1000;
  const WB_CHEST_TREASURE_CHANCE = 1; // 世界BOSS宝箱开出灵宝的几率（%）
  const WB_ARENA_X = 4000000;         // 世界BOSS战场（远离公路，不会影响路上的敌人）
  const WB_BOSS_SCREEN_X = 880;       // BOSS 在屏幕上的位置（略左移，方便挑战者围在脚下）
  const WB_GHOST_COUNT = 11;          // 场上"其他玩家"虚影数量
  const WB_LOOT_SECONDS = 60;         // 击杀BOSS后，走过去开箱的倒计时（到点自动开）
  const WB_LOOT_CHEST_SCREEN_X = 640; // 宝箱出现的位置：屏幕正中央
  const WB_LOOT_CHEST_DRAW_W = 150;   // 宝箱绘制宽度（原图 895×736）
  const WB_LOOT_REACH = 170;          // 站得多近才能开箱（屏幕像素）
  const WB_GIFT_NOTE = '这只是世界BOSS散落的战利品，正在展示中——谁都拿不走。';
  const WB_GHOST_NAMES = [
    '夜行者', '孤帆远影', '铁匠老王', '白露未晞', '沙漠之鹰', '青衫客',
    '一剑霜寒', '荒野郎中', '逐日者', '月下独行', '半盏清茶', '拾荒人'
  ];

  const wb = {
    phase: 'wait',   // wait = 等刷新；alive = 已刷新可挑战
    nextAt: 0,       // 下一次刷新时间戳
    expiresAt: 0,    // 本次刷新的最后时刻（3 分钟后自己消失）
    spawnedAt: 0,    // 本次刷新的时刻
    maxHp: 0,
    hp: 0,
    killed: 0,
    killedAt: 0,     // 本轮被击杀的刷新时刻（防止同一轮重复刷新）
    claimed: false,  // 本轮（每次刷新）是否已经发过参与奖励
    lastRewardDay: '' // 每日奖励记录（上海日期，每天只能领一次）
  };
  let wbClockSkew = 0;        // 联网校时偏移（毫秒）
  let wbClockSource = 'local'; // 'local' = 本机时钟；'internet' = 已用互联网时间校准
  let wbOverride = null;      // 调试/测试用：直接指定本次刷新窗口
  let wbScheduleClock = 0;    // 面板倒计时刷新节流

  // ---------- 世界BOSS 战场环境效果（血沙场景）----------
  const wbEmbers = [];        // 上升的余烬火星
  let wbDustBands = [];       // 贴地的沙尘带
  let wbClouds = [];          // 血色云层
  let wbEnvReady = false;
  let wbFlash = 0;            // 血雷闪强度（1 → 0）
  let wbFlashTimer = 5;       // 距离下一次血雷闪
  let wbFlashHold = false;    // 调试用：钉住闪光强度方便测试
  let lastBossArtRect = null; // 最近一次绘制的 BOSS 立绘屏幕矩形（调试用）

  function initWbEnv() {
    if (wbEnvReady) return;
    wbEnvReady = true;
    wbClouds = [];
    for (let i = 0; i < 6; i++) {
      wbClouds.push({
        x: rand(-240, W + 240), y: rand(30, 230), rx: rand(120, 260), ry: rand(26, 60),
        spd: rand(5, 16), a: rand(0.05, 0.14), seed: rand(0, 10)
      });
    }
    wbDustBands = [];
    for (let i = 0; i < 8; i++) {
      wbDustBands.push({
        x: rand(-360, W), y: GROUND_Y + rand(-30, 6), w: rand(180, 460), h: rand(9, 24),
        spd: rand(26, 68), a: rand(0.05, 0.13)
      });
    }
    wbEmbers.length = 0;
    wbFlash = 0;
    wbFlashTimer = rand(4, 9);
  }

  function updateWbEnv(dt) {
    if (!worldBossRun) {
      wbEnvReady = false;
      wbEmbers.length = 0;
      wbDustBands = [];
      wbClouds = [];
      wbFlash = 0;
      wbFlashHold = false;
      lastBossArtRect = null;
      return;
    }
    initWbEnv();
    // 血色云层：慢慢横移，飘出画面就从另一侧补进来
    wbClouds.forEach(function (c) {
      c.x -= c.spd * dt;
      if (c.x + c.rx < -60) {
        c.x = W + rand(60, 260);
        c.y = rand(30, 230);
        c.rx = rand(120, 260);
        c.ry = rand(26, 60);
        c.a = rand(0.05, 0.14);
      }
    });
    // 贴地沙尘带：顺着风向往左掠过地面
    wbDustBands.forEach(function (b) {
      b.x -= b.spd * dt;
      if (b.x + b.w < -80) {
        b.x = W + rand(40, 320);
        b.y = GROUND_Y + rand(-30, 6);
        b.w = rand(180, 460);
        b.h = rand(9, 24);
        b.a = rand(0.05, 0.13);
      }
    });
    // 余烬：从地面慢慢往上飘，带一点左右摇摆
    if (wbEmbers.length < 70 && Math.random() < dt * 28) {
      const n = randInt(1, 3);
      for (let i = 0; i < n; i++) {
        wbEmbers.push({
          x: rand(-20, W + 20),
          y: GROUND_Y - rand(-14, 26),
          vx: rand(-16, 16),
          vy: -rand(16, 48),
          r: rand(1.1, 3.2),
          t: 0,
          life: rand(1.5, 3.4),
          sway: rand(0, 10),
          hot: Math.random() < 0.35
        });
      }
    }
    for (let i = wbEmbers.length - 1; i >= 0; i--) {
      const e = wbEmbers[i];
      e.t += dt;
      e.x += (e.vx + Math.sin((e.t + e.sway) * 2.4) * 12) * dt;
      e.y += e.vy * dt;
      e.vy -= 4 * dt;                 // 越飘越慢
      if (e.t >= e.life || e.y < GROUND_Y - 430) wbEmbers.splice(i, 1);
    }
    // 血雷闪：隔一段时间来一下，照亮整片战场
    wbFlashTimer -= dt;
    if (wbFlashTimer <= 0) {
      wbFlash = 1;
      wbFlashTimer = rand(7, 15);
    }
    if (wbFlash > 0 && !wbFlashHold) wbFlash = Math.max(0, wbFlash - dt * 2.4);
  }

  function drawWbClouds() {
    wbClouds.forEach(function (c) {
      const g = ctx.createRadialGradient(c.x, c.y, 1, c.x, c.y, c.rx);
      g.addColorStop(0, 'rgba(96, 18, 16, ' + c.a.toFixed(3) + ')');
      g.addColorStop(1, 'rgba(96, 18, 16, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, c.rx, c.ry, 0, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  function drawWbDustBands() {
    wbDustBands.forEach(function (b) {
      const g = ctx.createLinearGradient(b.x, 0, b.x + b.w, 0);
      g.addColorStop(0, 'rgba(226, 186, 132, 0)');
      g.addColorStop(0.4, 'rgba(226, 186, 132, ' + b.a.toFixed(3) + ')');
      g.addColorStop(0.75, 'rgba(214, 168, 112, ' + (b.a * 0.75).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(198, 150, 96, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(b.x + b.w / 2, b.y, b.w / 2, b.h, 0, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  function drawWbEmbers() {
    if (!wbEmbers.length) return;
    ctx.save();
    wbEmbers.forEach(function (e) {
      const p = clamp(e.t / e.life, 0, 1);
      const a = (p < 0.15 ? p / 0.15 : 1 - (p - 0.15) / 0.85) * (e.hot ? 0.9 : 0.6);
      const glow = ctx.createRadialGradient(e.x, e.y, 0, e.x, e.y, e.r * 4);
      glow.addColorStop(0, 'rgba(255, 222, 156, ' + a.toFixed(3) + ')');
      glow.addColorStop(0.4, 'rgba(255, 142, 62, ' + (a * 0.6).toFixed(3) + ')');
      glow.addColorStop(1, 'rgba(255, 90, 40, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r * 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255, 238, 196, ' + Math.min(1, a * 1.2).toFixed(3) + ')';
      ctx.beginPath();
      ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  function drawWbFlash() {
    if (wbFlash <= 0) return;
    ctx.save();
    ctx.fillStyle = 'rgba(255, 96, 72, ' + (wbFlash * 0.16).toFixed(3) + ')';
    ctx.fillRect(-40, -40, W + 80, H + 80);
    ctx.restore();
  }

  // ---------- 等级门槛：境界低于世界BOSS → 全部 MISS ----------
  const WB_LEVEL_BOOST = 5;   // 稀有灵宝「破境符诏」：挑战世界BOSS时临时 +5 级
  // 世界BOSS 的门槛按「境界」显示（武者三境 · 炼气境 …），不再显示 Lv.N
  function stageText(level) {
    return stageName(clamp(Math.round(Number(level) || 1), 1, MAX_STAGE));
  }
  function worldBossPlayerLevel() {
    return playerLevel() + (treasureSpecial('levelBoost') ? WB_LEVEL_BOOST : 0);
  }
  function worldBossLevelOk() {
    if (!worldBossRun) return true;
    return worldBossPlayerLevel() >= worldBossRun.boss.level;
  }
  function worldBossMiss(message) {
    if (!worldBossRun) return;
    const boss = worldBossRun.boss;
    addTextPopup(boss.x - cameraX() + 70, GROUND_Y - 190, 'MISS', '#9fb6d4');
    const msg = message || ('你的境界低于「' + worldBossRun.def.name + '」（需要 ' + stageText(boss.level) +
      '，你当前是 ' + stageText(worldBossPlayerLevel()) + '），攻击完全无法破防');
    setBattleLog(msg + '。');
    pushBattleLog(msg, 'system');
    updateBattleHud();
  }

  // ---------- 时间：一律按东八区（上海）算，和本机时区无关 ----------
  function wbNow() { return Date.now() + wbClockSkew; }
  function pad2(n) { return String(n).padStart(2, '0'); }
  function shanghaiParts(ms) {
    const d = new Date(ms + WB_TZ_OFFSET_MS);
    return {
      y: d.getUTCFullYear(), mo: d.getUTCMonth() + 1, d: d.getUTCDate(),
      h: d.getUTCHours(), mi: d.getUTCMinutes(), s: d.getUTCSeconds(), wd: d.getUTCDay()
    };
  }
  function shanghaiDayKey(ms) {
    const p = shanghaiParts(ms);
    return p.y + '-' + pad2(p.mo) + '-' + pad2(p.d);
  }
  function shanghaiClockText(ms) {
    const p = shanghaiParts(ms);
    return pad2(p.h) + ':' + pad2(p.mi) + ':' + pad2(p.s);
  }
  function shanghaiDayStart(ms) {
    return Math.floor((ms + WB_TZ_OFFSET_MS) / 86400000) * 86400000 - WB_TZ_OFFSET_MS;
  }
  function wbSpawnAtInDay(dayStartMs, hour) {
    return dayStartMs + hour * 3600000 + WB_SPAWN_MINUTE * 60000;
  }
  // <= ms 的最近一次刷新时刻
  function wbLastSpawnAt(ms) {
    const dayStart = shanghaiDayStart(ms);
    for (let day = 0; day >= -2; day--) {
      for (let i = WB_SCHEDULE_HOURS.length - 1; i >= 0; i--) {
        const t = wbSpawnAtInDay(dayStart + day * 86400000, WB_SCHEDULE_HOURS[i]);
        if (t <= ms) return t;
      }
    }
    return wbSpawnAtInDay(dayStart - 3 * 86400000, WB_SCHEDULE_HOURS[0]);
  }
  // > ms 的下一次刷新时刻
  function wbNextSpawnAt(ms) {
    const dayStart = shanghaiDayStart(ms);
    for (let day = 0; day <= 2; day++) {
      for (let i = 0; i < WB_SCHEDULE_HOURS.length; i++) {
        const t = wbSpawnAtInDay(dayStart + day * 86400000, WB_SCHEDULE_HOURS[i]);
        if (t > ms) return t;
      }
    }
    return wbSpawnAtInDay(dayStart + 3 * 86400000, WB_SCHEDULE_HOURS[0]);
  }
  // 当天全部刷新时刻（面板展示用）
  function wbTodayTimes(ms) {
    const dayStart = shanghaiDayStart(ms);
    return WB_SCHEDULE_HOURS.map(function (h) { return wbSpawnAtInDay(dayStart, h); });
  }
  // 可选的互联网校时：成功就用网络时间，失败/离线就继续用本机时钟（东八区换算）
  function syncInternetTime() {
    if (skipLoginRequested()) return;          // 自动化测试模式不外联
    if (navigator.onLine === false) return;
    let settled = false;
    const finish = function (utcMs) {
      if (settled || !utcMs || isNaN(utcMs)) return;
      settled = true;
      wbClockSkew = utcMs - Date.now();
      wbClockSource = 'internet';
      if (!ui.wbPopup.classList.contains('hidden')) renderWorldBossPanel();
    };
    const timer = setTimeout(function () { settled = true; }, 5000);
    fetch('https://worldtimeapi.org/api/timezone/Asia/Shanghai', { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (d) { clearTimeout(timer); finish(Date.parse(d.utc_datetime || d.datetime)); })
      .catch(function () { clearTimeout(timer); /* 失败就静默用本机时钟 */ });
  }
  let worldBossRun = null;   // 正在打的世界BOSS战场状态
  let wbGhostSeq = 0;

  function worldBossDefOf(id) {
    return WORLD_BOSSES.filter(function (d) { return d.id === id; })[0] || null;
  }
  function openWorldBossDef() {
    return WORLD_BOSSES.filter(function (d) { return d.open; })[0] || null;
  }
  function wbAlive() {
    return wb.phase === 'alive' && wbNow() < wb.expiresAt;
  }
  function fmtClock(ms) {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return m + ':' + String(s).padStart(2, '0');
  }
  // 长时间用「X 小时 Y 分」，短时间内用 mm:ss
  function fmtDuration(ms) {
    const total = Math.max(0, Math.ceil(ms / 1000));
    if (total < 3600) return fmtClock(ms);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    return h + ' 小时 ' + m + ' 分';
  }

  // 刷新 / 消失：完全按东八区（上海）的现实时刻推演
  // 每 2 小时 15 分整点刷新一次 → 刷新后 3 分钟内可以打，没人打死就自己消失。
  function updateWorldBossHud() {
    if (!ui.btnWorldBoss) return;
    const alive = wbAlive();
    if (ui.btnWorldBoss.classList.contains('alive') !== alive) {
      ui.btnWorldBoss.classList.toggle('alive', alive);
    }
    ui.btnWorldBoss.title = alive
      ? '世界BOSS已刷新，快去挑战！'
      : '查看世界BOSS刷新时刻（上海时间 每 2 小时 15 分）';
  }

  function updateWorldBossCycle() {
    const def = openWorldBossDef();
    if (!def) return;
    // 标题 / 加载 / 阵亡界面不推进世界BOSS节奏（免得在标题页弹提示）
    if (gameState !== 'walk' && gameState !== 'battle') return;
    const now = wbNow();
    updateWorldBossHud();

    let spawnedAt;
    let windowEnd;
    if (wbOverride) {
      // 调试/测试：直接指定刷新窗口
      spawnedAt = wbOverride.spawnedAt;
      windowEnd = wbOverride.expiresAt;
    } else {
      spawnedAt = wbLastSpawnAt(now);
      windowEnd = spawnedAt + WB_WINDOW_SEC * 1000;
    }
    wb.nextAt = wbOverride ? wbOverride.expiresAt : wbNextSpawnAt(now);

    if (now < windowEnd) {
      // 这一轮已经被玩家打死过：本次窗口内不再刷新（等下一个刷新点）
      if (wb.killedAt && wb.killedAt === spawnedAt) {
        wb.phase = 'wait';
        wb.spawnedAt = spawnedAt;
        wb.expiresAt = windowEnd;
        return;
      }
      if (wb.spawnedAt !== spawnedAt || wb.phase !== 'alive') {
        wb.spawnedAt = spawnedAt;
        wb.expiresAt = windowEnd;
        wb.maxHp = def.hp;
        wb.hp = def.hp;
        wb.claimed = false;
        wb.killedAt = 0;
        pushBattleLog('世界BOSS「' + def.name + '」已在「' + def.place + '」刷新（上海时间 ' +
          shanghaiClockText(spawnedAt) + '）', 'system');
        showToast('世界BOSS「' + def.name + '」刷新了！3 分钟内没被击杀就会消失。');
        saveGame();
      }
      wb.phase = 'alive';
      wb.expiresAt = windowEnd;
      return;
    }

    // 窗口结束：没被击杀就自己消失
    if (wb.phase === 'alive') {
      wb.phase = 'wait';
      wb.hp = 0;
      pushBattleLog('世界BOSS「' + def.name + '」没被击杀，已经离开了', 'system');
      if (worldBossRun) {
        leaveWorldBoss('世界BOSS消失在风沙里，你被传送回公路。');
      } else {
        showToast('世界BOSS「' + def.name + '」没被击杀，已经消失了。');
      }
    }
    wb.phase = 'wait';
    wb.spawnedAt = spawnedAt;
    wb.expiresAt = windowEnd;
    wb.maxHp = def.hp;
  }

  // ---------- 世界BOSS 列表窗口 ----------
  function openWorldBossPanel() {
    syncInternetTime();     // 打开窗口时顺手校一次时（失败就用本机时钟）
    renderWorldBossPanel();
    toggleWorldBossHelp(false);
    ui.wbPopup.classList.remove('hidden');
  }
  function closeWorldBossPanel() {
    hideWorldBossTip();
    toggleWorldBossHelp(false);
    ui.wbPopup.classList.add('hidden');
  }

  // 右上角「?」帮助：规则说明不再占着窗口主区域
  function toggleWorldBossHelp(show) {
    if (!ui.wbHelp) return;
    const on = show === undefined ? ui.wbHelp.classList.contains('hidden') : !!show;
    ui.wbHelp.classList.toggle('hidden', !on);
    if (ui.wbHelpBtn) ui.wbHelpBtn.classList.toggle('on', on);
  }

  function renderWorldBossPanel() {
    if (!ui.wbList) return;
    // 注意：这里**不能**先 hideWorldBossTip()。
    // 这个窗口每秒重绘一次（刷新倒计时），重建行会把鼠标底下的元素换成新的，
    // 卡片被收起、紧接着 mouseenter 又把它弹回来 —— 看起来就是"每 1~2 秒闪一下"。
    // 现在的做法：重建后如果指针还停在同一行，就地重新贴一次位置（全程不隐藏，不闪）。
    const now = wbNow();
    const open = openWorldBossDef();
    const alive = wbAlive();
    ui.wbNote.textContent = open
      ? (alive
        ? '「' + open.name + '」正在「' + open.place + '」肆虐 · 剩余 ' + fmtClock(wb.expiresAt - now) + '（未被击杀就会消失）'
        : '「' + open.name + '」下次刷新还有 ' + fmtDuration(wb.nextAt - now) +
          '（上海时间 ' + shanghaiClockText(wb.nextAt).slice(0, 5) + '）· 刷新后可挑战 3 分钟')
      : '暂无开放的世界BOSS';
    ui.wbNote.style.color = alive ? '#ffb08a' : '';

    // 上海时间 / 时间源 / 今日奖励
    if (ui.wbClockNow) {
      ui.wbClockNow.textContent = shanghaiClockText(now) +
        '（' + shanghaiDayKey(now) + ' 周' + '日一二三四五六'[shanghaiParts(now).wd] + '）';
    }
    if (ui.wbClockSrc) {
      ui.wbClockSrc.textContent = wbClockSource === 'internet'
        ? '互联网时间（已校准）' : '本机时钟（东八区换算）';
    }
    if (ui.wbRewardState) {
      const canReward = wbRewardAvailable();
      ui.wbRewardState.textContent = canReward ? '未领取（本次可得）' : '今日已领取';
      ui.wbRewardState.className = canReward ? 'ready' : 'done';
    }
    // 当天 12 个刷新时刻
    if (ui.wbSchedule) {
      ui.wbSchedule.textContent = '';
      const times = wbTodayTimes(now);
      const next = wb.nextAt;
      const nextIsToday = shanghaiDayKey(next) === shanghaiDayKey(now);
      const nextClock = shanghaiClockText(next).slice(0, 5);
      times.forEach(function (t) {
        const chip = document.createElement('span');
        const p = shanghaiParts(t);
        const clock = pad2(p.h) + ':' + pad2(p.mi);
        const isTomorrowNext = !nextIsToday && clock === nextClock;
        let cls = 'wbTimeChip';
        if (alive && t === wb.spawnedAt) cls += ' live';
        else if (isTomorrowNext) cls += ' next tomorrow';
        else if (t === next) cls += ' next';
        else if (t < now) cls += ' passed';
        chip.className = cls;
        chip.textContent = clock;
        chip.title = shanghaiDayKey(t) + ' ' + clock + '（上海时间）' + (isTomorrowNext ? ' · 明天' : '');
        ui.wbSchedule.appendChild(chip);
      });
    }

    ui.wbList.textContent = '';
    const rowByDef = {};      // def.id -> 这一行的 DOM（重建后用来把资料图贴回去）
    WORLD_BOSSES.forEach(function (def) {
      const row = document.createElement('div');
      row.className = 'wbRow' + (def.open ? '' : ' locked');

      const lv = document.createElement('span');
      lv.className = 'wbLv';
      lv.textContent = stageText(def.level);

      const info = document.createElement('div');
      info.className = 'wbInfo';
      const name = document.createElement('div');
      name.className = 'wbName';
      name.textContent = def.name;
      const place = document.createElement('span');
      place.className = 'wbPlace';
      const myLv = worldBossPlayerLevel();
      // 刷新节奏在窗口头部和下面 12 个时刻里都写了，行里不再重复，免得每行挤成三行要滚动
      place.textContent = '地点：' + def.place +
        '　·　需要境界 ' + stageText(def.level) +
        (def.open ? '（你是 ' + stageText(myLv) +
          (myLv > playerLevel() ? '，含灵宝 +' + (myLv - playerLevel()) + ' 级' : '') + '）' : '');
      if (def.open && myLv < def.level) place.style.color = '#ff8a6a';
      info.appendChild(name);
      info.appendChild(place);

      const state = document.createElement('div');
      state.className = 'wbState' + (def.open && alive ? ' alive' : '');
      const stateMain = document.createElement('span');
      const count = document.createElement('span');
      count.className = 'wbCount';
      if (!def.open) {
        stateMain.textContent = '未开放';
        count.textContent = '后续版本开放';
      } else if (alive) {
        stateMain.textContent = '已刷新';
        count.textContent = '剩余 ' + fmtClock(wb.expiresAt - now);
      } else {
        stateMain.textContent = '刷新时间';
        count.textContent = shanghaiClockText(wb.nextAt).slice(0, 5) + '（还有 ' + fmtDuration(wb.nextAt - now) + '）';
      }
      state.appendChild(stateMain);
      state.appendChild(count);

      const go = document.createElement('button');
      go.type = 'button';
      go.className = 'wbGo';
      go.textContent = '传送';
      const levelOk = myLv >= def.level;      // 境界门槛：达标才允许传送
      go.disabled = !def.open || !alive || !levelOk;
      go.title = !def.open ? '该世界BOSS尚未开放'
        : (!alive ? '世界BOSS还没刷新'
          : (!levelOk ? '境界不足：需要 ' + stageText(def.level) + '，你现在是 ' + stageText(myLv)
            : '传送到「' + def.place + '」'));
      go.addEventListener('click', function () { teleportToWorldBoss(def); });

      row.appendChild(lv);
      row.appendChild(info);
      row.appendChild(state);
      row.appendChild(go);
      // 鼠标停在这一行 → 弹出该BOSS的资料图（没配 info 的BOSS不弹）
      if (def.info) {
        row.classList.add('hasInfo');
        row.setAttribute('data-boss-id', def.id);
        row.addEventListener('mouseenter', function () { showWorldBossTip(row, def); });
        row.addEventListener('mouseleave', syncWorldBossTip);
      }
      rowByDef[def.id] = row;
      ui.wbList.appendChild(row);
    });
    // 重绘后校正一次：指针还在同一行就把卡片贴回新行（不隐藏 → 不闪）
    if (wbTipBossId) syncWorldBossTip();
  }

  /* ---------- 世界BOSS 资料图（鼠标悬停在列表行上） ---------- */
  // defs.js 里资料图写的是相对路径（assets/img/boss/stos_info.png），
  // 这里统一补成绝对地址：挂第三方平台 iframe 时也不会去请求平台域名。
  function wbInfoUrl(def) {
    return (def && def.info) ? assetUrl(def.info) : null;
  }

  // 软加载：悬停时才显示，先预取一下避免第一次悬停空白（缺图也不影响游戏）
  function preloadWorldBossInfoImages() {
    WORLD_BOSSES.forEach(function (d) {
      if (!d.info) return;
      const img = new Image();
      img.src = wbInfoUrl(d);
    });
  }

  let wbTipBossId = null;     // 当前资料图对应的 BOSS id
  let wbTipSyncTimer = null;  // 延迟校正用的定时器

  function hideWorldBossTip() {
    wbTipBossId = null;
    if (ui.wbBossTip) ui.wbBossTip.classList.add('hidden');
  }

  // 指针现在停在哪个 BOSS 行上（面板重绘后也能查出来）
  function wbHoveredRow() {
    return document.querySelector('#wbList .wbRow.hasInfo:hover');
  }

  function bossDefById(id) {
    return WORLD_BOSSES.filter(function (d) { return d.id === id; })[0] || null;
  }

  /* 面板每秒重绘一次倒计时：重建时**旧行被移除也会触发 mouseleave**，
     如果直接在 mouseleave 里关卡片，就会出现"每 1~2 秒闪一下"。
     所以这里不立刻关，而是延迟一帧按"指针实际还停在哪一行"来校正：
     仍停在某行上 → 就地贴回去（全程不隐藏，不闪）；确实移开了 → 才关。 */
  function syncWorldBossTip() {
    if (wbTipSyncTimer) clearTimeout(wbTipSyncTimer);
    wbTipSyncTimer = setTimeout(function () {
      wbTipSyncTimer = null;
      if (!ui.wbPopup || ui.wbPopup.classList.contains('hidden')) { hideWorldBossTip(); return; }
      const row = wbHoveredRow();
      if (!row) { hideWorldBossTip(); return; }
      const def = bossDefById(row.getAttribute('data-boss-id'));
      if (def && def.info) showWorldBossTip(row, def);
      else hideWorldBossTip();
    }, 40);
  }

  function showWorldBossTip(row, def) {
    const tip = ui.wbBossTip;
    if (!tip || !def || !def.info) return;
    wbTipBossId = def.id;
    if (ui.wbTipName) ui.wbTipName.textContent = def.name;
    if (ui.wbTipPlace) ui.wbTipPlace.textContent = stageText(def.level) + ' · ' + def.place;
    if (ui.wbTipDetail) {
      ui.wbTipDetail.textContent = def.infoDetail || '';
      ui.wbTipDetail.classList.toggle('hidden', !def.infoDetail);
    }
    const infoUrl = wbInfoUrl(def);
    if (ui.wbTipImg && infoUrl && ui.wbTipImg.getAttribute('src') !== infoUrl) ui.wbTipImg.src = infoUrl;

    // 先显示再量尺寸，位置以 #wrap 为基准（跟图鉴/装备的悬停提示同一套算法）
    tip.classList.remove('hidden');
    const wrapRect = (ui.wrap || document.body).getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    // 默认贴在鼠标那一行的右侧；右边放不下就放左侧
    let left = rowRect.right - wrapRect.left + 10;
    if (left + tw > wrapRect.width - 6) left = rowRect.left - wrapRect.left - tw - 10;
    let top = rowRect.top - wrapRect.top + rowRect.height / 2 - th / 2;
    // 放大后的卡片很宽，两边都塞不下 → 改成画面居中、贴在鼠标那一行的上方（放不下就放下方），
    // 这样尽量不压住正在看的那一行
    if (left < 6 || left + tw > wrapRect.width - 6) {
      left = (wrapRect.width - tw) / 2;
      top = rowRect.top - wrapRect.top - th - 12;
      if (top < 6) top = rowRect.bottom - wrapRect.top + 12;
    }
    left = Math.max(6, Math.min(left, wrapRect.width - tw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - th - 6));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
  }

  // 窗口开着的时候每秒刷新倒计时
  let wbPanelClock = 0;
  function updateWorldBossPanelClock(dt) {
    if (!ui.wbPopup || ui.wbPopup.classList.contains('hidden')) return;
    wbPanelClock += dt;
    if (wbPanelClock < 1) return;
    wbPanelClock = 0;
    renderWorldBossPanel();
  }

  // ---------- 传送 / 离开 ----------
  function teleportToWorldBoss(def) {
    if (!def || !def.open) {
      showToast('该世界BOSS尚未开放。');
      return false;
    }
    if (!(arguments.length > 1 && arguments[1] && arguments[1].force)) {
      // 境界不够就不让进（以前能进去但全是 MISS，现在直接拦在门口）
      const myLv = worldBossPlayerLevel();
      if (myLv < def.level) {
        showToast('境界不足：需要 ' + stageText(def.level) + '（你是 ' + stageText(myLv) +
          '），境界达标后才能传送过去。');
        renderWorldBossPanel();
        return false;
      }
    }
    if (gameState !== 'walk') {
      showToast('战斗中（或正在结算）无法传送，先回到路上。');
      return false;
    }
    if (cave) {
      showToast('洞府内无法传送，先离开洞府。');
      return false;
    }
    if (!wbAlive()) {
      showToast('世界BOSS还没刷新（或者已经消失）。');
      renderWorldBossPanel();
      return false;
    }
    if (worldBossRun) return false;

    closeWorldBossPanel();
    if (autoAdventure) stopAutoAdventure('已进入世界BOSS战场，自动冒险暂停。');

    const level = def.level;   // 世界BOSS 境界固定（森特欧斯萨满 = 武者四境·初期，对应 10 级）
    const boss = buildEnemy(level, 'legend', { worldX: 0 });
    boss.sprite = def.sprite;
    boss.kindName = def.name;
    boss.name = def.name;
    boss.tier = 'legend';
    boss.elite = true;
    boss.legend = true;
    boss.skill = null;          // 世界BOSS不玩精英技能（避免治疗干扰血量统计）
    boss.skills = [];
    boss.wbScale = def.scale;   // 体型放大
    boss.wbBossArt = true;      // 用专属立绘绘制
    boss.maxHp = wb.maxHp;      // 全服共享血量：本轮刷新后所有人打的是同一管血
    boss.hp = wb.hp > 0 ? wb.hp : wb.maxHp;

    const returnX = player.worldX;
    player.worldX = WB_ARENA_X;
    player.vx = 0;
    boss.x = WB_ARENA_X + (WB_BOSS_SCREEN_X - START_X);
    boss.baseX = boss.x;
    activeEnemy = boss;
    battleGroup.length = 0;
    battleGroup.push(boss);

    worldBossRun = {
      def: def,
      boss: boss,
      level: level,
      returnX: returnX,
      claimed: false,
      down: false,
      playerDamage: 0,
      ghostDamage: 0,
      tick: 0,
      ghosts: []
    };
    spawnWorldBossGhosts();
    initWorldBossDamageBoard();     // BOSS 出现：本轮伤害榜建档（所有人从 0 开始）

    gameState = 'battle';
    input.moving = false;
    player.vx = 0;
    battleTurn = 'player';
    enemyTurnTimer = 0;
    playerIdleTimer = 10;
    activeEnemy.openingAttack = false;
    pendingVictory = 0;
    showBattleUi(true);
    setControls('battle');
    syncMoveButton();
    showWorldBossBar(true);
    updateWorldBossBar();
    setBattleLog('你被传送到「' + def.place + '」，' + def.name + ' 正在肆虐！');
    pushBattleLog('传送至世界BOSS战场：' + def.name + '（' + stageText(level) + '）', 'system');
    if (worldBossPlayerLevel() < level) {
      showToast('境界不足：需要 ' + stageText(level) + '，你当前是 ' + stageText(worldBossPlayerLevel()) +
        '，打上去全是 MISS（找一件带「破境符诏」的灵宝可以临时 +5 级）。');
      pushBattleLog('境界不足（' + stageText(worldBossPlayerLevel()) + ' < ' + stageText(level) +
        '）：对世界BOSS的攻击会全部 MISS', 'system');
    }
    saveGame();
    return true;
  }

  function leaveWorldBoss(message) {
    if (!worldBossRun) return false;
    const run = worldBossRun;
    // 玩家是否对BOSS造成过伤害（虚影打掉的不算）
    const playerDamage = run.boss
      ? Math.max(0, Math.round(run.boss.maxHp - run.ghostDamage - Math.max(0, run.boss.hp)))
      : 0;
    if (run.boss && run.boss.hp > 0) wb.hp = Math.max(0, Math.round(run.boss.hp));
    worldBossRun = null;
    activeEnemy = null;
    battleGroup.length = 0;
    pendingVictory = 0;
    damagePopups = [];
    pets.forEach(function (p) { p.attackT = 0; });
    player.worldX = run.returnX;
    player.vx = 0;
    combat.hp = Math.max(1, combat.hp);   // 被踢出战场不会直接死
    gameState = 'walk';
    showBattleUi(false);
    showWorldBossBar(false);
    setControls('walk');
    syncMoveButton();
    spawnNextEnemy();
    updateDist();
    updateBattleHud();
    saveGame();
    if (message) showToast(message);
    // 「首次挑战奖励」在离开地图时发放：屏幕中央三个宝箱（每天只给一次）
    if (playerDamage > 0) {
      if (run.down) {
        // BOSS 打赢了：奖励本该在地图的宝箱里；万一是没开箱就离开（撤离 / 倒下），这里补发
        if (!run.loot || !run.loot.granted) grantWorldBossRewardChests('离开战场时补发');
      } else {
        // 参与过但没打赢（含中途倒下被传送出场）：直接给三个箱子
        grantWorldBossRewardChests('参与了世界BOSS战斗');
      }
    }
    // 本次挑战结束（击杀 / 撤离 / 到期都走这里）：清空所有人的伤害记录
    clearWorldBossDamageBoard();
    return true;
  }

  // 击杀结算：参与奖励在"第一次造成伤害"时就已经发了，这里只做收尾
  function finishWorldBossFight() {
    const run = worldBossRun;
    if (!run) return;
    const def = run.def;
    const level = run.level || def.level;
    wb.phase = 'wait';
    wb.nextAt = wbOverride ? wbOverride.expiresAt : wbNextSpawnAt(wbNow());
    wb.hp = 0;
    wb.killed = (wb.killed || 0) + 1;
    wb.killedAt = wb.spawnedAt || Date.now();   // 本轮已被击杀，窗口内不再刷新
    defeatedEnemies++;
    gainXp(10 * Math.max(1, level));
    addFlaskKill(true);     // 世界BOSS算作精英级击杀 → 血瓶 +1 充能
    pushBattleLog('世界BOSS「' + def.name + '」被击杀！下次刷新 ' +
      shanghaiClockText(wb.nextAt).slice(0, 5) + '（上海时间，还有 ' + fmtClock(wb.nextAt - wbNow()) + '）', 'reward');
    showToast('世界BOSS「' + def.name + '」被击败！下次刷新还有 ' + fmtClock(wb.nextAt - wbNow()) + '。');
    // 伤害榜第一名：必得 1 个天地灵物（下面这个 0.01% 是"参与奖"，两者独立）
    awardWorldBossTopDamage();
    // 挑战到此结束（伤害榜已经结算完）：清空本轮所有人的伤害记录
    clearWorldBossDamageBoard();
    // 击杀后不立刻送出场：在战场中央放一个宝箱，给玩家 60 秒走过去亲手开启
    startWorldBossLootPhase(run);
  }

  /* ============================================================
   * 击杀后的「战场开箱」阶段
   *   · BOSS 倒下 → 屏幕正中央出现宝箱（美术 wb_chest_big.png）
   *   · 同时在地上散落几件战利品展示（只是给玩家看的，不能拾取）
   *   · 玩家可以走动，走到宝箱前点击开箱；60 秒倒计时结束会自动开启
   *   · 开箱拿的就是「世界BOSS挑战奖励」三个宝箱（每天一次）
   * ============================================================ */
  function worldBossLootActive() {
    return !!(worldBossRun && worldBossRun.loot && !worldBossRun.loot.done);
  }

  function worldBossLootChestX() {
    return WB_ARENA_X + (WB_LOOT_CHEST_SCREEN_X - START_X);
  }

  function startWorldBossLootPhase(run) {
    if (!run) return;
    run.loot = {
      x: worldBossLootChestX(),
      timeLeft: WB_LOOT_SECONDS,
      done: false,        // 箱子已经开过了
      granted: false,     // 奖励已经发出去了
      auto: false         // 是不是倒计时到点自动开的
    };
    // 地上散落的战利品展示（纯装饰，不可拾取）
    run.gifts = [
      // 位置以 BOSS 脚下为参照，整体偏右，避开场地中央那个宝箱
      { key: 'wbGift1', dx: -108, dy: 2, w: 74 },
      { key: 'wbGift2', dx: -34, dy: 10, w: 58 },
      { key: 'wbGift3', dx: 62, dy: -2, w: 72 },
      { key: 'wbGift4', dx: 150, dy: 8, w: 66 }
    ].map(function (g, i) {
      return { key: g.key, x: run.boss.x + g.dx, dy: g.dy, w: g.w, t: i * 0.7 };
    });
    battleTurn = 'player';
    pendingVictory = 0;
    enemyTurnTimer = 0;
    input.moving = false;
    player.vx = 0;
    setControls('walk');       // 允许走动去开箱
    syncMoveButton();
    if (ui.wbBarTime) ui.wbBarTime.textContent = '宝箱 ' + WB_LOOT_SECONDS + 's 后自动开启';
    setBattleLog('「' + run.def.name + '」倒下了！走到场地中央的宝箱前开启战利品（' +
      WB_LOOT_SECONDS + ' 秒后自动开启）。');
    pushBattleLog('世界BOSS已击败：中央出现战利品宝箱，' + WB_LOOT_SECONDS +
      ' 秒内走过去点击开启（倒计时结束会自动开启）', 'system');
    showToast('世界BOSS被击败！走到场地中央的宝箱前开箱（' + WB_LOOT_SECONDS + ' 秒后自动开启）');
  }

  // 每帧推进：倒计时 + 允许在战场里走动
  function updateWorldBossLoot(dt) {
    const run = worldBossRun;
    if (!run || !run.loot) return;
    const loot = run.loot;
    if (!loot.done) {
      loot.timeLeft = Math.max(0, loot.timeLeft - dt);
      if (ui.wbBarTime) {
        ui.wbBarTime.textContent = '宝箱 ' + Math.ceil(loot.timeLeft) + 's 后自动开启';
      }
      // 走动：只在战场这一段范围内活动
      const target = input.moving ? WALK_SPEED * derived.speedMult : 0;
      const ease = input.moving ? 8 : 14;
      player.vx += (target - player.vx) * Math.min(1, dt * ease);
      if (Math.abs(player.vx) < 1) player.vx = 0;
      if (player.vx > 0) {
        player.worldX = Math.min(loot.x + 70, player.worldX + player.vx * dt);
      } else if (player.vx < 0) {
        player.worldX = Math.max(WB_ARENA_X - 420, player.worldX + player.vx * dt);
      }
      player.walkT = player.vx > 0 ? player.walkT + dt * (8 + player.vx / 50) : 0;
      if (loot.timeLeft <= 0) openWorldBossLootChest(true);
    }
    run.gifts.forEach(function (g) { g.t += dt; });
  }

  // 开箱：亲手点开 or 倒计时到点自动开
  function openWorldBossLootChest(auto) {
    const run = worldBossRun;
    if (!run || !run.loot || run.loot.done) return false;
    const loot = run.loot;
    loot.done = true;
    loot.auto = !!auto;
    input.moving = false;
    player.vx = 0;
    syncMoveButton();
    if (ui.wbBarTime) ui.wbBarTime.textContent = '宝箱已开启';
    pushBattleLog(auto ? '倒计时结束：宝箱自动开启' : '你走到宝箱前，亲手开启了战利品宝箱', 'reward');
    // ★ 极低几率的额外彩蛋：只有"击杀BOSS后留在战场开箱"的人才有这个机会
    rollSpiritItemFromLootChest(run);
    if (!wbRewardAvailable()) {
      showToast('今日世界BOSS奖励已经领取过了（每日 0 点重置，一天只能领一次）。');
      pushBattleLog('今日世界BOSS奖励已领取过，这个宝箱里没有新的奖励', 'system');
      return false;
    }
    loot.granted = true;
    showWorldBossRewardChests(true);
    return true;
  }

  // 把奖励三个宝箱发出去（离开战场时的补发也走这里）
  function grantWorldBossRewardChests(reason) {
    if (!wbRewardAvailable()) {
      showToast('今日世界BOSS奖励已经领取过了（每日 0 点重置，一天只能领一次）。');
      pushBattleLog('今日世界BOSS奖励已领取过，本次不再发放宝箱', 'system');
      return false;
    }
    showWorldBossRewardChests(true);
    if (reason) pushBattleLog('世界BOSS奖励已发放（' + reason + '）：三个宝箱在屏幕中央，点开看看', 'reward');
    return true;
  }

  /* ---------- 开箱彩蛋：天地灵物 ----------
   * 只有"击杀BOSS后留在战场、亲手/到点开启中央宝箱"的人才有这个机会，几率 0.01%（万分之一）。
   * 中途被打倒传送出场的人走的是另一条路（grantWorldBossRewardChests），永远拿不到。 */
  const WB_LOOT_SPIRIT_PCT = 0.01;
  function rollSpiritItemFromLootChest(run) {
    if (!run || !(run.playerDamage > 0)) return null;              // 连BOSS都没打到：没有
    if (player.level > TRIBULATION_STAGE || hasSpiritItem()) return null;  // 已经有 / 用不上了
    const forced = spiritRollForced;
    spiritRollForced = false;
    if (!forced && Math.random() * 100 >= WB_LOOT_SPIRIT_PCT) return null;
    const item = makeSpiritItem();
    inventory.push(item);
    showToast('★★★ 宝箱底部还压着一件「天地灵物」！');
    pushBattleLog('★★★ 战场中央的宝箱额外开出「天地灵物」×1（留场开箱 · 0.01%）', 'reward');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    return item;
  }

  /* ============================================================
   * 世界BOSS 伤害榜（每次BOSS出现时统计所有人）
   *   · 建档：BOSS 出现（传送进战场）时，本地玩家 + 所有虚影各一条记录
   *   · 统计：虚影每次出手累加自己那份；玩家那份取"BOSS 掉血 - 虚影贡献"
   *   · 结算：BOSS 被击杀时取第一名 → 是玩家自己就必得 1 个天地灵物
   *   · 清空：本次挑战结束（击杀 / 撤离 / 到期）时整体清掉，下一轮从零开始
   * ============================================================ */
  let wbDamageBoard = null;      // { round, startedAt, entries: [{ name, isPlayer, damage, ghost }] }
  let wbDamageRound = 0;

  function initWorldBossDamageBoard() {
    const run = worldBossRun;
    if (!run) { wbDamageBoard = null; return null; }
    wbDamageRound += 1;
    const entries = (run.ghosts || []).map(function (g) {
      return { name: g.name, isPlayer: false, damage: 0, ghost: g };
    });
    entries.push({ name: chatNick(), isPlayer: true, damage: 0, ghost: null });
    wbDamageBoard = { round: wbDamageRound, startedAt: Date.now(), entries: entries };
    return wbDamageBoard;
  }

  function clearWorldBossDamageBoard() {
    if (wbDamageBoard) {
      wbDamageBoard.entries.forEach(function (e) { e.damage = 0; e.ghost = null; });
    }
    wbDamageBoard = null;      // 记录整体丢弃：下一轮重新建档
  }

  // 按当前数据刷新一遍伤害并排序；并列时玩家优先（自己并列第一就算第一）
  function worldBossDamageRanking() {
    const run = worldBossRun;
    const board = wbDamageBoard;
    if (!run || !board) return [];
    board.entries.forEach(function (e) {
      e.damage = e.isPlayer
        ? Math.round(worldBossPlayerDealt(run))
        : Math.round((e.ghost && e.ghost.damage) || 0);
    });
    return board.entries.slice().sort(function (a, b) {
      if (b.damage !== a.damage) return b.damage - a.damage;
      return (b.isPlayer ? 1 : 0) - (a.isPlayer ? 1 : 0);
    });
  }

  // BOSS 被击杀：取伤害第一名，是玩家就必掉 1 个天地灵物
  function awardWorldBossTopDamage() {
    const ranking = worldBossDamageRanking();
    if (!ranking.length) return null;
    const top = ranking[0];
    const topLines = ranking.slice(0, 3).map(function (e, i) {
      return '第' + (i + 1) + '名 ' + e.name + '（' + e.damage + '）';
    }).join('　');
    pushBattleLog('本轮伤害榜：' + topLines + (ranking.length > 3 ? '　…共 ' + ranking.length + ' 人' : ''), 'system');
    if (!top.isPlayer) {
      pushBattleLog('伤害第一是「' + top.name + '」（' + top.damage + '），本轮天地灵物归他。', 'system');
      return null;
    }
    // 已经蜕凡入仙 / 身上已经有一颗：拿了也用不上
    if (player.level > TRIBULATION_STAGE || hasSpiritItem()) {
      pushBattleLog('你以 ' + top.damage + ' 伤害拿下第一，但当前已经不需要「天地灵物」了。', 'system');
      return null;
    }
    const item = makeSpiritItem();
    inventory.push(item);
    showToast('★★★ 伤害榜第一（' + top.damage + '）！获得「天地灵物」×1');
    pushBattleLog('★ 伤害榜第一（' + top.damage + ' 伤害）：获得「天地灵物」×1', 'reward');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    return item;
  }

  /* ---------- 天地灵物（世界BOSS限定掉落） ----------
   * 现在的来源只有两个，且都绑定在"世界BOSS这一轮"里：
   *   ① 本轮伤害榜第一名 → 必得 1 个
   *   ② 击杀BOSS后留在战场开箱 → 极低几率（0.01%）额外开出
   * 中途被打倒传送出场的人两条都拿不到（见 grantWorldBossRewardChests 那条路）。
   * 不再通过活力兑换 / 精英 / 传奇敌人掉落获得。 */
  const SPIRIT_ITEM_DROP_PCT = 0.01;   // 与开箱彩蛋同一个数量级（保留给调试/展示用）
  let spiritRollForced = false;    // 测试钩子：强制必出

  // 这一场世界BOSS里玩家自己打掉的伤害（虚影打的不算）
  function worldBossPlayerDealt(run) {
    if (!run) return 0;
    if (run.playerDamage > 0) return run.playerDamage;
    if (!run.boss) return 0;
    // 收尾那一帧 playerDamage 可能还没刷新，这里按"BOSS 掉的血 - 虚影的贡献"补一次
    return Math.max(0, run.boss.maxHp - run.ghostDamage - Math.max(0, run.boss.hp));
  }

  // ---------- 参与奖励 ----------
  function makeWorldBossChest() {
    return {
      id: 'wbchest' + (++itemSeq),
      type: 'worldChest',
      name: '世界BOSS宝箱',
      icon: '匣',
      detail: '打开可获得金币 / 材料 / 装备（极小几率开出灵宝）'
    };
  }

  // 今天还能不能领世界BOSS奖励（每天一次，按上海日期算）
  function wbRewardAvailable() {
    return wb.lastRewardDay !== shanghaiDayKey(wbNow());
  }

  // ---------- 首次挑战奖励：离开世界BOSS地图时，屏幕中央出三个宝箱 ----------
  // ① 金币箱 ② 当前境界的黄色稀有装备 ③ 世界BOSS宝箱（极低几率灵宝 / 主动技能石 / 被动技能石 / 1 点活力）
  const WB_CHEST_TREASURE_PCT = 0.5;   // 「非常非常低」的灵宝几率
  let wbRewardOpening = null;          // 正在开的三个宝箱

  /* 世界BOSS宝箱掉落（详细版）
   *   返回 { text, name, desc, color, tier, item, icon }
   *   text 给战报/老逻辑用；其余给新的开箱界面用（稀有度配色 + 真实物品图标） */
  function rollWorldBossChestLootDetailed(kind) {
    if (kind === 'gold') {
      gold += WB_REWARD_GOLD;
      updateGold();
      return {
        text: '金币 +' + WB_REWARD_GOLD,
        name: '金币', desc: WB_REWARD_GOLD + ' 枚金币已收入行囊',
        color: '#ffd76a', tier: 'gold', icon: '金'
      };
    }
    if (kind === 'gear') {
      const gear = makeEquipment(playerLevel(), 'rare');
      inventory.push(gear);
      // 词缀太长不往槽位里塞，只留前两条（完整属性看装备栏）
      const parts = String(gear.detail || '').split('　').filter(Boolean);
      const brief = parts.slice(0, 2).join('　') + (parts.length > 2 ? '　…' : '');
      return {
        text: '黄色稀有「' + gear.name + '」',
        name: gear.name,
        desc: (brief || '黄色稀有装备') + '　·　等级 ' + gear.level,
        color: (RARITY_DEFS[gear.rarity] || RARITY_DEFS.rare).color,
        tier: gear.rarity || 'rare', item: gear, icon: '装'
      };
    }
    // 世界BOSS宝箱：按几率表随机
    const r = Math.random() * 100;
    if (r < WB_CHEST_TREASURE_PCT) {
      const t = makeTreasureItem(playerLevel() + 2);
      inventory.push(t);
      return {
        text: '★ 灵宝「' + t.name + '」',
        name: t.name,
        desc: '灵宝　·　' + String(t.detail || '附带特殊属性').split('：')[0].split('　')[0],
        color: RARITY_DEFS.legend.color, tier: 'legend', item: t, icon: '灵'
      };
    }
    if (r < 12) {
      const s = makeActiveSkillStone();
      inventory.push(s);
      return {
        text: '主动技能石 ×1（可学 / 升级主动技能）',
        name: '主动技能石',
        desc: '使用后可随机习得一个主动技能，或给已有主动技能升 1 级',
        color: s.rarityColor || '#ffd265', tier: 'rare', item: s, icon: '技'
      };
    }
    if (r < 37) {
      const g = gemOptionToItem(GEM_OPTIONS[randInt(0, GEM_OPTIONS.length - 1)]);
      inventory.push(g);
      return {
        text: '被动技能石「' + (g.detail || g.name) + '」',
        name: '被动技能石',
        desc: (g.detail || g.name) + '　·　镶嵌到主动技能后生效',
        color: g.rarityColor || '#7dffa8', tier: 'magic', item: g, icon: '石'
      };
    }
    if (r < 67) {
      vitality += 1;
      updateVitalityUI();
      return {
        text: '活力 +1（当前 ' + vitality + '）',
        name: '活力 +1', desc: '当前活力 ' + vitality + '（每 1 点可自动冒险 10km）',
        color: '#8fffd0', tier: 'magic', icon: '活'
      };
    }
    const g2 = randInt(300, 1200);
    gold += g2;
    updateGold();
    return {
      text: '金币 +' + g2,
      name: '金币 +' + g2, desc: '世界BOSS的战利品，已收入行囊',
      color: '#ffd76a', tier: 'gold', icon: '金'
    };
  }

  // 老接口：只要一行文字（战报 / 自动化测试用）
  function rollWorldBossChestLoot(kind) {
    return rollWorldBossChestLootDetailed(kind).text;
  }

  function renderWorldBossRewardChests() {
    if (!ui.wbRewardChests || !wbRewardOpening) return;
    ui.wbRewardChests.textContent = '';
    wbRewardOpening.chests.forEach(function (c) {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'wbChest' + (c.opened ? ' opened' : '') + (c.burst ? ' burst' : '');
      if (c.opened && c.color) card.style.setProperty('--lc', c.color);

      // 掉落光柱：开启后按稀有度升起（越稀有越亮，一眼看出值不值）
      const beam = document.createElement('div');
      beam.className = 'wbChestBeam';

      // 方形槽位：没开时里面是 CSS 画的宝箱，开了就换成战利品图标
      const box = document.createElement('div');
      box.className = 'wbChestBox';
      if (c.opened) {
        const itemEl = document.createElement('div');
        itemEl.className = 'wbChestItem';
        fillItemIcon(itemEl, c.item, c.icon);
        box.appendChild(itemEl);
      } else {
        const icon = document.createElement('div');
        icon.className = 'wbChestIcon';
        // 箱子外观用美术资源（Boss_box.png）
        const img = document.createElement('img');
        img.className = 'wbChestImg';
        img.alt = '';
        img.src = assetUrl('assets/img/boss/wb_chest_box.png');
        icon.appendChild(img);
        box.appendChild(icon);
      }

      const tag = document.createElement('div');
      tag.className = 'wbChestTag';
      tag.textContent = c.opened ? '已开启' : '未开启';

      const name = document.createElement('div');
      name.className = 'wbChestName';
      name.textContent = c.opened ? (c.name || c.label) : '？？？';

      const divider = document.createElement('div');
      divider.className = 'wbChestDivider';

      const loot = document.createElement('div');
      loot.className = 'wbChestLoot';
      loot.textContent = c.opened ? (c.desc || c.text) : '点击开启';

      card.appendChild(beam);
      card.appendChild(tag);
      card.appendChild(box);
      card.appendChild(name);
      card.appendChild(divider);
      card.appendChild(loot);
      card.addEventListener('click', function () { openWorldBossRewardChest(c); });
      ui.wbRewardChests.appendChild(card);
    });
    const left = wbRewardOpening.chests.length - wbRewardOpening.opened;
    ui.wbRewardClose.disabled = left > 0;
    ui.wbRewardClose.textContent = left > 0 ? ('还有 ' + left + ' 个宝箱没打开') : '收下全部奖励';
    if (ui.wbRewardProgressText) {
      ui.wbRewardProgressText.textContent = '已开启 ' + wbRewardOpening.opened + ' / ' + wbRewardOpening.chests.length;
    }
    if (ui.wbRewardDots) {
      ui.wbRewardDots.textContent = '';
      wbRewardOpening.chests.forEach(function (c) {
        const dot = document.createElement('i');
        dot.className = c.opened ? 'on' : '';
        ui.wbRewardDots.appendChild(dot);
      });
    }
    if (ui.wbRewardNote) {
      ui.wbRewardNote.textContent = left > 0
        ? '离开世界BOSS战场后开启战利品 · 三个宝箱都要打开'
        : '全部开启完毕，点下方按钮收下并关闭';
    }
  }

  function openWorldBossRewardChest(c) {
    if (!c || c.opened || !wbRewardOpening) return;
    const reward = rollWorldBossChestLootDetailed(c.kind);
    c.opened = true;
    c.text = reward.text;
    c.name = reward.name;
    c.desc = reward.desc;
    c.color = reward.color;
    c.item = reward.item || null;
    if (reward.icon) c.icon = reward.icon;
    // 只有刚点开的这个箱子弹一次开箱光效，已经开过的不要跟着重放
    wbRewardOpening.chests.forEach(function (o) { o.burst = false; });
    c.burst = true;
    wbRewardOpening.opened += 1;
    pushBattleLog('世界BOSS挑战奖励 · ' + c.label + '：' + c.text, 'reward');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    renderWorldBossRewardChests();
  }

  // 屏幕中央弹出三个宝箱（顺序随机摆放）
  function showWorldBossRewardChests(dealtDamage) {
    const chests = [
      { kind: 'gold', label: '金币箱', icon: '金', opened: false, text: '', name: '', desc: '', color: '', item: null, burst: false },
      { kind: 'gear', label: '黄色稀有装备箱', icon: '装', opened: false, text: '', name: '', desc: '', color: '', item: null, burst: false },
      { kind: 'wbchest', label: '世界BOSS宝箱', icon: '匣', opened: false, text: '', name: '', desc: '', color: '', item: null, burst: false }
    ];
    for (let i = chests.length - 1; i > 0; i--) {
      const j = randInt(0, i);
      const t = chests[i]; chests[i] = chests[j]; chests[j] = t;
    }
    wbRewardOpening = {
      chests: chests, opened: 0,
      dealtDamage: dealtDamage !== false   // 默认视为造成过伤害（圣物掉落的前提）
    };
    renderWorldBossRewardChests();
    ui.wbRewardPopup.classList.remove('hidden');
    showToast('世界BOSS首次挑战奖励：屏幕中央出现三个宝箱，点开看看！');
  }

  function closeWorldBossRewardChests() {
    if (!wbRewardOpening) return;
    if (wbRewardOpening.opened < wbRewardOpening.chests.length) return;   // 必须全部打开
    const dealtDamage = !!wbRewardOpening.dealtDamage;
    wbRewardOpening = null;
    wb.lastRewardDay = shanghaiDayKey(wbNow());   // 每日一次：记下今天已领
    ui.wbRewardPopup.classList.add('hidden');
    pushBattleLog('世界BOSS首次挑战奖励已领取（今日不再发放，每日 0 点重置）', 'reward');
    showToast('世界BOSS挑战奖励已收下（今日奖励已领取）。');
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    // 森特欧斯独特圣物：前提是"对BOSS造成过伤害"，掉落率 0.001%
    if (dealtDamage) rollSantosRelic();
  }

  // 森特欧斯圣物掉落判定：0.001%（十万分之一）出其中一件，已有的一件不会重复给
  let santosRollForced = false;
  function rollSantosRelic() {
    if (!santosRollForced && Math.random() * 100 >= SANTOS_RELIC_CHANCE) return null;
    santosRollForced = false;
    const pool = ['skull', 'totem'].filter(function (k) { return !hasSantosRelic(k); });
    if (!pool.length) return null;
    const key = pool[randInt(0, pool.length - 1)];
    const item = makeSantosRelic(key);
    inventory.push(item);
    recalcPlayerStats();
    showToast('★★★ 森特欧斯的圣物：「' + item.name + '」！（' + item.detail + '）');
    pushBattleLog('★ 获得森特欧斯圣物「' + item.name + '」· ' + item.detail, 'reward');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    return item;
  }
  // 开世界BOSS宝箱：金币 + 材料 + 装备，极小几率开出灵宝
  function openWorldBossChest(item) {
    if (!item) return;
    inventory = inventory.filter(function (it) { return it.id !== item.id; });
    // 和老版本开出来的「世界BOSS宝箱」共用同一张掉落表
    const text = rollWorldBossChestLoot('wbchest');
    const lines = [text];
    pushBattleLog('开启世界BOSS宝箱：' + text, 'reward');
    showToast(text.indexOf('灵宝') >= 0 ? ('★ 世界BOSS宝箱开出了' + text + '！') : ('世界BOSS宝箱：' + text));
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    saveGame();
    return lines;
  }

  // ---------- 世界BOSS战斗条 ----------
  function showWorldBossBar(show) {
    if (ui.wbBar) ui.wbBar.classList.toggle('hidden', !show);
  }
  function updateWorldBossBar() {
    if (!ui.wbBar || !worldBossRun) return;
    const run = worldBossRun;
    const boss = run.boss;
    const pct = clamp(boss.hp / Math.max(1, boss.maxHp), 0, 1) * 100;
    ui.wbHpFill.style.width = pct.toFixed(1) + '%';
    ui.wbBarName.textContent = run.def.name;
    ui.wbBarLv.textContent = stageText(boss.level) + ' · ' + run.def.place;
    if (ui.wbBarReq) {
      const ok2 = worldBossLevelOk();
      ui.wbBarReq.textContent = ok2
        ? '境界达标（' + stageText(worldBossPlayerLevel()) + '）'
        : '需要 ' + stageText(boss.level) + '（你是 ' + stageText(worldBossPlayerLevel()) + '，攻击 MISS）';
      ui.wbBarReq.className = ok2 ? 'wbReqOk' : 'wbReqBad';
    }
    ui.wbHpText.textContent = Math.max(0, Math.ceil(boss.hp)) + ' / ' + boss.maxHp;
    ui.wbBarTime.textContent = '剩余 ' + fmtClock(run.endsAt - wbNow());
  }

  // ---------- 虚影（模拟其他玩家一起打BOSS）----------
  function spawnWorldBossGhosts() {
    if (!worldBossRun) return;
    const list = [];
    const names = WB_GHOST_NAMES.slice();
    for (let i = 0; i < WB_GHOST_COUNT; i++) {
      const usePlayer = Math.random() < 0.45;
      const sprite = ENEMY_SPRITES[randInt(0, ENEMY_SPRITES.length - 1)].key;
      list.push({
        isPlayer: usePlayer,
        sprite: sprite,
        name: names.splice(randInt(0, names.length - 1), 1)[0] || '无名侠客',
        // 挑战者都围在 BOSS 脚下（山丘近侧），不再散得太开
        offset: rand(-360, -60),
        offY: rand(-26, -6),
        phase: rand(0, 10),
        scale: rand(0.82, 1.02),
        attackT: 0,
        damage: 0,          // 本轮该虚影对BOSS造成的伤害（伤害榜用）
        hitTimer: rand(0.4, 2.2),
        interval: rand(1.7, 2.6),
        dmgMin: 24,
        dmgMax: 60
      });
    }
    worldBossRun.ghosts = list;
  }

  function updateWorldBossGhosts(dt) {
    const run = worldBossRun;
    if (!run) return;
    const boss = run.boss;
    run.tick += dt;
    run.ghosts.forEach(function (g) {
      g.phase += dt;
      if (g.attackT > 0) g.attackT = Math.max(0, g.attackT - dt);
      g.hitTimer -= dt;
      if (g.hitTimer > 0) return;
      g.hitTimer = g.interval * rand(0.8, 1.25);
      g.attackT = 0.34;
      if (boss.hp <= 0) return;
      const dmg = randInt(g.dmgMin, g.dmgMax);
      boss.hp = Math.max(0, boss.hp - dmg);
      boss.hurtT = Math.max(boss.hurtT, 0.16);
      run.ghostDamage += dmg;
      g.damage = (g.damage || 0) + dmg;   // 伤害榜：虚影各自记账
      const bx = boss.x - cameraX();
      addDamagePopup(bx + rand(-110, 120), GROUND_Y - 230 + rand(-60, 60), dmg, '#ffe08a');
      if (boss.hp <= 0) worldBossBossDown();
    });
  }

  function worldBossBossDown() {
    const run = worldBossRun;
    if (!run || run.down) return;
    run.down = true;
    setBattleLog('「' + run.def.name + '」倒下了！');
    pushBattleLog('世界BOSS「' + run.def.name + '」被击杀（虚影合计 ' + Math.round(run.ghostDamage) +
      ' 伤害，你贡献 ' + Math.round(run.playerDamage) + '）', 'reward');
    pendingVictory = 0.6;
  }

  // 每帧推进世界BOSS战场：血量回写、倒计时、参与奖励判定
  function updateWorldBossRun(dt) {
    const run = worldBossRun;
    if (!run) return;
    const boss = run.boss;
    wb.hp = Math.max(0, Math.round(boss.hp));   // 全服共享血量（本轮内保留）
    updateWorldBossGhosts(dt);
    updateWorldBossBar();

    // 记录玩家造成的伤害（奖励在"离开世界BOSS地图"时用三个宝箱发，见 leaveWorldBoss）
    if (worldBossLevelOk() && boss.hp < boss.maxHp - run.ghostDamage - 0.5) {
      run.playerDamage = Math.max(1, Math.round(boss.maxHp - run.ghostDamage - boss.hp));
      run.claimed = true;   // 表示"已经打出过有效伤害"
    }

    // 3 分钟到点（或到了下一次刷新时刻）：BOSS 自己消失，玩家被传送回公路
    if (wbNow() >= run.endsAt) {
      wb.phase = 'wait';
      wb.nextAt = wbOverride ? wbOverride.expiresAt : wbNextSpawnAt(wbNow());
      wb.hp = 0;
      leaveWorldBoss('3 分钟到了，世界BOSS「' + run.def.name + '」消失在风沙里，你被传送回公路。');
      return;
    }
    // 玩家被打倒：不重置进度，直接被踢出战场
    if (combat.hp <= 0) {
      combat.hp = Math.max(1, Math.round(combat.maxHp * 0.3));
      updateBattleHud();
      pushBattleLog('你在「' + run.def.name + '」的战场上倒下了，被传送回公路（参战奖励照发）', 'system');
      leaveWorldBoss('你倒下了，被传送出世界BOSS战场（生命恢复到 30%）。');
    }
  }

  // ---------- 世界BOSS场景绘制 ----------
  function drawWorldBossScene() {
    const run = worldBossRun;
    const cam = cameraX();
    const quake = quakeOffset();
    ctx.save();
    if (quake.x || quake.y) ctx.translate(quake.x, quake.y);

    // 血沙天空
    const sky = ctx.createLinearGradient(0, 0, 0, GROUND_Y);
    sky.addColorStop(0, '#25090c');
    sky.addColorStop(0.45, '#5c1f16');
    sky.addColorStop(0.78, '#9a4a22');
    sky.addColorStop(1, '#c4763a');
    ctx.fillStyle = sky;
    ctx.fillRect(-40, -40, W + 80, GROUND_Y + 40);

    // 血月
    const moonX = 300;
    const moonY = 150;
    const mg = ctx.createRadialGradient(moonX, moonY, 10, moonX, moonY, 220);
    mg.addColorStop(0, 'rgba(255, 170, 130, .55)');
    mg.addColorStop(1, 'rgba(255, 120, 80, 0)');
    ctx.fillStyle = mg;
    ctx.beginPath();
    ctx.arc(moonX, moonY, 220, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(255, 208, 170, .92)';
    ctx.beginPath();
    ctx.arc(moonX, moonY, 62, 0, Math.PI * 2);
    ctx.fill();

    // 血色云层（在血月之上、远山之下慢慢飘动）
    drawWbClouds();

    // 远景沙丘剪影
    ctx.fillStyle = 'rgba(60, 20, 14, .55)';
    ctx.beginPath();
    ctx.moveTo(-40, GROUND_Y);
    for (let x = -40; x <= W + 40; x += 40) {
      ctx.lineTo(x, 470 + Math.sin((x + cam * 0.02) / 240) * 46 + Math.sin((x + cam * 0.05) / 90) * 14);
    }
    ctx.lineTo(W + 40, GROUND_Y);
    ctx.closePath();
    ctx.fill();

    // 地面
    const ground = ctx.createLinearGradient(0, GROUND_Y - 30, 0, H);
    ground.addColorStop(0, '#7a3a1c');
    ground.addColorStop(1, '#2b120a');
    ctx.fillStyle = ground;
    ctx.fillRect(-40, GROUND_Y, W + 80, H - GROUND_Y + 40);

    // 贴地沙尘带（贴着地面往左掠过的风沙）
    drawWbDustBands();

    // 飘散的余烬（画在角色之前，让角色压在火星之上）
    drawWbEmbers();

    // 层次：BOSS（站在山丘后面）→ 前景山丘 → 法阵 / 挑战者 → 玩家
    drawEnemy();
    drawWbForegroundHill(cam);
    drawWbAltarRing(cam);
    drawWorldBossGhosts();
    drawWorldBossLoot(cam);      // 击杀后：地上的战利品展示 + 中央宝箱
    drawBattleGroup();
    drawBloodWavesGround();
    drawShockWavesGround();
    drawDefendFxBehind();   // 防御护盾：先铺一层在角色后面
    drawPlayer();
    drawDirtyOnPlayer();
    drawDefendFxFront();    // 角色前面再罩一层薄的
    drawBloodWavesAura();
    drawPet();
      drawAlly();
      drawAttackFx();
      drawAirSlashFx();
      drawSlashFx();
      drawYangClawFx();
      drawCageFx();
      drawTotemFx();
      drawDamagePopups();
    drawWbFlash();          // 血雷闪：整体提亮一瞬
    ctx.restore();
  }

  // 前景沙丘的脊线高度（x 越靠右越高，正好把 BOSS 的腿脚挡住）
  let wbHillEnabled = true;   // 调试用：可关掉山丘看 BOSS 全身
  function wbHillLift(x, cam) {
    const t = clamp((x - 240) / 800, 0, 1);
    return GROUND_Y - 18 - Math.sin((x + cam * 0.12) / 300) * 12 - t * 130;
  }

  // 前景沙丘：盖住 BOSS 的下半身，做出"站在山丘后面"的层次
  function drawWbForegroundHill(cam) {
    if (!wbHillEnabled) return;
    const lift = function (x) { return wbHillLift(x, cam); };
    const surge = ctx.createLinearGradient(0, GROUND_Y - 130, 0, H + 40);
    surge.addColorStop(0, '#9a5528');
    surge.addColorStop(0.45, '#5f2c13');
    surge.addColorStop(1, '#2a1108');
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(-40, H + 40);
    ctx.lineTo(-40, lift(-40));
    for (let x = -40; x <= W + 40; x += 20) ctx.lineTo(x, lift(x));
    ctx.lineTo(W + 40, H + 40);
    ctx.closePath();
    ctx.fillStyle = surge;
    ctx.fill();
    // 脊线高光（背光边缘）
    ctx.beginPath();
    ctx.moveTo(-40, lift(-40));
    for (let x = -40; x <= W + 40; x += 20) ctx.lineTo(x, lift(x));
    ctx.strokeStyle = 'rgba(255, 196, 140, .22)';
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.restore();
  }

  // 祭坛法阵：画在山丘近侧的地面上（BOSS 脚下的光圈）
  function drawWbAltarRing(cam) {
    const run = worldBossRun;
    if (!run) return;
    const bossSx = run.boss.x - cam;
    const pulse = 0.5 + Math.sin(run.tick * 1.6) * 0.12;
    ctx.save();
    ctx.translate(bossSx, GROUND_Y + 8);
    ctx.scale(1, 0.3);
    const ring = ctx.createRadialGradient(0, 0, 20, 0, 0, 260);
    ring.addColorStop(0, 'rgba(255, 90, 60, ' + (0.46 * pulse + 0.2).toFixed(3) + ')');
    ring.addColorStop(0.6, 'rgba(255, 140, 60, .2)');
    ring.addColorStop(1, 'rgba(255, 140, 60, 0)');
    ctx.fillStyle = ring;
    ctx.beginPath();
    ctx.arc(0, 0, 260, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255, 130, 90, .5)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(0, 0, 186 + Math.sin(run.tick * 1.1) * 6, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  function drawWorldBossGhosts() {
    const run = worldBossRun;
    if (!run) return;
    const bossSx = run.boss.x - cameraX();
    run.ghosts.forEach(function (g) {
      const lunge = g.attackT > 0 ? Math.sin((1 - g.attackT / 0.34) * Math.PI) * 54 : 0;
      const sx = bossSx + g.offset + lunge;
      const bob = Math.sin(g.phase * 2.8) * 3;
      ctx.save();
      ctx.globalAlpha = 0.46;
      try { ctx.filter = 'saturate(0.25) brightness(1.55)'; } catch (e) { /* 浏览器不支持就算了 */ }
      if (g.isPlayer && images.player_idle_1) {
        drawPlayerFrame(images.player_idle_1, sx, GROUND_Y + g.offY + bob,
          playerFx.scale * g.scale, PLAYER_SPRITE_FLIP, 0);
      } else {
        const img = enemyFrameImage({ sprite: g.sprite, animPhase: g.phase }, 6);
        drawEnemySprite(img, sx, GROUND_Y + g.offY + bob, g.scale * 0.88, true, 0,
          enemySpriteDef({ sprite: g.sprite }));
      }
      ctx.restore();

      ctx.save();
      ctx.globalAlpha = 0.72;
      ctx.textAlign = 'center';
      ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 3;
      ctx.strokeStyle = 'rgba(20, 6, 4, .8)';
      ctx.strokeText(g.name, sx, GROUND_Y - 158 * g.scale + g.offY);
      ctx.fillStyle = '#cfe6ff';
      ctx.fillText(g.name, sx, GROUND_Y - 158 * g.scale + g.offY);
      ctx.restore();
    });
  }

  /* ---------- 击杀后：地上的战利品展示 + 场地中央的宝箱 ---------- */
  function worldBossLootChestRect() {
    const run = worldBossRun;
    if (!run || !run.loot) return null;
    const img = images.wbChestBig;
    const w = WB_LOOT_CHEST_DRAW_W;
    const ratio = (img && img.naturalWidth) ? (img.naturalHeight / img.naturalWidth) : (736 / 895);
    const h = w * ratio;
    const cx = run.loot.x - cameraX();
    const ground = GROUND_Y + 6;
    return { cx: cx, x: cx - w / 2, y: ground - h, w: w, h: h, groundY: ground };
  }

  function worldBossGiftRect(g) {
    const img = images[g.key];
    const w = g.w;
    const h = w * ((img && img.naturalWidth) ? (img.naturalHeight / img.naturalWidth) : 1);
    const cx = g.x - cameraX();
    return { cx: cx, x: cx - w / 2, y: GROUND_Y + 6 - h + g.dy, w: w, h: h };
  }

  function drawWorldBossLoot(cam) {
    const run = worldBossRun;
    if (!run) return;

    // ① 散落在地上的战利品展示：只是给玩家看的，不能拾取
    (run.gifts || []).forEach(function (g) {
      const img = images[g.key];
      if (!img || !img.complete) return;
      const r = worldBossGiftRect(g);
      const bob = Math.sin(enemyAnimClock * 1.6 + g.t) * 3;
      // 一束淡淡的光，让"这堆是战利品"一眼可见
      const beam = ctx.createLinearGradient(0, GROUND_Y - 110, 0, GROUND_Y + 6);
      beam.addColorStop(0, 'rgba(255, 214, 142, 0)');
      beam.addColorStop(1, 'rgba(255, 196, 96, .26)');
      ctx.fillStyle = beam;
      ctx.fillRect(r.cx - r.w * 0.42, GROUND_Y - 110, r.w * 0.84, 116);
      // 地面投影
      ctx.save();
      ctx.globalAlpha = 0.34;
      ctx.fillStyle = 'rgba(40, 12, 4, .75)';
      ctx.beginPath();
      ctx.ellipse(r.cx, GROUND_Y + 6, r.w * 0.46, 7, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      ctx.drawImage(img, r.x, r.y + bob, r.w, r.h);
    });

    // ② 场地中央的宝箱
    const rect = worldBossLootChestRect();
    if (!rect) return;
    const img = images.wbChestBig;
    const opened = !!(run.loot && run.loot.done);
    const pulse = 0.5 + Math.sin(enemyAnimClock * 2.2) * 0.5;
    // 宝箱底下的金色光晕
    const glow = ctx.createRadialGradient(rect.cx, rect.groundY - 10, 6, rect.cx, rect.groundY - 10, rect.w * 0.95);
    glow.addColorStop(0, 'rgba(255, 214, 130, ' + (opened ? 0.18 : (0.34 + pulse * 0.16)).toFixed(3) + ')');
    glow.addColorStop(1, 'rgba(255, 190, 90, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(rect.cx - rect.w * 1.1, rect.groundY - rect.h * 1.25, rect.w * 2.2, rect.h * 1.5);
    if (img && img.complete) ctx.drawImage(img, rect.x, rect.y, rect.w, rect.h);

    if (!opened) {
      // 头顶倒计时
      const secs = Math.max(0, Math.ceil(run.loot.timeLeft));
      const text = '宝箱 ' + secs + 's 后自动开启';
      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = 'bold 17px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(28, 8, 1, .92)';
      ctx.strokeText(text, rect.cx, rect.y - 16);
      ctx.fillStyle = secs <= 10 ? '#ffd06a' : '#ffe9c0';
      ctx.fillText(text, rect.cx, rect.y - 16);
      ctx.restore();
      // 提示可以走过去点
      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(28, 8, 1, .85)';
      ctx.globalAlpha = 0.62 + pulse * 0.38;
      ctx.strokeText('走近后点击开启', rect.cx, rect.groundY + 22);
      ctx.fillStyle = '#ffdfa8';
      ctx.fillText('走近后点击开启', rect.cx, rect.groundY + 22);
      ctx.restore();
    } else {
      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = 'bold 15px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(28, 8, 1, .9)';
      ctx.strokeText('宝箱已开启', rect.cx, rect.y - 16);
      ctx.fillStyle = '#b6ffcf';
      ctx.fillText('宝箱已开启', rect.cx, rect.y - 16);
      ctx.restore();
    }
  }

  const REFORGE_UNIT = 100;    // 每级基础费用：100 × 装备等级
  const REFORGE_MAX = 20;      // 每件装备的洗练次数上限

  // 洗练费用 = 100 × 装备等级 ×（1 + 锁定条数）
  // 例：10 级装备洗一次 1000；锁 1 条 → 1000 + 1000 = 2000；锁 2 条 → 3000
  function reforgeCost(item) {
    if (!item) return 0;
    const base = REFORGE_UNIT * Math.max(1, item.level || 1);
    const locked = (item.mods || []).filter(function (m) { return m.locked; }).length;
    return base * (1 + locked);
  }

  function openReforge(item) {
    if (!item || item.type !== 'equipment') return;
    reforgeItem = item;
    renderReforge();
    ui.reforgePopup.classList.remove('hidden');
  }

  function closeReforge() {
    ui.reforgePopup.classList.add('hidden');
    reforgeItem = null;
  }

  // 金币或锁定变化后，重算按钮上的费用显示
  function refreshReforgeIfOpen() {
    if (reforgeItem && !ui.reforgePopup.classList.contains('hidden')) renderReforge();
  }

  function renderReforge() {
    const item = reforgeItem;
    if (!item) return;
    // 左侧：装备模型（用装备图标 + 品级配色）
    const color = item.rarityColor || '#ffd9a0';
    fillItemIcon(ui.reforgeIcon, item, item.icon || '装');
    ui.reforgeIcon.style.borderColor = color;
    ui.reforgeIcon.style.color = color;
    ui.reforgeModelName.textContent = item.name;
    ui.reforgeModelName.style.color = color;
    const slotLabel = SLOT_DEFS[item.slot] ? SLOT_DEFS[item.slot].label : '装备';
    ui.reforgeModelMeta.textContent = slotLabel +
      ' · 等级 ' + item.level +
      (item.weaponType ? ' · ' + item.weaponType : '') +
      ' · 已洗练 ' + (item.reforgeCount || 0) + ' / ' + REFORGE_MAX + ' 次';

    // 右侧：当前词缀（逐条来自 item.mods，可单独上锁）
    ensureItemMods(item);
    ui.reforgeList.textContent = '';
    const mods = item.mods || [];
    if (!mods.length) {
      const empty = document.createElement('div');
      empty.className = 'reforgeAffix empty';
      empty.textContent = '这件装备没有词缀';
      ui.reforgeList.appendChild(empty);
    } else {
      mods.forEach(function (mod, i) {
        const row = document.createElement('div');
        row.className = 'reforgeAffix' + (mod.locked ? ' locked' : '');
        const tag = document.createElement('i');
        tag.textContent = '词缀 ' + (i + 1);
        const span = document.createElement('span');
        span.textContent = modToText(mod);
        const lock = document.createElement('button');
        lock.type = 'button';
        lock.className = 'lockBtn' + (mod.locked ? ' on' : '');
        lock.textContent = mod.locked ? '🔒' : '🔓';
        lock.title = mod.locked ? '已锁定：洗练时保留这条词缀（点击解锁）' : '点击锁定这条词缀';
        lock.addEventListener('click', function (e) {
          e.stopPropagation();
          mod.locked = !mod.locked;
          renderReforge();
        });
        row.appendChild(tag);
        row.appendChild(span);
        row.appendChild(lock);
        ui.reforgeList.appendChild(row);
      });
      // 灵宝特殊属性：单独一行，洗练不会改动
      if (item.special) {
        const sp = document.createElement('div');
        sp.className = 'reforgeAffix special';
        const tag = document.createElement('i');
        tag.textContent = '灵宝';
        const span = document.createElement('span');
        span.textContent = '【' + item.special.name + '】' + item.special.desc;
        sp.appendChild(tag);
        sp.appendChild(span);
        ui.reforgeList.appendChild(sp);
      }
    }
    // 洗练按钮上的费用提示（随锁定条数变化）
    const cost = reforgeCost(item);
    const reachedCap = (item.reforgeCount || 0) >= REFORGE_MAX;
    if (ui.btnReforge) {
      // 按钮上只留两个字，费用写在下面的说明里
      ui.btnReforge.textContent = reachedCap ? '已达上限' : '洗练';
      ui.btnReforge.disabled = reachedCap || gold < cost;
      ui.btnReforge.title = reachedCap
        ? ('每件装备最多洗练 ' + REFORGE_MAX + ' 次')
        : (gold < cost ? ('金币不足：本次需要 ' + cost + ' 金币') : ('本次洗练花费 ' + cost + ' 金币'));
    }
    // 说明：数量不变 + 锁定的不重掷 + 数值按等级档位
    const bandIdx = Math.min(9, Math.floor((Math.max(1, item.level) - 1) / AFFIX_BAND_SIZE));
    const lockedCount = mods.filter(function (m) { return m.locked; }).length;
    const base = REFORGE_UNIT * Math.max(1, item.level || 1);
    ui.reforgeHint.innerHTML =
      '洗练只重掷未锁定的词缀，<b>词缀数量不变</b>（当前 ' + mods.length + ' 条' +
      (lockedCount ? '，其中 ' + lockedCount + ' 条已锁定' : '') + '）。' +
      '数值只会落在「' + (bandIdx * AFFIX_BAND_SIZE + 1) + '-' + (bandIdx * AFFIX_BAND_SIZE + AFFIX_BAND_SIZE) +
      ' 级」及以下的档位区间——等级 ' + item.level + ' 的装备洗不出高等级装备的数值。' +
      '<br>本次费用：<b>' + cost + ' 金币</b>（基础 100 × 等级 ' + item.level + ' = ' + base +
      (lockedCount ? '，锁定 ' + lockedCount + ' 条 × ' + base : '') + '）' +
      '<br>洗练次数：<b>' + (item.reforgeCount || 0) + ' / ' + REFORGE_MAX + '</b>' +
      (reachedCap ? '——已达上限，这件装备不能再洗了。' : '');
  }

  function doReforge() {
    const item = reforgeItem;
    if (!item) return;
    // 每件装备最多洗练 REFORGE_MAX 次
    if ((item.reforgeCount || 0) >= REFORGE_MAX) {
      showToast('这件装备已经洗练 ' + REFORGE_MAX + ' 次，达到上限了。');
      renderReforge();
      return;
    }
    // 费用 = 100 × 装备等级 ×（1 + 锁定条数）
    const cost = reforgeCost(item);
    const base = REFORGE_UNIT * Math.max(1, item.level || 1);
    const lockedNow = (item.mods || []).filter(function (m) { return m.locked; }).length;
    if (gold < cost) {
      showToast('金币不足：本次洗练需要 ' + cost + ' 金币（100 × 等级 ' + item.level + ' = ' + base +
        (lockedNow ? ' + 锁定 ' + lockedNow + ' 条 × ' + base : '') + '），当前只有 ' + gold + '。');
      return;
    }
    gold -= cost;
    updateGold();
    pushBattleLog('洗练「' + item.name + '」花费 ' + cost + ' 金币' +
      (lockedNow ? '（含锁定 ' + lockedNow + ' 条）' : ''), 'system');
    // 核心：数量不变，锁定的词缀整条保留，其余按装备等级对应的档位重掷
    const lockedBefore = (item.mods || []).filter(function (m) { return m.locked; }).length;
    rollItemAffixes(item);
    item.reforgeCount = (item.reforgeCount || 0) + 1;
    recalcPlayerStats();              // 已穿戴，属性立刻生效
    saveGame();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    renderReforge();
    showToast('洗练完成（重掷 ' + ((item.mods || []).length - lockedBefore) + ' 条' +
      (lockedBefore ? '，保留锁定 ' + lockedBefore + ' 条' : '') + '）：' + (item.detail || ''));
  }

  // ---------- 宠物窗口 ----------
  function openPetPanel() {
    renderPetPanel();
    ui.petPopup.classList.remove('hidden');
  }

  function closePetPanel() {
    ui.petPopup.classList.add('hidden');
  }

  function petTierInfo(tier) {
    return tier === 'rare' ? { name: '稀有', color: '#ffd265' }
      : tier === 'magic' ? { name: '魔法', color: '#67b6ff' }
      : { name: '普通', color: '#d8d3c2' };
  }

  // 宠物单次出手伤害的估算（取普攻区间中值，不带随机）
  function petDamageEstimate() {
    return petDamageEstimateOf(leadPet());
  }

  function petDamageEstimateOf(p) {
    if (!p) return 0;
    const base = 28.5 + derived.flatAtk;
    const petSkill = petSkillDef(p);
    const gemMult = 1 + skillGemSum(p.skillId || 'summonPet', ['summonDamage']) / 100;
    return Math.max(1, Math.round(
      base * 0.35 * (p.atkMult || 1) * gemMult * skillLevelMult(petSkill) *
        supportDamageMult(p.skillId || 'summonPet', activeEnemy)
    ) + Math.max(0, Math.round(combat.maxMp)));
  }

  function renderPetPanel() {
    if (!ui.petPopup) return;
    ui.petList.textContent = '';

    if (!petAlive()) {
      ui.petNote.textContent = '当前没有宠物';
      const empty = document.createElement('div');
      empty.className = 'petCard empty';
      empty.textContent = '还没有宠物。装备法杖会出现灵宠；学会「召唤蝎子 / 召唤鬣狗 / 召唤毒蛇 / 召唤猎鹰」等召唤技能，也能召唤宠物跟随。';
      ui.petList.appendChild(empty);
      return;
    }

    const list = alivePets();
    ui.petNote.textContent = '当前出战 ' + list.length + ' / ' + PET_MAX +
      ' 只宠物 · 按等级从前往后排在玩家身后（等级高的靠前）';
    list.forEach(function (p, idx) {
      renderPetCard(p, idx + 1);
    });
  }

  function renderPetCard(pet, slot) {
    const info = petTierInfo(pet.tier);
    const card = document.createElement('div');
    card.className = 'petCard';
    card.style.borderColor = info.color;

    const head = document.createElement('div');
    head.className = 'petHead';
    const name = document.createElement('b');
    name.textContent = '#' + slot + ' ' + pet.name;
    const tier = document.createElement('span');
    tier.className = 'petTier';
    tier.textContent = info.name + ' · 等级 ' + (pet.level || 1);
    tier.style.color = info.color;
    tier.style.borderColor = info.color;
    head.appendChild(name);
    head.appendChild(tier);
    card.appendChild(head);

    const source = document.createElement('div');
    source.className = 'petSource';
    source.textContent = '来源：' + (pet.source || '未知途径') +
      '　·　站位：玩家身后第 ' + slot + ' 位';
    card.appendChild(source);

    // 生命条
    const hpRow = document.createElement('div');
    hpRow.className = 'petHpRow';
    const hpText = document.createElement('span');
    hpText.textContent = '生命 ' + Math.ceil(pet.hp) + ' / ' + pet.maxHp +
      '（玩家生命的 35% × 生命系数 ' + (pet.hpMult || 1).toFixed(2) + '）';
    hpRow.appendChild(hpText);
    card.appendChild(hpRow);

    const barWrap = document.createElement('div');
    barWrap.className = 'petHpBar';
    const bar = document.createElement('i');
    bar.style.width = Math.max(0, Math.min(100, (pet.hp / pet.maxHp) * 100)) + '%';
    barWrap.appendChild(bar);
    card.appendChild(barWrap);

    const stats = document.createElement('div');
    stats.className = 'petStats';
    const lines = [
      ['攻击系数', '×' + (pet.atkMult || 1).toFixed(2) + '（宠物按玩家攻击的 35% 计算）'],
      ['预计出手伤害', '约 ' + petDamageEstimateOf(pet) + ' 点 / 次'],
      ['魔力加成', '每 1 点魔力 +1 点宠物伤害（当前 +' + Math.max(0, Math.round(combat.maxMp)) + '）'],
      ['召唤伤害宝石', '+' + skillGemSum(pet.skillId || 'summonPet', ['summonDamage']) + '%' +
        (pet.skillId ? '（镶嵌在「' + (petSkillDef(pet) ? petSkillDef(pet).name : '召唤技能') + '」上）' : '')],
      ['状态', '跟随中 · 战斗内外都会跟随玩家（可被宠物技能书强化）']
    ];
    lines.forEach(function (pair) {
      const row = document.createElement('div');
      row.className = 'petStat';
      const k = document.createElement('span');
      k.textContent = pair[0];
      const v = document.createElement('b');
      v.textContent = pair[1];
      row.appendChild(k);
      row.appendChild(v);
      stats.appendChild(row);
    });
    card.appendChild(stats);
    ui.petList.appendChild(card);
  }

  // ---------- 角色属性窗口（属性点分配 + 时装展示槽）----------
  // ---------- 技能总览窗口 ----------
  const SKILL_TYPE_LABEL = {
    melee: '近战', spell: '法术', summon: '召唤', heal: '恢复', toggle: '持续'
  };

  // 技能所属的大境界：武者境（武者十境）/ 炼气境 / 筑基境 …
  function bigRealmOfStage(stage) {
    const info = stageInfo(stage);
    if (info.realmIndex < 10) return { name: '武者境', index: 0 };
    // 炼气 / 筑基 / 结丹 … 统一叫「X境」
    return { name: info.realm + '境', index: info.realmIndex - 9 };
  }

  function playerBigRealm() {
    return bigRealmOfStage(player.level);
  }

  // 当前境界能看到的全部技能（低境界技能 + 当前境界技能；更高境界完全不可见）
  // 初始技能「劈砍」不在主动技能池里，单独定义一份（改名/改描述后靠它同步老存档）
  const STARTER_SKILL_DEF = {
    id: 'slash', name: '劈砍', type: 'melee', cost: 10, dmg: [34, 48], minStage: 1,
    desc: '挥动武器劈向敌人，造成 34~48 点伤害。'
  };

  // 技能定义查表（含初始「劈砍」）：用于技能改名 / 调数值后同步老存档
  function skillDefById(id) {
    if (id === STARTER_SKILL_DEF.id) return STARTER_SKILL_DEF;
    return ACTIVE_SKILL_POOL.filter(function (d) { return d.id === id; })[0] || null;
  }

  function visibleSkillDefs() {
    const list = [STARTER_SKILL_DEF];
    ACTIVE_SKILL_POOL.forEach(function (s) {
      if (!list.some(function (k) { return k.id === s.id; })) list.push(s);
    });
    const mine = playerBigRealm().index;
    return list.filter(function (s) {
      return bigRealmOfStage(s.minStage || 1).index <= mine;
    }).map(function (s) {
      const realm = bigRealmOfStage(s.minStage || 1);
      return {
        def: s,
        realmName: realm.name,
        learned: knownSkills.some(function (k) { return k.id === s.id; })
      };
    });
  }

  function openSkillPanel() {
    renderSkillPanel();
    ui.skillPopup.classList.remove('hidden');
  }

  function closeSkillPanel() {
    usingStone = null; // 直接关掉 = 取消使用技能石（技能石留在背包里）
    ui.skillPopup.classList.add('hidden');
  }

  function renderSkillPanel() {
    if (!ui.skillPopup) return;
    ui.skillList.textContent = '';
    const realm = playerBigRealm();
    const all = visibleSkillDefs();
    const learnedCount = all.filter(function (s) { return s.learned; }).length;
    if (usingStone) {
      ui.skillNote.textContent = '★ 正在使用主动技能石（还剩 ' + countSkillStones() +
        ' 颗）：点击灰色技能可【学习】，点击已学技能可【升级 +1】';
      ui.skillNote.style.color = '#ffd970';
    } else {
      ui.skillNote.textContent = '当前境界：' + stageName(player.level) +
        '（' + realm.name + '）· 该境界可学技能 ' + all.length + ' 个，已学 ' + learnedCount + ' 个' +
        '　·　施放法术消耗魔力（' + Math.ceil(combat.mp) + ' / ' + combat.maxMp + '）';
      ui.skillNote.style.color = '';
    }

    all.forEach(function (entry) {
      const def = entry.def;
      const learned = entry.learned;
      const known = knownSkills.filter(function (k) { return k.id === def.id; })[0];

      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'skillTile' + (learned ? ' learned' : ' locked') +
        (usingStone ? ' stoneMode' : '');
      tile.setAttribute('data-skill', def.id);
      tile.addEventListener('click', function () { useStoneOnSkill(def.id, learned); });

      const name = document.createElement('div');
      name.className = 'skillTileName';
      name.textContent = def.name;
      tile.appendChild(name);

      const type = document.createElement('div');
      type.className = 'skillTileType';
      type.textContent = (SKILL_TYPE_LABEL[def.type] || '技能') + ' · ' + entry.realmName;
      tile.appendChild(type);

      const cost = document.createElement('div');
      cost.className = 'skillTileCost';
      // 持续类 / 召唤类技能会占用魔力上限（保留）
      const reserveNote = def.type === 'toggle' ? '占用 35%'
        : def.type === 'summon' ? '占用 50%' : '';
      cost.textContent = (def.cost > 0 ? ('魔力 ' + def.cost) : '被动') +
        (reserveNote ? ' · ' + reserveNote : '');
      tile.appendChild(cost);

      if (learned) {
        if (def.type === 'toggle') {
          const state = document.createElement('div');
          state.className = 'skillTileState';
          state.textContent = bloodRebound ? '● 已激活' : '○ 未激活';
          state.style.color = bloodRebound ? '#7dffa8' : '#c9b391';
          tile.appendChild(state);
        }
        const info = document.createElement('div');
        info.className = 'skillTileInfo';
        info.textContent = usingStone
          ? ('升级到 Lv.' + ((known ? known.level || 1 : 1) + 1))
          : ((known ? 'Lv.' + (known.level || 1) : '已学习') +
            (known && known.sockets && known.sockets.length
              ? ' · 宝石 ' + known.sockets.length + '/' + socketSlotsOf(known) : ''));
        tile.appendChild(info);
      } else {
        const info = document.createElement('div');
        info.className = 'skillTileInfo';
        info.textContent = usingStone ? '点击学习' : '未学习';
        tile.appendChild(info);
      }

      tile.title = (learned ? '已学习　' : '未学习　') + def.name + '（' +
        (SKILL_TYPE_LABEL[def.type] || '技能') + '，' + entry.realmName + '）\n' + def.desc +
        (usingStone
          ? (learned ? '\n点击消耗 1 颗主动技能石，升 1 级' : '\n点击消耗 1 颗主动技能石，学会这个技能')
          : (learned ? '' : '\n获取方式：主动技能石（精英挑战 / 传奇敌人掉落 / 活力兑换）'));
      ui.skillList.appendChild(tile);
    });
  }

  function updateCharButton() {
    if (!ui.btnChar) return;
    const has = player.attrPoints > 0;
    setHudBtnLabel(ui.btnChar, has ? '角色 ·' + player.attrPoints : '角色');
    ui.btnChar.classList.toggle('hasPoints', has);
    ui.btnChar.title = has
      ? ('还有 ' + player.attrPoints + ' 点属性点可以分配')
      : '查看角色属性';
  }

  function openCharPanel() {
    renderCharPanel();
    ui.charPopup.classList.remove('hidden');
  }

  function closeCharPanel() {
    ui.charPopup.classList.add('hidden');
  }

  // 分配 1 点属性点：力量 / 敏捷 / 智力
  function allocateAttr(id) {
    if (!player.attrs[id] && player.attrs[id] !== 0) return;
    if (player.attrPoints <= 0) {
      showToast('没有可用属性点，提升境界可以获得。');
      return;
    }
    const label = (ATTR_DEFS.filter(function (a) { return a.id === id; })[0] || {}).label || id;
    const beforeHp = combat.maxHp;
    const beforeMp = combat.maxMp;
    player.attrs[id] += 1;
    player.attrPoints -= 1;
    recalcPlayerStats();
    // 加点带来的生命 / 魔力上限提升直接补满差额，避免出现空血条
    combat.hp = Math.min(combat.maxHp, combat.hp + Math.max(0, combat.maxHp - beforeHp));
    combat.mp = Math.min(derived.mpUsable, combat.mp + Math.max(0, derived.mpUsable - beforeMp));
    updateBattleHud();
    updateCharButton();
    renderCharPanel();
    pushBattleLog(
      '分配属性点：' + label + ' +1（剩余 ' + player.attrPoints + ' 点）',
      'reward'
    );
    showToast(label + ' +1，剩余属性点 ' + player.attrPoints + '。');
    saveGame();
  }

  function charStatRow(parent, label, value) {
    const row = document.createElement('div');
    row.className = 'charStat';
    const name = document.createElement('span');
    name.textContent = label;
    const val = document.createElement('b');
    val.textContent = String(value);
    row.appendChild(name);
    row.appendChild(val);
    parent.appendChild(row);
  }

  function renderCharPanel() {
    if (!ui.charPopup) return;
    ui.charStage.textContent =
      '境界 ' + stageName(player.level) + '（第 ' + player.level + ' 层）· ' +
      '每提升一个境界获得 ' + ATTR_POINTS_PER_LEVEL + ' 点属性点';
    ui.charPoints.textContent = String(player.attrPoints);
    // 说明文字直接读常量，避免和实际发点数（每境界 +1）对不上
    if (ui.charPointNote) {
      ui.charPointNote.textContent = '每提升一个境界 +' + ATTR_POINTS_PER_LEVEL + ' 点';
    }

    // 属性点分配区
    ui.charAttrs.textContent = '';
    ATTR_DEFS.forEach(function (def) {
      const box = document.createElement('div');
      box.className = 'charAttr';

      const name = document.createElement('div');
      name.className = 'charAttrName';
      name.textContent = def.label;

      const value = document.createElement('div');
      value.className = 'charAttrValue';
      value.textContent = String(player.attrs[def.id] || 0);

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'charAttrAdd';
      add.textContent = '+1';
      add.disabled = player.attrPoints <= 0;
      add.title = player.attrPoints > 0 ? ('分配 1 点' + def.label) : '没有可用属性点';
      add.addEventListener('click', function () { allocateAttr(def.id); });

      const note = document.createElement('div');
      note.className = 'charAttrNote';
      note.textContent = def.note;

      box.appendChild(name);
      box.appendChild(add);
      box.appendChild(value);
      box.appendChild(note);
      ui.charAttrs.appendChild(box);
    });

    // 时装展示槽：只展示当前穿戴的外观，不可在这里穿脱
    // （保留第一个方格里的角色立绘，只重建 5 个装备方格）
    Array.prototype.slice.call(ui.charSlots.querySelectorAll('.charSlot'))
      .forEach(function (node) { node.remove(); });
    Object.keys(SLOT_DEFS).forEach(function (slot) {
      const def = SLOT_DEFS[slot];
      const gear = equipment[slot];
      const row = document.createElement('div');
      row.className = 'charSlot' + (gear ? '' : ' charSlotEmpty');
      row.setAttribute('data-slot', slot); // 供 CSS 摆成“J”字形

      const icon = document.createElement('div');
      icon.className = 'charSlotIcon';
      if (gear) fillItemIcon(icon, gear, gear.icon || def.icon || '装');
      else icon.textContent = def.icon || '空';

      const text = document.createElement('div');
      text.className = 'charSlotText';
      const label = document.createElement('b');
      label.textContent = def.label;
      const name = document.createElement('span');
      if (gear) {
        name.textContent = gear.name;
        name.style.color = gear.rarityColor || '#fff';
      } else {
        name.textContent = '未穿戴';
      }
      text.appendChild(label);
      text.appendChild(name);

      row.appendChild(icon);
      row.appendChild(text);
      ui.charSlots.appendChild(row);
    });

    // 详细属性
    ui.charStats.textContent = '';
    const addSection = function (title) {
      const sec = document.createElement('div');
      sec.className = 'charStatSection';
      sec.textContent = title;
      ui.charStats.appendChild(sec);
    };
    addSection('基础属性');
    // 生命：同时显示当前生命 / 上限，方便边看装备边确认血量
    charStatRow(ui.charStats, '生命（当前 / 上限）',
      Math.max(0, Math.round(combat.hp)) + ' / ' + combat.maxHp);
    charStatRow(ui.charStats, '魔力（当前 / 上限）',
      Math.max(0, Math.round(combat.mp)) + ' / ' + combat.maxMp);
    charStatRow(
      ui.charStats,
      '魔力可用（已占用 ' + derived.mpReservePct + '%）',
      derived.mpUsable + (derived.mpReserved > 0 ? '（保留 ' + derived.mpReserved + '）' : '')
    );
    charStatRow(ui.charStats, '攻击力（攻击加成）', '+' + derived.flatAtk);
    charStatRow(
      ui.charStats,
      '防御力（护甲）',
      derived.armor + (derived.armorPct ? '（+' + derived.armorPct + '%）' : '')
    );
    charStatRow(ui.charStats, '移动速度', Math.round(derived.speedMult * 100) + '%');
    charStatRow(ui.charStats, '攻击速度', '+' + derived.atkSpeed + '%');
    addSection('三维 / 进阶属性');
    charStatRow(
      ui.charStats,
      '力量',
      derived.strengthTotal + '（加点 ' + derived.attrStr + ' + 装备 ' + derived.strength + '）'
    );
    charStatRow(
      ui.charStats,
      '敏捷',
      derived.agilityTotal + '（加点 ' + derived.attrAgi + ' + 装备 ' + derived.agility + '）' +
      '　攻速 +' + Math.round(derived.atkSpeed) + '%'
    );
    charStatRow(
      ui.charStats,
      '智力',
      (derived.attrInt + derived.relicInt + derived.intelligence) +
      '（加点 ' + derived.attrInt + ' + 装备 ' + derived.intelligence +
      (derived.relicInt ? ' + 圣物 ' + derived.relicInt : '') + '）'
    );
    if (derived.relicInt) charStatRow(ui.charStats, '圣物智力加成', '+' + derived.relicInt);
    charStatRow(ui.charStats, '智力合计（含圣物）', derived.intTotal + '　魔力 +' + (derived.intTotal * 2));
    charStatRow(ui.charStats, '力量合计换算生命', '+' + derived.strengthHp);
    charStatRow(ui.charStats, '免疫中毒', derived.poisonImmune ? '是（森特欧斯的头骨）' : '否');
    charStatRow(ui.charStats, '免疫控制', derived.controlImmune ? '是（森特欧斯的图腾）' : '否');
    charStatRow(ui.charStats, '法术伤害提高', '+' + derived.spellDmgPct + '%');
    charStatRow(ui.charStats, '暴击率', derived.critChance + '%');
    charStatRow(ui.charStats, '暴击伤害', '+' + derived.critDamage + '%');
    charStatRow(ui.charStats, '全元素抗性', derived.res + '%');
    charStatRow(ui.charStats, '混沌抗性', derived.chaosRes + '%');
    charStatRow(ui.charStats, '格挡几率', derived.blockPct + '%');
    charStatRow(ui.charStats, '荆棘伤害', derived.thorns);
    charStatRow(ui.charStats, '生命每回合再生', derived.lifeRegen + '/回合');
    charStatRow(ui.charStats, '暴击伤害降低', derived.critReduce + '%');
    charStatRow(
      ui.charStats,
      '世界BOSS挑战境界',
      stageText(worldBossPlayerLevel()) + (worldBossPlayerLevel() > playerLevel()
        ? '（灵宝破境符诏 +' + (worldBossPlayerLevel() - playerLevel()) + ' 级）' : '')
    );
    charStatRow(ui.charStats, '金币', gold);
  }

  function renderGearPanel() {
    ui.equipSlots.textContent = '';
    hideItemTip();
    Object.keys(SLOT_DEFS).forEach(function (slot) {
      const def = SLOT_DEFS[slot];
      const gear = equipment[slot];
      // 每格外面套一层，方便在右上角放「洗练」小按钮
      const wrap = document.createElement('div');
      wrap.className = 'gearSlotWrap';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gearSlot';
      if (!gear) {
        btn.disabled = true;
        const empty = document.createElement('span');
        empty.className = 'gearEmpty';
        empty.textContent = '空';
        const label = document.createElement('b');
        label.textContent = def.label;
        btn.appendChild(label);
        btn.appendChild(empty);
        btn.title = def.label + '尚未装备';
      } else {
        // 装备栏也显示美术图标（头盔 / 衣服 / 鞋子 / 手套 / 武器）
        const slotIcon = document.createElement('div');
        slotIcon.className = 'gearSlotIcon';
        fillItemIcon(slotIcon, gear, gear.icon || def.icon || '装');
        const label = document.createElement('b');
        label.textContent = def.label;
        const name = document.createElement('span');
        name.className = 'gearName';
        name.textContent = gear.name;
        name.style.color = gear.rarityColor || '#fff';
        const tip = document.createElement('small');
        tip.textContent = '卸下';
        btn.appendChild(slotIcon);
        btn.appendChild(label);
        btn.appendChild(name);
        btn.appendChild(tip);
        btn.addEventListener('click', function () { unequipSlot(slot); });
        // 鼠标悬停显示这件装备的属性（替代原来顶部那一长串总属性）
        bindItemTip(btn, gear);
        // 洗练按钮：打开洗练窗口（左模型 / 右词缀 / 下洗练）
        const rb = document.createElement('button');
        rb.type = 'button';
        rb.className = 'reforgeBtn';
        rb.textContent = '洗练';
        rb.title = '洗练「' + gear.name + '」的全部词缀';
        rb.addEventListener('click', function (e) {
          e.stopPropagation();
          openReforge(gear);
        });
        wrap.appendChild(rb);
      }
      wrap.appendChild(btn);
      ui.equipSlots.appendChild(wrap);
    });

    ui.bagList.textContent = '';
    // 装备 → 道具 → 材料，按类型显式分组，避免排序歧义
    const bagItems = [];
    ['equipment', 'santosRelic', 'gem', 'skillStone', 'legendStone', 'legendTicket', 'petEgg', 'petBook', 'spiritItem', 'potion', 'worldChest', 'material'].forEach(function (type) {
      inventory.forEach(function (it) {
        if (it.type === type) bagItems.push(it);
      });
    });

    // 可叠加物品：技能石 / 回血药瓶 等同类同名道具合成一格，显示 ×N
    const STACKABLE = ['potion', 'skillStone', 'petEgg', 'petBook', 'spiritItem', 'legendTicket', 'legendStone', 'material'];
    const bagEntries = [];
    const stackIndex = {};
    bagItems.forEach(function (it) {
      if (STACKABLE.indexOf(it.type) < 0) {
        bagEntries.push({ item: it, count: 1, items: [it] });
        return;
      }
      // 回血药瓶：按名字叠加（恢复量会随等级/随机浮动，不能因为数值不同就分成两格）
      const key = it.type === 'potion'
        ? it.type + '|' + it.name
        : it.type + '|' + it.name + '|' + (it.detail || '');
      if (stackIndex[key] === undefined) {
        stackIndex[key] = bagEntries.length;
        bagEntries.push({ item: it, count: 1, items: [it] });
      } else {
        const entry = bagEntries[stackIndex[key]];
        entry.count += 1;
        entry.items.push(it);
      }
    });

    if (!bagItems.length) {
      const empty = document.createElement('div');
      empty.className = 'bagRow';
      empty.textContent = '背包空空如也，搜索尸体试试手气吧。';
      ui.bagList.appendChild(empty);
    }
    bagEntries.forEach(function (entry) {
      const item = entry.item;
      const row = document.createElement('div');
      row.className = 'bagRow';

      const icon = document.createElement('div');
      icon.className = 'bagIcon';
      fillItemIcon(icon, item, item.icon || '?');
      row.appendChild(icon);

      const info = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'bagName';
      name.textContent = item.name;
      name.style.color = item.rarityColor || '#fff';
      if (entry.count > 1) {
        const stack = document.createElement('span');
        stack.className = 'bagStack';
        stack.textContent = '×' + entry.count;
        name.appendChild(stack);
      }
      const meta = document.createElement('div');
      meta.className = 'bagMeta';
      // 叠加起来的药瓶恢复量可能不同，这里显示区间，别只显示第一瓶的数
      let detailText = item.detail || '';
      if (item.type === 'potion' && entry.count > 1) {
        const heals = entry.items.map(function (x) { return x.heal || 0; }).filter(function (n) { return n > 0; });
        if (heals.length) {
          const lo = Math.min.apply(null, heals);
          const hi = Math.max.apply(null, heals);
          detailText = lo === hi ? ('恢复 ' + lo + ' 点生命') : ('恢复 ' + lo + '~' + hi + ' 点生命');
        }
      }
      meta.textContent = detailText;
      info.appendChild(name);
      info.appendChild(meta);
      row.appendChild(info);

      if (item.type === 'gem') {
        row.addEventListener('contextmenu', function (e) {
          e.preventDefault();
          if (!item.identified) openGemIdentify(item);
          else openSocketPanel(item);
        });
      }

      if (item.type === 'equipment') {
        bindItemTip(row, item);   // 背包里的装备同样悬停看属性
        const actions = document.createElement('div');
        actions.className = 'bagActions';

        const equip = document.createElement('button');
        equip.type = 'button';
        equip.className = 'bagAction';
        equip.textContent = equipment[item.slot] ? '替换' : '装备';
        equip.addEventListener('click', function () { equipItem(item); });
        actions.appendChild(equip);

        const sell = document.createElement('button');
        sell.type = 'button';
        sell.className = 'bagAction sell';
        sell.textContent = '出售 ' + sellPrice(item);
        sell.addEventListener('click', function () { sellItem(item); });
        actions.appendChild(sell);

        row.appendChild(actions);
      } else if (item.type === 'gem') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = item.identified ? '镶嵌' : '选择效果';
        action.addEventListener('click', function () {
          if (item.identified) openSocketPanel(item);
          else openGemIdentify(item);
        });
        row.appendChild(action);
      } else if (item.type === 'skillStone') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = '使用';
        action.addEventListener('click', function () { useActiveSkillStone(item); });
        row.appendChild(action);
      } else if (item.type === 'legendStone') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = '召唤';
        action.addEventListener('click', function () { useLegendStone(item); });
        row.appendChild(action);
      } else if (item.type === 'legendTicket') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = '使用';
        action.addEventListener('click', function () { useLegendTicket(item); });
        row.appendChild(action);
      } else if (item.type === 'petEgg') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = '孵化';
        action.addEventListener('click', function () { usePetEgg(item); });
        row.appendChild(action);
      } else if (item.type === 'petBook') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = '使用';
        action.addEventListener('click', function () { usePetSkillBook(item); });
        row.appendChild(action);
      } else if (item.type === 'spiritItem') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        if (player.bottleneck) {
          action.textContent = '突破';
          action.addEventListener('click', breakthrough);
        } else {
          action.textContent = '突破材料';
          action.disabled = true;
        }
        row.appendChild(action);
      } else if (item.type === 'potion') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = '使用';
        action.addEventListener('click', function () { usePotion(item); });
        row.appendChild(action);
      } else if (item.type === 'worldChest') {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = '打开';
        action.title = '开启世界BOSS宝箱（极小几率开出灵宝）';
        action.addEventListener('click', function () { openWorldBossChest(item); });
        row.appendChild(action);
      } else if (item.type === 'santosRelic') {
        // 森特欧斯圣物：带在身上就生效，不需要装备
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = '携带生效';
        action.disabled = true;
        action.title = item.detail + '\n（圣物带在背包里就会生效，不需要装备）';
        row.appendChild(action);
      } else {
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'bagAction';
        action.textContent = '材料';
        action.disabled = true;
        row.appendChild(action);
      }
      ui.bagList.appendChild(row);
    });

    // 顶部不再堆一长串总属性：总结属性在「角色」窗口，这里只保留悬停查看单件装备
  }

  // ---------- 商店 ----------
  // 商店商品等级跟随玩家当前进度 / 已穿戴装备，品质和词缀随机，价格按属性高低计算
  function getShopItemLevel() {
    // 商店等级跟随玩家境界（以前按击杀数狂涨，才会出现"四境后期刷 60 级装备"）
    return clamp(Math.round(player.level || 1), 1, MAX_STAGE);
  }

  // 商店常驻商品：回血药瓶（固定 100 金币一瓶，不参与随机刷新，也不能锁定）
  const SHOP_POTION_PRICE = 100;
  function makeShopPotion() {
    const pot = makePotion(player.level);
    pot.price = SHOP_POTION_PRICE;
    pot.alwaysAvailable = true;
    pot.rarityName = '常驻道具';
    return pot;
  }

  function openShop() {
    const level = getShopItemLevel();
    // 已锁定的商品继续保留，其余位置刷新出新商品
    shopItems = lockedShopItems.slice();
    const count = randInt(4, 5);
    for (let i = shopItems.length; i < count; i++) {
      const gearLevel = Math.max(1, level + randInt(-1, 1));
      shopItems.push(makeEquipment(gearLevel));
    }
    // 商店至少保证有一件武器（玩家主要就是来淘武器的）
    if (shopItems.length && !shopItems.some(function (it) { return it.slot === 'weapon'; })) {
      shopItems[shopItems.length - 1] = makeEquipment(level, null, 'weapon');
    }
    // 固定商品：回血药瓶（每次开店都在，价格固定 100 金币）
    shopItems.push(makeShopPotion());
    renderShop();
    ui.shopPopup.classList.remove('hidden');
  }

  function closeShop() {
    ui.shopPopup.classList.add('hidden');
  }

  function buyShopItem(item) {
    // 常驻商品（回血药瓶）：买完货架上还留着，背包里每次都是新的道具
    if (item && item.type === 'potion') {
      if (gold < item.price) {
        showToast('金币不足，「回血药瓶」需要 ' + item.price + ' 金币。');
        return;
      }
      gold -= item.price;
      updateGold();
      inventory.push(makePotion(player.level));
      renderShop();
      if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
      showToast('已购买「回血药瓶」×1（-' + item.price + ' 金币），可在背包中使用。');
      return;
    }
    if (gold < item.price) {
      showToast('金币不足，无法购买这件装备。');
      return;
    }
    gold -= item.price;
    updateGold();
    inventory.push(item);
    shopItems = shopItems.filter(function (it) { return it.id !== item.id; });
    lockedShopItems = lockedShopItems.filter(function (it) { return it.id !== item.id; });
    renderShop();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('已购买「' + item.name + '」，可在装备栏中穿戴。');
  }

  function toggleLockShopItem(item) {
    if (item.locked) {
      item.locked = false;
      lockedShopItems = lockedShopItems.filter(function (it) { return it.id !== item.id; });
      renderShop();
      showToast('已解锁「' + item.name + '」，下次开店会刷新。');
      return;
    }
    const cost = SHOP_LOCK_COST[item.rarity] || 10;
    if (gold < cost) {
      showToast('锁定需要 ' + cost + ' 金币，金币不足。');
      return;
    }
    gold -= cost;
    updateGold();
    item.locked = true;
    lockedShopItems.push(item);
    renderShop();
    showToast('已锁定「' + item.name + '」（花费 ' + cost + ' 金币），下次开店仍会保留。');
  }

  function renderShop() {
    ui.shopGold.textContent = String(gold);
    ui.shopList.textContent = '';
    if (!shopItems.length) {
      const empty = document.createElement('div');
      empty.className = 'shopItem';
      empty.textContent = '这一轮商品已售罄，下次到访会补货。';
      ui.shopList.appendChild(empty);
      return;
    }
    shopItems.forEach(function (item) {
      const card = document.createElement('div');
      card.className = 'shopItem';

      const top = document.createElement('div');
      top.style.display = 'flex';
      top.style.alignItems = 'center';
      top.style.gap = '8px';
      const icon = document.createElement('div');
      icon.className = 'shopIcon';
      fillItemIcon(icon, item, item.icon);
      icon.style.color = item.rarityColor;
      const info = document.createElement('div');
      const name = document.createElement('div');
      name.className = 'shopName';
      name.textContent = item.name;
      name.style.color = item.rarityColor;
      const lv = document.createElement('div');
      lv.className = 'shopLv';
      // 装备显示装备等级；常驻道具（回血药瓶）显示品类
      lv.textContent = item.type === 'equipment'
        ? ('装备等级 ' + item.level)
        : (item.rarityName || '道具');
      info.appendChild(name);
      info.appendChild(lv);
      top.appendChild(icon);
      top.appendChild(info);
      card.appendChild(top);

      const mods = document.createElement('div');
      mods.className = 'shopMods';
      mods.textContent = item.detail;
      card.appendChild(mods);

      const actions = document.createElement('div');
      actions.className = 'shopActions';

      // 常驻商品不用锁定（每轮都在货架上）
      if (item.type === 'equipment') {
        const lock = document.createElement('button');
        lock.type = 'button';
        lock.className = 'shopLock' + (item.locked ? ' on' : '');
        lock.textContent = item.locked ? '已锁定' : '锁定 ' + (SHOP_LOCK_COST[item.rarity] || 10);
        lock.addEventListener('click', function () { toggleLockShopItem(item); });
        actions.appendChild(lock);
      }

      const buy = document.createElement('button');
      buy.type = 'button';
      buy.className = 'shopBuy';
      buy.textContent = '购买 · ' + item.price + ' 金币';
      buy.disabled = gold < item.price;
      buy.addEventListener('click', function () { buyShopItem(item); });
      actions.appendChild(buy);
      card.appendChild(actions);
      ui.shopList.appendChild(card);
    });
  }

  // ---------- 活力兑换 ----------
  function openExchange() {
    renderExchange();
    ui.exchangePopup.classList.remove('hidden');
  }

  function closeExchange() {
    ui.exchangePopup.classList.add('hidden');
  }

  function renderExchange() {
    ui.exchangeNote.textContent = '当前活力 ' + vitality + '（签到与自动冒险会消耗/获得活力）';
    ui.exchangeList.textContent = '';
    EXCHANGE_ITEMS.forEach(function (entry) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'socketSkill';
      const name = document.createElement('span');
      name.textContent = entry.label;
      const meta = document.createElement('small');
      meta.textContent = entry.cost + ' 活力 · ' + entry.note +
        (entry.owned ? '　（' + entry.owned() + '）' : '');
      btn.appendChild(name);
      btn.appendChild(meta);
      btn.disabled = vitality < entry.cost;
      btn.addEventListener('click', function () { buyExchange(entry); });
      ui.exchangeList.appendChild(btn);
    });
  }

  function buyExchange(entry) {
    if (vitality < entry.cost) {
      showToast('活力不足，无法兑换「' + entry.label + '」。');
      return;
    }
    vitality -= entry.cost;
    saveGame();
    updateVitalityUI();
    if (entry.grant) {
      // 令牌这类不放进背包的兑换物，直接计数
      entry.grant();
      saveGame();
      renderExchange();
      showToast('消耗 ' + entry.cost + ' 活力，兑换了「' + entry.label + '」' +
        (entry.owned ? '，' + entry.owned() : '') + '。');
      return;
    }
    const item = entry.make();
    inventory.push(item);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    renderExchange();
    showToast('消耗 ' + entry.cost + ' 活力，兑换了「' + item.name + '」。');
  }

  // ---------- 首次宝箱 ----------
  function openChestPanel() {
    if (!chest) return;
    chest.opened = true;
    renderChestPanel();
    ui.chestPopup.classList.remove('hidden');
  }

  function closeChestPanel() {
    ui.chestPopup.classList.add('hidden');
    if (chest && chest.weaponTaken && chest.gemTaken && chest.stoneTaken) {
      chest.claimed = true;
      showToast('宝箱已经取空，祝你旅途顺利。');
    }
  }

  // 新手宝箱的第三份礼物：主动技能石
  function takeChestStone() {
    if (!chest || chest.stoneTaken) return;
    const stone = makeActiveSkillStone();
    inventory.push(stone);
    chest.stoneItem = stone;
    chest.stoneTaken = true;
    renderChestPanel();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('获得「主动技能石」：在背包中使用可随机习得一个主动技能。');
  }

  function takeChestWeapon(item) {
    if (!chest || chest.weaponTaken) return;
    inventory.push(item);
    chest.weaponTaken = true;
    chest.chosenWeapon = item.id;
    renderChestPanel();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('获得白色武器「' + item.name + '」，已放入背包。');
  }

  function takeChestGem() {
    if (!chest || chest.gemTaken) return;
    const gem = makeEmptyGem();
    inventory.push(gem);
          chest.gemItem = gem;
    chest.gemTaken = true;
    renderChestPanel();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('获得「空白被动技能石」：在背包中右键点击可选择被动效果。');
  }

  function renderChestPanel() {
    if (!chest) return;
    ui.chestWeapons.textContent = '';
    chest.weapons.forEach(function (item) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'chestWeapon' + (chest.chosenWeapon === item.id ? ' chosen' : '');
      if (chest.weaponTaken && chest.chosenWeapon !== item.id) btn.disabled = true;
      const icon = document.createElement('b');
      fillItemIcon(icon, item, item.icon);
      const name = document.createElement('span');
      name.textContent = item.weaponType;
      const affix = document.createElement('small');
      affix.textContent = item.detail;
      btn.appendChild(icon);
      btn.appendChild(name);
      btn.appendChild(affix);
      btn.addEventListener('click', function () { takeChestWeapon(item); });
      ui.chestWeapons.appendChild(btn);
    });

    ui.chestGem.textContent = '';
    const box = document.createElement('div');
    box.className = 'chestGemBox';
    const icon = document.createElement('div');
    icon.className = 'gemIcon';
    icon.textContent = '石';
    const info = document.createElement('div');
    const name = document.createElement('div');
    name.className = 'bagName';
    const detail = document.createElement('div');
    detail.className = 'bagMeta';
    if (chest.gemTaken && chest.gemItem) {
      name.textContent = chest.gemItem.name;
      detail.textContent = chest.gemItem.detail;
    } else {
      name.textContent = '空白被动技能石';
      detail.textContent = '右键点击后可挑选属性增幅';
      const take = document.createElement('button');
      take.type = 'button';
      take.className = 'bagAction';
      take.textContent = '领取';
      take.style.marginLeft = 'auto';
      take.addEventListener('click', takeChestGem);
      box.appendChild(take);
    }
    name.style.color = '#bfe1ff';
    info.appendChild(name);
    info.appendChild(detail);
    box.insertBefore(icon, box.firstChild);
    box.insertBefore(info, box.children[1] || null);
    ui.chestGem.appendChild(box);

    // 主动技能石（第三份礼物）
    if (ui.chestStone) {
      ui.chestStone.textContent = '';
      const sbox = document.createElement('div');
      sbox.className = 'chestGemBox';
      const sicon = document.createElement('div');
      sicon.className = 'gemIcon';
      sicon.textContent = '技';
      const sinfo = document.createElement('div');
      const sname = document.createElement('div');
      sname.className = 'bagName';
      const sdetail = document.createElement('div');
      sdetail.className = 'bagMeta';
      if (chest.stoneTaken && chest.stoneItem) {
        sname.textContent = chest.stoneItem.name;
        sdetail.textContent = chest.stoneItem.detail;
      } else {
        sname.textContent = '主动技能石';
        sdetail.textContent = '使用后随机习得一个主动技能';
        const stake = document.createElement('button');
        stake.type = 'button';
        stake.className = 'bagAction';
        stake.textContent = '领取';
        stake.style.marginLeft = 'auto';
        stake.addEventListener('click', takeChestStone);
        sbox.appendChild(stake);
      }
      sname.style.color = '#ffd265';
      sinfo.appendChild(sname);
      sinfo.appendChild(sdetail);
      sbox.insertBefore(sicon, sbox.firstChild);
      sbox.insertBefore(sinfo, sbox.children[1] || null);
      ui.chestStone.appendChild(sbox);
    }
  }

  // ---------- 被动技能石：挑选效果 ----------
  function openGemIdentify(item) {
    if (!item || item.type !== 'gem') return;
    identifyingGem = item;
    ui.gemOptions.textContent = '';
    GEM_OPTIONS.forEach(function (opt) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gemOption';
      btn.textContent = opt.support
        ? opt.name
        : (opt.name + ' +' + opt.min + '~' + opt.max + opt.unit);
      const tip = document.createElement('small');
      tip.textContent = !opt.support
        ? (opt.id === 'summonDamage' ? '作用于召唤出来的宠物' : '镶嵌到主动技能后生效')
        : opt.projectileOnly
          ? '投射物辅助：只能镶嵌到投射物技能（当前：破空斩的剑气）'
          : opt.onlySkill
            ? '专属辅助：只能镶嵌到「' +
              ((knownSkills.filter(function (s) { return s.id === opt.onlySkill; })[0] || {}).name || opt.onlySkill) + '」'
            : '辅助：镶嵌到任意击中敌人的技能后生效';
      btn.appendChild(tip);
      btn.addEventListener('click', function () { chooseGemOption(opt); });
      ui.gemOptions.appendChild(btn);
    });
    ui.gemPopup.classList.remove('hidden');
  }

  function closeGemPanel() {
    identifyingGem = null;
    ui.gemPopup.classList.add('hidden');
  }

  function chooseGemOption(opt) {
    if (!identifyingGem) return;
    const oldId = identifyingGem.id;
    const gem = gemOptionToItem(opt);
    inventory = inventory.filter(function (it) { return it.id !== oldId; });
    inventory.push(gem);
    identifyingGem = null;
    ui.gemPopup.classList.add('hidden');
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    showToast('生成「' + gem.name + '」：' + gem.detail);
  }

  // ---------- 被动技能石：镶嵌 ----------
  function openSocketPanel(gem) {
    if (!gem || !gem.identified) return;
    socketingGem = gem;
    renderSocketOptions();
    ui.socketPopup.classList.remove('hidden');
  }

  function closeSocketPanel() {
    socketingGem = null;
    ui.socketPopup.classList.add('hidden');
  }

  function renderSocketOptions() {
    ui.socketOptions.textContent = '';
    knownSkills.forEach(function (skill) {
      const sockets = skill.sockets || (skill.sockets = []);
      const slots = socketSlotsOf(skill);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'socketSkill';
      const name = document.createElement('span');
      name.textContent = skill.name;
      const meta = document.createElement('small');
      meta.textContent = '镶孔 ' + sockets.length + ' / ' + slots;
      btn.appendChild(name);
      btn.appendChild(meta);
      // 投射物辅助只能进投射物技能；专属辅助只能进指定技能
      const gem = socketingGem;
      const wrongSkill = !!(gem && ((isProjectileOnlyGem(gem) && !isProjectileSkill(skill.id)) ||
        (gem.onlySkill && skill.id !== gem.onlySkill)));
      btn.disabled = wrongSkill || sockets.length >= slots;
      if (wrongSkill) {
        btn.title = isProjectileOnlyGem(gem)
          ? '「' + gem.name.replace(/^辅助技能石：/, '') + '」只能镶嵌到投射物技能（当前：破空斩的剑气）'
          : '「' + gem.name.replace(/^辅助技能石：/, '') + '」只能镶嵌到指定技能';
      } else if (btn.disabled) {
        btn.title = slots < SOCKET_SLOTS_MAX
          ? '镶孔已满，可花 ' + SOCKET_SLOT_COST + ' 金币开一个新槽'
          : '镶孔已满（已达上限 ' + SOCKET_SLOTS_MAX + ' 个）';
      }
      btn.addEventListener('click', function () { socketGemInto(skill, socketingGem); });
      ui.socketOptions.appendChild(btn);
      // 镶孔满了但还没到 6 个 → 直接在这里开槽
      if (sockets.length >= slots && slots < SOCKET_SLOTS_MAX) {
        const buy = document.createElement('button');
        buy.type = 'button';
        buy.className = 'socketSkill slotBuy';
        const bName = document.createElement('span');
        bName.textContent = '＋ 给「' + skill.name + '」开一个镶孔';
        const bMeta = document.createElement('small');
        bMeta.textContent = SOCKET_SLOT_COST + ' 金币（' + slots + ' → ' + (slots + 1) + ' / ' + SOCKET_SLOTS_MAX + '）';
        buy.appendChild(bName);
        buy.appendChild(bMeta);
        buy.disabled = gold < SOCKET_SLOT_COST;
        if (buy.disabled) buy.title = '金币不足';
        buy.addEventListener('click', function () { buySocketSlot(skill); });
        ui.socketOptions.appendChild(buy);
      }
    });
  }

  function socketGemInto(skill, gem) {
    if (!skill || !gem) return;
    // 投射物辅助只能镶嵌到投射物技能（破空斩的剑气）
    if (isProjectileOnlyGem(gem) && !isProjectileSkill(skill.id)) {
      showToast('「' + gem.name.replace(/^辅助技能石：/, '') +
        '」只能镶嵌到投射物技能（当前：破空斩的剑气）。');
      return;
    }
    // 专属辅助只能镶嵌到指定技能（例：震撼大地 → 阳爪）
    if (gem.onlySkill && skill.id !== gem.onlySkill) {
      const target = knownSkills.filter(function (s) { return s.id === gem.onlySkill; })[0];
      showToast('「' + gem.name.replace(/^辅助技能石：/, '') + '」只能镶嵌到「' +
        (target ? target.name : gem.onlySkill) + '」上。');
      return;
    }
    if (!skill.sockets) skill.sockets = [];
    const slots = socketSlotsOf(skill);
    if (skill.sockets.length >= slots) {
      showToast(slots < SOCKET_SLOTS_MAX
        ? '「' + skill.name + '」的镶孔已满（' + slots + ' 个），可花 ' + SOCKET_SLOT_COST + ' 金币再开一个槽。'
        : '「' + skill.name + '」的镶孔已满（已达上限 ' + SOCKET_SLOTS_MAX + ' 个）。');
      return;
    }
    gem.socketedTo = skill.id;
    skill.sockets.push(gem);
    inventory = inventory.filter(function (it) { return it.id !== gem.id; });
    socketingGem = null;
    ui.socketPopup.classList.add('hidden');
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    showToast('已将「' + gem.name + '」镶嵌到「' + skill.name + '」。');
  }

  function unsocketGem(skill, gem) {
    if (!skill || !gem) return;
    skill.sockets = (skill.sockets || []).filter(function (g) { return g.id !== gem.id; });
    gem.socketedTo = null;
    inventory.push(gem);
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    renderSpellList();
    showToast('已取下「' + gem.name + '」，放回背包。');
  }

  // ---------- 技能悬停提示：卡片内只放摘要，完整描述与镶嵌技能石随鼠标显示 ----------
  const SKILL_TYPE_LABELS = {
    melee: '近战技能', spell: '法术', summon: '召唤技能', heal: '恢复技能', toggle: '持续技能'
  };

  function hideSkillTip() {
    if (ui.skillTip) ui.skillTip.classList.add('hidden');
  }

  /* ============================================================
   * 装备悬停提示：鼠标停在装备/背包物品上 → 小窗口显示该装备属性，移开即消失
   * （和技能提示同一套定位方式：贴在目标旁边，放不下就换到另一侧）
   * ============================================================ */
  function hideItemTip() {
    if (ui.itemTip) ui.itemTip.classList.add('hidden');
  }

  function itemTipLines(item) {
    const lines = [];
    const detail = String(item.detail || '').trim();
    if (detail) detail.split('　').filter(Boolean).forEach(function (t) { lines.push(t); });
    return lines;
  }

  function showItemTip(anchor, item) {
    if (!ui.itemTip || !anchor || !item || item.type !== 'equipment') return;
    const tip = ui.itemTip;
    const wrapEl = ui.wrap || document.getElementById('wrap');
    const rar = RARITY_DEFS[item.rarity] || RARITY_DEFS.common;
    tip.textContent = '';
    tip.style.borderColor = item.rarityColor || rar.color || 'rgba(255, 214, 142, .5)';

    // 标题：品级 + 装备名
    const head = document.createElement('div');
    head.className = 'tipHead';
    const dot = document.createElement('i');
    dot.style.background = item.rarityColor || rar.color;
    const name = document.createElement('b');
    name.textContent = item.name;
    name.style.color = item.rarityColor || '#ffe6bb';
    const tier = document.createElement('span');
    const slotDef = SLOT_DEFS[item.slot];
    tier.textContent = (rar.rarityName || rar.name) + ' · ' + (slotDef ? slotDef.label : '装备');
    tier.style.color = item.rarityColor || '#a8d1ff';
    head.appendChild(dot);
    head.appendChild(name);
    head.appendChild(tier);
    tip.appendChild(head);

    // 数值区
    const mods = document.createElement('div');
    mods.className = 'tipMods';
    const lvLine = document.createElement('em');
    lvLine.textContent = '装备等级 ' + (item.level || 1) +
      (item.weaponType ? '　·　武器类型 ' + item.weaponType : '') +
      (item.reforgeCount ? '　·　已洗练 ' + item.reforgeCount + ' 次' : '');
    mods.appendChild(lvLine);

    const lines = itemTipLines(item);
    if (!lines.length) {
      const empty = document.createElement('b');
      empty.textContent = '（这件装备没有词缀）';
      mods.appendChild(empty);
    } else {
      lines.forEach(function (t) {
        const line = document.createElement('b');
        line.textContent = '· ' + t;
        mods.appendChild(line);
      });
    }
    // 已装备的顺带提示怎么卸下 / 洗练
    if (item.slot && equipment[item.slot] === item) {
      const hint = document.createElement('em');
      hint.textContent = '左键卸下　·　右上角按钮洗练';
      mods.appendChild(hint);
    }
    tip.appendChild(mods);

    // 定位：优先放右边，右边放不下放左边
    tip.classList.remove('hidden');
    tip.style.left = '0px';
    tip.style.top = '0px';
    const wrapRect = wrapEl.getBoundingClientRect();
    const aRect = anchor.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    let left = aRect.right - wrapRect.left + 12;
    if (left + tw > wrapRect.width - 6) left = aRect.left - wrapRect.left - tw - 12;
    let top = aRect.top - wrapRect.top + aRect.height / 2 - th / 2;
    left = Math.max(6, Math.min(left, wrapRect.width - tw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - th - 6));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
  }

  // 给一个元素挂上"悬停显示装备属性"
  function bindItemTip(el, itemGetter) {
    if (!el) return el;
    el.addEventListener('mouseenter', function () {
      const item = typeof itemGetter === 'function' ? itemGetter() : itemGetter;
      if (item && item.type === 'equipment') showItemTip(el, item);
    });
    el.addEventListener('mouseleave', hideItemTip);
    return el;
  }

  function showSkillTip(card, skill) {
    if (!ui.skillTip || !skill || !card) return;
    const tip = ui.skillTip;
    tip.textContent = '';

    // 标题行：技能名 + 等级 + 类型
    const head = document.createElement('div');
    head.className = 'tipHead';
    const dot = document.createElement('i');
    dot.style.background = '#ffd08a';
    const name = document.createElement('b');
    name.textContent = skill.name + (skill.level ? ' Lv.' + skill.level : '');
    const type = document.createElement('span');
    type.textContent = SKILL_TYPE_LABELS[skill.type] || '技能';
    type.style.color = '#a8d1ff';
    head.appendChild(dot);
    head.appendChild(name);
    head.appendChild(type);
    tip.appendChild(head);

    // 数值行：魔力消耗 / 基础伤害 / 魔力占用
    const mods = document.createElement('div');
    mods.className = 'tipMods';
    const costLine = document.createElement('b');
    costLine.textContent = skill.id === 'summonPet'
      ? '魔力消耗 0（法杖被动）'
      : '魔力消耗 ' + skill.cost;
    mods.appendChild(costLine);
    if (skill.dmg) {
      const dmgLine = document.createElement('b');
      dmgLine.textContent = skill.id === 'yangClaw'
        ? '每次基础伤害 ' + skill.dmg[0] + '~' + skill.dmg[1] + '（连续 6 次，逐次独立结算）'
        : '基础伤害 ' + skill.dmg[0] + '~' + skill.dmg[1];
      mods.appendChild(dmgLine);
    }
    if (skill.type === 'summon') {
      const reserveLine = document.createElement('b');
      reserveLine.textContent = '召唤技能：占用 50% 魔力上限';
      mods.appendChild(reserveLine);
    } else if (skill.type === 'toggle') {
      const reserveLine = document.createElement('b');
      reserveLine.textContent = '持续技能：占用 35% 魔力上限';
      mods.appendChild(reserveLine);
    }
    if (skill.level > 1) {
      const lvLine = document.createElement('b');
      lvLine.textContent = '等级加成 +' + levelBonusPct(skill) + '%' +
        (skill.id === 'slash' || skill.id === 'yangClaw' ? '（每级 +10% 基础伤害）' : '');
      mods.appendChild(lvLine);
    }
    tip.appendChild(mods);

    // 完整描述（卡片里被截断的部分在这里看全）
    const desc = document.createElement('div');
    desc.className = 'tipDesc';
    desc.textContent = skill.desc || '';
    tip.appendChild(desc);

    // 已镶嵌的技能石 + 槽位情况
    const socketList = skill.sockets || [];
    const slots = socketSlotsOf(skill);
    const box = document.createElement('div');
    box.className = 'tipSockets';
    const title = document.createElement('em');
    title.textContent = '镶孔 ' + socketList.length + ' / ' + slots +
      (slots < SOCKET_SLOTS_MAX ? '（每个 +500 金币开槽，上限 ' + SOCKET_SLOTS_MAX + '）' : ' · 已开满');
    box.appendChild(title);
    if (socketList.length) {
      socketList.forEach(function (gem) {
        const line = document.createElement('b');
        line.textContent = '· ' + gem.name + '：' + (gem.detail || '');
        box.appendChild(line);
      });
    } else {
      const line = document.createElement('b');
      line.textContent = '· 还没有镶嵌技能石';
      box.appendChild(line);
    }
    tip.appendChild(box);

    // 先显示再量尺寸，位置以 #wrap 为基准（保证不被窗口 / 屏幕裁掉）
    const wrapEl = ui.wrap || document.body;
    tip.classList.remove('hidden');
    tip.style.left = '0px';
    tip.style.top = '0px';
    const wrapRect = wrapEl.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;
    // 法术窗口贴右侧边缘，所以提示优先放在卡片左侧，左侧放不下再放右侧
    let left = cardRect.left - wrapRect.left - tw - 12;
    if (left < 6) left = cardRect.right - wrapRect.left + 12;
    let top = cardRect.top - wrapRect.top + cardRect.height / 2 - th / 2;
    left = Math.max(6, Math.min(left, wrapRect.width - tw - 6));
    top = Math.max(6, Math.min(top, wrapRect.height - th - 6));
    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
  }

  function renderSpellList() {
    ui.spellList.textContent = '';
    hideSkillTip();
    // 非战斗状态：提示只能查看，不能施法
    const canCastNow = gameState === 'battle' && !!activeEnemy;
    ui.spellHint.classList.toggle('hidden', canCastNow);
    // 自动冒险的优先技能提示
    if (ui.spellActiveHint) {
      const fav = autoSkillId
        ? knownSkills.filter(function (s) { return s.id === autoSkillId; })[0] : null;
      ui.spellActiveHint.textContent = fav
        ? '★ 已激活「' + fav.name + '」：自动冒险时会优先使用它（用不了时退回劈砍 / 普通攻击）'
        : '点击技能格右上角的「☆ 激活」，自动冒险时会优先使用这个技能（同时只能激活一个）';
      ui.spellActiveHint.classList.toggle('on', !!fav);
    }
    // 固定 2 行 × 3 列槽位，空槽位以虚线圈出，方便看到新技能将出现的位置
    const totalSlots = Math.max(6, knownSkills.length);
    for (let i = 0; i < totalSlots; i++) {
      const skill = knownSkills[i];
      if (!skill) {
        const empty = document.createElement('div');
        empty.className = 'spellCard slot';
        ui.spellList.appendChild(empty);
        continue;
      }
      const isFav = autoSkillId === skill.id;
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'spellCard' + (isFav ? ' favored' : '');

      // 右上角「激活」按钮：自动冒险时优先使用这个技能
      const favBtn = document.createElement('span');
      favBtn.className = 'skillActiveBtn' + (isFav ? ' on' : '');
      favBtn.textContent = isFav ? '★ 已激活' : '☆ 激活';
      favBtn.title = isFav
        ? '已激活：自动冒险会优先使用它（点击取消）'
        : '激活后，自动冒险时会优先使用这个技能';
      favBtn.addEventListener('click', function (e) {
        e.stopPropagation();
        toggleAutoSkill(skill);
      });
      card.appendChild(favBtn);

      const info = document.createElement('div');
      info.className = 'spellInfo';
      const name = document.createElement('div');
      name.className = 'spellName';
      name.textContent = skill.name + ' Lv.' + (skill.level || 1);
      const desc = document.createElement('div');
      desc.className = 'spellDesc';
      desc.textContent = skill.desc +
        (skill.level > 1 ? '（等级加成 +' + levelBonusPct(skill) + '%）' : '');
      info.appendChild(name);
      info.appendChild(desc);

      const sockets = document.createElement('div');
      sockets.className = 'spellSockets';
      const socketList = skill.sockets || (skill.sockets = []);
      const slots = socketSlotsOf(skill);
      socketList.forEach(function (gem) {
        const chip = document.createElement('span');
        chip.className = 'gemChip';
        chip.textContent = gem.detail;
        chip.title = '左键点击可将技能石取回背包';
        chip.addEventListener('click', function (e) {
          e.stopPropagation();
          unsocketGem(skill, gem);
        });
        sockets.appendChild(chip);
      });
      for (let s = socketList.length; s < slots; s++) {
        const empty = document.createElement('span');
        empty.className = 'gemChip empty';
        empty.textContent = '◇';
        empty.title = '空镶孔（把技能石镶嵌进来即可生效）';
        sockets.appendChild(empty);
      }
      // 还没开满 6 个槽：给一个花 500 金币开槽的小按钮
      if (slots < SOCKET_SLOTS_MAX) {
        const buy = document.createElement('span');
        buy.className = 'gemChip slotBuy';
        buy.textContent = '+500';
        buy.title = '花费 ' + SOCKET_SLOT_COST + ' 金币，给「' + skill.name +
          '」增加 1 个镶嵌槽（当前 ' + slots + ' / ' + SOCKET_SLOTS_MAX + '）';
        buy.addEventListener('click', function (e) {
          e.stopPropagation();
          buySocketSlot(skill);
        });
        sockets.appendChild(buy);
      }

      const meta = document.createElement('div');
      meta.className = 'spellMeta';
      meta.textContent = (SKILL_TYPE_LABELS[skill.type] || '技能').replace('技能', '') +
        ' · 魔力 ' + skill.cost;

      card.appendChild(info);
      card.appendChild(meta);
      card.appendChild(sockets);
      // 持续型技能显示当前开关状态
      if (skill.type === 'toggle') {
        const state = document.createElement('div');
        state.className = 'spellMeta';
        state.textContent = bloodRebound ? '● 已激活（持续生效）' : '○ 未激活（点击开启）';
        state.style.color = bloodRebound ? '#7dffa8' : '#c9b391';
        card.appendChild(state);
      }
      card.addEventListener('click', function () {
        hideSkillTip();
        if (gameState !== 'battle' || !activeEnemy) {
          // 非战斗状态：窗口保留，只提示不能施法
          showToast('路上没有敌人，法术只能在战斗中使用。');
          return;
        }
        closeSpellPanel();
        castSkill(skill);
      });
      // 鼠标停在技能格上：显示完整描述与镶嵌的技能石，移开即关闭
      card.addEventListener('mouseenter', function () { showSkillTip(card, skill); });
      card.addEventListener('mouseleave', hideSkillTip);
      ui.spellList.appendChild(card);
    }
  }

  // 战斗中使用已学会的技能
  function castSkill(skill) {
    if (gameState !== 'battle' || !activeEnemy) return;
    if (pendingVictory > 0) return;
    if (battleTurn !== 'player') return;
    if (skill.id === 'summonPet') {
      setBattleLog('「召唤宠物」由法杖被动生效，可用辅助宝石强化宠物。');
      return;
    }
    if (slashFx) {
      setBattleLog('上一招尚未落下，稍等片刻。');
      return;
    }
    if (combat.mp < skill.cost) {
      setBattleLog('魔力不足，无法施展「' + skill.name + '」。');
      updateBattleHud();
      return;
    }
    combat.mp -= skill.cost;
    resetPlayerIdleTimer();

    // 持续型开关技能：气血反弹（激活后一直生效，可再次点击关闭）
    if (skill.type === 'toggle') {
      bloodRebound = !bloodRebound;
      recalcPlayerStats(); // 持续技能开关会改变魔力占用（35%）
      // 激活瞬间来一圈更粗的血色波纹，关闭时让已有波纹自然散尽
      bloodWaveTimer = 0;
      if (bloodRebound) {
        spawnBloodWave(1.8, 0);
        spawnBloodWave(1.2, 0.16);
      }
      const toggleMsg = bloodRebound
        ? '「' + skill.name + '」已激活：每次被敌人攻击都会反噬 ' + bloodReboundValue() + ' 点伤害。'
        : '「' + skill.name + '」已关闭，不再反噬敌人。';
      setBattleLog(toggleMsg);
      pushBattleLog(
        (bloodRebound ? '激活' : '关闭') + '「' + skill.name + '」持续状态',
        bloodRebound ? 'reward' : 'system'
      );
      updateBattleHud();
      // 用同一条提示进入敌人回合，避免被“准备反击”覆盖掉
      beginEnemyTurn(toggleMsg);
      return;
    }

    // 恢复术
    if (skill.type === 'heal') {
      const heal = Math.max(1, Math.round(combat.maxHp * 0.35 * skillLevelMult(skill)));
      combat.hp = Math.min(combat.maxHp, combat.hp + heal);
      addDamagePopup(
        player.worldX - cameraX() - 82,
        GROUND_Y - 160,
        heal,
        '#6dff8d',
        true
      );
      setBattleLog('你施展「恢复术」，恢复 ' + heal + ' 点生命。');
      updateBattleHud();
      beginEnemyTurn();
      return;
    }

    // 召唤宠物：召唤出来的伙伴会一直跟随玩家（战斗结束也不消失）
    if (skill.type === 'summon') {
      const lvMult = skillLevelMult(skill);
      // 召唤物→素材映射：蝎子 / 鬣狗 / 毒蛇 / 猎鹰（蝙蝠沿用猎鹰的飞行素材）
      const profile = skill.id === 'summonScorpion' ? { sprite: 'scorpio', atkMult: 1.1 }
        : skill.id === 'summonHyena' ? { sprite: 'hyena', atkMult: 1.15 }
        : skill.id === 'summonSnake' ? { sprite: 'snake', atkMult: 1.0 }
        : skill.id === 'summonVulture' ? { sprite: 'vulture', atkMult: 1.05 }
        : skill.id === 'summonBats' ? { sprite: 'vulture', atkMult: 1.3 }
        : { sprite: 'scorpio', atkMult: 1.1 };
      profile.name = PET_SPRITES[profile.sprite].name;
      if (alivePets().length >= PET_MAX) {
        setBattleLog('出战宠物已达上限（' + PET_MAX + ' 只），无法再召唤。');
        updateBattleHud();
        return;
      }
      const summoned = {
        name: profile.name,
        tier: 'magic',
        level: petLevelOf('magic'),
        sprite: profile.sprite,
        source: '召唤技能「' + skill.name + '」',
        sourceKind: 'skill',
        atkMult: profile.atkMult * lvMult,
        hpMult: 1,
        hp: 1,
        maxHp: 1,
        hurtT: 0,
        animT: 0,
        attackT: 0,
        walkT: rand(0, 10),
        skillId: skill.id
      };
      addPet(summoned);
      refreshPetStats();
      summoned.hp = summoned.maxHp;
      recalcPlayerStats(); // 召唤物在场 → 占用 50% 魔力上限
      if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
      setBattleLog(
        '你召唤出「' + profile.name + '」，它会一直跟随你作战！' +
        '（当前出战 ' + alivePets().length + ' 只宠物）'
      );
      updateBattleHud();
      beginEnemyTurn();
      return;
    }

    // 世界BOSS：境界不足时，所有伤害类技能一律 MISS（治疗 / 召唤等辅助技能不受影响）
    if (worldBossRun && skill.dmg && !worldBossLevelOk()) {
      playPlayerAttack();
      worldBossMiss('你使出「' + skill.name + '」，但境界差距太大，打在「' +
        worldBossRun.def.name + '」身上毫无效果——MISS');
      beginEnemyTurn();
      return;
    }

    // 「森特欧斯的囚笼」：强控敌人 2 秒 + 100% 智力伤害（头骨圣物附带）
    if (skill.id === 'santosCage') {
      playPlayerAttack();
      const intTotal = Math.max(1, derived.intTotal || 1);
      const dmg = Math.max(1, Math.round(intTotal));      // 100% 智力
      activeEnemy.stunT = Math.max(activeEnemy.stunT || 0, WB_CAGE_SECONDS);
      activeEnemy.hp = Math.max(0, activeEnemy.hp - dmg);
      activeEnemy.hurtT = HURT_TIME;
      addDamagePopup(activeEnemy.x - cameraX() + 88, GROUND_Y - 148, dmg, '#c08cff');
      spawnCageFx();
      setBattleLog('你施展「森特欧斯的囚笼」，把' + enemyLabel(activeEnemy, false) +
        '困在原地 ' + WB_CAGE_SECONDS + ' 秒（无法移动与攻击），并造成 ' + dmg + ' 点伤害（100% 智力）。');
      pushBattleLog('「森特欧斯的囚笼」：' + enemyLabel(activeEnemy, false) + ' −' + dmg +
        '，并被困住 ' + WB_CAGE_SECONDS + ' 秒', 'player');
      updateBattleHud();
      if (activeEnemy.hp <= 0) {
        if (!resolveEnemyDown()) {
          afterPlayerOffense();
          updateBattleHud();
          return;
        }
        pendingVictory = 0.4;
        return;
      }
      afterPlayerOffense();
      updateBattleHud();
      return;
    }

    // “劈砍”走完整演出：刀光出现 -> 劈到敌人身上 -> 结算伤害
    if (skill.id === 'slash') {
      playPlayerAttack(); // 血刀劈落时角色一起挥砍
      const lvMult = skillDamageLevelMult(skill);   // 劈砍：每级 +10% 基础伤害
      slashFx = {
        t: 0,
        dur: SLASH_DUR,
        impactAt: SLASH_IMPACT,
        enemyWx: activeEnemy.x,
        hit: false,
        particles: [],
        dmg: [Math.round(skill.dmg[0] * lvMult), Math.round(skill.dmg[1] * lvMult)]
      };
      setBattleLog('你施展「劈砍」，血刀高高举起，向敌人当头劈落！');
      updateBattleHud();
      return;
    }

    // 其它伤害技能：火球术 / 雷电术 / 闪电箭 / 破空斩
    if (!skill.dmg) return;
    // 智力提供的「法术伤害提高」只作用于法术类技能，不作用于近战技能
    const attrSpell = skill.type === 'spell' ? (derived.spellDmgPct || 0) : 0;
    const mult = (1 + (skillGemSum(skill.id, ['weaponDamage', 'meleeDamage', 'spellDamage']) + attrSpell) / 100) *
      skillDamageLevelMult(skill);
    // 辅助技能石（处决 / 多重投射物）对主目标的伤害总增总降
    const supMainMult = supportDamageMult(skill.id, activeEnemy);
    const baseSkillDmg = Math.max(1, Math.round(
      (randInt(skill.dmg[0], skill.dmg[1]) + Math.floor(activeEnemy.level / 3)) * mult * supMainMult
    ));

    // 「破空斩」是群攻：一条直线上的所有敌人一起吃剑气（洞府多对一时尤其明显）
    if (skill.id === 'airSlash') {
      // 特效：连续 2 道剑气向前扩散，飞到屏幕最右侧边界消失
      playPlayerAttack();
      startAirSlashFx();
      const targets = battleAlive();
      const parts = [];
      targets.forEach(function (e) {
        // 每个敌人单独判定：处决对低血敌人加成（被辅助技能造成的伤害）
        const supT = supportDamageMult(skill.id, e);
        const base = Math.max(1, Math.round(
          (randInt(skill.dmg[0], skill.dmg[1]) + Math.floor(e.level / 3)) * mult * supT
        ));
        const hit = rollPlayerDamage(base);
        e.hp = Math.max(0, e.hp - hit.dmg);
        e.hurtT = HURT_TIME;
        caveReflectOnPlayerHit(e, hit.dmg);   // 洞府「反伤」增幅
        addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 148, hit.dmg, hit.crit ? '#ff4d4d' : '#ffb34d');
        parts.push(enemyLabel(e, false) + ' −' + hit.dmg + (hit.crit ? '(暴击)' : ''));
        maybeApplyBurn(e, skill.id);   // 炎军：剑气命中
      });
      const projCount = projectileCountOf(skill.id, AIRSLASH_FX_WAVES);
      const supNote = (function () {
        const notes = [];
        if (skillHasGem(skill.id, 'multipleProjectiles')) notes.push('多重投射物 ×' + projCount + ' 道（伤害总降 25%、速度总降 20%）');
        if (skillHasGem(skill.id, 'projectileSpeed')) notes.push('投射物加速 +40%');
        if (skillHasGem(skill.id, 'execute')) {
          const bits = [];
          if (playerLowLife()) bits.push('你低血 +30%');
          if (targets.some(enemyLowLife)) bits.push('对低血敌人 +30%');
          if (bits.length) notes.push('处决：' + bits.join('、'));
        }
        return notes.length ? '（' + notes.join('；') + '）' : '';
      })();
      setBattleLog('你使出「' + skill.name + '」（群攻·贯穿一线' + supNote + '）：' + parts.join('，'));
      pushBattleLog('「' + skill.name + '」群攻命中 ' + targets.length + ' 个敌人：' + parts.join('，'), 'player');
      updateBattleHud();
      if (activeEnemy && activeEnemy.hp <= 0) {
        if (!resolveEnemyDown()) {
          afterPlayerOffense();
          updateBattleHud();
          return;
        }
        pendingVictory = 0.4;
        return;
      }
      afterPlayerOffense();
      updateBattleHud();
      return;
    }

    // 「阳爪」：裂伤挥斩主目标（距离越远伤害越高，最多 +100%），并让被裂伤波及的敌人流血
    if (skill.id === 'yangClaw') {
      playPlayerAttack();  // 出手动作：和劈砍一样带动角色挥砍
      startYangClawFx();   // 特效：2 秒内白色虚影分身突进
      const main = activeEnemy;
      const dist = Math.abs(main.x - player.worldX);
      const distBonus = Math.min(1, dist / 600);        // 0~600px → +0%~+100%
      // 6 次连击：每次 6~10 点基础伤害，逐次独立结算与显示
      const hits = [];
      let totalDmg = 0;
      for (let i = 0; i < YANG_CLAW_HITS; i++) {
        const base = Math.max(1, Math.round(
          (randInt(YANG_CLAW_MIN, YANG_CLAW_MAX) + Math.floor(main.level / 3)) *
          mult * supMainMult * (1 + distBonus)
        ));
        const hit = rollPlayerDamage(base);
        main.hp = Math.max(0, main.hp - hit.dmg);
        main.hurtT = HURT_TIME;
        caveReflectOnPlayerHit(main, hit.dmg);   // 洞府「反伤」增幅（每次连击单独结算）
        totalDmg += hit.dmg;
        addDamagePopup(
          main.x - cameraX() + 88 + (i % 2 === 0 ? -30 : 30),
          GROUND_Y - 148 - i * 16,
          hit.dmg,
          hit.crit ? '#ff4d4d' : '#ffb34d'
        );
        hits.push({ n: i + 1, dmg: hit.dmg, crit: hit.crit });
        maybeApplyBurn(main, skill.id);        // 炎军：每一次连击都判定
        if (main.hp <= 0) break;               // 打死了就不再加后面的段数
      }
      lastSkillCalc = { id: skill.id, level: skill.level || 1, mult: Math.round(mult * 1000) / 1000,
        distBonus: Math.round(distBonus * 1000) / 1000, hits: hits.length, total: totalDmg };

      // 裂伤：被挥斩波及的敌人（同一战场的敌人）全部陷入流血
      const bleedTurns = 3;
      const bleedDmg = Math.max(1, Math.round(totalDmg * 0.25 / Math.max(1, hits.length)));
      const hitList = battleAlive();
      hitList.forEach(function (e) { e.bleed = { dmg: bleedDmg, turns: bleedTurns }; });

      let logText = '你使出「' + skill.name + '」（6 连击·共 ' + totalDmg + ' 点）：' +
        hits.map(function (h) { return '第' + h.n + '击 ' + h.dmg + (h.crit ? '(暴击)' : ''); }).join('、') +
        '（距离加成 +' + Math.round(distBonus * 100) + '%）；裂伤使 ' + hitList.length +
        ' 个敌人流血（每回合 ' + bleedDmg + ' 点，持续 ' + bleedTurns + ' 回合）';

      // 辅助「震撼大地」：脚步震裂大地，冲击波随机 1~5 次（每次独立计算、独立显示）
      const hasQuake = (skill.sockets || []).some(function (g) { return g.effect === 'earthshaker'; });
      if (hasQuake) {
        const waves = randInt(1, 5);
        const targets = battleAlive().slice();
        const waveParts = [];
        let quakeTotal = 0;
        for (let i = 0; i < waves; i++) {
          spawnShockWave(i * 0.1);
          const perParts = [];
          targets.forEach(function (e) {
            if (e.hp <= 0) return;
            // 每一次冲击波单独掷伤害（不再整轮共用同一个数）
            const atkBase = randInt(23 + derived.flatAtk, 34 + derived.flatAtk);
            const quakeDmg = Math.max(1, Math.round(atkBase * 0.5 + derived.strengthTotal));
            const h = rollPlayerDamage(quakeDmg);
            e.hp = Math.max(0, e.hp - h.dmg);
            e.hurtT = HURT_TIME;
            caveReflectOnPlayerHit(e, h.dmg);   // 洞府「反伤」增幅
            quakeTotal += h.dmg;
            addDamagePopup(
              e.x - cameraX() + 88 + (i % 2 === 0 ? -26 : 26),
              GROUND_Y - 112 - i * 14,
              h.dmg,
              '#ffd280'
            );
            maybeApplyBurn(e, skill.id);   // 炎军：冲击波也算一次伤害
            perParts.push(enemyLabel(e, false) + ' −' + h.dmg + (h.crit ? '(暴击)' : ''));
          });
          if (perParts.length) waveParts.push('第' + (i + 1) + '次 ' + perParts.join('、'));
        }
        logText += '；辅助「震撼大地」触发 ' + waves + ' 次冲击波（共 ' + quakeTotal + ' 点）：' + waveParts.join('；');
      }
      setBattleLog(logText + '。');
      pushBattleLog(logText, 'player');
      updateBattleHud();
      if (main.hp <= 0) {
        if (!resolveEnemyDown()) {
          afterPlayerOffense();
          updateBattleHud();
          return;
        }
        pendingVictory = 0.4;
        return;
      }
      afterPlayerOffense();
      updateBattleHud();
      return;
    }

    const hitInfo = rollPlayerDamage(baseSkillDmg);
    const dmg = hitInfo.dmg;
    activeEnemy.hp -= dmg;
    activeEnemy.hurtT = HURT_TIME;
    caveReflectOnPlayerHit(activeEnemy, dmg);   // 洞府「反伤」增幅
    addDamagePopup(
      activeEnemy.x - cameraX() + 88,
      GROUND_Y - 148,
      dmg,
      hitInfo.crit ? '#ff4d4d' : '#ffb34d'
    );
    setBattleLog('你使出「' + skill.name + '」，造成 ' + dmg + ' 点伤害' +
      (hitInfo.crit ? '（暴击）' : '') + '。');
    if (activeEnemy.hp <= 0) {
      activeEnemy.hp = 0;
      setBattleLog('「' + skill.name + '」击败了拦路者！');
      updateBattleHud();
      if (!resolveEnemyDown()) {
        afterPlayerOffense();
        updateBattleHud();
        return;
      }
      pendingVictory = 0.4;
      return;
    }
    afterPlayerOffense();
    updateBattleHud();
  }

  // ---------- 森特欧斯的图腾：每回合随机召唤法术 ----------
  const TOTEM_SPELLS = [
    { id: 'meteor', name: '陨石', color: '#ff9a44' },
    { id: 'tree', name: '树木', color: '#7de08a' },
    { id: 'spore', name: '有毒的孢子', color: '#b6e05a' }
  ];
  const totemFx = [];   // 图腾法术的小特效

  // 每回合开始（玩家回合）随机触发一种法术
  function totemTurnSpell() {
    if (!hasSantosRelic('totem')) return false;
    if (gameState !== 'battle' || !activeEnemy || pendingVictory > 0) return false;
    const spell = TOTEM_SPELLS[randInt(0, TOTEM_SPELLS.length - 1)];
    const intTotal = Math.max(1, derived.intTotal || 1);
    const targets = battleAlive().length ? battleAlive() : [activeEnemy];
    const parts = [];
    let note = '';
    targets.forEach(function (e) {
      let dmg = 0;
      if (spell.id === 'meteor') {
        dmg = Math.round(intTotal * 1.2 + randInt(10, 26));
        note = '（120% 智力）';
      } else if (spell.id === 'tree') {
        dmg = Math.round(intTotal * 0.8);
        e.rootT = Math.max(e.rootT || 0, 1);
        note = '（80% 智力 + 缠绕 1 回合）';
      } else {
        dmg = Math.round(intTotal * 0.5);
        e.poison = { dmg: Math.max(1, Math.round(intTotal * 0.3)), turns: 3 };
        note = '（50% 智力 + 中毒 3 回合）';
      }
      dmg = Math.max(1, dmg);
      e.hp = Math.max(0, e.hp - dmg);
      e.hurtT = HURT_TIME;
      addDamagePopup(e.x - cameraX() + 88, GROUND_Y - 128, dmg, spell.color);
      parts.push(enemyLabel(e, false) + ' −' + dmg);
      spawnTotemFx(spell.id, e.x);
    });
    const msg = '图腾法术「' + spell.name + '」' + (parts.length ? '：' + parts.join('，') : '');
    setBattleLog(msg);
    pushBattleLog(msg + note, 'player');
    updateBattleHud();
    if (activeEnemy && activeEnemy.hp <= 0) {
      if (resolveEnemyDown()) pendingVictory = 0.4;
    }
    return true;
  }

  function spawnTotemFx(kind, wx) {
    totemFx.push({ kind: kind, wx: wx, t: 0, life: kind === 'meteor' ? 0.75 : 0.95 });
  }

  function updateTotemFx(dt) {
    for (let i = totemFx.length - 1; i >= 0; i--) {
      totemFx[i].t += dt;
      if (totemFx[i].t >= totemFx[i].life) totemFx.splice(i, 1);
    }
  }

  function drawTotemFx() {
    if (!totemFx.length) return;
    ctx.save();
    totemFx.forEach(function (f) {
      const sx = f.wx - cameraX();
      const p = clamp(f.t / f.life, 0, 1);
      const a = (p < 0.15 ? p / 0.15 : 1 - (p - 0.15) / 0.85);
      ctx.globalAlpha = Math.max(0, a);
      if (f.kind === 'meteor') {
        // 陨石：从天上砸下来 + 落地冲击环
        const y = GROUND_Y - 420 + p * 420;
        ctx.fillStyle = 'rgba(255, 150, 60, .5)';
        ctx.fillRect(sx - 7, y - 90, 14, 90);
        ctx.beginPath();
        ctx.arc(sx, y, 17, 0, Math.PI * 2);
        ctx.fillStyle = '#ffb055';
        ctx.fill();
        if (p > 0.75) {
          ctx.strokeStyle = 'rgba(255, 160, 80, ' + ((1 - p) * 3).toFixed(3) + ')';
          ctx.lineWidth = 5;
          ctx.beginPath();
          ctx.ellipse(sx, GROUND_Y + 4, 30 + (p - 0.75) * 500, 12 + (p - 0.75) * 90, 0, 0, Math.PI * 2);
          ctx.stroke();
        }
      } else if (f.kind === 'tree') {
        // 树木：从地面窜出几根树苗把敌人缠住
        ctx.strokeStyle = '#6fbf6a';
        ctx.lineWidth = 6;
        for (let i = -2; i <= 2; i++) {
          const h = (86 + Math.abs(i) * -12) * Math.min(1, p * 2);
          ctx.beginPath();
          ctx.moveTo(sx + i * 22, GROUND_Y + 4);
          ctx.quadraticCurveTo(sx + i * 30, GROUND_Y - h * 0.6, sx + i * 40, GROUND_Y - h);
          ctx.stroke();
        }
      } else {
        // 有毒的孢子：绿色毒雾团
        for (let i = 0; i < 4; i++) {
          const r = 26 + i * 16 + p * 40;
          ctx.fillStyle = 'rgba(150, 210, 80, ' + (0.22 * (1 - p)).toFixed(3) + ')';
          ctx.beginPath();
          ctx.arc(sx + Math.sin(i * 2 + p * 4) * 26, GROUND_Y - 90 + Math.cos(i * 3) * 26, r, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    });
    ctx.restore();
  }

  // 囚笼特效：敌人身上浮现一圈紫色符文牢笼
  const cageFx = [];
  function spawnCageFx() {
    if (!activeEnemy) return;
    cageFx.push({ wx: activeEnemy.x, t: 0, life: WB_CAGE_SECONDS });
  }
  function updateCageFx(dt) {
    for (let i = cageFx.length - 1; i >= 0; i--) {
      cageFx[i].t += dt;
      if (cageFx[i].t >= cageFx[i].life) cageFx.splice(i, 1);
    }
  }
  function drawCageFx() {
    if (!cageFx.length) return;
    ctx.save();
    cageFx.forEach(function (f) {
      const sx = f.wx - cameraX();
      const p = clamp(f.t / f.life, 0, 1);
      const a = (p < 0.1 ? p / 0.1 : 1 - (p - 0.1) / 0.9) * 0.85;
      ctx.globalAlpha = a;
      ctx.strokeStyle = '#c08cff';
      ctx.lineWidth = 3;
      // 竖着的符文柱子
      for (let i = 0; i < 5; i++) {
        const x = sx - 60 + i * 30;
        ctx.beginPath();
        ctx.moveTo(x, GROUND_Y + 4);
        ctx.lineTo(x + (i % 2 ? 4 : -4), GROUND_Y - 168);
        ctx.stroke();
      }
      // 上下两道符文环
      [GROUND_Y - 6, GROUND_Y - 166].forEach(function (y) {
        ctx.beginPath();
        ctx.ellipse(sx, y, 74, 16, 0, 0, Math.PI * 2);
        ctx.stroke();
      });
      ctx.fillStyle = 'rgba(180, 120, 255, .18)';
      ctx.beginPath();
      ctx.ellipse(sx, GROUND_Y - 84, 76, 92, 0, 0, Math.PI * 2);
      ctx.fill();
    });
    ctx.restore();
  }

  // 学习新技能的唯一下发点：击败 BOSS 掉落的技能书
  function learnSkillFromBook(skillDef) {
    if (!knownSkills.some(function (s) { return s.id === skillDef.id; })) {
      knownSkills.push(skillDef);
    }
  }

  function enemyCounterAttack(opening, idleSurprise) {
    // 玩家出手后的敌方回合：先结算流血 + 燃烧（「炎军」叠加的层数）
    if (applyBleedTick() && activeEnemy && activeEnemy.hp <= 0) {
      if (resolveEnemyDown()) { pendingVictory = 0.4; return; }
    }
    if (applyBurnTick() && activeEnemy && activeEnemy.hp <= 0) {
      if (resolveEnemyDown()) { pendingVictory = 0.4; return; }
    }
    // 玩家身上的「点燃」（洞府敌人的增幅）：同样每个敌方回合结算一次
    if (applyPlayerBurnTick() > 0 && combat.hp <= 0) return;   // 被烧死就交给阵亡流程
    // 被「森特欧斯的囚笼」/「树木」控住的敌人：这一回合无法移动与攻击
    if (activeEnemy && (activeEnemy.stunT > 0 || activeEnemy.rootT > 0)) {
      const label = enemyLabel(activeEnemy, false);
      const why = activeEnemy.stunT > 0 ? '被「森特欧斯的囚笼」困住' : '被树根缠住';
      if (activeEnemy.rootT > 0) activeEnemy.rootT -= 1;
      setBattleLog(label + why + '，这一回合无法移动与攻击！');
      pushBattleLog(label + why + '，无法行动', 'player');
      enemyHelpersTurn();
      if (gameState === 'battle' && activeEnemy && pendingVictory <= 0) {
        battleTurn = 'player';
        resetPlayerIdleTimer();
        setControls('battle');
        applyTurnRegen();
        totemTurnSpell();
        updateBattleHud();
      }
      defending = false;   // 敌人这回合动不了，防御姿态也随之结束
      return;
    }
    // 主目标先出手（内部可能因为格挡 / 恢复 / 被反伤打死而提前结束这次出手）
    enemyCounterAttackCore(opening, idleSurprise);
    // 同一场战斗里的其他敌人不受影响，照样一起围攻玩家（真正的多对一）
    enemyHelpersTurn();
    // 回到玩家回合：结算「生命每回合再生」
    if (gameState === 'battle' && activeEnemy && pendingVictory <= 0) {
      applyTurnRegen();
      totemTurnSpell();   // 「森特欧斯的图腾」：每回合随机召唤一种法术
    }
    defending = false;    // 一轮打完，防御姿态清除
    // 玩家被击晕 / 冰冻：这一回合直接跳过，敌人再动一次（计时器触发）
    if (gameState === 'battle' && activeEnemy && pendingVictory <= 0 && consumePlayerSkipTurn()) return;
  }

  // 围攻：同一场战斗里除当前目标外的敌人都会攻击玩家
  function enemyHelpersTurn() {
    if (gameState !== 'battle' || !activeEnemy || pendingVictory > 0) return;
    const helpers = battleAlive().filter(function (e) { return e !== activeEnemy; });
    if (helpers.length) {
      const reduction = clamp(
        derived.armor / (derived.armor + 120) + derived.res / 250 + derived.chaosRes / 300,
        0,
        0.85
      );
      const parts = [];
      // 每个围攻者用不同的颜色，名字相同也能一眼区分
      const HIT_COLORS = ['#ff6a5a', '#ffb24d', '#ff7fd0', '#8fd0ff'];
      // 伤害数字要贴在"被打的玩家"身上，不是贴在敌人身上
      const px = player.worldX - cameraX();
      helpers.forEach(function (e, hi) {
        const raw = Math.max(1, Math.round(e.atk * 0.7 * rand(0.85, 1.15)));
        const dmg = Math.max(1, Math.round(raw * (1 - reduction)));
        combat.hp = Math.max(0, combat.hp - dmg);   // 允许被打到 0（触发阵亡）
        e.hurtT = 0;
        const label = enemyLabel(e);
        const color = HIT_COLORS[hi % HIT_COLORS.length];
        // 多个围攻者同时打中：都飘在玩家身侧，按 34px 一层往上错开，
        // 起点比"主目标那一击"（GROUND_Y-148）高一档，避免两处数字叠在一起
        const stackY = GROUND_Y - 190 - hi * 34;
        addTextPopup(px - 96, stackY - 30, label, color);
        addDamagePopup(px - 96, stackY, dmg, color);
        pushBattleLog(label + '围攻，你损失 ' + dmg + ' 点生命', 'enemy');
        parts.push(label + ' −' + dmg);
      });
      player.hurtT = HURT_TIME;
      // 战报按敌人逐条列出，不再合成一个总数
      setBattleLog('围攻：' + parts.join('，'));
      updateBattleHud();
    }
    // 洞府里被围攻后，附近的敌人还可能继续加入
    if (cave) tryCaveJoinBattle();
  }

  function enemyCounterAttackCore(opening, idleSurprise) {
    const isOpening = !!opening;
    const isIdle = !!idleSurprise;
    let skillNote = '';
    let mult = 1;
    // 传奇BOSS出手：地面震一下
    if (activeEnemy && activeEnemy.legend) enemyQuake = 0.45;

    // 精英自带技能
    const eliteSkill = activeEnemy.skills && activeEnemy.skills.length
      ? activeEnemy.skills[randInt(0, activeEnemy.skills.length - 1)]
      : activeEnemy.skill;
    if (activeEnemy.elite && eliteSkill) {
      const sk = eliteSkill;
      if (sk.id === 'heal' && !activeEnemy.healUsed && activeEnemy.hp <= activeEnemy.maxHp * 0.5) {
        const heal = Math.max(1, Math.round(activeEnemy.maxHp * 0.12));
        activeEnemy.healUsed = true;
        activeEnemy.hp = Math.min(activeEnemy.maxHp, activeEnemy.hp + heal);
        setBattleLog('精英拦路者施展「恢复术」，恢复了 ' + heal + ' 点生命！');
        updateBattleHud();
        battleTurn = 'player';
        resetPlayerIdleTimer();
        setControls('battle');
        return;
      }
      if (sk.id === 'fireball') { mult = 1.6; skillNote = '火球术'; }
      else if (sk.id === 'lightning') { mult = 1.5; skillNote = '雷电术'; }
      else if (sk.id === 'summonEnemy') {
        skillNote = '召唤普通敌人';
        enemyMinionTurns = 3;
        // 召唤物也随机挑一种敌人美术，面朝玩家对面的敌人
        ally = { turns: 3, sprite: ENEMY_SPRITES[randInt(0, ENEMY_SPRITES.length - 1)].key };
      }
    }

    let raw = randInt(activeEnemy.atk, activeEnemy.atk + 5);
    if (mult > 1) raw = Math.round(raw * mult);
    if (enemyMinionTurns > 0) {
      raw += Math.round(activeEnemy.atk * 0.6);
      enemyMinionTurns -= 1;
      skillNote = skillNote ? '召唤物协助' : '召唤物';
    }

    // 出手者写出具体是谁（洞府里可能同时有好几个敌人）
    const attackerName = enemyLabel(activeEnemy);
    const who = skillNote
      ? attackerName + '施展「' + skillNote + '」'
      : (isOpening ? attackerName + '抢先出手'
        : isIdle ? '你迟迟没有攻击，' + attackerName + '抢先进攻'
        : attackerName + '反击');

    // 宠物在场时敌人有 50% 概率先攻击宠物
    if (petAlive() && Math.random() < 0.5) {
      // 随机挑一只出战宠物挨打
      const targets = alivePets();
      const victim = targets[randInt(0, targets.length - 1)];
      const victimSlot = pets.indexOf(victim);
      victim.hp -= raw;
      victim.hurtT = HURT_TIME;
      addDamagePopup(
        petScreenX(Math.max(0, victimSlot)),
        GROUND_Y - 86,
        raw,
        '#ff8f72'
      );
      if (victim.hp <= 0) {
        victim.hp = 0;
        pets.splice(pets.indexOf(victim), 1);
        recalcPlayerStats(); // 宠物消失 → 释放召唤类占用的魔力
        if (!ui.petPopup.classList.contains('hidden')) renderPetPanel();
        setBattleLog(who + '，「' + victim.name + '」被重创倒下了！');
      } else {
        setBattleLog(who + '，「' + victim.name + '」损失 ' + raw + ' 点生命。');
      }
    } else {
      // 格挡判定：成功则完全免伤（格挡几率上限 75%）
      if (derived.blockPct > 0 && Math.random() * 100 < derived.blockPct) {
        addTextPopup(player.worldX - cameraX() - 88, GROUND_Y - 148, '格挡', '#9fd4ff');
        const rbBlock = applyBloodRebound();
        setBattleLog(who + '，你成功格挡了这次攻击！' +
          (rbBlock ? '（气血反弹反噬 ' + rbBlock + ' 点）' : ''));
        if (rbBlock && (!activeEnemy || activeEnemy.hp <= 0)) return;
        battleTurn = 'player';
        resetPlayerIdleTimer();
        setControls('battle');
        updateBattleHud();
        return;
      }
      // 护甲 + 元素抗性 + 混沌抗性 共同提供减伤
      const reduction = clamp(
        derived.armor / (derived.armor + 120) + derived.res / 250 + derived.chaosRes / 300,
        0,
        0.85
      );
      // 敌人暴击：普通 5% / 蓝色 8% / 精英 12% / 传奇 15%，暴击伤害 1.5 倍（被"暴击伤害降低"削减）
      const critChance = activeEnemy.legend ? 15
        : activeEnemy.elite ? 12
        : activeEnemy.tier === 'magic' ? 8 : 5;
      const isCrit = Math.random() * 100 < critChance;
      const critMult = isCrit ? Math.max(1, 1.5 - derived.critReduce / 100) : 1;

      // 「防御」姿态：受到的伤害 -35%，另有 5% 几率完全格挡
      if (defending) {
        if (Math.random() * 100 < DEFEND_BLOCK_PCT) {
          addTextPopup(player.worldX - cameraX() - 88, GROUND_Y - 152, '完全格挡', '#9fd4ff');
          const rbD = applyBloodRebound();
          setBattleLog(who + '，你举盾完全格挡了这次攻击！' +
            (rbD ? '（气血反弹反噬 ' + rbD + ' 点）' : ''));
          pushBattleLog(who + '：被你完全格挡（' + DEFEND_BLOCK_PCT + '% 几率）', 'player');
          if (rbD && activeEnemy && activeEnemy.hp <= 0) return;
          battleTurn = 'player';
          resetPlayerIdleTimer();
          setControls('battle');
          updateBattleHud();
          return;
        }
        raw = Math.max(1, Math.round(raw * (1 - DEFEND_REDUCE)));
      }
      const dmg = Math.max(1, Math.round(raw * (1 - reduction) * critMult));
      combat.hp = Math.max(0, combat.hp - dmg);   // 允许被打到 0（触发阵亡）
      player.hurtT = HURT_TIME;
      addDamagePopup(
        player.worldX - cameraX() - 88,
        GROUND_Y - 148,
        dmg,
        isCrit ? '#ff4d4d' : '#ff8f72'
      );
      // 荆棘反伤：被攻击时把荆棘伤害反弹给敌人
      let reflect = 0;
      if (derived.thorns > 0 && activeEnemy) {
        reflect = derived.thorns;
        activeEnemy.hp -= reflect;
        activeEnemy.hurtT = HURT_TIME;
        addDamagePopup(
          activeEnemy.x - cameraX() + 88,
          GROUND_Y - 200,
          reflect,
          '#ffb066'
        );
        if (activeEnemy.hp <= 0) {
          activeEnemy.hp = 0;
          setBattleLog('荆棘反伤 ' + reflect + ' 点，敌人被反伤击败！');
          updateBattleHud();
          if (!resolveEnemyDown()) return;
          pendingVictory = 0.5;
          battleTurn = 'player';
          resetPlayerIdleTimer();
          setControls('battle');
          return;
        }
      }
      // 气血反弹（持续生效）：每次被敌人攻击都会反噬
      const rebound = applyBloodRebound();
      if (rebound && activeEnemy && activeEnemy.hp <= 0) return;
      setBattleLog(
        who + (isCrit ? '（暴击）' : '') + '，你损失 ' + dmg + ' 点生命。' +
        (defending ? '（防御减伤 ' + Math.round(DEFEND_REDUCE * 100) + '%）' : '') +
        (reflect ? '（荆棘反伤 ' + reflect + ' 点）' : '') +
        (rebound ? '（气血反弹反噬 ' + rebound + ' 点）' : '')
      );
      // 洞府敌人增幅：命中后的附加效果（震慑 / 点燃 / 寒冰）
      caveEnemyOnHit(activeEnemy);
    }
    battleTurn = 'player';
    resetPlayerIdleTimer();
    setControls('battle');
    updateBattleHud();

    // 其余敌人的围攻统一在 enemyHelpersTurn() 里处理（由外层 enemyCounterAttack 调用）
  }

  // ---------- “防御”护盾特效 ----------
  // 资源：pipo-btleffect208（192×192，20 帧）
  // 触发点：点击右下角「防御」→ 护盾在角色身上撑开，防御姿态期间一直裹着角色，
  //         敌方回合结束（defending 变 false）后收盾（原淡蓝护盾光弧已移除）
  let defendFx = null; // { t, phase: 'in' | 'hold' | 'out' }

  function defendSpriteReady() {
    const first = images.defend_1;
    return !!(first && first.complete && first.naturalWidth > 0);
  }

  function defendLoadedFrames() {
    let n = 0;
    for (let i = 1; i <= DEFEND_FX_FRAMES; i++) {
      const im = images['defend_' + i];
      if (im && im.complete && im.naturalWidth > 0) n++;
    }
    return n;
  }

  function startDefendFx() {
    defendFx = { t: 0, phase: 'in' };
  }

  function updateDefendFx(dt) {
    if (!defendFx) return;
    defendFx.t += dt;
    if (defendFx.phase === 'in') {
      // 撑开播完：还在防御就转"维持"，防御已经结束就直接收盾
      if (defendFx.t >= DEFEND_FX_IN_DUR) {
        defendFx.phase = defending ? 'hold' : 'out';
        defendFx.t = 0;
      }
    } else if (defendFx.phase === 'hold') {
      // 防御姿态结束（敌方回合打完 / 敌人被控住没出手）→ 收盾
      if (!defending) {
        defendFx.phase = 'out';
        defendFx.t = 0;
      }
    } else if (defendFx.t >= DEFEND_FX_OUT_DUR) {
      defendFx = null;
    }
  }

  // 按阶段取当前该画第几帧（收盾阶段也用于整体淡出）
  function defendFxFrameNo() {
    if (!defendFx) return 0;
    if (defendFx.phase === 'in') {
      const k = Math.max(0, Math.min(1, defendFx.t / DEFEND_FX_IN_DUR));
      return Math.min(DEFEND_FX_IN_TO, DEFEND_FX_IN_FROM + Math.floor(k * (DEFEND_FX_IN_TO - DEFEND_FX_IN_FROM + 1)));
    }
    if (defendFx.phase === 'hold') {
      const span = DEFEND_FX_HOLD_TO - DEFEND_FX_HOLD_FROM + 1;
      return DEFEND_FX_HOLD_FROM + (Math.floor(defendFx.t * DEFEND_FX_HOLD_FPS) % span);
    }
    const k = Math.max(0, Math.min(1, defendFx.t / DEFEND_FX_OUT_DUR));
    return Math.min(DEFEND_FX_OUT_TO, DEFEND_FX_OUT_FROM + Math.floor(k * (DEFEND_FX_OUT_TO - DEFEND_FX_OUT_FROM + 1)));
  }

  // 贴图按"盾面重心对齐"摆放：盾心钉在角色身上，护盾把角色整个裹住。
  // 贴图中心是不透明的实心光团，所以分两层画：角色后面一层完整的（当背景光罩），
  // 角色前面再盖一层半透明的 —— 角色始终看得见，但整体被护盾"包"住。
  const DEFEND_FX_FRONT_ALPHA = 0.34;

  function drawDefendFxLayer(alphaScale, withTrace) {
    if (!defendFx || !defendSpriteReady()) return;
    const idx = defendFxFrameNo();
    const img = images['defend_' + idx];
    if (!img) return;
    const scale = playerFx.scale * caveScale();
    const size = DEFEND_FX_SIZE * scale;
    const sx = player.worldX - cameraX();
    const ax = sx;                                    // 护盾圆心 = 角色身上（不再偏向敌人一侧）
    const ay = GROUND_Y - DEFEND_FX_LIFT * scale;     // 胸腹之间
    const dx = ax - DEFEND_FX_ORIGIN.x * size;
    const dy = ay - DEFEND_FX_ORIGIN.y * size;
    ctx.save();
    ctx.imageSmoothingEnabled = false;                // 像素风：不插值
    // 收盾阶段整体淡出，避免"啪"地消失
    const fade = defendFx.phase === 'out'
      ? Math.max(0, 1 - Math.min(1, defendFx.t / DEFEND_FX_OUT_DUR) * 0.85)
      : 1;
    ctx.globalAlpha = playerFx.alpha * fade * alphaScale;
    if (DEFEND_FX_FLIP) {
      // 水平镜像：护盾从角色前方撑开
      ctx.translate(dx + size, dy);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0, size, size);
    } else {
      ctx.drawImage(img, dx, dy, size, size);
    }
    if (withTrace && fxTrace.length < 600) {
      fxTrace.push({
        kind: 'defend', t: Math.round(defendFx.t * 1000), phase: defendFx.phase, frame: idx,
        flip: DEFEND_FX_FLIP,
        x: Math.round(dx), y: Math.round(dy), size: Math.round(size),
        anchorX: Math.round(ax), anchorY: Math.round(ay)
      });
    }
    ctx.restore();
  }

  // 画在角色之后（背景光罩）
  function drawDefendFxBehind() {
    drawDefendFxLayer(1, true);
  }

  // 画在角色之前（薄薄一层，做出"被护盾裹住"的感觉，同时不遮住角色）
  function drawDefendFxFront() {
    drawDefendFxLayer(DEFEND_FX_FRONT_ALPHA, false);
  }

  // ---------- “破空斩”剑气特效 ----------
  // 资源：HolySlash_B（64×64，4 帧红色弧形剑气）
  // 表现：连放 2 道 → 向前扩散 → 飞到屏幕最右侧边界淡出消失
  let airSlashFx = null; // { waves: [{ t, big }] }

  function airSlashSpriteReady() {
    const first = images.holyslash_1;
    return !!(first && first.complete && first.naturalWidth > 0);
  }

  // 出招：第一道立刻出发，后续几道依次跟上（t 为负数表示还没出场）
  // 辅助技能石：「多重投射物」+2 道、出手间隔 +25%（技能速度 -20%）；「投射物加速」飞行速度 ×1.4
  // 多投射物时按"齐射"处理：几道几乎同时出发、上下散开成扇形（不然间隔太大，屏幕上一次只看得到 1~2 道）
  function airSlashWaveGap(count) {
    const n = count || projectileCountOf('airSlash', AIRSLASH_FX_WAVES);
    const volley = n > AIRSLASH_FX_WAVES;
    return (volley ? AIRSLASH_FX_GAP * 0.5 : AIRSLASH_FX_GAP) / skillSpeedMult('airSlash');
  }

  function startAirSlashFx() {
    const id = 'airSlash';
    const count = projectileCountOf(id, AIRSLASH_FX_WAVES);
    const speedMult = projectileSpeedMult(id);
    const gap = airSlashWaveGap(count);
    // 多道剑气时稍微上下散开，看起来才像扇形扩散
    const spread = [-18, 6, -8, 14, -12, 10];
    const waves = [];
    for (let i = 0; i < count; i++) {
      waves.push({
        t: -i * gap,
        big: i > 0,
        speedMult: speedMult,
        yOff: count > AIRSLASH_FX_WAVES ? spread[i % spread.length] : (i > 0 ? 6 : 0)
      });
    }
    airSlashFx = { waves: waves };
  }

  // 剑气当前在屏幕上的 x（世界坐标 → 屏幕坐标，战斗中镜头不动）
  function airSlashWaveScreenX(w) {
    return player.worldX + AIRSLASH_FX_START + AIRSLASH_FX_SPEED * (w.speedMult || 1) * w.t - cameraX();
  }

  function updateAirSlashFx(dt) {
    if (!airSlashFx) return;
    const waves = airSlashFx.waves;
    for (let i = waves.length - 1; i >= 0; i--) {
      const w = waves[i];
      w.t += dt;
      if (w.t < 0) continue;                       // 还在"连续释放"的间隔里
      // 冲到屏幕最右侧边界之外就回收（视觉上是在右边界处淡出消失）
      if (airSlashWaveScreenX(w) - AIRSLASH_FX_SIZE * 0.5 > W + 30) waves.splice(i, 1);
    }
    if (!waves.length) airSlashFx = null;
  }

  function drawAirSlashFx() {
    if (!airSlashFx || !airSlashSpriteReady()) return;
    const scale = caveScale();
    const cy = GROUND_Y - AIRSLASH_FX_LIFT * scale;
    airSlashFx.waves.forEach(function (w) {
      if (w.t < 0) return;
      const size = AIRSLASH_FX_SIZE * scale * (w.big ? 1.12 : 1);
      const sx = airSlashWaveScreenX(w);
      const cyW = cy + (w.yOff || 0) * scale;
      const idx = Math.floor(w.t * AIRSLASH_FX_FPS) % AIRSLASH_FX_FRAMES;
      const img = images['holyslash_' + (idx + 1)];
      if (!img) return;
      // 起手快速淡入 + 抵达右边界前淡出
      const fadeIn = Math.min(1, w.t / 0.06);
      const edgeFade = sx > W - 150 ? Math.max(0, 1 - (sx - (W - 150)) / 150) : 1;
      ctx.save();
      ctx.imageSmoothingEnabled = false;          // 像素风：不插值
      ctx.globalAlpha = playerFx.alpha * fadeIn * edgeFade;
      ctx.drawImage(img, sx - size / 2, cyW - size / 2, size, size);
      ctx.restore();
      if (fxTrace.length < 600) {
        fxTrace.push({
          kind: 'airslash', t: Math.round(w.t * 1000), frame: idx + 1,
          x: Math.round(sx - size / 2), y: Math.round(cyW - size / 2), size: Math.round(size),
          centerX: Math.round(sx), centerY: Math.round(cyW)
        });
      }
    });
  }

  // ---------- “劈砍”特效 ----------
  // 刀光资源：itch.io「Pixel Art Animations - Slashes」的红色 9 帧刀光（128×128）
  const SLASH_FRAMES = 9;
  const SLASH_SPRITE_SIZE = 380; // 屏幕上的绘制尺寸（像素风放大）
  // 「劈砍」刀光贴图中心相对地面的高度：数值越小越靠下。
  // 该贴图内刀光的可视重心约在竖直 48% 处，中心压到 84px 时刀身正好横过敌人胸腹。
  const SLASH_SPRITE_LIFT = 84;
  function slashSpriteReady() {
    const first = images.slash1_1;
    return !!(first && first.complete && first.naturalWidth > 0);
  }

  // 普通攻击用的轻量刀光（同一套贴图，更小更快，不影响回合与施法）
  let attackFx = null;
  const ATTACK_FX_DUR = 0.42;
  const ATTACK_SPRITE_SIZE = 200; // 普通攻击刀光（Slash 3，64×64 放大）
  // 刀光贴图中心相对地面的高度：数值越小越靠下。
  // 贴图内刀光的可视重心在竖直方向约 30% 处（偏上），所以中心要压到敌人胸口以下，
  // 可视部分才会正好落在敌人身上（约 52px ≈ 贴图中心落在膝盖~腰部之间，刀身横过胸腹）
  const ATTACK_SPRITE_LIFT = 52; // 普通攻击：刀光直接砍在敌人身上
  function attackSpriteReady() {
    const first = images.slash3_1;
    return !!(first && first.complete && first.naturalWidth > 0);
  }
  function startAttackFx(enemyWx) {
    if (!attackSpriteReady()) return;
    attackFx = { t: 0, dur: ATTACK_FX_DUR, enemyWx: enemyWx };
  }

  function updateAttackFx(dt) {
    if (!attackFx) return;
    attackFx.t += dt;
    if (attackFx.t >= attackFx.dur) attackFx = null;
  }

  function drawAttackFx() {
    if (!attackFx || !attackSpriteReady()) return;
    const p = Math.min(1, attackFx.t / attackFx.dur);
    const idx = Math.min(SLASH_FRAMES - 1, Math.floor(p * SLASH_FRAMES));
    // 普通攻击用 Slash 3（64×64）这套刀光
    const img = images['slash3_' + (idx + 1)];
    const size = ATTACK_SPRITE_SIZE;
    const ex = attackFx.enemyWx - cameraX();
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.globalAlpha = Math.min(1, (1 - p) * 1.9); // 快速淡出
    ctx.drawImage(
      img,
      ex - size * 0.42,
      GROUND_Y - ATTACK_SPRITE_LIFT - size * 0.5,
      size,
      size
    );
    if (fxTrace.length < 600) {
      fxTrace.push({ kind: 'attack', t: Math.round(attackFx.t * 1000), frame: idx + 1, x: Math.round(ex - size * 0.42), y: Math.round(GROUND_Y - ATTACK_SPRITE_LIFT - size * 0.5), size: size });
    }
    ctx.restore();
  }

  // 刀光路径（资源缺失时的兜底）：以敌人胸腹为落点，从左上高处一路劈下。
  // 用三次贝塞尔曲线采样，程序化绘制 = 免费自绘特效，无第三方许可负担。
  function slashCurvePoint(u) {
    const inv = 1 - u;
    const x =
      -105 * inv * inv * inv +
      3 * 20 * inv * inv * u +
      3 * 85 * inv * u * u +
      42 * u * u * u;
    const y =
      -265 * inv * inv * inv +
      3 * (-240) * inv * inv * u +
      3 * (-45) * inv * u * u +
      6 * u * u * u;
    return { x: x, y: y };
  }

  function spawnSlashParticles(fx) {
    const baseX = fx.enemyWx - cameraX();
    const baseY = GROUND_Y - 90; // 与刀光的落点一致：火花从敌人胸腹处迸出
    for (let i = 0; i < 18; i++) {
      const angle = rand(0, Math.PI * 2);
      const speed = rand(55, 255);
      fx.particles.push({
        x: baseX + rand(-10, 18),
        y: baseY + rand(-8, 10),
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 65,
        life: rand(0.32, 0.68),
        t: 0,
        size: rand(2, 5.5),
        color: ['#ff3b2a', '#ff6a3c', '#ffd6a0'][randInt(0, 2)]
      });
    }
  }

  // 刀光劈落并命中：在这里结算伤害
  function slashImpact() {
    if (!activeEnemy) {
      slashFx = null;
      return;
    }
    const baseSlashDmg = Math.max(1, Math.round(
      (randInt(slashFx.dmg[0], slashFx.dmg[1]) + Math.floor(activeEnemy.level / 3)) *
      gemDamageMult('slash') * supportDamageMult('slash', activeEnemy)
    ));
    const slashHit = rollPlayerDamage(baseSlashDmg);
    const dmg = slashHit.dmg;
    activeEnemy.hp -= dmg;
    activeEnemy.hurtT = HURT_TIME;
    addDamagePopup(
      activeEnemy.x - cameraX() + 88,
      GROUND_Y - 168,
      dmg,
      slashHit.crit ? '#ff2b2b' : '#ff402a'
    );
    maybeApplyBurn(activeEnemy, 'slash');   // 炎军：劈砍命中
    spawnSlashParticles(slashFx);
    setBattleLog('「劈砍」的血刀劈中身体，造成 ' + dmg + ' 点伤害' +
      (slashHit.crit ? '（暴击）' : '') + '！');
    if (activeEnemy.hp <= 0) {
      activeEnemy.hp = 0;
      setBattleLog('「劈砍」化作一道血光，击败了拦路者！');
      updateBattleHud();
      if (!resolveEnemyDown()) {
        afterPlayerOffense();
        updateBattleHud();
        return;
      }
      pendingVictory = 0.5;
      return;
    }
    afterPlayerOffense();
    updateBattleHud();
  }

  function updateSlashFx(dt) {
    if (!slashFx) return;
    slashFx.t += dt;

    if (!slashFx.hit && slashFx.t >= slashFx.impactAt) {
      slashFx.hit = true;
      slashImpact();
    }

    // 命中火花自行飘散
    for (let i = slashFx.particles.length - 1; i >= 0; i--) {
      const p = slashFx.particles[i];
      p.t += dt;
      p.vy += 420 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.t >= p.life) slashFx.particles.splice(i, 1);
    }

    if (slashFx.t >= slashFx.dur) slashFx = null;
  }

  // 画“从刀柄到刀尖”的当前一段刀光（随劈落进度延伸）
  function strokeSlashPath(ex, hy, progress, width, color, alpha) {
    const N = 26;
    const raw = Math.max(0.005, progress);
    const k = (N - 1) * Math.min(1, raw);
    const i = Math.floor(k);
    const frac = k - i;
    const end = slashCurvePoint((i + Math.min(1, frac + 0.001)) / (N - 1));
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    for (let n = 0; n <= Math.min(i, N - 1); n++) {
      const pt = slashCurvePoint(n / (N - 1));
      const x = ex + pt.x;
      const y = hy + pt.y;
      if (n === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    if (i < N - 1 && i >= 0) {
      ctx.lineTo(ex + end.x, hy + end.y);
    }
    ctx.stroke();
    ctx.restore();
  }

  function drawSlashFx() {
    if (!slashFx) return;
    const ex = slashFx.enemyWx - cameraX();
    const hy = GROUND_Y - 96; // 兜底刀光的锚点：刀尖落在敌人胸腹（贴图资源正常时走贴图分支）
    const p = Math.min(1, slashFx.t / slashFx.dur);
    const impactRatio = slashFx.impactAt / slashFx.dur;
    const cutP = Math.min(1, slashFx.t / slashFx.impactAt);
    const impactAge = slashFx.hit
      ? (slashFx.t - slashFx.impactAt) / (slashFx.dur - slashFx.impactAt)
      : 0;

    // 整体淡入 + 收尾淡出
    const fadeIn = Math.min(1, slashFx.t / 0.08);
    const fadeOut = slashFx.hit
      ? Math.max(0, Math.min(1, 1.25 - impactAge * 1.1)) // 收刀后仍保留一会儿，让刀光看得清
      : 1;
    const trailAlpha = Math.max(0, Math.min(1, fadeIn * fadeOut));
    if (trailAlpha <= 0.01 && slashFx.particles.length === 0) return;

    // ① 刀光本体：优先用 itch 的像素刀光资源（9 帧循环播完一次）
    if (slashSpriteReady()) {
      const idx = Math.min(SLASH_FRAMES - 1, Math.floor(p * SLASH_FRAMES));
      const img = images['slash1_' + (idx + 1)];
      // 每一帧的亮部大小不同，后段略微放大一点，收刀更利落
      const size = SLASH_SPRITE_SIZE * (1 + p * 0.12);
      ctx.save();
      ctx.imageSmoothingEnabled = false; // 像素风：不插值
      ctx.globalAlpha = trailAlpha;
      ctx.drawImage(
        img,
        ex - size * 0.52,
        GROUND_Y - SLASH_SPRITE_LIFT - size * 0.5,
        size,
        size
      );
      if (fxTrace.length < 600) {
        fxTrace.push({ kind: 'slash', t: Math.round(slashFx.t * 1000), frame: idx + 1, x: Math.round(ex - size * 0.52), y: Math.round(GROUND_Y - SLASH_SPRITE_LIFT - size * 0.5), size: Math.round(size), enemyX: Math.round(ex), enemyTop: Math.round(GROUND_Y - 178 * enemyDrawScale(activeEnemy) * caveScale() + 4) });
      }
      ctx.restore();
    } else {
      // 兜底：资源没加载出来时用原来程序化绘制的血刀
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      strokeSlashPath(ex, hy, cutP, 34, 'rgba(255, 45, 20, .26)', trailAlpha);
      strokeSlashPath(ex, hy, cutP, 16, 'rgba(255, 92, 42, .58)', trailAlpha);
      strokeSlashPath(ex, hy, cutP, 4.5, 'rgba(255, 238, 218, .78)', trailAlpha);
      if (cutP < 1) {
        const tip = slashCurvePoint(Math.max(0.04, cutP));
        const next = slashCurvePoint(Math.min(1, cutP + 0.035));
        const dx = next.x - tip.x;
        const dy = next.y - tip.y;
        const len = Math.max(0.001, Math.hypot(dx, dy));
        const g = ctx.createRadialGradient(
          ex + tip.x, hy + tip.y, 0,
          ex + tip.x, hy + tip.y, 26
        );
        g.addColorStop(0, 'rgba(255, 245, 235, .95)');
        g.addColorStop(0.28, 'rgba(255, 128, 72, .75)');
        g.addColorStop(1, 'rgba(255, 45, 20, 0)');
        ctx.globalAlpha = trailAlpha;
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.ellipse(ex + tip.x, hy + tip.y, 20, 12, Math.atan2(dy, dx), 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 0.8 * trailAlpha;
        ctx.strokeStyle = '#fff4e0';
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(ex + tip.x - dx / len * 22, hy + tip.y - dy / len * 22);
        ctx.lineTo(ex + tip.x, hy + tip.y);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
      ctx.restore();
    }

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // 命中瞬间的红色闪光
    if (slashFx.hit) {
      // 命中闪光收敛一些，避免盖住刀光本体
      const flash = Math.max(0, 1 - impactAge * 4) * 0.62;
      const fx = ex;
      const fy = GROUND_Y - 92; // 命中闪光跟着刀光落在敌人身上
      const radius = 28 + impactAge * 66;
      const fg = ctx.createRadialGradient(fx, fy, 0, fx, fy, radius);
      fg.addColorStop(0, 'rgba(255, 250, 240, ' + (1.1 * flash).toFixed(3) + ')');
      fg.addColorStop(0.22, 'rgba(255, 105, 55, ' + (0.75 * flash).toFixed(3) + ')');
      fg.addColorStop(1, 'rgba(255, 30, 15, 0)');
      ctx.globalAlpha = 1;
      ctx.fillStyle = fg;
      ctx.beginPath();
      ctx.arc(fx, fy, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    // 红色火花（普通叠加，保证醒目且不依赖混合模式）
    for (let i = 0; i < slashFx.particles.length; i++) {
      const sp = slashFx.particles[i];
      const a = Math.max(0, 1 - sp.t / sp.life);
      ctx.save();
      ctx.globalAlpha = a;
      ctx.fillStyle = sp.color;
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, sp.size * (0.55 + a * 0.45), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = a * 0.25;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(sp.x, sp.y, sp.size * 0.38 * a, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  // ---------- 回合顺序 / 受击 / 伤害数字 / 击败演出 ----------
  function updateTurnTimer(dt) {
    if (gameState !== 'battle' || battleTurn !== 'enemy' || pendingVictory > 0) return;
    if (worldBossLootActive()) return;   // BOSS已倒下、正在开箱阶段：敌人不再出手
    if (!activeEnemy) return;
    enemyTurnTimer -= dt;
    if (enemyTurnTimer <= 0) {
      enemyTurnTimer = 0;
      const opening = activeEnemy.openingAttack === true;
      activeEnemy.openingAttack = false;
      enemyCounterAttack(opening);
    }
  }

  function updateDamageFx(dt) {
    enemyAnimClock += dt; // 敌人帧动画时钟
    // 新加入的敌人小跑到阵位（当前敌人身后）
    battleGroup.forEach(function (e) {
      if (e.moveToX === undefined) return;
      e.x += (e.moveToX - e.x) * Math.min(1, dt * 6);
      if (Math.abs(e.moveToX - e.x) < 2) {
        e.x = e.moveToX;
        delete e.moveToX;
      }
    });
    // 震动与血色划痕倒计时
    if (player.hurtT > 0) player.hurtT = Math.max(0, player.hurtT - dt);
    if (activeEnemy && activeEnemy.hurtT > 0) {
      activeEnemy.hurtT = Math.max(0, activeEnemy.hurtT - dt);
    }
    // 「森特欧斯的囚笼」：控制时间是真实秒数，到点自动解除
    if (activeEnemy && activeEnemy.stunT > 0) {
      activeEnemy.stunT = Math.max(0, activeEnemy.stunT - dt);
    }
    updateCageFx(dt);
    updateTotemFx(dt);
    pets.forEach(function (p) {
      if (p.hurtT > 0) p.hurtT = Math.max(0, p.hurtT - dt);
      if (p.hp > 0) {
        p.walkT += dt * 8;
        p.animT = (p.animT || 0) + dt;          // 精灵图播放进度
        if (p.attackT > 0) p.attackT = Math.max(0, p.attackT - dt);
      }
    });

    // 伤害数字向上飘散
    for (let i = damagePopups.length - 1; i >= 0; i--) {
      const p = damagePopups[i];
      p.t += dt;
      p.y -= 58 * dt;
      if (p.t >= p.life) damagePopups.splice(i, 1);
    }

    // 最后一击先展示 0 血与受击反馈，稍后再关闭战斗
    if (pendingVictory > 0 && gameState === 'battle') {
      pendingVictory -= dt;
      if (pendingVictory <= 0) {
        pendingVictory = 0;
        winBattle();
      }
    }
  }

  // ---------- 更新（走路状态） ----------
  function updateEnemyPatrol(dt) {
    if (!activeEnemy) return;
    activeEnemy.walkT += dt * 9;
    activeEnemy.offset += activeEnemy.dir * activeEnemy.speed * dt;
    if (activeEnemy.offset >= activeEnemy.range) {
      activeEnemy.offset = activeEnemy.range;
      activeEnemy.dir = -1;
    } else if (activeEnemy.offset <= -activeEnemy.range) {
      activeEnemy.offset = -activeEnemy.range;
      activeEnemy.dir = 1;
    }
    activeEnemy.x = activeEnemy.baseX + activeEnemy.offset;
  }

  function update(dt) {
    if (gameState !== 'walk') return;
    if (caveTransition) return; // 进洞过场期间不响应移动输入

    // 敌人始终在附近左右巡逻
    updateEnemyPatrol(dt);

    // 点击一次移动按钮后自动前进；再次点击或进入战斗即停下（鞋子可提高移速）
    const target = input.moving ? WALK_SPEED * derived.speedMult : 0;
    // 起步稍缓、收步干脆一点：按了「休息」要很快站住，别拖着走半秒
    const ease = input.moving ? 8 : 14;
    player.vx += (target - player.vx) * Math.min(1, dt * ease);
    if (Math.abs(player.vx) < 1) player.vx = 0;

    if (activeEnemy) {
      const limit = activeEnemy.x - ENEMY_GAP_PX;
      if (player.vx > 0 && player.worldX < limit) {
        player.worldX = Math.min(limit, player.worldX + player.vx * dt);
      }
      // 靠近 / 敌人巡逻靠近时开战
      if (player.worldX >= limit) {
        startBattle();
        return;
      }
    }

    if (player.vx > 0) {
      player.walkT += dt * (8 + player.vx / 50);
    } else {
      player.walkT = 0;
    }

    hudTimer += dt;
    if (hudTimer >= 0.1) {
      hudTimer = 0;
      updateDist();
      if (legendMap) {
        legendMap.km = Math.max(0, (player.worldX - legendMap.startX) / KM_UNIT);
        updateLegendBar();
      }
    }
  }

  // ---------- 绘制：天空 ----------
  function drawSky() {
    const g = ctx.createLinearGradient(0, 0, 0, 540);
    g.addColorStop(0, '#cfe8f5');
    g.addColorStop(0.42, '#f8e6bd');
    g.addColorStop(0.72, '#f6cd94');
    g.addColorStop(1, '#e9aa70');
    ctx.fillStyle = g;
    ctx.fillRect(-30, -30, W + 60, 640);

    const sx = SUN_X, sy = SUN_Y;
    // 太阳外围的柔和光晕
    const halo = ctx.createRadialGradient(sx, sy, 20, sx, sy, 320);
    halo.addColorStop(0, 'rgba(255, 242, 208, .30)');
    halo.addColorStop(0.45, 'rgba(255, 238, 196, .12)');
    halo.addColorStop(1, 'rgba(255, 238, 196, 0)');
    ctx.fillStyle = halo;
    ctx.fillRect(sx - 330, sy - 330, 660, 660);

    const glow = ctx.createRadialGradient(sx, sy, 10, sx, sy, 175);
    glow.addColorStop(0, 'rgba(255, 246, 216, .95)');
    glow.addColorStop(0.25, 'rgba(255, 232, 172, .55)');
    glow.addColorStop(1, 'rgba(255, 232, 172, 0)');
    ctx.fillStyle = glow;
    ctx.fillRect(sx - 190, sy - 190, 380, 380);
    ctx.fillStyle = '#fff6dc';
    ctx.beginPath();
    ctx.arc(sx, sy, 48, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(255, 244, 224, .16)';
    for (let i = 0; i < 4; i++) {
      const cx = 110 + i * 320 + hash01(i) * 130;
      const cy = 80 + hash01(i + 8) * 120;
      ctx.beginPath();
      ctx.ellipse(cx, cy, 135 + hash01(i + 20) * 60, 11 + hash01(i + 4) * 8, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function drawDuneLayer(color, baseY, amp, wave, phase, cam) {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(-40, H + 60);
    for (let x = -40; x <= W + 40; x += 22) {
      const y = baseY +
        Math.sin((x + cam) / wave + phase) * amp +
        Math.sin((x + cam) / (wave * 0.45) + phase * 3.1) * amp * 0.26;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(W + 40, H + 60);
    ctx.closePath();
    ctx.fill();
  }

  function drawGround(cam) {
    const groundG = ctx.createLinearGradient(0, 560, 0, H);
    groundG.addColorStop(0, '#dda876');
    groundG.addColorStop(1, '#c88e5a');
    ctx.fillStyle = groundG;
    ctx.fillRect(-30, 560, W + 60, H - 560 + 40);

    ctx.save();
    ctx.globalAlpha = 0.22;
    ctx.fillStyle = '#8a5a33';
    for (let i = 0; i < 70; i++) {
      const span = 900;
      const gx = i * (span / 70) - cam;
      const k = Math.floor(gx / span);
      const x = ((gx - k * span) + span) % span - 20;
      const y = 580 + hash01(i + k * 7.3) * 140;
      const size = 1 + hash01(i * 2.1 + k) * 2.8;
      if (hash01(i + 1.7 * k) < 0.2) continue;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    const roadG = ctx.createLinearGradient(0, 642, 0, H);
    roadG.addColorStop(0, '#a9885f');
    roadG.addColorStop(0.2, '#9a7b54');
    roadG.addColorStop(1, '#775a3b');
    ctx.fillStyle = roadG;
    ctx.fillRect(-30, 642, W + 60, H - 642 + 30);
    ctx.fillStyle = 'rgba(255, 226, 177, .14)';
    ctx.fillRect(-30, 638, W + 60, 7);

    ctx.save();
    for (let i = 0; i < 16; i++) {
      const span = 1100;
      const gx = i * (span / 16) - cam;
      const k = Math.floor(gx / span);
      const x = ((gx - k * span) + span) % span - 30;
      const seed = hash01(i * 3.17 + k * 9.1);
      const yy = 664 + hash01(i + k * 5.5) * 48;
      ctx.globalAlpha = 0.08 + seed * 0.15;
      ctx.strokeStyle = '#513b22';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(x, yy);
      ctx.lineTo(x + 5 + seed * 8, yy + 3);
      ctx.stroke();
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  function drawBackground(cam) {
    drawSky();
    drawDuneLayer('rgba(226, 177, 126, .9)', 392, 20, 330, 1.0, cam * 0.055);
    drawDuneLayer('rgba(214, 156, 104, .92)', 452, 27, 270, 2.2, cam * 0.12);
    drawDuneLayer('#cf9a69', 536, 22, 360, 4.0, cam * 0.21);
    drawGround(cam);
  }

  // ---------- 环境：远处沙暴 / 风沙 / 落叶 / 光影 ----------
  function initWeather() {
    sandFar.length = 0;
    sandNear.length = 0;
    leaves.length = 0;
    for (let i = 0; i < 70; i++) {
      sandFar.push({
        x: rand(0, W + 240),
        y: rand(300, 645),
        len: rand(10, 34),
        speed: rand(70, 170),
        alpha: rand(0.04, 0.13),
        w: rand(1, 2)
      });
    }
    for (let i = 0; i < 46; i++) {
      sandNear.push({
        x: rand(0, W + 240),
        y: rand(520, 720),
        len: rand(18, 52),
        speed: rand(220, 430),
        alpha: rand(0.06, 0.19),
        w: rand(1, 2.4)
      });
    }
    for (let i = 0; i < 10; i++) {
      leaves.push({
        x: rand(0, W + 240),
        y: rand(180, 600),
        vx: -rand(30, 90),
        vy: rand(8, 26),
        rot: rand(0, Math.PI * 2),
        vr: rand(-2.4, 2.4),
        size: rand(6, 12),
        tone: randInt(0, 2)
      });
    }
  }

  function updateWeather(dt) {
    weatherTime += dt;
    const span = W + 240;
    for (let i = 0; i < sandFar.length; i++) {
      const p = sandFar[i];
      p.x -= p.speed * dt;
      p.y += Math.sin(weatherTime * 1.6 + i) * 6 * dt;
      if (p.x < -60) p.x += span;
    }
    for (let i = 0; i < sandNear.length; i++) {
      const p = sandNear[i];
      p.x -= p.speed * dt;
      p.y += Math.sin(weatherTime * 2.4 + i * 1.7) * 10 * dt;
      if (p.x < -80) p.x += span;
    }
    for (let i = 0; i < leaves.length; i++) {
      const lf = leaves[i];
      lf.x += lf.vx * dt;
      lf.y += (lf.vy + Math.sin(weatherTime * 3 + i) * 14) * dt;
      lf.rot += lf.vr * dt;
      if (lf.x < -40) {
        lf.x = W + rand(20, 200);
        lf.y = rand(160, 560);
      }
      if (lf.y > 660) lf.y = rand(140, 300);
    }
  }

  // 远处的龙卷风（纯视觉表现，不影响任何玩法数值）
  function drawTornado(cam) {
    const span = W + 520;
    // 位置：太阳（SUN_X=995, SUN_Y=128）左下方的远处沙丘；
    // 缓慢随镜头左移，移出屏幕后从右侧循环出现
    const cx = wrapX(760 + 260 - cam * 0.05, span) - 260;
    const scale = 1.38;                             // 整体尺寸：原版 165 × 1.38 ≈ 228
    const baseY = 472;                              // 落在远处沙丘上
    const height = 165 * scale;
    const t = weatherTime;
    window.__tornado = { x: Math.round(cx), baseY: baseY, height: height }; // 调试用

    ctx.save();
    // 漏斗主体：下细上粗，逐层摆动 + 旋转，形成旋转感
    for (let i = 0; i < 9; i++) {
      const p = i / 8;                              // 0 = 底部，1 = 顶部
      const y = baseY - p * height;
      const x = cx + Math.sin(t * 1.3 + p * 2.4) * (4 + p * 14) * scale;
      const radius = (9 + p * 26) * scale;
      const spin = Math.sin(t * 2.6 + i * 1.1);
      ctx.globalAlpha = 0.36 - p * 0.11;
      const g = ctx.createLinearGradient(x - radius, 0, x + radius, 0);
      g.addColorStop(0, 'rgba(228, 198, 152, 0)');
      g.addColorStop(0.45 + spin * 0.12, 'rgba(248, 228, 192, .98)');
      g.addColorStop(1, 'rgba(228, 198, 152, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(x, y, radius, radius * 0.30, 0, 0, Math.PI * 2);
      ctx.fill();
      // 左侧稍暗，增加立体感
      ctx.globalAlpha = (0.20 - p * 0.06) * 0.7;
      ctx.fillStyle = 'rgba(150, 112, 68, 1)';
      ctx.beginPath();
      ctx.ellipse(x - radius * 0.28, y + radius * 0.04, radius * 0.52, radius * 0.22, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // 底部扬尘
    ctx.globalAlpha = 0.3;
    for (let i = 0; i < 5; i++) {
      const px = cx + Math.sin(t * 2 + i * 1.7) * (18 + i * 6) * scale;
      const pr = (14 + i * 7) * scale;
      const pg = ctx.createRadialGradient(px, baseY + 4, 2, px, baseY + 4, pr);
      pg.addColorStop(0, 'rgba(234, 206, 164, .8)');
      pg.addColorStop(1, 'rgba(234, 206, 164, 0)');
      ctx.fillStyle = pg;
      ctx.beginPath();
      ctx.ellipse(px, baseY + 4, pr, pr * 0.35, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // 顶部扩散的尘土
    ctx.globalAlpha = 0.18;
    ctx.fillStyle = '#f0dcb4';
    ctx.beginPath();
    ctx.ellipse(
      cx + Math.sin(t * 1.1) * 10 * scale,
      baseY - height - 6,
      46 * scale,
      12 * scale,
      0, 0, Math.PI * 2
    );
    ctx.fill();
    ctx.restore();
  }

  // 远处沙暴：地平线上缓缓推进的沙墙
  function drawSandstorm() {
    const t = weatherTime;
    const haze = ctx.createLinearGradient(0, 250, 0, 500);
    haze.addColorStop(0, 'rgba(232, 196, 146, 0)');
    haze.addColorStop(0.45, 'rgba(226, 184, 132, .22)');
    haze.addColorStop(1, 'rgba(214, 170, 120, 0)');
    ctx.fillStyle = haze;
    ctx.fillRect(-20, 245, W + 40, 250);

    for (let i = 0; i < 8; i++) {
      const w = 260 + hash01(i) * 260;
      const y = 296 + hash01(i + 7) * 92;
      const x = wrapX(i * 260 - t * (16 + i * 5), W + 520) - 260;
      const g = ctx.createRadialGradient(x, y, 8, x, y, w * 0.5);
      g.addColorStop(0, 'rgba(232, 198, 152, .20)');
      g.addColorStop(1, 'rgba(232, 198, 152, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(x, y, w * 0.5, 40 + hash01(i + 3) * 24, 0, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function drawWeatherBack(cam) {
    const span = W + 240;
    ctx.save();
    ctx.lineCap = 'round';
    for (let i = 0; i < sandFar.length; i++) {
      const p = sandFar[i];
      const x = wrapX(p.x - cam * 0.12, span) - 120;
      ctx.globalAlpha = p.alpha;
      ctx.strokeStyle = '#e8d0a6';
      ctx.lineWidth = p.w;
      ctx.beginPath();
      ctx.moveTo(x, p.y);
      ctx.lineTo(x - p.len, p.y + 2);
      ctx.stroke();
    }
    ctx.restore();
  }

  function drawWeatherFront(cam) {
    const span = W + 240;
    ctx.save();
    ctx.lineCap = 'round';
    for (let i = 0; i < sandNear.length; i++) {
      const p = sandNear[i];
      const x = wrapX(p.x - cam * 0.3, span) - 120;
      ctx.globalAlpha = p.alpha;
      ctx.strokeStyle = '#f0dcb4';
      ctx.lineWidth = p.w;
      ctx.beginPath();
      ctx.moveTo(x, p.y);
      ctx.lineTo(x - p.len, p.y + 3);
      ctx.stroke();
    }
    const tones = ['#c08a45', '#a8722f', '#d8b06a'];
    for (let i = 0; i < leaves.length; i++) {
      const lf = leaves[i];
      const x = wrapX(lf.x - cam * 0.34, span) - 120;
      ctx.save();
      ctx.globalAlpha = 0.75;
      ctx.translate(x, lf.y);
      ctx.rotate(lf.rot);
      ctx.fillStyle = tones[lf.tone];
      ctx.beginPath();
      ctx.ellipse(0, 0, lf.size, lf.size * 0.42, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(90, 52, 18, .5)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(-lf.size, 0);
      ctx.lineTo(lf.size, 0);
      ctx.stroke();
      ctx.restore();
    }
    ctx.restore();
  }

  // 梭梭草：沙漠常见的细枝灌木
  function drawSaxaulBush(x, baseY, sc) {
    ctx.save();
    ctx.fillStyle = 'rgba(60, 38, 14, .16)';
    ctx.beginPath();
    ctx.ellipse(x - 6 * sc, baseY + 3, 30 * sc, 6 * sc, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineCap = 'round';
    for (let i = 0; i < 9; i++) {
      const seed = hash01(x * 0.13 + i * 3.7);
      const lean = (seed - 0.5) * 1.5;
      const h = (30 + seed * 46) * sc;
      const sway = Math.sin(weatherTime * 2.2 + i) * 4 * sc;
      ctx.strokeStyle = i % 3 === 0 ? 'rgba(136, 148, 104, .9)' : 'rgba(108, 120, 80, .82)';
      ctx.lineWidth = (1.6 + seed) * sc;
      ctx.beginPath();
      ctx.moveTo(x, baseY);
      ctx.quadraticCurveTo(
        x + lean * 18 * sc + sway,
        baseY - h * 0.62,
        x + lean * 30 * sc + sway,
        baseY - h
      );
      ctx.stroke();
      ctx.lineWidth = 1 * sc;
      ctx.beginPath();
      ctx.moveTo(x + lean * 20 * sc + sway, baseY - h * 0.66);
      ctx.lineTo(x + lean * 38 * sc + sway + 8 * sc, baseY - h * 0.78);
      ctx.stroke();
    }
    ctx.restore();
  }

  // 干草簇
  function drawDryTuft(x, baseY, sc) {
    ctx.save();
    ctx.lineCap = 'round';
    for (let i = 0; i < 7; i++) {
      const seed = hash01(x * 0.21 + i * 5.1);
      const lean = (seed - 0.5) * 20;
      const h = (10 + seed * 18) * sc;
      ctx.strokeStyle = 'rgba(184, 142, 78, .78)';
      ctx.lineWidth = 1.4 * sc;
      ctx.beginPath();
      ctx.moveTo(x, baseY);
      ctx.quadraticCurveTo(x + lean * 0.4, baseY - h * 0.7, x + lean, baseY - h);
      ctx.stroke();
    }
    ctx.restore();
  }

  function drawImageFlipped(img, cx, groundY, w, h, flip) {
    ctx.save();
    if (flip) {
      ctx.translate(cx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -w / 2, groundY - h, w, h);
    } else {
      ctx.drawImage(img, cx - w / 2, groundY - h, w, h);
    }
    ctx.restore();
  }

  // ---------- 绘制：路旁装饰 ----------
  function drawDecorRow(scroll, baseY, span, scaleMax, kinds) {
    const start = Math.floor(scroll / span) - 2;
    const end = start + Math.ceil(W / span) + 4;
    for (let i = start; i <= end; i++) {
      const j = wrapInt(i, 100000);
      const seed = hash01(j);
      const x = i * span - scroll + (seed - 0.5) * span * 0.55;
      const kind = wrapInt(j, kinds);
      const sc = scaleMax * (0.72 + hash01(j + 3) * 0.55);

      if (kind === 0 || kind === 4) {
        if (images.cactus) {
          const h = 135 * sc;
          ctx.fillStyle = 'rgba(60, 38, 14, .18)';
          ctx.beginPath();
          ctx.ellipse(x - 8 * sc, baseY + 3, 34 * sc, 7 * sc, 0, 0, Math.PI * 2);
          ctx.fill();
          drawImageFlipped(images.cactus, x, baseY, h, h, false);
        }
      } else if (kind === 1) {
        ctx.strokeStyle = 'rgba(75, 52, 30, .9)';
        ctx.lineWidth = 5 * sc;
        ctx.beginPath();
        ctx.moveTo(x, baseY);
        ctx.lineTo(x, baseY - 135 * sc);
        ctx.stroke();
        ctx.strokeStyle = 'rgba(75, 52, 30, .75)';
        ctx.lineWidth = 2.2 * sc;
        ctx.beginPath();
        ctx.moveTo(x, baseY - 110 * sc);
        ctx.lineTo(x + 15 * sc, baseY - 118 * sc);
        ctx.moveTo(x, baseY - 128 * sc);
        ctx.lineTo(x - 13 * sc, baseY - 138 * sc);
        ctx.stroke();
      } else if (kind === 2 && images.rock) {
        const h = 52 * sc;
        const w = 66 * sc;
        ctx.fillStyle = 'rgba(60, 38, 14, .2)';
        ctx.beginPath();
        ctx.ellipse(x - 5 * sc, baseY + 2, 40 * sc, 7 * sc, 0, 0, Math.PI * 2);
        ctx.fill();
        drawImageFlipped(images.rock, x, baseY, w, h, false);
      } else if (kind === 3 || kind === 5) {
        ctx.fillStyle = 'rgba(176, 112, 53, .72)';
        ctx.beginPath();
        ctx.arc(x, baseY - 12 * sc, 12 * sc, Math.PI, 0);
        ctx.arc(x + 12 * sc, baseY - 7 * sc, 10 * sc, Math.PI, 0);
        ctx.arc(x - 12 * sc, baseY - 8 * sc, 10 * sc, Math.PI, 0);
        ctx.fill();
      } else if (kind === 6 || kind === 7) {
        drawSaxaulBush(x, baseY, sc * 0.95);
      } else if (kind === 8 || kind === 9) {
        drawDryTuft(x, baseY, sc);
      }
    }
  }

  function drawDecor(cam) {
    drawDecorRow(cam * 0.22, 545, 115, 0.72, 10);
    drawDecorRow(cam * 0.55, 618, 72, 1.0, 8);
  }

  function drawMilestones(cam) {
    const n0 = Math.max(1, Math.floor((cam - 300) / KM_UNIT));
    const n1 = Math.ceil((cam + W + 200) / KM_UNIT);
    for (let n = n0; n <= n1; n++) {
      const wx = START_X + n * KM_UNIT;
      const sx = wx - cam;
      if (sx < -160 || sx > W + 160) continue;

      // 直接“印”在马路路面上：压低透视并做旧
      ctx.save();
      ctx.translate(sx, 700);
      ctx.scale(1, 0.56);
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.globalAlpha = 0.9;
      ctx.fillStyle = 'rgba(70, 45, 21, .92)';
      ctx.fillText(n + 'km', 0, 0);
      ctx.globalAlpha = 0.28;
      ctx.fillStyle = '#ffeac2';
      ctx.fillText(n + 'km', -1.5, -1.5);
      ctx.globalAlpha = 0.5;
      ctx.strokeStyle = 'rgba(255, 228, 180, .28)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-26, 14);
      ctx.lineTo(26, 14);
      ctx.stroke();
      ctx.restore();
    }
  }

  // ---------- 绘制：玩家 / 敌人 ----------
  // 敌人绘制缩放：传奇 BOSS 体型是原来的 2 倍（1.32 × 2）
  const LEGEND_SCALE = 2.64;
  // 洞府内的整体缩放：玩家与敌人都缩小 25%（空间更挤，观感更像洞窟）
  const CAVE_SCALE = 0.75;
  function caveScale() {
    return cave ? CAVE_SCALE : 1;
  }
  function enemyDrawScale(enemy) {
    if (!enemy) return 1;
    if (enemy.wbScale) return enemy.wbScale;   // 世界BOSS：固定放大体型
    return enemy.legend ? LEGEND_SCALE
      : enemy.elite ? 1.16
      : enemy.tier === 'magic' ? 1.08 : 1;
  }

  function updateEnemyQuake(dt) {
    if (enemyQuake > 0) enemyQuake = Math.max(0, enemyQuake - dt);
  }

  // 地面震动偏移：传奇BOSS走动时持续轻微抖动，落步 / 出手时叠加一次更明显的余震
  function quakeOffset() {
    let amp = 0;
    if (activeEnemy && activeEnemy.legend) {
      if (gameState === 'walk') amp = 1.9;
      if (enemyQuake > 0) amp = Math.max(amp, 5.5 * (enemyQuake / 0.45));
    }
    if (amp <= 0) return { x: 0, y: 0 };
    const t = performance.now() / 1000;
    return {
      x: Math.round(Math.sin(t * 46) * amp * 10) / 10,
      y: Math.round(Math.cos(t * 63) * amp * 0.7 * 10) / 10
    };
  }

  function drawCharacterCrop(img, sx, groundY, flip, bobY, scale) {
    if (!img) return;
    const srcX = 42, srcY = 48, srcW = 176, srcH = 208;
    const sc = scale || 1;
    const dw = 148 * sc, dh = 178 * sc;
    const top = groundY - dh + 4 + (bobY || 0);
    ctx.save();
    if (flip) {
      ctx.translate(sx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, srcX, srcY, srcW, srcH, -dw / 2, top, dw, dh);
    } else {
      ctx.drawImage(img, srcX, srcY, srcW, srcH, sx - dw / 2, top, dw, dh);
    }
    ctx.restore();
  }

  // 受击震动：值从强到弱地左右/上下抖动
  function shakeOffset(hurtT) {
    if (!hurtT || hurtT <= 0) return { x: 0, y: 0 };
    const elapsed = HURT_TIME - hurtT;
    const k = hurtT / HURT_TIME;
    return {
      x: Math.sin(elapsed * 112) * 7 * k,
      y: Math.cos(elapsed * 88) * 3.5 * k
    };
  }

  // 被击中时身体上的斜向血色划痕
  function drawHurtSlash(sx, groundY, hurtT) {
    if (!hurtT || hurtT <= 0) return;
    const k = clamp(hurtT / HURT_TIME, 0, 1);
    const clipW = 160;
    const clipH = 184;
    ctx.save();
    ctx.beginPath();
    ctx.rect(sx - clipW / 2, groundY - clipH + 5, clipW, clipH);
    ctx.clip();
    ctx.lineCap = 'round';

    // 外层暗红刀伤
    ctx.strokeStyle = 'rgba(152, 8, 6, ' + (0.85 * k).toFixed(3) + ')';
    ctx.lineWidth = 17;
    ctx.beginPath();
    ctx.moveTo(sx - 66, groundY - 132);
    ctx.lineTo(sx + 58, groundY - 52);
    ctx.stroke();

    // 里层鲜红划痕
    ctx.strokeStyle = 'rgba(255, 52, 28, ' + (0.95 * k).toFixed(3) + ')';
    ctx.lineWidth = 9;
    ctx.beginPath();
    ctx.moveTo(sx - 62, groundY - 129);
    ctx.lineTo(sx + 54, groundY - 50);
    ctx.stroke();

    // 高光白刃
    ctx.strokeStyle = 'rgba(255, 224, 200, ' + (0.75 * k).toFixed(3) + ')';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(sx - 57, groundY - 125);
    ctx.lineTo(sx + 49, groundY - 47);
    ctx.stroke();
    ctx.restore();
  }

  // 敌人头顶血条与脚下名字（替代原右上角敌方属性 UI）
  function drawEnemyStatus(sx) {
    if (!activeEnemy) return;

    if (gameState === 'battle') {
      const w = 158;
      const h = 22;
      const x = sx - w / 2;
      // 体型越大，血条抬得越高（传奇 BOSS 头顶上方）
      const scale = enemyDrawScale(activeEnemy) * caveScale();
      const y = Math.max(56, GROUND_Y - 238 - Math.round((scale - 1) * 150));
      const pct = clamp(activeEnemy.hp / activeEnemy.maxHp, 0, 1);

      ctx.save();
      ctx.fillStyle = 'rgba(16, 5, 1, .68)';
      roundRect(x - 2, y - 2, w + 4, h + 4, 13);
      ctx.fill();

      ctx.fillStyle = 'rgba(84, 22, 8, .92)';
      roundRect(x, y, w, h, 11);
      ctx.fill();

      if (pct > 0.015) {
        const hpW = Math.max(8, Math.round(w * pct));
        const grad = ctx.createLinearGradient(x, y, x + w, y);
        if (activeEnemy.legend) {
          grad.addColorStop(0, '#d62a6a');
          grad.addColorStop(1, '#ffb3d6');
        } else if (activeEnemy.elite) {
          grad.addColorStop(0, '#8b3bd6');
          grad.addColorStop(1, '#ffb0ff');
        } else if (activeEnemy.tier === 'magic') {
          grad.addColorStop(0, '#2f7fd6');
          grad.addColorStop(1, '#9fd4ff');
        } else {
          grad.addColorStop(0, pct > 0.5 ? '#43b45f' : '#e26a28');
          grad.addColorStop(1, pct > 0.5 ? '#9ae88a' : '#ff9c52');
        }
        ctx.fillStyle = grad;
        roundRect(x, y, hpW, h, 11);
        ctx.fill();
      }

      ctx.fillStyle = '#fff6e8';
      ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(
        Math.max(0, Math.ceil(activeEnemy.hp)) + ' / ' + activeEnemy.maxHp,
        sx,
        y + h / 2 + 0.5
      );
      if (activeEnemy.elite && activeEnemy.skill) {
        const skillLabel = activeEnemy.skills && activeEnemy.skills.length > 1
          ? activeEnemy.skills.map(function (s) { return s.name; }).join(' / ')
          : activeEnemy.skill.name;
        ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
        ctx.lineWidth = 4;
        ctx.strokeStyle = 'rgba(30, 6, 40, .9)';
        ctx.strokeText('技能：' + skillLabel, sx, y - 9);
        ctx.fillStyle = activeEnemy.legend ? '#ffc0e4' : '#e9b6ff';
        ctx.fillText('技能：' + skillLabel, sx, y - 9);
      }
      // 流血状态：血条下方标一行，附一滴血
      if (activeEnemy.bleed && activeEnemy.bleed.turns > 0) {
        ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
        ctx.lineWidth = 4;
        ctx.strokeStyle = 'rgba(40, 0, 0, .92)';
        const bleedLabel = '流血 ' + activeEnemy.bleed.turns + ' 回合（' + Math.round(activeEnemy.bleed.dmg) + '/回合）';
        ctx.strokeText(bleedLabel, sx + 10, y + h + 12);
        ctx.fillStyle = '#ff5b5b';
        ctx.fillText(bleedLabel, sx + 10, y + h + 12);
        ctx.fillStyle = '#e01f2b';
        ctx.beginPath();
        ctx.ellipse(sx - 62, y + h + 9, 5, 7, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // 燃烧层数（「炎军」叠加）：血条左侧标出层数与每回合伤害
      if ((activeEnemy.burnStacks || 0) > 0) {
        const per = Math.max(1, Math.round(combat.maxHp * BURN_PCT));
        const burnLabel = '燃烧 ' + activeEnemy.burnStacks + ' 层（' + (per * activeEnemy.burnStacks) + '/回合）';
        ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
        ctx.lineWidth = 4;
        ctx.strokeStyle = 'rgba(52, 18, 0, .92)';
        ctx.strokeText(burnLabel, sx + 10, y + h + 30);
        ctx.fillStyle = '#ff9a3c';
        ctx.fillText(burnLabel, sx + 10, y + h + 30);
        // 小火苗
        const fx0 = sx - 70;
        const fy0 = y + h + 27;
        ctx.fillStyle = '#ff6a1f';
        ctx.beginPath();
        ctx.moveTo(fx0, fy0 + 6);
        ctx.quadraticCurveTo(fx0 - 6, fy0 - 2, fx0, fy0 - 9);
        ctx.quadraticCurveTo(fx0 + 6, fy0 - 2, fx0, fy0 + 6);
        ctx.fill();
        ctx.fillStyle = '#ffd26a';
        ctx.beginPath();
        ctx.ellipse(fx0, fy0 + 2, 2.2, 3.4, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    }

    // 名字放在敌人脚下
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.font = 'bold 17px "Microsoft YaHei", sans-serif';
    ctx.lineWidth = 5;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(28, 8, 1, .9)';
    const label = (activeEnemy.legend ? '★★ ' : activeEnemy.elite ? '★ ' : '') +
      (activeEnemy.name || '拦路者') + ' · Lv.' + activeEnemy.level;
    ctx.strokeText(label, sx, GROUND_Y + 29);
    ctx.fillStyle = activeEnemy.legend
      ? '#ffc0e4'
      : activeEnemy.elite ? '#f0c7ff'
      : activeEnemy.tier === 'magic' ? '#a9d6ff'
      : (gameState === 'battle' ? '#ffd9bd' : '#ffe9c8');
    ctx.fillText(label, sx, GROUND_Y + 29);
    ctx.restore();

    // 洞府增幅标记：名字下面挂一个小色块，让玩家一眼看出这怪带什么效果
    if (gameState === 'battle' && activeEnemy.caveBuff) {
      const b = activeEnemy.caveBuff;
      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = 'bold 12.5px "Microsoft YaHei", sans-serif';
      const text = b.short + ' ' + b.name;
      const tw = ctx.measureText(text).width + 20;
      const bx = sx - tw / 2;
      const by = GROUND_Y + 38;
      ctx.fillStyle = 'rgba(12, 6, 2, .74)';
      roundRect(bx, by, tw, 20, 10);
      ctx.fill();
      ctx.strokeStyle = b.color;
      ctx.lineWidth = 1.4;
      roundRect(bx, by, tw, 20, 10);
      ctx.stroke();
      ctx.fillStyle = b.color;
      ctx.fillText(text, sx, by + 10.5);
      ctx.restore();
    }
  }

  /* ---------- 洞府增幅的特效 ---------- */
  // 一簇小火苗（和血条旁边那朵同款造型）
  function drawFlameShape(x, y, s, outer, inner) {
    ctx.fillStyle = outer;
    ctx.beginPath();
    ctx.moveTo(x, y + 8 * s);
    ctx.quadraticCurveTo(x - 7 * s, y - 2 * s, x, y - 13 * s);
    ctx.quadraticCurveTo(x + 7 * s, y - 2 * s, x, y + 8 * s);
    ctx.fill();
    ctx.fillStyle = inner;
    ctx.beginPath();
    ctx.ellipse(x, y + 2 * s, 2.6 * s, 4 * s, 0, 0, Math.PI * 2);
    ctx.fill();
  }

  function caveBuffEnemies() {
    if (gameState !== 'battle') return [];
    const list = battleGroup.length ? battleGroup : (activeEnemy ? [activeEnemy] : []);
    return list.filter(function (e) { return aliveEnemy(e) && e.caveBuff; });
  }

  let caveFxGroundCalls = 0;   // 调试用：确认绘制函数有没有被调用

  // 地面层：脚下光环（烈焰 / 寒冰）——画在敌人之前，光环压在脚下
  function drawCaveBuffGroundFx() {
    caveFxGroundCalls += 1;
    caveBuffEnemies().forEach(function (e) {
      const id = e.caveBuff.id;
      if (id !== 'flameAura' && id !== 'freeze') return;
      const sx = e.x - cameraX();
      if (sx < -200 || sx > W + 200) return;
      const k = caveScale();
      const hot = id === 'flameAura';
      const rgb = hot ? '255, 138, 60' : '150, 215, 255';
      const pulse = 0.5 + Math.sin(enemyAnimClock * (hot ? 3.4 : 2.2)) * 0.5;
      const rx = (hot ? 84 : 74) * k;
      const ry = (hot ? 20 : 17) * k;
      // 底色光晕
      const g = ctx.createRadialGradient(sx, GROUND_Y + 4, 5, sx, GROUND_Y + 4, rx);
      g.addColorStop(0, 'rgba(' + rgb + ',' + (0.30 + pulse * 0.20).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(' + rgb + ',0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(sx, GROUND_Y + 4, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
      // 两圈向外荡开的涟漪
      ctx.save();
      ctx.lineWidth = 2.4;
      for (let i = 0; i < 2; i++) {
        const t = (enemyAnimClock * 0.85 + i * 0.5) % 1;
        ctx.globalAlpha = (1 - t) * 0.75;
        ctx.strokeStyle = 'rgba(' + rgb + ',0.95)';
        ctx.beginPath();
        ctx.ellipse(sx, GROUND_Y + 4, rx * (0.42 + t * 0.7), ry * (0.42 + t * 0.7), 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
      if (hot) {
        // 烈焰：往上窜的火苗
        for (let i = 0; i < 6; i++) {
          const ph = (enemyAnimClock * 1.5 + i * 0.17) % 1;
          const dx = Math.sin(i * 2.1 + enemyAnimClock * 1.6) * 30 * k;
          ctx.save();
          ctx.globalAlpha = (1 - ph) * 0.9;
          drawFlameShape(sx + dx, GROUND_Y + 4 - ph * 62 * k, (0.72 - ph * 0.2) * k, '#ff5a14', '#ffd26a');
          ctx.restore();
        }
      } else {
        // 寒冰：几根往上飘的冰刺
        ctx.save();
        ctx.strokeStyle = 'rgba(200, 240, 255, .95)';
        ctx.lineWidth = 2 * k;
        for (let i = 0; i < 6; i++) {
          const ph = (enemyAnimClock * 0.7 + i * 0.17) % 1;
          const dx = (i - 2.5) * 18 * k;
          const fy = GROUND_Y + 4 - ph * 40 * k;
          ctx.globalAlpha = (1 - ph) * 0.85;
          ctx.beginPath();
          ctx.moveTo(sx + dx, fy);
          ctx.lineTo(sx + dx + 5 * k, fy - 12 * k);
          ctx.stroke();
        }
        ctx.restore();
      }
    });
  }

  // 覆盖层：手上的火焰（点燃）——画在敌人之后，火苗盖在手上
  function drawCaveBuffOverFx() {
    caveBuffEnemies().forEach(function (e) {
      if (e.caveBuff.id !== 'ignite') return;
      const sx = e.x - cameraX();
      if (sx < -200 || sx > W + 200) return;
      const k = caveScale() * enemyDrawScale(e);
      const flick = 0.85 + Math.sin(enemyAnimClock * 6.2 + (e.animPhase || 0)) * 0.15;
      const hx = sx - 30 * k;
      const hy = GROUND_Y - 74 * k;
      ctx.save();
      ctx.globalAlpha = 0.95;
      drawFlameShape(hx, hy, 1.35 * k * flick, '#ff5a14', '#ffd26a');
      drawFlameShape(hx + 17 * k, hy + 9 * k, 0.9 * k * flick, '#ff7a26', '#ffe08a');
      drawFlameShape(hx - 13 * k, hy + 11 * k, 0.7 * k * flick, '#ff6a1f', '#ffe6a8');
      ctx.restore();
      // 手边一点点暖光
      const g = ctx.createRadialGradient(hx + 6 * k, hy, 2, hx + 6 * k, hy, 58 * k);
      g.addColorStop(0, 'rgba(255, 150, 60, .34)');
      g.addColorStop(1, 'rgba(255, 150, 60, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(hx + 6 * k, hy, 58 * k, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  // 伤害数字飘字
  function drawDamagePopups() {
    for (let i = 0; i < damagePopups.length; i++) {
      const p = damagePopups[i];
      const a = Math.max(0, 1 - p.t / p.life);
      const pop = p.t < 0.12 ? 1 + Math.sin(p.t / 0.12 * Math.PI) * 0.14 : 1;
      ctx.save();
      ctx.globalAlpha = a;
      ctx.textAlign = 'center';
      ctx.font = 'bold 30px Arial, sans-serif';
      ctx.lineWidth = 6;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(38, 5, 0, .92)';
      const label = p.text || ((p.positive ? '+' : '-') + p.dmg);
      ctx.strokeText(label, p.x, p.y);
      ctx.fillStyle = p.color;
      ctx.fillText(label, p.x, p.y);
      ctx.font = 'bold 30px "Microsoft YaHei", sans-serif';
      ctx.restore();
    }
  }

  function cameraX() {
    return Math.max(0, player.worldX - START_X);
  }

  function drawPlayer() {
    const sx = player.worldX - cameraX();
    const shake = shakeOffset(player.hurtT);
    const fx = { scale: playerFx.scale * caveScale(), alpha: playerFx.alpha };
    if (fx.alpha <= 0.01) return; // 进洞过场：完全没入洞口后不再绘制
    if (debugHidePlayer) return;  // 调试/测试：临时隐藏玩家

    const air = 0;
    ctx.save();
    ctx.globalAlpha = fx.alpha;
    ctx.fillStyle = 'rgba(60, 32, 8, ' + ((0.38 - air * 0.2) * fx.alpha).toFixed(3) + ')';
    ctx.beginPath();
    ctx.ellipse(sx - 14, GROUND_Y + 5, 58 * fx.scale, 9 * fx.scale, -0.06, 0, Math.PI * 2);
    ctx.fill();

    const frame = playerFrameImage();
    const walking = gameState === 'walk' && player.vx > 0 && playerAtkT <= 0;
    // 赶路时上下起伏；休息时只留一点点呼吸感（躺/坐的姿态基本是静的）
    const bob = walking ? Math.sin(player.walkT * 1.6) * 1.5
      : (playerResting ? Math.sin(player.animT * 1.4) * 0.9 : 0);
    drawPlayerFrame(frame, sx + shake.x, GROUND_Y + shake.y, fx.scale, PLAYER_SPRITE_FLIP, bob);
    drawHurtSlash(sx + shake.x, GROUND_Y + shake.y, player.hurtT);
    ctx.restore();
  }

  // 玩家当前该画哪一帧：出手动作 > 赶路 > 待机
  function playerFrameImage() {
    let idx = 0;
    if (playerAtkT > 0) {
      const p = 1 - playerAtkT / PLAYER_ATK_DUR;
      idx = Math.min(PLAYER_ATK_FRAMES - 1, Math.floor(p * PLAYER_ATK_FRAMES));
      const atk = images['player_atk_' + (idx + 1)];
      if (atk) {
        lastPlayerFrameKey = 'player_atk_' + (idx + 1);
        return atk;
      }
    } else if (gameState === 'walk' && player.vx > 0) {
      idx = Math.floor(player.walkT * (PLAYER_WALK_FPS / 8.5)) % 4;
    } else if (playerResting && playerRestFrame(restPoseIdx)) {
      // 休息：用随机到的那张休息姿态（5 选 1，过一会儿再随机换一张）
      lastPlayerFrameKey = 'player_rest_' + (restPoseIdx + 1);
      return playerRestFrame(restPoseIdx);
    } else {
      idx = Math.floor(player.animT * PLAYER_IDLE_FPS) % 4;
    }
    lastPlayerFrameKey = 'player_idle_' + (idx + 1);
    return images['player_idle_' + (idx + 1)] || images.player_idle_1;
  }

  // 播放一次出手动作（普通攻击 / 劈砍）
  function playPlayerAttack() {
    playerAtkT = PLAYER_ATK_DUR;
  }

  // 休息帧是"软加载"：不进资源清单，缺图也不会卡住加载进度条，只是回落到待机呼吸
  /* 首页（还没点「开始游戏」）也要能看到角色站在路上：
   * 单独把玩家自己的帧先软加载进来（不计入 loadTask 进度，避免影响加载条数字），
   * 点「开始游戏」时 loadAssets() 会按正常流程再走一遍（同 URL 走浏览器缓存，不会重复下载）。 */
  function preloadIntroPlayerFrames() {
    const keys = ['player_idle_1', 'player_idle_2', 'player_idle_3', 'player_idle_4'];
    keys.forEach(function (key) {
      const cur = images[key];
      if (cur && cur.complete && cur.naturalWidth > 0) return;
      const def = ASSETS.filter(function (a) { return a[0] === key; })[0];
      if (!def) return;
      const im = new Image();
      im.src = def[1];
      images[key] = im;
    });
  }

  function preloadPlayerRestFrames() {
    for (let i = 1; i <= PLAYER_REST_FRAMES; i++) {
      const im = new Image();
      im.src = assetUrl('assets/img/player/player_rest_' + i + '.png');
      playerRestFrames.push(im);
    }
  }

  function playerRestFrame(idx) {
    const im = playerRestFrames[idx];
    return (im && im.complete && im.naturalWidth > 0) ? im : null;
  }

  function playerRestLoadedCount() {
    let n = 0;
    for (let i = 0; i < playerRestFrames.length; i++) {
      const im = playerRestFrames[i];
      if (im && im.complete && im.naturalWidth > 0) n++;
    }
    return n;
  }

  // 休息帧里第 5 张（下标 4）是躺卧姿势：战斗中不抽它，免得打着仗突然躺下
  const PLAYER_REST_LYING = [4];
  // 首页（打开网页、还没登录）固定展示这张休息姿态：
  //   public/assets/img/player/player_rest_3.png（下标从 0 开始，所以是 2）
  const INTRO_REST_POSE = 2;

  // 可用的休息姿态池：战斗中只留站立姿态，其它场合五张都能用
  function restPosePool() {
    const pool = [];
    for (let i = 0; i < PLAYER_REST_FRAMES; i++) {
      if (gameState === 'battle' && PLAYER_REST_LYING.indexOf(i) >= 0) continue;
      pool.push(i);
    }
    return pool.length ? pool : [0];
  }

  // 随机挑一个休息姿态；再次随机时保证换一张，避免"看着没变"
  function rerollRestPose() {
    const pool = restPosePool();
    if (pool.length < 2) {
      restPoseIdx = pool[0];
    } else {
      let next = restPoseIdx;
      let guard = 0;
      while (next === restPoseIdx && guard++ < 40) next = pool[randInt(0, pool.length - 1)];
      restPoseIdx = next;
    }
    restPoseTimer = rand(PLAYER_REST_REROLL_MIN, PLAYER_REST_REROLL_MAX);
  }

  // 每帧判定：只要"站着不动"就算休息 —— 标题/加载阶段（游戏尚未开始）、
  // 路上停下、战斗中待机，都不该再播走路那一套帧。
  function updateRestPose(dt) {
    // intro = 首页"角色在路上休息，点他登录"的状态
    const standingState = gameState === 'title' || gameState === 'walk' || gameState === 'battle' ||
      gameState === 'intro';
    const resting = standingState && !input.moving && player.vx <= 0 && playerAtkT <= 0;
    if (!resting) {
      playerResting = false;
      restPoseTimer = 0;
      return;
    }
    // 首页固定用第 3 张休息姿态（assets/img/player/player_rest_3.png），不随机换姿势
    if (gameState === 'intro') {
      playerResting = true;
      restPoseIdx = INTRO_REST_POSE;
      restPoseTimer = 0;
      return;
    }
    if (!playerResting) {
      playerResting = true;
      restPoseIdx = -1;
      rerollRestPose();
      return;
    }
    // 战斗中若刚抽到躺卧姿态，立刻换成站立的
    if (restPosePool().indexOf(restPoseIdx) < 0) {
      rerollRestPose();
      return;
    }
    restPoseTimer -= dt;
    if (restPoseTimer <= 0) rerollRestPose();
  }

  // 玩家素材整帧绘制：脚底基线固定在 groundY + 4
  function drawPlayerFrame(img, sx, groundY, scale, flip, bob) {
    if (!img) return;
    const w = PLAYER_FRAME.w * scale;
    const h = PLAYER_FRAME.h * scale;
    const top = groundY + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * scale + (bob || 0) * scale;
    if (flip) {
      ctx.save();
      ctx.translate(sx, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, -w / 2, top, w, h);
      ctx.restore();
    } else {
      ctx.drawImage(img, sx - w / 2, top, w, h);
    }
  }

  // ---------- 气血反弹：周身血色波纹（中心深、外围淡，一圈圈荡开）----------
  function spawnBloodWave(strength, delay) {
    bloodWaves.push({
      t: -(delay || 0), // 负值表示还没开始荡漾，先不出画面
      life: rand(1.15, 1.5),
      maxR: rand(132, 190),
      strength: strength || 1
    });
    if (bloodWaves.length > 12) bloodWaves.shift();
  }

  function updateBloodWaves(dt) {
    // 没学「气血反弹」就不该有波纹（防止读取到旧存档里的过期开关状态）
    if (bloodRebound && !hasBloodReboundSkill()) bloodRebound = false;
    // 激活期间持续从角色脚下荡开
    if (bloodRebound && (gameState === 'walk' || gameState === 'battle')) {
      bloodWaveTimer -= dt;
      if (bloodWaveTimer <= 0) {
        bloodWaveTimer = rand(0.95, 1.25); // 放慢荡漾频率
        spawnBloodWave(1);
      }
    } else {
      bloodWaveTimer = 0;
    }
    for (let i = bloodWaves.length - 1; i >= 0; i--) {
      bloodWaves[i].t += dt;
      if (bloodWaves[i].t >= bloodWaves[i].life) bloodWaves.splice(i, 1);
    }
  }

  function bloodWaveProgress(w) {
    const p = clamp(w.t / w.life, 0, 1);
    // 先快后慢地荡开，接近外圈时明显变淡
    return { p: p, r: w.maxR * (1 - Math.pow(1 - p, 2.2)), fade: (1 - p) * (1 - p) * w.strength };
  }

  // 调试用：查看波纹与玩家屏幕坐标（测试脚本会读取）
  window.__bloodWaves = bloodWaves;
  window.__playerScreen = function () {
    return { sx: player.worldX - cameraX(), groundY: GROUND_Y };
  };
  // 调试用：一次拿到测试关心的运行时状态
  // 调试用：记录刀光贴图每一帧真实的绘制矩形（x / y / 尺寸），便于核对落点
  const fxTrace = [];
  window.__debugFxTrace = function () {
    return fxTrace.splice(0, fxTrace.length);
  };

  window.__debugState = function () {
    return {
      gameState: gameState,
      worldX: Math.round(player.worldX),
      distanceKm: Math.round((player.worldX - START_X) / KM_UNIT * 100) / 100,
      level: player.level,
      stageName: stageName(player.level),
      xp: Math.floor(player.xp),
      gold: gold,
      vitality: vitality,
      attrPoints: player.attrPoints,
      hp: Math.round(combat.hp),
      maxHp: combat.maxHp,
      mp: Math.round(combat.mp * 10) / 10,
      maxMp: combat.maxMp,
      mpUsable: derived.mpUsable,
      mpReservePct: derived.mpReservePct,
      pet: leadPet() ? {
        name: leadPet().name,
        hp: Math.round(leadPet().hp),
        maxHp: leadPet().maxHp,
        skillId: leadPet().skillId || null
      } : null,
      pets: alivePets().map(function (p) {
        const slot = pets.indexOf(p);
        return {
          name: p.name, level: p.level || 1, sprite: p.sprite || null, slot: slot,
          hp: Math.round(p.hp), maxHp: p.maxHp,
          x: Math.round(petScreenX(slot)), playerX: Math.round(player.worldX - cameraX())
        };
      }),
      petAttacks: petAttackCount,
      ally: ally ? { name: ally.name, turns: ally.turns } : null,
      autoAdventure: autoAdventure,
      logLen: battleLogHistory.length,
      logOpen: !ui.logPopup.classList.contains('hidden'),
      enemy: activeEnemy ? {
        name: activeEnemy.name,
        tier: activeEnemy.tier,
        legend: !!activeEnemy.legend,
        elite: !!activeEnemy.elite,
        scale: enemyDrawScale(activeEnemy)
      } : null,
      quake: quakeOffset()
      ,
      caveImg: (function () {
        const im = images.caveEntrance;
        return im && im.complete && im.naturalWidth > 0 ? im.naturalWidth + 'x' + im.naturalHeight : null;
      })(),
      caveToken: caveToken,
      skillStones: countSkillStones(),
      turn: battleTurn,
      cave: cave ? { id: cave.def.id, name: cave.def.name, total: cave.total, remaining: cave.remaining, killed: cave.killed } : null,
      caveBarVisible: !ui.caveBar.classList.contains('hidden'),
      group: battleAlive().length,
      transition: caveTransition ? { t: Math.round(caveTransition.t * 100) / 100, dur: caveTransition.dur, def: caveTransition.def.id, playerAlpha: Math.round(playerFx.alpha * 100) / 100, playerScale: Math.round(playerFx.scale * 100) / 100 } : null,
      entrances: caveEntrances.map(function (c) {
        return { id: c.def.id, used: c.used, worldX: Math.round(c.worldX), sx: Math.round(c.worldX - cameraX()) };
      })
    };
  };

  // 调试用：直接召唤一只传奇BOSS（省去跑完 10 波地图）
  window.__debugSpawnLegend = function () {
    spawnLegendEnemy();
    return activeEnemy ? activeEnemy.name : null;
  };
  // 调试用：直接发一次精英挑战机会
  window.__debugEliteCharge = function () {
    eliteCharges += 1;
    updateEliteButton();
    return eliteCharges;
  };
  // 调试用：玩家角色素材与当前动作（待机 / 赶路 / 出手）
  window.__debugPlayerAnim = function () {
    return {
      key: lastPlayerFrameKey,
      atkT: Math.round(playerAtkT * 1000) / 1000,
      atkDur: PLAYER_ATK_DUR,
      animT: Math.round(player.animT * 100) / 100,
      walkT: Math.round(player.walkT * 100) / 100,
      vx: Math.round(player.vx * 100) / 100,
      moving: !!input.moving,
      resting: playerResting,
      restPose: restPoseIdx,
      restPoseTimer: Math.round(restPoseTimer * 10) / 10,
      restLoaded: playerRestLoadedCount(),
      restTotal: PLAYER_REST_FRAMES,
      flip: PLAYER_SPRITE_FLIP,
      sx: Math.round(player.worldX - cameraX()),
      groundY: GROUND_Y,
      drawTop: Math.round(GROUND_Y + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * playerFx.scale * caveScale()),
      frame: { w: PLAYER_FRAME.w, h: PLAYER_FRAME.h, basePad: PLAYER_FRAME.basePad },
      loaded: [1, 2, 3, 4].every(function (i) {
        const im = images['player_idle_' + i];
        return !!(im && im.complete && im.naturalWidth > 0);
      }) && [1, 2, 3, 4, 5].every(function (i) {
        const im = images['player_atk_' + i];
        return !!(im && im.complete && im.naturalWidth > 0);
      }),
      size: (function () {
        const im = images.player_idle_1;
        return im ? im.naturalWidth + 'x' + im.naturalHeight : null;
      })()
    };
  };
  // 调试用：手动播一次出手动作
  window.__debugPlayerAttack = function () {
    playPlayerAttack();
    return playerAtkT;
  };
  // 调试用：临时隐藏玩家角色（量屏幕位置用）
  window.__debugHidePlayer = function (on) {
    debugHidePlayer = !!on;
    return debugHidePlayer;
  };
  // 调试用：隐藏敌人 / 查敌人美术信息 / 指定类型刷一个敌人（截图用）
  window.__debugHideEnemies = function (on) {
    debugHideEnemies = !!on;
    return debugHideEnemies;
  };
  window.__debugEnemyInfo = function () {
    const def = enemySpriteDef(activeEnemy);
    const im = images[def.key + '_1'];
    return {
      name: activeEnemy ? activeEnemy.name : null,
      sprite: activeEnemy ? activeEnemy.sprite : null,
      tier: activeEnemy ? activeEnemy.tier : null,
      frames: def.frames,
      frameSize: im && im.complete && im.naturalWidth > 0 ? im.naturalWidth + 'x' + im.naturalHeight : null,
      loadedFrames: (function () {
        let n = 0;
        for (let i = 1; i <= def.frames; i++) {
          const f = images[def.key + '_' + i];
          if (f && f.complete && f.naturalWidth > 0) n++;
        }
        return n;
      })(),
      allTypes: ENEMY_SPRITES.map(function (s) { return s.key; }),
      loadedTypes: ENEMY_SPRITES.filter(function (s) {
        const f = images[s.key + '_1'];
        return f && f.complete && f.naturalWidth > 0;
      }).length,
      frame: { w: ENEMY_FRAME.w, h: ENEMY_FRAME.h, basePad: ENEMY_FRAME.basePad, anchorX: ENEMY_FRAME.anchorX },
      drawTop: Math.round(GROUND_Y + 4 - (ENEMY_FRAME.h - ENEMY_FRAME.basePad) * (activeEnemy ? enemyDrawScale(activeEnemy) * caveScale() : 1)),
      baselineY: Math.round(GROUND_Y + 4),
      sx: activeEnemy ? Math.round(activeEnemy.x - cameraX()) : null
    };
  };
  window.__debugSpawnEnemyType = function (key) {
    const def = ENEMY_SPRITES.filter(function (s) { return s.key === key; })[0];
    if (!def) return null;
    spawnNextEnemy();
    if (!activeEnemy) return null;
    activeEnemy.sprite = def.key;
    activeEnemy.kindName = def.name;
    activeEnemy.name = def.name;
    activeEnemy.baseX = player.worldX + 420;
    activeEnemy.x = activeEnemy.baseX;
    return { name: activeEnemy.name, sx: Math.round(activeEnemy.x - cameraX()) };
  };
  // 调试用：重新刷一个敌人（每次都会随机一种类型），用来验证"随机模型"
  window.__debugRespawnEnemy = function () {
    spawnNextEnemy();
    return activeEnemy ? { sprite: activeEnemy.sprite, name: activeEnemy.name } : null;
  };
  // 调试用：直接给一件装备并穿上（测洗练 / 截图用）
  window.__debugGiveGear = function (level, rarity, slot, weaponKind) {
    const item = makeEquipment(level || 1, rarity || 'rare', slot, weaponKind);
    equipItem(item);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { name: item.name, slot: item.slot, level: item.level, rarity: item.rarity, detail: item.detail, icon: item.icon, iconImg: item.iconImg || 0, weaponType: item.weaponType };
  };
  // 调试用：造一件装备（不穿上），用于检查图标分配
  window.__debugMakeEquipment = function (level, rarity, slot, weaponKind) {
    const item = makeEquipment(level || 10, rarity || 'rare', slot, weaponKind);
    return {
      id: item.id, name: item.name, slot: item.slot, level: item.level, rarity: item.rarity,
      icon: item.icon, iconSet: item.iconSet || null, iconImg: item.iconImg || 0,
      weaponType: item.weaponType || null, detail: item.detail
    };
  };
  // 调试用：直接改金币（测洗练费用）
  window.__debugGold = function (value) {
    if (typeof value === 'number') {
      gold = Math.max(0, Math.round(value));
      updateGold();
      refreshReforgeIfOpen();
    }
    return gold;
  };
  // 调试用：当前洗练窗口的费用（含锁定加成）
  window.__debugReforgeCost = function () {
    if (!reforgeItem) return null;
    return {
      cost: reforgeCost(reforgeItem),
      level: reforgeItem.level,
      locked: (reforgeItem.mods || []).filter(function (m) { return m.locked; }).length,
      count: reforgeItem.reforgeCount || 0,
      max: REFORGE_MAX
    };
  };
  // 调试用：给当前装备加/减锁定条数（批量验证费用）
  window.__debugSetLocks = function (n) {
    if (!reforgeItem) return null;
    (reforgeItem.mods || []).forEach(function (m, i) { m.locked = i < n; });
    refreshReforgeIfOpen();
    return (reforgeItem.mods || []).filter(function (m) { return m.locked; }).length;
  };
  // 调试用：把当前洗练窗口里的装备次数清零（测洗练逻辑时反复用）
  window.__debugResetReforgeCount = function () {
    if (!reforgeItem) return null;
    reforgeItem.reforgeCount = 0;
    refreshReforgeIfOpen();
    return 0;
  };
  // 调试用：返回某类装备的词缀池（含各档位区间），测试用它核对"洗练不越级"
  window.__debugAffixPool = function (slot, weaponType) {
    const pool = affixPoolFor(slot, weaponType);
    return pool.map(function (m) {
      return { key: m.key, bands: m.bands || null, bands2: m.bands2 || null };
    });
  };
  // 调试用：直接对当前穿着的装备洗练一次（不经过界面，批量验证用）
  window.__debugReforgeOnce = function (slotKey) {
    const key = slotKey || Object.keys(equipment).filter(function (k) { return equipment[k]; })[0];
    const item = equipment[key];
    if (!item) return null;
    rollItemAffixes(item);
    return item.detail;
  };
  // 调试用：最近一次词缀随机的明细（每条词缀用了哪一档、抽到多少）
  window.__debugLastRoll = function () {
    return lastAffixRolls.map(function (r) { return { key: r.key, level: r.level, bandIdx: r.bandIdx, value: r.value, value2: r.value2 }; });
  };
  // 调试用：武器图标 —— 造一批武器看随机分配 / 已加载数量 / 每个物品的图标
  window.__debugWeaponIcons = function (n) {
    const items = [];
    const total = n || 12;
    for (let i = 0; i < total; i++) {
      const it = makeEquipment(1 + i, null, 'weapon');
      items.push({ name: it.name, weaponType: it.weaponType, iconImg: it.iconImg });
    }
    let loaded = 0;
    for (let i = 1; i <= WEAPON_ICON_COUNT; i++) {
      const im = images['wicon_' + i];
      if (im && im.complete && im.naturalWidth > 0) loaded++;
    }
    return { total: WEAPON_ICON_COUNT, loaded: loaded, items: items };
  };
  // 调试用：给背包塞 N 件随机武器（看图标列表）
  window.__debugFillBagWithWeapons = function (n) {
    for (let i = 0; i < (n || 6); i++) inventory.push(makeEquipment(1 + i * 2, null, 'weapon'));
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return inventory.filter(function (it) { return it.type === 'equipment'; }).length;
  };
  // 调试用：账号 / 存档
  // 角色结算后的进阶属性（图鉴 / 装备加成是否真的进了面板，用这个断言）
  window.__debugDerived = function () {
    return {
      maxHp: combat.maxHp,
      maxMp: combat.maxMp,
      hp: Math.round(combat.hp),
      armor: Math.round(derived.armor || 0),
      blockPct: derived.blockPct || 0,
      thorns: Math.round(derived.thorns || 0),
      res: derived.res || 0,
      chaosRes: derived.chaosRes || 0,
      strength: derived.strength || 0,
      agility: derived.agility || 0,
      intelligence: derived.intelligence || 0
    };
  };
  window.__debugGacha = function () {
    const last = gachaResultList && gachaResultList.length ? gachaResultList[gachaResultList.length - 1] : null;
    return {
      gold: gold,
      pity: gachaPity,
      pityMax: GACHA_PITY_MAX,
      count: gachaCount,
      single: GACHA_SINGLE_COST,
      ten: GACHA_TEN_COST,
      lastTier: last ? last.tier : null,
      lastTierName: last ? last.tierName : null,
      lastPityFlag: last ? !!last.pity : false
    };
  };
  window.__debugSetPity = function (n) { gachaPity = Math.max(0, Math.floor(n || 0)); renderGacha(); return gachaPity; };

  /* 调试用：抽卡动画状态（播放中 / 已经揭晓了几张卡） */
  window.__debugGachaAnim = function () {
    return {
      playing: gachaAnimPlaying(),
      stageHidden: !ui.gachaStage || ui.gachaStage.classList.contains('hidden'),
      backCount: ui.gachaStageCards ? ui.gachaStageCards.children.length : 0,
      cardCount: ui.gachaResults ? ui.gachaResults.querySelectorAll('.gachaCard').length : 0,
      results: (gachaResultList || []).length,
      oneDisabled: !!(ui.gachaOne && ui.gachaOne.disabled),
      tenDisabled: !!(ui.gachaTen && ui.gachaTen.disabled)
    };
  };
  // 调试用：跳过动画直接揭晓
  window.__debugGachaSkipAnim = function () {
    if (gachaAnimPlaying()) finishGachaAnimation(function () { renderGachaResults(false); });
    return window.__debugGachaAnim();
  };
  // 调试用：属性点 / 在线奖励 / 装备价格
  window.__debugGainXp = function (n) { gainXp(n || 0); return { level: player.level, xp: Math.floor(player.xp), attrPoints: player.attrPoints }; };
  // 调试用：尸体掉落（光柱判定用）
  window.__debugClearCorpse = function () { corpse = null; return true; };
  // 调试用：直接搜索当前尸体（等同点击地上的尸体）
  window.__debugSearchCorpse = function () { searchCorpse(false); return true; };
  // 调试用：给全身普通装备槽塞上装备（测洞府阵亡掉落）
  window.__debugEquipFullSet = function () {
    ['helmet', 'weapon', 'clothes', 'shoes', 'gloves', 'jewelry'].forEach(function (slot) {
      const g = makeEquipment(player.level, 'common', slot);   // 第三个参数要传字符串槽位
      g.iconSet = slot === 'weapon' ? 'weapon' : (GEAR_ICON_SET_BY_SLOT[slot] || null);
      ensureItemIcon(g);
      equipment[slot] = g;
    });
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return Object.keys(equipment).filter(function (s) { return !!equipment[s]; })
      .map(function (s) {
        const g = equipment[s];
        return s + ':' + g.name + '(槽=' + g.slot + ' 类型=' + (g.weaponType || '-') + ')';
      });
  };
  // 调试用：在洞府里直接召一只指定品级的怪（测洞府掉落用）
  window.__debugSpawnCaveEnemyTier = function (tier) {
    if (!cave) return false;
    const def = cave.def;
    const enemy = buildEnemy(playerLevel() + (def.id === 'gold' ? 2 : def.id === 'blue' ? 1 : 0),
      tier || def.tiers[0], { worldX: player.worldX + 560, cave: true });
    battleGroup.length = 0;
    battleGroup.push(enemy);
    activeEnemy = enemy;
    battleTurn = 'player';
    resetPlayerIdleTimer();
    enemyTurnTimer = 0;
    gameState = 'walk';
    return { tier: enemy.tier, name: enemy.name };
  };

  /* ---------- 洞府敌人增幅 调试钩子 ---------- */
  window.__debugCaveBuffList = function () {
    return CAVE_BUFFS.map(function (b) { return { id: b.id, name: b.name, desc: b.desc, color: b.color }; });
  };
  window.__debugCaveBuffState = function () {
    return {
      // 场上带增幅的敌人（野外敌人应为空）
      buffs: (battleGroup.length ? battleGroup : (activeEnemy ? [activeEnemy] : []))
        .filter(function (e) { return e && e.caveBuff; })
        .map(function (e) { return { name: e.name, id: e.caveBuff.id, name2: e.caveBuff.name, stunUsed: !!e.stunUsed }; }),
      stunTurns: playerStunTurns,
      frozenTurns: playerFrozenTurns,
      burnStacks: playerBurnStacks,
      hp: Math.round(combat.hp),
      maxHp: combat.maxHp,
      turn: battleTurn,
      inBattle: gameState === 'battle'
    };
  };
  // 给当前敌人强制指定一种增幅（不传就随机）
  window.__debugSetCaveBuff = function (id) {
    if (!activeEnemy) return null;
    if (!id) { activeEnemy.caveBuff = CAVE_BUFFS[randInt(0, CAVE_BUFFS.length - 1)]; }
    else {
      activeEnemy.caveBuff = CAVE_BUFFS.filter(function (b) { return b.id === id; })[0] || null;
    }
    activeEnemy.stunUsed = false;
    return window.__debugCaveBuffState();
  };
  // 立刻结算一次敌方回合（含增幅的命中效果）
  window.__debugEnemyTurnNow = function () {
    if (gameState !== 'battle' || !activeEnemy) return null;
    battleTurn = 'enemy';
    enemyCounterAttack(false);
    const snap = window.__debugCaveBuffState();
    // 测试用：把回合安顿回玩家，免得后台计时器又补一次敌方行动，干扰断言
    if (battleTurn === 'enemy') {
      battleTurn = 'player';
      enemyTurnTimer = 0;
      resetPlayerIdleTimer();
      setControls('battle');
    }
    return snap;
  };
  // 直接把玩家血量设成指定值（测反伤 / 光环时用，避免被打死）
  window.__debugSetHpExact = function (v) {
    combat.hp = clamp(Number(v) || 0, 0, combat.maxHp);
    updateBattleHud();
    return Math.round(combat.hp);
  };
  // 调试用：洞府增幅特效的自查（画了几个敌人、在屏幕哪个位置）
  window.__debugCaveFx = function () {
    const list = caveBuffEnemies();
    return {
      gameState: gameState,
      battleGroup: battleGroup.length,
      count: list.length,
      list: list.map(function (e) {
        return { id: e.caveBuff.id, sx: Math.round(e.x - cameraX()), hp: Math.round(e.hp), scale: Math.round(caveScale() * 100) / 100 };
      }),
      groundY: GROUND_Y
      , groundCalls: caveFxGroundCalls
    };
  };
  // 调试用：一击放倒当前敌人（走正常出手流程，会正常留下可搜索的尸体）
  window.__debugKillEnemy = function () {
    if (gameState !== 'battle' || !activeEnemy || battleTurn !== 'player' || pendingVictory > 0) return false;
    activeEnemy.hp = 1;
    doPlayerAction('attack');
    return true;
  };
  // 调试用：量尸体上方那束光柱（只有掉装备时才会亮起来）。
  // 沙漠背景本身很亮，所以用"和没有尸体时的底图做差分"挑出光柱，而不是看绝对亮度。
  window.__debugSamplePillar = function (mode) {
    const cv = document.getElementById('game');
    const g = cv.getContext('2d');
    const probe = (typeof window.__pillarProbeX === 'number') ? window.__pillarProbeX : 510;
    const x0 = Math.max(0, probe - 90);
    const w = Math.min(180, W - x0);
    const y0 = Math.max(0, GROUND_Y - 320);
    const h = Math.min(320, GROUND_Y - y0);
    const cur = g.getImageData(x0, y0, w, h).data;
    if (mode === 'base') {
      window.__pillarProbeX = probe;
      window.__pillarBase = { x0: x0, y0: y0, w: w, h: h, d: cur };
      return { stored: true, x0: x0, y0: y0, w: w, h: h };
    }
    const base = window.__pillarBase;
    if (!base || base.x0 !== x0) return null;
    let n = 0, r = 0, gg = 0, bb = 0, minY = 1e9, maxY = -1;
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const dr = cur[i] - base.d[i];
      const dg = cur[i + 1] - base.d[i + 1];
      const db = cur[i + 2] - base.d[i + 2];
      // 光柱是加法混合：三个通道一起被提亮
      if (dr > 14 && dg > 14 && db > 14) {
        n++; r += dr; gg += dg; bb += db;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
      }
    }
    return {
      brightened: n, corpseSx: corpse ? Math.round(corpse.worldX - cameraX()) : null,
      r: n ? Math.round(r / n) : 0, g: n ? Math.round(gg / n) : 0, b: n ? Math.round(bb / n) : 0,
      topY: n ? (y0 + minY) : null, bottomY: n ? (y0 + maxY) : null
    };
  };
  window.__debugCorpseState = function () {
    const drops = (corpse && corpse.drops) ? corpse.drops.map(function (d) {
      return {
        kind: d.kind || d.type, name: d.name || '',
        rarity: d.rarity || null, rarityColor: d.rarityColor || null, coins: d.coins || 0
      };
    }) : null;
    return {
      hasCorpse: !!corpse, searched: corpse ? !!corpse.searched : null,
      worldX: corpse ? Math.round(corpse.worldX) : null,
      forceGear: corpse ? !!corpse.forceGear : null,
      drops: drops, topRarity: corpseTopRarity(),
      gearRarities: (corpse && corpse.drops)
        ? corpse.drops.filter(function (d) { return d.type === 'equipment'; }).map(function (d) { return d.rarity; })
        : []
    };
  };
  // 调试用：抽 N 次装备掉落，看概率分布（force=洞府必掉）
  window.__debugGearDropSample = function (tier, n, force) {
    const out = { tier: tier || 'common', force: force !== false, n: Math.max(1, n || 300), counts: {}, perKill: {}, has: {} };
    const order = ['common', 'magic', 'rare', 'legend'];
    for (let i = 0; i < out.n; i++) {
      const list = rollGearRarities(out.tier, out.force);
      out.perKill[list.length] = (out.perKill[list.length] || 0) + 1;
      list.forEach(function (r) { out.counts[r] = (out.counts[r] || 0) + 1; });
      order.forEach(function (r) { if (list.indexOf(r) >= 0) out.has[r] = (out.has[r] || 0) + 1; });
    }
    out.rate = {};       // 每件装备的品级占比
    out.hasRate = {};    // "这只怪掉出了该品级"的比例
    order.forEach(function (k) {
      if (out.counts[k]) out.rate[k] = Math.round(out.counts[k] / out.n * 1000) / 10;
      if (out.has[k]) out.hasRate[k] = Math.round(out.has[k] / out.n * 1000) / 10;
    });
    out.none = out.perKill[0] || 0;                     // 空手的次数
    out.avgPieces = Math.round(
      Object.keys(out.perKill).reduce(function (s, k) { return s + Number(k) * out.perKill[k]; }, 0) / out.n * 100
    ) / 100;
    out.maxRarity = order.filter(function (k) { return out.counts[k]; }).pop() || null;
    return out;
  };
  // 调试用：光柱的"柔和度"剖面（中心轴纵向衰减 + 最亮那行横向羽化）
  window.__debugPillarProfile = function () {
    if (!corpse || !window.__pillarBase) return null;
    const cv = document.getElementById('game');
    const g = cv.getContext('2d');
    const base = window.__pillarBase;
    const cur = g.getImageData(base.x0, base.y0, base.w, base.h).data;
    const csx = Math.round(corpse.worldX - cameraX()) - base.x0;
    if (csx < 0 || csx >= base.w) return null;
    const delta = function (x, y) {
      const i = (y * base.w + x) * 4;
      return Math.max(0, (cur[i] - base.d[i]) + (cur[i + 1] - base.d[i + 1]) + (cur[i + 2] - base.d[i + 2]));
    };
    const axis = [];
    // 只量"尸体上方"那一段：尸体的美术和「点击搜索」文字本身也会让像素变化，
    // 混进来会把纵向曲线搞出一堆假跳变（那不是光柱的锅）
    const axisEnd = Math.max(4, Math.min(base.h, GROUND_Y - 80 - base.y0));
    for (let y = 0; y < axisEnd; y += 4) axis.push(delta(csx, y));
    let bestRow = 0, bestVal = -1;
    axis.forEach(function (v, k) { if (v > bestVal) { bestVal = v; bestRow = k; } });
    const row = [];
    for (let x = 0; x < base.w; x++) row.push(delta(x, bestRow * 4));
    return {
      axis: axis, row: row, peak: bestVal, peakY: base.y0 + bestRow * 4,
      cx: base.x0 + csx, rowWidth: base.w, rowHeight: base.h
    };
  };
  // 调试用：直接在路边摆一具带指定品级装备的尸体（验证光柱特效）
  window.__debugForceCorpse = function (rarity) {
    if (!corpse || corpse.searched) {
      const level = Math.max(1, player.level);
      const drops = [{ kind: 'gold', name: '金币', icon: '金', detail: '+10', coins: 10, rarityColor: '#ffd970' }];
      if (rarity) drops.push(makeEquipment(level, rarity));
      corpse = {
        worldX: player.worldX + 150, level: level, tier: 'common',
        sprite: (ENEMY_SPRITES[0] && ENEMY_SPRITES[0].key) || 'bingfei', searched: false, drops: drops
      };
    }
    return window.__debugCorpseState();
  };
  // 调试用：血瓶充能进度 / 商店等级
  window.__debugFlask = function (charges, common, elite) {
    if (typeof charges === 'number') flaskCharges = clamp(Math.round(charges), 0, 5);
    if (typeof common === 'number') flaskKillCommon = Math.max(0, Math.round(common));
    if (typeof elite === 'number') flaskKillElite = Math.max(0, Math.round(elite));
    updateFlaskUI();
    return { charges: flaskCharges, common: flaskKillCommon, elite: flaskKillElite,
      needCommon: FLASK_COMMON_NEED, needElite: FLASK_ELITE_NEED,
      title: ui.btnFlask ? ui.btnFlask.title : null };
  };
  window.__debugAddKill = function (isElite) { addFlaskKill(!!isElite, true); return window.__debugFlask(); };
  window.__debugShopLevel = function () {
    return { playerLevel: player.level, defeated: defeatedEnemies, shopLevel: getShopItemLevel() };
  };
  // 调试用：清空本机所有档案（验证"新建档案从起点开始"）
  window.__debugWipeAll = function () { resetAllSaves(); resetProgressState(); return true; };
  // 调试用：赶路扬尘（验证跑步扬尘）
  window.__debugRunDust = function () {
    return {
      running: gameState === 'walk' && !deathActive && player.vx > 0 && playerAtkT <= 0,
      vx: Math.round(player.vx),
      playerX: Math.round(player.worldX - cameraX()),
      count: runDust.length,
      drawn: dustDrawn,   // 累计绘制过的尘粒数（>0 说明渲染路径确实跑到了）
      particles: runDust.slice(0, 8).map(function (d) {
        return {
          x: Math.round(d.x), y: Math.round(d.y),
          r: Math.round(d.r * 10) / 10,
          progress: Math.round(d.t / d.life * 100) / 100,
          behind: d.x < (player.worldX - cameraX())
        };
      })
    };
  };
  // 调试用：当前存档里的里程（验证移动距离存档）
  window.__debugSavedDistance = function () {
    try {
      const d = JSON.parse(window.localStorage.getItem(activeSaveKey()) || '{}');
      return { distance: d.distance, defeatedEnemies: d.defeatedEnemies };
    } catch (e) { return null; }
  };
  // 调试用：灵宝（金色洞府终点掉落）
  window.__debugMakeTreasure = function (level, forceKey) {
    const it = makeEquipment(level || 10, 'legend', 'treasure');
    if (forceKey) {
      const sp = TREASURE_SPECIALS.filter(function (s) { return s.key === forceKey; })[0] || TREASURE_SPECIALS[0];
      attachTreasureSpecial(it, sp);
    } else {
      attachTreasureSpecial(it, TREASURE_SPECIALS[randInt(0, TREASURE_SPECIALS.length - 1)]);
    }
    inventory.push(it);
    return { name: it.name, level: it.level, rarity: it.rarity, special: it.special, detail: it.detail };
  };
  window.__debugTreasureState = function () {
    const it = equipment.treasure;
    return {
      equipped: it ? { name: it.name, special: it.special } : null,
      maxHp: derived.maxHp,
      flatAtk: derived.flatAtk,
      strengthTotal: derived.strengthTotal,
      strengthHp: derived.strengthHp,
      strHpPer: treasureSpecial('strToHpDouble') ? 10 : 5,
      alwaysFirst: treasureSpecial('alwaysFirst'),
      goldPerWin: treasureSpecial('goldPerWin'),
      hpFlat1000: treasureSpecial('hpFlat1000'),
      lifesteal: treasureSpecial('lifesteal10'),
      skills: knownSkills.map(function (s) { return s.name; })
    };
  };
  window.__debugClearTreasure = function () {
    if (equipment.treasure) inventory.push(equipment.treasure);
    equipment.treasure = null;
    recalcPlayerStats();
    return true;
  };
  // 调试用：直接学会某个技能（按 id）
  window.__debugLearnSkill = function (id) {
    const s = learnSkillById(id);
    return s ? s.name : null;
  };
  // 调试用：自动冒险优先技能（激活 / 取消 / 查询）
  window.__debugAutoSkill = function () {
    const fav = autoSkillId ? knownSkills.filter(function (s) { return s.id === autoSkillId; })[0] : null;
    return { id: autoSkillId, name: fav ? fav.name : null };
  };
  window.__debugToggleAutoSkill = function (id) {
    const skill = knownSkills.filter(function (s) { return s.id === id; })[0];
    if (!skill) return { id: autoSkillId };
    toggleAutoSkill(skill);
    return window.__debugAutoSkill();
  };
  // 调试用：技能的镶嵌槽信息 / 花钱开槽 / 指定镶嵌槽数量
  window.__debugSocketInfo = function (skillId) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return null;
    return {
      id: skill.id, name: skill.name,
      slots: socketSlotsOf(skill),
      sockets: (skill.sockets || []).length,
      max: SOCKET_SLOTS_MAX, cost: SOCKET_SLOT_COST, gold: gold
    };
  };
  window.__debugBuySlot = function (skillId) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return null;
    buySocketSlot(skill);
    return window.__debugSocketInfo(skillId);
  };
  window.__debugSetSocketSlots = function (skillId, n) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return null;
    skill.socketSlots = clamp(Math.floor(n), SOCKET_SLOTS_DEFAULT, SOCKET_SLOTS_MAX);
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    return window.__debugSocketInfo(skillId);
  };
  // 调试用：休息回血（把休息计时快进 seconds 秒，返回这次恢复的生命）
  window.__debugRestHeal = function (seconds) {
    const before = combat.hp;
    restHealTimer += Math.max(0, Number(seconds) || 0);
    updateRestHeal(0);
    return {
      timer: Math.round(restHealTimer * 100) / 100,
      hpBefore: Math.round(before), hpAfter: Math.round(combat.hp),
      healed: Math.round(combat.hp - before), maxHp: combat.maxHp
    };
  };
  window.__debugSetHp = function (v) {
    combat.hp = clamp(Number(v) || 0, 1, combat.maxHp);
    updateBattleHud();
    return Math.round(combat.hp);
  };
  // 调试用：直接施放某个技能，返回命中明细（验证群攻）
  // 调试用：给某个技能镶嵌一个指定效果的技能石（验证专属辅助）
  window.__debugSocketGem = function (effect, skillId, value) {
    const skill = knownSkills.filter(function (s) { return s.id === skillId; })[0];
    if (!skill) return null;
    const opt = GEM_OPTIONS.filter(function (o) { return o.id === effect; })[0];
    if (!opt) return null;
    const gem = gemOptionToItem(opt);
    if (typeof value === 'number' && !opt.support) {
      gem.value = value;
      gem.detail = opt.name + ' +' + value + opt.unit;
    }
    inventory.push(gem);
    socketGemInto(skill, gem);
    return {
      skill: skill.name,
      effect: gem.effect,
      sockets: (skill.sockets || []).map(function (g) { return g.name; }),
      refused: (skill.sockets || []).indexOf(gem) < 0
    };
  };
  // 调试用：流血 / 冲击波状态
  // 调试用：阳爪特效状态（分身数量 / 进度 / 当前位置）
  window.__debugYangClawFx = function () {
    if (!yangClawFx) return null;
    const fromX = yangClawFx.fromX - cameraX();
    const toX = yangClawFx.toX - cameraX();
    return {
      t: Math.round(yangClawFx.t * 100) / 100,
      dur: yangClawFx.dur,
      clones: yangClawFx.clones.map(function (c) {
        const local = yangClawFx.t - c.delay;
        const p = local < 0 ? 0 : clamp(local / c.dur, 0, 1);
        const ease = 1 - Math.pow(1 - p, 3);
        return { delay: Math.round(c.delay * 100) / 100, p: Math.round(p * 100) / 100,
          x: Math.round(fromX + (toX - fromX) * ease), active: local >= 0 && local <= c.dur };
      })
    };
  };
  // 调试用：立刻进入敌方回合（结算流血等）
  window.__debugEndTurn = function () {
    afterPlayerOffense();
    return battleTurn;
  };
  // 调试用：核对「震撼大地」冲击波的伤害公式（普通攻击 50% + 力量）
  // 调试用：新手宝箱 / 阵亡相关
  // 调试用：已学技能快照（含镶嵌的技能石）
  window.__debugSkillState = function () {
    return {
      skills: knownSkills.map(function (s) {
        return {
          id: s.id, name: s.name, level: s.level || 1, type: s.type,
          sockets: (s.sockets || []).map(function (g) { return g.name + '(' + (g.detail || '') + ')'; })
        };
      })
    };
  };
  // 调试用：直接设定某个技能的等级（验证"每级 +10% 基础伤害"）
  window.__debugSetSkillLevel = function (id, lv) {
    const skill = knownSkills.filter(function (s) { return s.id === id; })[0];
    if (!skill) return null;
    skill.level = Math.max(1, Math.floor(lv || 1));
    if (!ui.spellPopup.classList.contains('hidden')) renderSpellList();
    return window.__debugSkillInfo(id);
  };
  window.__debugSkillInfo = function (id) {
    const skill = knownSkills.filter(function (s) { return s.id === id; })[0];
    if (!skill) return null;
    return {
      id: skill.id, name: skill.name, level: skill.level || 1,
      dmg: skill.dmg ? [skill.dmg[0], skill.dmg[1]] : null,
      levelMult: Math.round(skillLevelMult(skill) * 1000) / 1000,
      dmgLevelMult: Math.round(skillDamageLevelMult(skill) * 1000) / 1000,
      // 实际结算时的基础伤害区间（劈砍走刀光演出，单独看 slashFx）
      effDmg: skill.dmg
        ? [Math.round(skill.dmg[0] * skillDamageLevelMult(skill)),
           Math.round(skill.dmg[1] * skillDamageLevelMult(skill))]
        : null
    };
  };
  // 调试用：已移除的技能不该还能学到
  window.__debugLearnRemoved = function (id) {
    return isRemovedSkillId(id) ? 'REMOVED' : (learnSkillById(id) ? 'LEARNED' : 'NO_SUCH_SKILL');
  };
  // 调试用：最近一次技能伤害计算（等级加成 / 距离加成 / 最终基础伤害）
  window.__debugLastSkillCalc = function () { return lastSkillCalc; };
  // 调试用：主循环 / 后台继续运行的状态
  window.__debugLoopState = function () {
    return {
      hidden: !!document.hidden,
      bgTicker: !!bgTicker,
      bgTicks: worldClock.bgTicks,
      catchupSeconds: Math.round(worldClock.catchupSeconds * 10) / 10,
      lastTime: Math.round(lastTime),
      gameState: gameState,
      worldX: Math.round(player.worldX),
      distanceKm: Math.round((player.worldX - START_X) / KM_UNIT * 100) / 100,
      vitality: vitality,
      onlineSeconds: Math.round(onlineSeconds),
      autoAdventure: autoAdventure
    };
  };
  // 调试用：手动推进世界（等价于"网页在后台待了 N 秒"）
  window.__debugAdvance = function (seconds) {
    const before = window.__debugLoopState();
    advanceWorld(Number(seconds) || 0);
    lastTime = performance.now();
    const after = window.__debugLoopState();
    return { before: before, after: after };
  };
  // 调试用：世界BOSS 状态 / 强制刷新 / 传送 / 虚影信息
  window.__debugWorldBoss = function () {
    const def = openWorldBossDef();
    const run = worldBossRun;
    return {
      defs: WORLD_BOSSES.map(function (d) {
        return { id: d.id, level: d.level, name: d.name, place: d.place, open: !!d.open, refreshMin: d.refreshMin };
      }),
      openName: def ? def.name : null,
      phase: wb.phase,
      alive: wbAlive(),
      hp: Math.round(wb.hp),
      maxHp: wb.maxHp,
      killed: wb.killed,
      claimed: wb.claimed,
      lastRewardDay: wb.lastRewardDay,
      rewardAvailable: wbRewardAvailable(),
      nextInSec: Math.round((wb.nextAt - wbNow()) / 1000),
      windowLeftSec: wb.phase === 'alive' ? Math.round((wb.expiresAt - wbNow()) / 1000) : 0,
      inFight: !!run,
      fight: run ? {
        name: run.def.name,
        level: run.level,
        hp: Math.round(run.boss.hp),
        maxHp: run.boss.maxHp,
        ghosts: run.ghosts.length,
        ghostDamage: Math.round(run.ghostDamage),
        playerDamage: Math.round(run.playerDamage),
        claimed: run.claimed,
        scale: run.boss.wbScale
      } : null
    };
  };
  window.__debugWbRefresh = function () {
    const def = openWorldBossDef();
    if (!def) return null;
    wbOverride = { spawnedAt: wbNow(), expiresAt: wbNow() + WB_WINDOW_SEC * 1000 };
    wb.phase = 'alive';
    wb.maxHp = def.hp;
    wb.hp = def.hp;
    wb.spawnedAt = wbOverride.spawnedAt;
    wb.expiresAt = wbOverride.expiresAt;
    wb.nextAt = wbOverride.expiresAt;
    wb.claimed = false;
    return window.__debugWorldBoss();
  };
  window.__debugWbExpire = function () {
    // 把 3 分钟窗口立刻走完（验证"没击杀就自己消失"）
    if (!wbOverride) wbOverride = { spawnedAt: wb.spawnedAt || wbNow(), expiresAt: wbNow() - 1 };
    wbOverride.expiresAt = wbNow() - 1;
    wb.expiresAt = wbOverride.expiresAt;
    return window.__debugWorldBoss();
  };
  window.__debugWbClearOverride = function () { wbOverride = null; return window.__debugWorldBoss(); };
  // 调试用：世界BOSS 战场环境效果（余烬 / 沙尘带 / 血云 / 血雷闪）
  // 调试用：森特欧斯圣物（发放 / 掉落判定 / 状态）
  window.__debugGrantSantosRelic = function (key) {
    const item = makeSantosRelic(key || 'skull');
    inventory.push(item);
    recalcPlayerStats();
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { name: item.name, relic: item.relic, detail: item.detail, state: window.__debugSantosState() };
  };
  window.__debugSantosRoll = function (force) {
    if (force) santosRollForced = true;
    const got = rollSantosRelic();
    return { got: got ? got.name : null, state: window.__debugSantosState() };
  };

  /* ---------- 天地灵物（世界BOSS限定掉落）调试钩子 ---------- */
  window.__debugSpiritState = function () {
    return {
      hasItem: hasSpiritItem(),
      dropPct: SPIRIT_ITEM_DROP_PCT,
      exchangeHasSpirit: EXCHANGE_ITEMS.some(function (e) { return e.label === '天地灵物'; }),
      bottleneck: !!player.bottleneck,
      level: player.level,
      stage: stageName(player.level)
    };
  };
  // force=true 必出；不传 force 走真实 0.01% 判定。第二个参数传 false 表示"没参与战斗"。
  // 走的还是"留场开箱彩蛋"那一条判定（现已不存在于BOSS死亡结算里）。
  window.__debugSpiritRoll = function (force, participated) {
    spiritRollForced = !!force;
    const run = (participated === false)
      ? { playerDamage: 0, ghostDamage: 0, boss: null }     // 没参与：不该掉
      : { playerDamage: 1, ghostDamage: 0, boss: null };
    const got = rollSpiritItemFromLootChest(run);
    spiritRollForced = false;
    return { got: got ? got.name : null, state: window.__debugSpiritState() };
  };

  /* ---------- 第一个任务 / 首页引路人对白 调试钩子 ---------- */
  window.__debugQuest = function () {
    return {
      accepted: !!quests.first.accepted,
      done: !!quests.first.done,
      targetStage: QUEST_FIRST_STAGE,
      targetName: stageName(QUEST_FIRST_STAGE),
      nowStage: stageName(player.level),
      popupOpen: !!(ui.questPopup && !ui.questPopup.classList.contains('hidden')),
      popupTitle: ui.questPopup ? (ui.questPopup.querySelector('h2') || {}).textContent : null
    };
  };
  window.__debugQuestAccept = function () { closeQuestPopup(); return window.__debugQuest(); };
  window.__debugShowIntroDialog = function () {
    if (gameState !== 'intro') return null;
    ui.introDialog.classList.remove('hidden');
    return window.__debugIntro();
  };
  window.__debugSantosState = function () {
    return {
      chance: SANTOS_RELIC_CHANCE,
      skull: hasSantosRelic('skull'),
      totem: hasSantosRelic('totem'),
      relicInt: derived.relicInt || 0,
      intTotal: derived.intTotal || 0,
      maxMp: combat.maxMp,
      poisonImmune: !!derived.poisonImmune,
      controlImmune: !!derived.controlImmune,
      hasCageSkill: knownSkills.some(function (s) { return s.id === 'santosCage'; }),
      cageSeconds: WB_CAGE_SECONDS,
      enemy: activeEnemy ? {
        name: activeEnemy.name, hp: Math.round(activeEnemy.hp),
        stunT: Math.round((activeEnemy.stunT || 0) * 100) / 100,
        rootT: activeEnemy.rootT || 0,
        poison: activeEnemy.poison ? { dmg: Math.round(activeEnemy.poison.dmg), turns: activeEnemy.poison.turns } : null
      } : null
    };
  };
  // 调试用：手动触发一次图腾法术 / 囚笼
  window.__debugTotemTurn = function () { return totemTurnSpell(); };
  // 调试用：防御姿态（是否处于防御 / 减伤与格挡参数）
  window.__debugDefendState = function () {
    return { defending: defending, reduce: DEFEND_REDUCE, blockPct: DEFEND_BLOCK_PCT,
      buttonLabel: ui.btnDefend ? (ui.btnDefend.getAttribute('title') || '') : null,
      buttonImg: ui.btnDefend && ui.btnDefend.querySelector('img')
        ? ui.btnDefend.querySelector('img').getAttribute('src') : null,
      disabled: ui.btnDefend ? ui.btnDefend.disabled : null,
      // 护盾特效：是不是在播、第几帧、画在哪（旧的淡蓝光弧已移除）
      fx: (function () {
        if (!defendFx) return null;
        return {
          t: Math.round(defendFx.t * 1000) / 1000,
          phase: defendFx.phase,
          frame: defendFxFrameNo(),
          frames: DEFEND_FX_FRAMES
        };
      })()
    };
  };
  // 调试用：护盾特效资源与落点（角色右侧 = 屏幕 x 更大的一侧）
  window.__debugDefendFx = function () {
    const first = images.defend_1;
    const scale = playerFx.scale * caveScale();
    const sx = player.worldX - cameraX();
    const size = DEFEND_FX_SIZE * scale;
    const ax = sx;
    const ay = GROUND_Y - DEFEND_FX_LIFT * scale;
    const idx = defendFxFrameNo();
    // 盾面（贴图里可见内容的范围：源图 x 20~160、y 11~181）换算到屏幕，核对是否罩住角色
    const ox = function (srcX) { return ax + (srcX / 192 - DEFEND_FX_ORIGIN.x) * size; };
    const oy = function (srcY) { return ay + (srcY / 192 - DEFEND_FX_ORIGIN.y) * size; };
    const mir = function (srcX) { return DEFEND_FX_FLIP ? 192 - srcX : srcX; };
    const box = {
      left: Math.round(ox(mir(161))), right: Math.round(ox(mir(20))),
      top: Math.round(oy(11)), bottom: Math.round(oy(182))
    };
    return {
      ready: defendSpriteReady(),
      frames: DEFEND_FX_FRAMES,
      loadedFrames: defendLoadedFrames(),
      size: first ? first.naturalWidth + 'x' + first.naturalHeight : null,
      playing: !!defendFx,
      phase: defendFx ? defendFx.phase : null,
      frame: idx,
      flip: DEFEND_FX_FLIP,
      drawSize: Math.round(size),
      // 护盾圆心：应压在角色身上（和角色中心 x 对齐）
      anchorX: Math.round(ax),
      anchorY: Math.round(ay),
      playerSx: Math.round(sx),
      groundY: GROUND_Y,
      centeredOnPlayer: Math.abs(Math.round(ax) - Math.round(sx)) <= 2,
      // 盾面覆盖范围（应把角色整个包进去，而不是铺到敌人脚下）
      shieldBox: box,
      coversPlayer: box.left <= sx + 6 && box.right >= sx - 6,
      draw: defendFx ? {
        x: Math.round(ax - DEFEND_FX_ORIGIN.x * size),
        y: Math.round(ay - DEFEND_FX_ORIGIN.y * size),
        w: Math.round(size)
      } : null
    };
  };
  // 调试用：「破空斩」剑气（连续 2 道 → 向前扩散 → 到最右侧边界消失）
  window.__debugAirSlashFx = function () {
    const loaded = (function () {
      let n = 0;
      for (let i = 1; i <= AIRSLASH_FX_FRAMES; i++) {
        const im = images['holyslash_' + i];
        if (im && im.complete && im.naturalWidth > 0) n++;
      }
      return n;
    })();
    const waves = airSlashFx ? airSlashFx.waves.map(function (w) {
      const sx = airSlashWaveScreenX(w);
      return {
        t: Math.round(w.t * 1000) / 1000,
        big: !!w.big,
        yOff: Math.round(w.yOff || 0),
        speedMult: w.speedMult || 1,
        visible: w.t >= 0,
        screenX: Math.round(sx),
        frame: w.t < 0 ? 0 : (Math.floor(w.t * AIRSLASH_FX_FPS) % AIRSLASH_FX_FRAMES) + 1,
        atRightEdge: sx >= W - 150,
        gone: sx - AIRSLASH_FX_SIZE * 0.5 > W
      };
    }) : [];
    return {
      ready: airSlashSpriteReady(),
      frames: AIRSLASH_FX_FRAMES,
      loadedFrames: loaded,
      active: !!airSlashFx,
      waveCount: waves.length,
      waves: waves,
      size: AIRSLASH_FX_SIZE,
      lift: AIRSLASH_FX_LIFT,
      speed: AIRSLASH_FX_SPEED,
      screenW: W,
      groundY: GROUND_Y,
      playerSx: Math.round(player.worldX - cameraX())
    };
  };
  // 调试用：辅助技能石（多重投射物 / 投射物加速 / 处决）当前生效情况
  window.__debugSupportState = function (skillId) {
    const id = skillId || 'airSlash';
    const skill = skillById(id);
    const sockets = skill ? (skill.sockets || []).map(function (g) {
      return { effect: g.effect, name: g.name, projectileOnly: isProjectileOnlyGem(g) };
    }) : [];
    return {
      skill: skill ? skill.name : null,
      sockets: sockets,
      isProjectileSkill: isProjectileSkill(id),
      projectileCount: projectileCountOf(id, AIRSLASH_FX_WAVES),
      projectileSpeedMult: Math.round(projectileSpeedMult(id) * 100) / 100,
      skillSpeedMult: Math.round(skillSpeedMult(id) * 100) / 100,
      waveGap: Math.round(airSlashWaveGap(projectileCountOf(id, AIRSLASH_FX_WAVES)) * 1000) / 1000,
      damagePct: supportDamagePct(id, activeEnemy),
      playerLowLife: playerLowLife(),
      playerHpPct: combat.maxHp > 0 ? Math.round(combat.hp / combat.maxHp * 1000) / 10 : null,
      enemyLowLife: enemyLowLife(activeEnemy),
      enemyHpPct: activeEnemy && activeEnemy.maxHp > 0
        ? Math.round(activeEnemy.hp / activeEnemy.maxHp * 1000) / 10 : null,
      lowLifePct: LOW_LIFE_PCT,
      gemPool: GEM_OPTIONS.filter(function (o) { return o.support; }).map(function (o) {
        return { id: o.id, name: o.name, projectileOnly: !!o.projectileOnly, onlySkill: o.onlySkill || null };
      })
    };
  };
  // 调试用：造一颗指定的技能石放进背包（测试辅助石用）
  window.__debugGrantGem = function (effect) {
    const opt = GEM_OPTIONS.filter(function (o) { return o.id === effect; })[0];
    if (!opt) return null;
    const gem = gemOptionToItem(opt);
    inventory.push(gem);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { id: gem.id, name: gem.name, effect: gem.effect, detail: gem.detail };
  };
  // 调试用：把背包里某个效果的技能石直接镶进指定技能（绕过 UI）
  window.__debugSocketGem = function (effect, skillId, value) {
    let gem = inventory.filter(function (it) { return it.type === 'gem' && it.effect === effect; })[0];
    // 调试便利：背包里没有这颗石头就现场造一颗（测试脚本不用先 grant）
    if (!gem) {
      const opt = GEM_OPTIONS.filter(function (o) { return o.id === effect; })[0];
      if (opt) { gem = gemOptionToItem(opt); inventory.push(gem); }
    }
    // 指定数值（测试用）：被动石想验证"数值保留"就传第三个参数
    if (gem && typeof value === 'number' && !gem.support) {
      gem.value = Math.round(value);
      const opt2 = GEM_OPTIONS.filter(function (o) { return o.id === effect; })[0];
      if (opt2) gem.detail = opt2.name + ' +' + gem.value + opt2.unit;
    }
    const skill = skillById(skillId);
    if (!gem || !skill) return { ok: false, reason: !gem ? '背包里没有这颗技能石' : '没有这个技能' };
    const before = (skill.sockets || []).length;
    socketGemInto(skill, gem);
    const after = (skill.sockets || []).length;
    return {
      ok: after > before,
      socketed: after > before,
      sockets: (skill.sockets || []).map(function (g) { return g.effect; }),
      // 可能同时挂着多条提示，全部取回来（判定用"任意一条"）
      toast: Array.prototype.map.call(document.querySelectorAll('#toast'), function (el) {
        return el.textContent;
      }).join(' | ')
    };
  };
  // 调试用：连续模拟 N 次敌人攻击，返回每次受到的伤害（用于核对 35% 减伤 / 5% 格挡）
  window.__debugSampleEnemyHits = function (defendingOn, n) {
    if (!activeEnemy) return null;
    const keepTurn = battleTurn;
    const out = [];
    const total = Math.max(1, Math.floor(n || 20));
    for (let i = 0; i < total; i++) {
      combat.hp = combat.maxHp;
      const before = combat.hp;
      defending = !!defendingOn;
      enemyCounterAttackCore(false, false);
      out.push(Math.round(before - combat.hp));
    }
    defending = false;
    battleTurn = keepTurn;
    return out;
  };
  window.__debugCage = function () { return castSkill(knownSkills.filter(function (s) { return s.id === 'santosCage'; })[0]); };

  window.__debugWbEnv = function () {
    return {
      ready: wbEnvReady,
      embers: wbEmbers.length,
      dustBands: wbDustBands.length,
      clouds: wbClouds.length,
      flash: Math.round(wbFlash * 100) / 100,
      nextFlashInSec: Math.round(wbFlashTimer * 10) / 10,
      bossArt: !!(activeEnemy && activeEnemy.wbBossArt),
      bossArtLoaded: !!(images[WB_BOSS_ART_KEY] && images[WB_BOSS_ART_KEY].complete && images[WB_BOSS_ART_KEY].naturalWidth > 0),
      bossArtSize: (images[WB_BOSS_ART_KEY] ? images[WB_BOSS_ART_KEY].naturalWidth + 'x' + images[WB_BOSS_ART_KEY].naturalHeight : null)
      ,
      bossArtRect: lastBossArtRect,
      flashHold: wbFlashHold
    };
  };
  // 调试用：战场层次信息（BOSS 立绘 / 前景山丘 / 挑战者位置）
  window.__debugWbScene = function () {
    const cam = cameraX();
    const run = worldBossRun;
    const bossSx = run ? run.boss.x - cam : null;
    return {
      hillEnabled: wbHillEnabled,
      bossArtRect: lastBossArtRect,
      hillTopAtBoss: bossSx === null ? null : Math.round(wbHillLift(bossSx, cam)),
      bossCoveredPx: (lastBossArtRect && bossSx !== null)
        ? Math.round(lastBossArtRect.bottom - wbHillLift(bossSx, cam)) : null,
      ghosts: run ? run.ghosts.map(function (g) {
        return { x: Math.round(bossSx + g.offset), sprite: g.isPlayer ? 'player' : g.sprite };
      }) : [],
      playerSx: Math.round(player.worldX - cam)
    };
  };
  window.__debugWbHill = function (on) {
    wbHillEnabled = on !== false;
    return window.__debugWbScene();
  };
  // 调试用：立刻触发一次血雷闪（验证闪光效果）
  // 传数字 = 钉住闪光强度（方便截帧对比）；传 'release' = 恢复自动衰减；不传 = 触发一次
  window.__debugWbFlash = function (v) {
    if (typeof v === 'number') { wbFlash = v; wbFlashHold = true; }
    else if (v === 'release') { wbFlashHold = false; }
    else { wbFlash = 1; wbFlashHold = false; }
    return window.__debugWbEnv();
  };
  // 调试用：直接抽一次世界BOSS宝箱的掉落（验证几率表）
  window.__debugRollWbChest = function () { return rollWorldBossChestLoot('wbchest'); };
  // 调试用：直接开关战斗日志窗口（战斗日志按钮现在只在战斗中显示）
  window.__debugOpenLog = function () { openBattleLog(); return true; };
  // 调试用：把"今日已领奖励"的日期改成指定值（验证每天一次的规则）
  window.__debugWbSetRewardDay = function (day) {
    wb.lastRewardDay = typeof day === 'string' ? day : shanghaiDayKey(wbNow());
    if (!ui.wbPopup.classList.contains('hidden')) renderWorldBossPanel();
    return { lastRewardDay: wb.lastRewardDay, rewardAvailable: wbRewardAvailable() };
  };
  // 调试用：模拟互联网校时（设定时钟偏移）
  window.__debugWbClock = function (skewMs, source) {
    wbClockSkew = Number(skewMs) || 0;
    wbClockSource = source || (wbClockSkew ? 'internet' : 'local');
    if (!ui.wbPopup.classList.contains('hidden')) renderWorldBossPanel();
    return window.__debugWbSchedule();
  };
  // 调试用：世界BOSS 时间表（上海时间）+ 时间源 + 今日奖励状态
  window.__debugWbSchedule = function () {
    const now = wbNow();
    return {
      clockSource: wbClockSource,
      skewMs: Math.round(wbClockSkew),
      nowUtc: new Date(now).toISOString(),
      nowShanghai: shanghaiDayKey(now) + ' ' + shanghaiClockText(now),
      todayTimes: wbTodayTimes(now).map(function (t) {
        const p = shanghaiParts(t);
        return pad2(p.h) + ':' + pad2(p.mi);
      }),
      nextAt: shanghaiDayKey(wbNextSpawnAt(now)) + ' ' + shanghaiClockText(wbNextSpawnAt(now)),
      nextInSec: Math.round((wbNextSpawnAt(now) - now) / 1000),
      lastAt: shanghaiDayKey(wbLastSpawnAt(now)) + ' ' + shanghaiClockText(wbLastSpawnAt(now)),
      alive: wbAlive(),
      phase: wb.phase,
      rewardDay: wb.lastRewardDay,
      rewardAvailable: wbRewardAvailable(),
      killed: wb.killed
    };
  };
  window.__debugWbTeleport = function () {
    // 默认 force=true（老测试直接进战场）；传 __debugWbTeleport(false) 走"真实按钮"那条路，含境界门槛
    return teleportToWorldBoss(openWorldBossDef(), { force: arguments.length ? arguments[0] !== false : true });
  };
  window.__debugWbGhosts = function () {
    if (!worldBossRun) return null;
    return worldBossRun.ghosts.map(function (g) {
      return { name: g.name, sprite: g.isPlayer ? 'player' : g.sprite, playerModel: g.isPlayer };
    });
  };
  // 调试用：世界BOSS 等级门槛（玩家按多少级计算 / 是否达标）
  window.__debugWbLevel = function () {
    const run = worldBossRun;
    return {
      playerLevel: playerLevel(),
      effectiveLevel: worldBossPlayerLevel(),
      boost: worldBossPlayerLevel() - playerLevel(),
      playerStage: stageText(playerLevel()),
      effectiveStage: stageText(worldBossPlayerLevel()),
      bossLevel: run ? run.boss.level : (openWorldBossDef() ? openWorldBossDef().level : null),
      bossStage: stageText(run ? run.boss.level : (openWorldBossDef() ? openWorldBossDef().level : 1)),
      canDamage: run ? worldBossLevelOk() : null,
      hasLevelBoostTreasure: treasureSpecial('levelBoost')
    };
  };
  // 调试用：统计灵宝特殊属性的抽取概率（验证「破境符诏」很稀有）
  window.__debugTreasureRates = function (n) {
    const count = {};
    const total = Math.max(1, Math.floor(n || 2000));
    for (let i = 0; i < total; i++) {
      const sp = pickTreasureSpecial();
      count[sp.key] = (count[sp.key] || 0) + 1;
    }
    return { total: total, count: count };
  };
  window.__debugWbHurt = function (dmg) {
    // 模拟玩家对BOSS造成伤害（验证参与奖励）
    if (!worldBossRun) return null;
    worldBossRun.boss.hp = Math.max(0, worldBossRun.boss.hp - Math.max(1, Math.round(dmg || 100)));
    worldBossRun.playerDamage += Math.max(1, Math.round(dmg || 100));
    if (worldBossRun.boss.hp <= 0) worldBossBossDown();
    return window.__debugWorldBoss();
  };

  /* ---------- 世界BOSS 伤害榜调试钩子 ---------- */
  /* 调试用：击杀后的战场开箱阶段状态 */
  window.__debugWbLoot = function () {
    const run = worldBossRun;
    const chest = worldBossLootChestRect();
    const playerSx = player.worldX - cameraX();
    return {
      active: worldBossLootActive(),
      timeLeft: run && run.loot ? Math.round(run.loot.timeLeft * 10) / 10 : null,
      opened: !!(run && run.loot && run.loot.done),
      granted: !!(run && run.loot && run.loot.granted),
      auto: !!(run && run.loot && run.loot.auto),
      chestOnScreen: chest ? { cx: Math.round(chest.cx), w: Math.round(chest.w), h: Math.round(chest.h),
        x: Math.round(chest.x), y: Math.round(chest.y), groundY: Math.round(chest.groundY) } : null,
      playerSx: Math.round(playerSx),
      distance: chest ? Math.round(Math.abs(playerSx - chest.cx)) : null,
      gifts: run && run.gifts ? run.gifts.map(function (g) {
        const r = worldBossGiftRect(g);
        return { key: g.key, cx: Math.round(r.cx), w: Math.round(r.w) };
      }) : [],
      rewardPopupOpen: !!(ui.wbRewardPopup && !ui.wbRewardPopup.classList.contains('hidden')),
      hp: Math.round(combat.hp),
      maxHp: combat.maxHp,
      inArena: !!worldBossRun
    };
  };
  // 调试用：把玩家瞬移到宝箱旁边 / 直接开箱 / 让倒计时归零
  window.__debugWbWalkToChest = function () {
    if (!worldBossRun || !worldBossRun.loot) return null;
    player.worldX = worldBossRun.loot.x - 40;
    player.vx = 0;
    return window.__debugWbLoot();
  };
  window.__debugWbOpenLootChest = function (auto) {
    openWorldBossLootChest(!!auto);
    return window.__debugWbLoot();
  };
  // 调试用：强制触发"开箱彩蛋"（天地灵物）
  window.__debugWbLootSpirit = function (force) {
    if (force) spiritRollForced = true;
    const got = rollSpiritItemFromLootChest(worldBossRun);
    spiritRollForced = false;
    return { got: got ? got.name : null, state: window.__debugSpiritState() };
  };
  window.__debugWbLootTimer = function (sec) {
    if (!worldBossRun || !worldBossRun.loot) return null;
    worldBossRun.loot.timeLeft = Math.max(0, Number(sec) || 0);
    return window.__debugWbLoot();
  };
  // 调试用：在BOSS战场里把玩家打死（走"倒下被传送出场"那一条分支）
  window.__debugWbKillPlayer = function () {
    combat.hp = 0;
    updateBattleHud();
    return true;
  };
  // 只把 BOSS 打死、不给玩家加伤害（用来测"第一名是虚影"的情况）
  window.__debugWbKillBoss = function () {
    if (!worldBossRun) return null;
    worldBossRun.boss.hp = 0;
    worldBossBossDown();
    return window.__debugWbDamage();
  };
  window.__debugWbDamage = function () {
    return {
      round: wbDamageBoard ? wbDamageBoard.round : wbDamageRound,
      tracking: !!wbDamageBoard,
      entries: wbDamageBoard ? wbDamageBoard.entries.length : 0,
      ranking: window.__debugWbDamageRanking()
    };
  };
  window.__debugWbDamageRanking = function () {
    return worldBossDamageRanking().map(function (e) {
      return { name: e.name, isPlayer: e.isPlayer, damage: e.damage };
    });
  };
  // 造场景：把某个虚影的伤害钉到指定值
  window.__debugWbSetGhostDamage = function (i, dmg) {
    if (!worldBossRun || !worldBossRun.ghosts) return null;
    const g = worldBossRun.ghosts[Math.max(0, Math.min(i | 0, worldBossRun.ghosts.length - 1))];
    if (!g) return null;
    g.damage = Math.max(0, Math.round(dmg || 0));
    worldBossRun.ghostDamage = worldBossRun.ghosts.reduce(function (s, x) { return s + (x.damage || 0); }, 0);
    return window.__debugWbDamageRanking();
  };
  // 造场景：把所有虚影压到很低，让玩家稳拿第一
  window.__debugWbForceTop = function (gap) {
    if (!worldBossRun) return null;
    worldBossRun.ghosts.forEach(function (g) { g.damage = 10; });
    worldBossRun.ghostDamage = worldBossRun.ghosts.length * 10;
    worldBossRun.playerDamage = worldBossRun.ghostDamage + Math.max(1, Math.round(gap || 100));
    return window.__debugWbDamageRanking();
  };
  // 造场景：让虚影稳拿第一（玩家排名靠后）
  window.__debugWbForceGhostTop = function () {
    if (!worldBossRun) return null;
    worldBossRun.ghosts.forEach(function (g, i) { g.damage = 900 - i * 10; });
    worldBossRun.ghostDamage = worldBossRun.ghosts.reduce(function (s, x) { return s + x.damage; }, 0);
    worldBossRun.playerDamage = 5;
    return window.__debugWbDamageRanking();
  };
  window.__debugInventory = function () {
    return inventory.map(function (it) {
      return { id: it.id, type: it.type, name: it.name, rarity: it.rarity || null, detail: it.detail || '' };
    });
  };
  window.__debugGiveWorldBossChest = function () {
    const chest = makeWorldBossChest();
    inventory.push(chest);
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return { id: chest.id, name: chest.name };
  };
  // 调试用：直接弹出"世界BOSS挑战奖励"三个宝箱（看美术 / 测开启流程）
  window.__debugWbRewardPopup = function () {
    showWorldBossRewardChests(true);
    return window.__debugWbChests();
  };
  window.__debugWbChests = function () {
    const box = ui.wbRewardChests;
    if (!box) return null;
    const cards = Array.prototype.slice.call(box.querySelectorAll('.wbChest'));
    return {
      open: !ui.wbRewardPopup.classList.contains('hidden'),
      count: cards.length,
      // 每格结构是不是齐全：掉落光柱 + 方形槽位（槽里要么是箱子图，要么是战利品）
      partsOk: cards.every(function (c) {
        return ['.wbChestBeam', '.wbChestBox'].every(function (sel) { return !!c.querySelector(sel); });
      }),
      // 未开启时用的箱子美术
      chestImgs: cards.map(function (c) {
        const img = c.querySelector('.wbChestImg');
        return img ? img.getAttribute('src') : null;
      }),
      tags: cards.map(function (c) { return (c.querySelector('.wbChestTag') || {}).textContent; }),
      loot: cards.map(function (c) { return (c.querySelector('.wbChestLoot') || {}).textContent; }),
      names: cards.map(function (c) { return (c.querySelector('.wbChestName') || {}).textContent; }),
      lidTransforms: cards.map(function (c) {
        const lid = c.querySelector('.wbChestLid');
        return lid ? getComputedStyle(lid).transform : null;
      }),
      // 新界面：稀有度主色 / 掉落光柱高度 / 槽位里是箱子还是战利品
      colors: cards.map(function (c) { return c.style.getPropertyValue('--lc') || null; }),
      beamHeights: cards.map(function (c) { return Math.round(c.querySelector('.wbChestBeam').getBoundingClientRect().height); }),
      slotItems: cards.map(function (c) {
        const it = c.querySelector('.wbChestItem');
        if (!it) return null;
        const img = it.querySelector('img');
        return img ? ('img:' + img.getAttribute('src')) : ('glyph:' + it.textContent);
      }),
      slotBox: (function () {
        const b = box.querySelector('.wbChestBox');
        if (!b) return null;
        const r = b.getBoundingClientRect();
        return { w: Math.round(r.width), h: Math.round(r.height), square: Math.abs(r.width - r.height) <= 1 };
      })(),
      progress: ui.wbRewardProgressText ? ui.wbRewardProgressText.textContent : null,
      columns: getComputedStyle(box).gridTemplateColumns
    };
  };
  window.__debugWbOpenChest = function (i) {
    if (!wbRewardOpening) return null;
    const c = wbRewardOpening.chests[Math.max(0, Math.min(i || 0, wbRewardOpening.chests.length - 1))];
    openWorldBossRewardChest(c);
    return window.__debugWbChests();
  };
  window.__debugOpenWorldBossChest = function () {
    const chest = inventory.filter(function (it) { return it.type === 'worldChest'; })[0];
    if (!chest) return null;
    return openWorldBossChest(chest);
  };
  window.__debugChest = function () {
    return chest ? {
      worldX: Math.round(chest.worldX),
      opened: !!chest.opened,
      weaponTaken: !!chest.weaponTaken,
      gemTaken: !!chest.gemTaken,
      stoneTaken: !!chest.stoneTaken
    } : null;
  };
  window.__debugOpenChest = function () { openChestPanel(); return !!chest; };
  window.__debugOpenSpell = function () { openSpellPanel(); return true; };
  window.__debugOpenGear = function () { openGearPanel(); return true; };
  window.__debugOpenShop = function () { openShop(); renderShop(); return true; };
  window.__debugOpenChar = function () { openCharPanel(); return true; };
  // 调试用：首页（点角色登录）/ 赞助窗口
  window.__debugIntro = function () {
    const sx = player.worldX - cameraX();
    return {
      gameState: gameState,
      loginHidden: ui.loginScreen.classList.contains('hidden'),
      titleHidden: ui.title.classList.contains('hidden'),
      resting: playerResting,
      restPose: restPoseIdx,
      frameKey: lastPlayerFrameKey,        // 当前实际绘制的帧（首页应为 player_rest_3）
      frameFile: lastPlayerFrameKey ? assetUrl('assets/img/player/' + lastPlayerFrameKey + '.png') : null,
      playerSx: Math.round(sx),
      groundY: GROUND_Y,
      playerTopY: Math.round(GROUND_Y + 4 - (PLAYER_FRAME.h - PLAYER_FRAME.basePad) * playerFx.scale * caveScale()),
      dialogVisible: introDialogVisible(),
      donateVisible: ui.btnDonate ? !ui.btnDonate.classList.contains('hidden') : null
    };
  };
  window.__debugClickPlayer = function () {
    const sx = player.worldX - cameraX();
    const hit = hitPlayerAt(sx, GROUND_Y - 90);
    if (hit) openLoginFromIntro();
    return {
      hit: hit,
      loginHidden: ui.loginScreen.classList.contains('hidden'),
      // 附带诊断信息，便于排查"首页看不到角色"之类的问题
      diag: {
        idLoaded: !!(images.player_idle_1 && images.player_idle_1.complete),
        alpha: playerFx.alpha,
        hide: debugHidePlayer,
        lastFrame: lastPlayerFrameKey,
        resting: playerResting,
        restIdx: restPoseIdx,
        restLoaded: playerRestLoadedCount()
      }
    };
  };
  window.__debugClickAt = function (sx, sy) {
    return hitPlayerAt(sx, sy);
  };
  window.__debugDonate = function () {
    return {
      visible: ui.btnDonate ? !ui.btnDonate.classList.contains('hidden') : null,
      popupOpen: ui.donatePopup ? !ui.donatePopup.classList.contains('hidden') : null,
      alipayLoaded: !!(ui.donateAlipay && ui.donateAlipay.naturalWidth > 0),
      wechatLoaded: !!(ui.donateWechat && ui.donateWechat.naturalWidth > 0),
      title: ui.donatePopup ? (ui.donatePopup.querySelector('h2') || {}).textContent : null
    };
  };
  window.__debugOpenDonate = function () { openDonate(); return window.__debugDonate(); };
  window.__debugCloseDonate = function () { closeDonate(); return window.__debugDonate(); };
  window.__debugCloseChar = function () { closeCharPanel(); return true; };
  window.__debugNewbieChestFlag = function () { return newbieChestUsed; };
  window.__debugKillPlayer = function () {
    combat.hp = 0;
    if (!deathActive) triggerDeath();   // 走完整阵亡流程（含洞府掉落惩罚）
    return {
      hp: combat.hp,
      death: deathActive,
      deathOpen: !ui.deathPopup.classList.contains('hidden'),
      lostRows: ui.deathLoss ? ui.deathLoss.querySelectorAll('.lootRow').length : 0
    };
  };
  window.__debugDeathState = function () {
    return {
      deathActive: deathActive,
      gameState: gameState,
      popup: ui.deathPopup ? !ui.deathPopup.classList.contains('hidden') : null,
      distanceKm: Math.round((Math.max(0, player.worldX - START_X) / KM_UNIT) * 10) / 10,
      hp: Math.round(combat.hp),
      maxHp: combat.maxHp,
      defeated: defeatedEnemies
    };
  };
  window.__debugQuakeDamage = function () {
    const atkMin = 23 + derived.flatAtk;
    const atkMax = 34 + derived.flatAtk;
    return {
      atkMin: atkMin, atkMax: atkMax, strength: derived.strengthTotal, flatAtk: derived.flatAtk,
      quake: Math.max(1, Math.round(randInt(atkMin, atkMax) * 0.5 + derived.strengthTotal))
    };
  };
  window.__debugBleed = function () {
    return {
      waves: shockWaves.length,
      enemies: battleAlive().map(function (e) {
        return { name: enemyLabel(e, false), hp: Math.round(e.hp),
          bleed: e.bleed ? { dmg: Math.round(e.bleed.dmg), turns: e.bleed.turns } : null };
      })
    };
  };
  window.__debugCastSkill = function (id) {
    const skill = knownSkills.filter(function (s) { return s.id === id; })[0];
    if (!skill) return null;
    const before = battleAlive().map(function (e) {
      return { name: enemyLabel(e, false), hp: e.hp, level: e.level };
    });
    castSkill(skill);
    const after = battleAlive().map(function (e) { return { name: enemyLabel(e, false), hp: e.hp }; });
    const hits = before.map(function (b) {
      const a = after.filter(function (x) { return x.name === b.name; })[0];
      return { name: b.name, dmg: a ? (b.hp - a.hp) : b.hp };
    });
    return { skill: skill.name, hits: hits, log: ui.battleLog ? ui.battleLog.textContent : '' };
  };
  // 调试用：把背包里最新的灵宝穿上
  // 调试用：造一件只有"力量 +N"的手套并穿上（核对力量→生命的换算）
  window.__debugMakeStrengthGloves = function (n) {
    const it = makeEquipment(1, 'common', 'gloves');
    it.mods = [{ key: 'strength', value: Math.max(1, Math.round(n || 6)), value2: 0, bandIdx: 0, locked: false }];
    applyItemMods(it);
    equipItem(it);
    return { name: it.name, detail: it.detail, strength: it.strength || 0, slot: it.slot };
  };
  // 调试用：造一双带指定三维词缀的鞋子并穿上（验收"装备敏捷/智力真的生效"）
  window.__debugMakeStatShoes = function (mods) {
    const it = makeEquipment(10, 'rare', 'shoes');
    const list = Array.isArray(mods) ? mods : [{ key: mods || 'agility', value: 20 }];
    it.mods = list.map(function (m) {
      return { key: m.key, value: Math.max(1, Math.round(m.value || 20)), value2: 0, bandIdx: 0, locked: false };
    });
    applyItemMods(it);
    equipItem(it);
    recalcPlayerStats();
    return {
      name: it.name, detail: it.detail, slot: it.slot,
      strength: it.strength || 0, agility: it.agility || 0, intelligence: it.intelligence || 0
    };
  };
  window.__debugEquipTreasure = function () {
    let idx = -1;
    inventory.forEach(function (it, i) { if (it.slot === 'treasure') idx = i; });
    if (idx < 0) return null;
    equipItem(inventory[idx]);
    return equipment.treasure
      ? { name: equipment.treasure.name, special: equipment.treasure.special }
      : null;
  };
  window.__debugUnequipTreasure = function () {
    if (!equipment.treasure) return null;
    unequipSlot('treasure');
    return { equipped: !!equipment.treasure, effLevel: worldBossPlayerLevel() };
  };
  // 调试用：直接结算一个洞府（验证金色洞府掉灵宝）
  window.__debugFinishCave = function (id) {
    const def = CAVE_DEFS.filter(function (d) { return d.id === (id || 'gold'); })[0] || CAVE_DEFS[2];
    const beforeCount = inventory.length;
    const goldBefore = gold;
    cave = { def: def, remaining: 0, total: def.enemies, killed: def.enemies, entrance: null };
    finishCave();
    const added = inventory.slice(beforeCount);
    const treasure = added.filter(function (it) { return !!it.special; })[0] || null;
    return {
      cave: def.id,
      goldGain: gold - goldBefore,
      added: added.map(function (it) { return it.name; }),
      treasure: treasure
        ? { name: treasure.name, special: treasure.special, detail: treasure.detail }
        : null
    };
  };
  // 调试用：造一个速度极高的敌人开战，看谁先手
  window.__debugTestFirstStrike = function () {
    spawnNextEnemy();
    if (!activeEnemy) return null;
    activeEnemy.combatSpeed = 9.9;
    player.worldX = activeEnemy.x - ENEMY_GAP_PX;
    startBattle();
    const turn = battleTurn;
    // 收尾：把这场假战斗清干净
    pendingVictory = 0;
    activeEnemy = null;
    battleGroup.length = 0;
    gameState = 'walk';
    showBattleUi(false);
    setControls('walk');
    syncMoveButton();
    return { turn: turn, playerSpd: derived.speedMult, enemySpd: 9.9 };
  };
  // 调试用：直接赢一场，看金币变化（验证「聚宝灵纹」）
  window.__debugTestBattleGold = function () {
    spawnNextEnemy();
    if (!activeEnemy) return null;
    const before = gold;
    activeEnemy.hp = 1;
    winBattle();
    return { before: before, after: gold, delta: gold - before };
  };
  // 调试用：存档快照 / 手动存档（验证读档恢复金币与装备）
  window.__debugSave = function () { saveGame(); return activeSaveKey(); };
  // 调试用：联网状态 / 登录 / 存档同步 / 聊天
  window.__debugNetState = function () {
    return {
      serverUrl: net.serverUrl,
      accountOnline: net.accountOnline,
      chatState: net.chatState,
      onlineCount: net.onlineCount,
      hasToken: !!net.token,
      pushCount: net.pushCount,
      lastPushAt: net.lastPushAt,
      pendingSave: !!net.pendingSave,
      lastError: net.lastError,
      accountName: account.name,
      saveKey: activeSaveKey(),
      localSavedAt: (function () {
        try {
          const raw = window.localStorage.getItem(activeSaveKey());
          return raw ? (JSON.parse(raw).savedAt || 0) : 0;
        } catch (e) { return 0; }
      })()
    };
  };
  // 调试用：直接走一遍"服务器登录"（等同在登录页点登录）
  window.__debugNetLogin = async function (user, pwd, server) {
    if (ui.loginUser) ui.loginUser.value = user || '';
    if (ui.loginPwd) ui.loginPwd.value = pwd || '';
    if (server && ui.loginIp) ui.loginIp.value = server;
    await doLogin();
    return window.__debugNetState();
  };
  // 调试用：把当前存档立刻推给服务器
  window.__debugNetFlush = async function () {
    net.pendingSave = buildSavePayload();
    const r = await flushRemoteSaveNow();
    return { pushed: !!r, state: window.__debugNetState() };
  };
  // 调试用：从服务器重新拉一次存档（返回服务器上那份的等级 / 金币）
  window.__debugNetFetchSave = async function () {
    if (!net.api || !net.token) return null;
    const r = await net.api.fetchSave(net.token);
    return {
      version: r.version, savedAt: r.savedAt,
      level: r.save ? r.save.level : null,
      gold: r.save ? r.save.gold : null,
      vitality: r.save ? r.save.vitality : null,
      distance: r.save ? r.save.distance : null,
      codexCount: r.save && r.save.codex ? r.save.codex.length : 0,
      equipmentCount: r.save && r.save.equipment ? Object.keys(r.save.equipment).length : 0
    };
  };
  // 调试用：发一条聊天（走联网通道）
  window.__debugChatSend = function (text) {
    if (!net.chat) return false;
    net.chat.send({ type: 'chat', channel: CHAT_CHANNEL, text: text });
    return true;
  };
  // 调试用：实名绑定状态 / 打开绑定窗口 / 直接绑定
  window.__debugIdentity = function () {
    return {
      bound: net.identityBound,
      profile: net.profile,
      popupOpen: ui.identityPopup ? !ui.identityPopup.classList.contains('hidden') : null,
      chatStatus: ui.chatStatus ? ui.chatStatus.textContent : null
    };
  };
  window.__debugOpenIdentity = function () { openIdentityPanel(''); return true; };
  window.__debugBindIdentity = async function (realName, idCard) {
    if (ui.idRealName) ui.idRealName.value = realName || '';
    if (ui.idCardNo) ui.idCardNo.value = idCard || '';
    if (ui.identityPopup) ui.identityPopup.classList.remove('hidden');
    const okDone = await submitIdentity();
    return { ok: okDone, state: window.__debugIdentity() };
  };
  // 调试用：金币等数值是否已经落盘（本地 / 云端）
  window.__debugNetToken = function () { return net.token; };
  // 调试用：燃烧层数 / 昵称 / 多段伤害
  window.__debugBurnState = function () {
    const list = battleAlive().map(function (e) {
      return { name: enemyLabel(e, false), stacks: e.burnStacks || 0, hp: Math.round(e.hp) };
    });
    return {
      stacks: activeEnemy ? (activeEnemy.burnStacks || 0) : 0,
      enemies: list,
      perStack: Math.max(1, Math.round(combat.maxHp * BURN_PCT)),
      maxHp: combat.maxHp,
      chance: BURN_CHANCE,
      hasGem: skillHasGem('yangClaw', 'flameLegion')
    };
  };
  window.__debugSetBurn = function (n) {
    if (!activeEnemy) return null;
    activeEnemy.burnStacks = Math.max(0, Math.min(BURN_STACK_MAX, Math.round(n || 0)));
    return activeEnemy.burnStacks;
  };
  window.__debugBurnTick = function () {
    const before = activeEnemy ? Math.round(activeEnemy.hp) : null;
    applyBurnTick();
    return { hpBefore: before, hpAfter: activeEnemy ? Math.round(activeEnemy.hp) : null };
  };
  window.__debugNick = function () {
    return {
      accountName: account.name,
      nickname: account.nickname,
      chatNick: chatNick(),
      popupOpen: ui.nickPopup ? !ui.nickPopup.classList.contains('hidden') : null,
      localSaved: loadLocalNickname(),
      battleWho: (function () {
        const el = document.querySelector('#battleHud .who');
        return el ? el.textContent : null;
      })()
    };
  };
  window.__debugSetNickname = async function (nick) {
    openNickPanel();                                   // 先开面板（会用当前昵称填充输入框）
    if (ui.nickInput) ui.nickInput.value = nick || '';  // 再写入要测的值
    const done = await submitNickname();
    return { ok: done, state: window.__debugNick() };
  };
  window.__debugSaveState = function () {
    let local = null;
    try {
      const raw = window.localStorage.getItem(activeSaveKey());
      local = raw ? JSON.parse(raw) : null;
    } catch (e) { local = null; }
    return {
      memoryGold: gold,
      memoryVitality: vitality,
      memoryLevel: player.level,
      localGold: local ? local.gold : null,
      localSavedAt: local ? local.savedAt : null,
      dirty: saveDirty,
      pendingUpload: !!net.pendingSave,
      accountOnline: net.accountOnline,
      pushCount: net.pushCount
    };
  };
  window.__debugChatRows = function () {
    if (!ui.chatList) return [];
    return Array.prototype.map.call(ui.chatList.children, function (row) {
      return row.textContent.replace(/\s+/g, ' ').trim();
    });
  };
  window.__debugSaveSnapshot = function () {
    return {
      key: activeSaveKey(),
      gold: gold,
      level: player.level,
      vitality: vitality,
      flask: flaskCharges,
      equipment: Object.keys(equipment).filter(function (k) { return equipment[k]; })
        .map(function (k) { return { slot: k, name: equipment[k].name, level: equipment[k].level, detail: equipment[k].detail }; }),
      bag: inventory.map(function (it) { return { type: it.type, name: it.name }; })
    };
  };
  window.__debugShopItems = function (n) {
    const out = [];
    for (let i = 0; i < (n || 12); i++) {
      const lv = getShopItemLevel();
      out.push({ level: Math.max(1, lv + randInt(-1, 1)), name: makeEquipment(lv).name });
    }
    return { shopLevel: getShopItemLevel(), playerLevel: player.level, levels: out.map(function (o) { return o.level; }) };
  };
  window.__debugAttrPoints = function () {
    const spent = ATTR_DEFS.reduce(function (sum, d) { return sum + (player.attrs[d.id] || 0); }, 0);
    return {
      level: player.level,
      attrs: { str: player.attrs.str, agi: player.attrs.agi, int: player.attrs.int },
      attrPoints: player.attrPoints,
      spent: spent,
      maxAvailable: Math.max(0, (player.level - 1) * ATTR_POINTS_PER_LEVEL - spent)
    };
  };
  window.__debugOnline = function (addSeconds) {
    if (typeof addSeconds === 'number' && addSeconds > 0) {
      onlineSeconds += addSeconds;
      const before = onlinePending;
      let lastStep = 0;
      while (onlineSeconds >= onlineNextStep()) {
        lastStep = onlineNextStep();
        onlineSeconds -= lastStep;
        onlineRewardIndex += 1;
        onlinePending += 1;
      }
      updateOnlineUI();
      // 注意：这里只累计"可领取"，活力要玩家自己点领取（对齐线上行为）
      return { pending: onlinePending, pendingGained: onlinePending - before, vitality: vitality,
        remainder: Math.round(onlineSeconds), lastStep: lastStep, nextStep: onlineNextStep(),
        index: onlineRewardIndex, claimable: onlinePending > 0 };
    }
    return { onlineSeconds: Math.round(onlineSeconds), nextStep: onlineNextStep(), vitality: vitality,
      index: onlineRewardIndex, pending: onlinePending, steps: ONLINE_REWARD_STEPS.slice(),
      text: ui.onlineTimer ? ui.onlineTimer.textContent : null,
      claimVisible: !!(ui.btnOnlineClaim && !ui.btnOnlineClaim.classList.contains('hidden')) };
  };
  // 调试用：手动领取在线奖励（等同点击 HUD 上的「领取」按钮）
  window.__debugOnlineClaim = function () {
    const before = vitality;
    claimOnlineReward();
    return { claimed: vitality - before, vitality: vitality, pending: onlinePending };
  };
  // 调试用：把在线奖励阶梯清零（重新从 3 分钟开始）
  window.__debugOnlineReset = function () {
    onlineSeconds = 0;
    onlineRewardIndex = 0;
    onlinePending = 0;
    updateOnlineUI();
    return onlineNextStep();
  };
  window.__debugPrices = function (n) {
    const out = { common: [], magic: [], rare: [] };
    ['common', 'magic', 'rare'].forEach(function (r) {
      for (let i = 0; i < (n || 12); i++) {
        out[r].push(makeEquipment(10, r, ['weapon', 'helmet', 'jewelry', 'clothes'][i % 4]).price);
      }
    });
    return out;
  };
  // 调试用：批量造鞋子，检查词缀库与数值上限
  window.__debugShoes = function (n, level) {
    const count = n || 60;
    const lv = level || 28;
    let hasAtk = 0, hasThorns = 0, hasStr = 0, hasAgi = 0, hasInt = 0;
    const max = { thornsMax: 0, hpMax: 0, armorMax: 0, speedMax: 0, resMax: 0, strengthMax: 0, agilityMax: 0, intelligenceMax: 0, chaosResMax: 0, regenMax: 0 };
    // 单条词缀的最大值（同一件装备可能有重复词缀叠加，所以单独统计"每条"的上限）
    const modMax = { strength: 0, agility: 0, intelligence: 0, hp: 0, armor: 0, thorns: 0, speedPct: 0, res: 0, mp: 0 };
    for (let i = 0; i < count; i++) {
      const it = makeEquipment(lv, ['common', 'magic', 'rare'][i % 3], 'shoes');
      (it.mods || []).forEach(function (m) {
        if (m.key === 'atk') hasAtk++;
        if (m.key === 'thorns') hasThorns++;
        if (m.key === 'strength') hasStr++;
        if (m.key === 'agility') hasAgi++;
        if (m.key === 'intelligence') hasInt++;
        if (modMax[m.key] !== undefined) modMax[m.key] = Math.max(modMax[m.key], m.value || 0);
      });
      max.thornsMax = Math.max(max.thornsMax, it.thorns || 0);
      max.hpMax = Math.max(max.hpMax, it.hp || 0);
      max.armorMax = Math.max(max.armorMax, it.armor || 0);
      max.speedMax = Math.max(max.speedMax, it.speedPct || 0);
      max.resMax = Math.max(max.resMax, it.res || 0);
      max.strengthMax = Math.max(max.strengthMax, it.strength || 0);
      max.agilityMax = Math.max(max.agilityMax, it.agility || 0);
      max.intelligenceMax = Math.max(max.intelligenceMax, it.intelligence || 0);
      max.chaosResMax = Math.max(max.chaosResMax, it.chaosRes || 0);
      max.regenMax = Math.max(max.regenMax, it.lifeRegen || 0);
    }
    return {
      count: count, level: lv,
      hasAtk: hasAtk, hasThorns: hasThorns,
      hasStr: hasStr, hasAgi: hasAgi, hasInt: hasInt,
      range: max, modMax: modMax
    };
  };
  // 调试用：造一双指定档位的鞋子并返回文本（预览/核对用）
  window.__debugShoeSample = function (i) {
    const rarities = ['common', 'magic', 'rare', 'rare', 'magic'];
    const it = makeEquipment(28, rarities[(i || 0) % rarities.length], 'shoes');
    return { name: it.name, detail: it.detail, level: it.level };
  };
  // 调试用：把几双 28 级鞋子塞进背包
  window.__debugShowBagShoes = function () {
    for (let i = 0; i < 6; i++) inventory.push(makeEquipment(28, ['common', 'magic', 'rare'][i % 3], 'shoes'));
    if (!ui.gearPopup.classList.contains('hidden')) renderGearPanel();
    return inventory.length;
  };
  window.__debugAccounts = function () {
    const store = readAccounts();
    return {
      current: account.hash ? { name: account.name, hash: account.hash, isNew: account.isNew } : null,
      accounts: store.accounts.map(function (a) {
        const raw = window.localStorage.getItem(saveKeyFor(a.hash));
        let lv = null;
        try { lv = raw ? (JSON.parse(raw).level || 1) : null; } catch (e) { lv = null; }
        return { name: a.name, hash: a.hash, savedLevel: lv, hasSave: !!raw };
      }),
      legacySaveExists: !!window.localStorage.getItem(LEGACY_SAVE_KEY),
      saveKeys: Object.keys(window.localStorage).filter(function (k) { return k.indexOf('homeward.save.') === 0; })
    };
  };
  window.__debugAccountHash = function (name, pwd) { return accountHash(name, pwd); };
  window.__debugWriteSaveLevel = function (level) {   // 造一个"已存在的档案"用来测续档
    player.level = Math.max(1, level || 3);
    player.xp = 0;
    saveGame();
    return activeSaveKey();
  };
  window.__debugResetSaves = function () {            // 一键抹掉本机所有档案（含旧版单存档）
    resetAllSaves();
    return true;
  };
  // 调试用：当前穿戴的装备快照（洗练前后对比用）
  window.__debugEquipped = function () {
    const out = {};
    Object.keys(equipment).forEach(function (k) {
      const it = equipment[k];
      if (!it) return;
      out[k] = { name: it.name, level: it.level, rarity: it.rarity, detail: it.detail,
        slot: it.slot, iconSet: it.iconSet || null, iconImg: it.iconImg || 0,
        stats: GEAR_STAT_KEYS.reduce(function (acc, key) { if (it[key]) acc[key] = it[key]; return acc; }, {}) };
    });
    return out;
  };
  // 调试用：休息姿态——传秒数可缩短"随机换姿态"的间隔；传对象可强制指定姿态
  window.__debugRestPose = function (arg) {
    if (typeof arg === 'number') {
      restPoseTimer = arg;
      return restPoseTimer;
    }
    if (arg && typeof arg.force === 'number') {
      restPoseIdx = ((arg.force % PLAYER_REST_FRAMES) + PLAYER_REST_FRAMES) % PLAYER_REST_FRAMES;
      playerResting = true;
    }
    return { resting: playerResting, pose: restPoseIdx, timer: Math.round(restPoseTimer * 10) / 10, loaded: playerRestLoadedCount() };
  };
  // 调试用：查看/修改精英按钮的"中央等待时长"（秒）与当前停靠状态
  window.__debugEliteDock = function (sec) {
    if (typeof sec === 'number') {
      eliteDockWait = sec;
      return eliteDockWait;
    }
    return {
      docked: eliteDocked,
      wait: eliteDockWait,
      shift: ui.btnElite ? (ui.btnElite.style.getPropertyValue('--eliteShift') || null) : null,
      className: ui.btnElite ? ui.btnElite.className : null,
      rect: ui.btnElite ? (function () {
        const r = ui.btnElite.getBoundingClientRect();
        return { left: Math.round(r.left), right: Math.round(r.right), cy: Math.round((r.top + r.bottom) / 2) };
      })() : null,
      wrap: ui.wrap ? (function () {
        const r = ui.wrap.getBoundingClientRect();
        return { left: Math.round(r.left), right: Math.round(r.right), cy: Math.round((r.top + r.bottom) / 2) };
      })() : null
    };
  };
  // 调试用：在玩家前方摆好三种洞府入口（测试用，正常游戏按路程随机生成）
  window.__debugPlaceCaves = function () {
    caveEntrances.length = 0;
    ['green', 'blue', 'gold'].forEach(function (id, i) {
      const def = CAVE_DEFS.filter(function (d) { return d.id === id; })[0];
      caveEntrances.push({ def: def, worldX: player.worldX + 520 + i * 420, used: false });
    });
    return caveEntrances.map(function (c) { return c.def.id + '@' + Math.round(c.worldX); });
  };
  // 调试用：直接进入指定洞府（相当于点击入口）
  window.__debugClickCave = function (id) {
    const entrance = caveEntrances.filter(function (c) {
      return c.def.id === id && !c.used;
    })[0];
    if (!entrance) return 'no entrance';
    clickCaveEntrance(entrance);
    return cave ? cave.def.name : (caveTransition ? 'entering' : 'blocked');
  };
  // 调试用：把角色瞬移到指定公里数（测试洞府生成）
  window.__debugTeleport = function (km) {
    player.worldX = START_X + km * KM_UNIT;
    defeatedEnemies = Math.max(defeatedEnemies, Math.floor(km / (ENEMY_GAP / KM_UNIT)));
    spawnNextEnemy();
    updateDist();
    return km;
  };
  // 调试用：把当前路上的洞府入口标记为已用过（模拟“路过不进”）
  window.__debugSkipEntrances = function () {
    caveEntrances.forEach(function (c) { c.used = true; });
    return true;
  };
  // 调试用：查看玩家 / 敌人的实际绘制缩放与战斗阵型
  window.__debugLogs = function () {
    return battleLogHistory.map(function (e) { return e.text; });
  };
  // 调试用：当前屏幕上的飘字（每个敌人的伤害应该是独立一条）
  window.__debugPopups = function () {
    return damagePopups.map(function (p) {
      return p.text ? p.text : String(p.dmg);
    });
  };
  // 调试用：飘字的位置（用来核对"伤害数字是不是飘在被攻击单位身边"）
  window.__debugPopupList = function () {
    return damagePopups.map(function (p) {
      return {
        text: p.text || ((p.positive ? '+' : '-') + p.dmg),
        x: Math.round(p.x),
        y: Math.round(p.y),
        color: p.color
      };
    });
  };
  // 调试用：清空当前飘字（测试时用来隔离"敌方出手"这一批）
  window.__debugClearPopups = function () {
    const n = damagePopups.length;
    damagePopups.length = 0;
    return n;
  };
  // 调试用：刀光资源与当前播放帧
  window.__debugSlashSprite = function () {
    return slashSpriteReady();
  };
  window.__debugSlashFx = function () {
    if (!slashFx) return null;
    const p = Math.min(1, slashFx.t / slashFx.dur);
    return {
      t: Math.round(slashFx.t * 1000) / 1000,
      dur: slashFx.dur,
      frame: Math.min(SLASH_FRAMES - 1, Math.floor(p * SLASH_FRAMES)) + 1,
      hit: slashFx.hit
    };
  };
  // 调试用：普通攻击的刀光（Slash 3 素材）
  window.__debugAttackSprite = function () {
    const first = images.slash3_1;
    return {
      ready: attackSpriteReady(),
      size: first ? first.naturalWidth + 'x' + first.naturalHeight : null,
      frame: attackFx ? Math.min(SLASH_FRAMES, Math.floor(attackFx.t / attackFx.dur * SLASH_FRAMES) + 1) : 0
    };
  };
  // 调试用：当前宠物的精灵图状态
  // 调试用：敌人身体与刀光在屏幕上的位置（核对特效是否落在敌人身上）
  window.__debugFxRects = function () {
    const sc = activeEnemy ? enemyDrawScale(activeEnemy) * caveScale() : 1;
    const ex = activeEnemy ? Math.round(activeEnemy.x - cameraX()) : null;
    const dh = 178 * sc;
    return {
      groundY: GROUND_Y,
      enemyX: ex,
      enemyBody: ex === null ? null : {
        left: Math.round(ex - 148 * sc / 2),
        top: Math.round(GROUND_Y - dh + 4),
        bottom: Math.round(GROUND_Y + 4),
        centerY: Math.round(GROUND_Y - dh / 2 + 4)
      },
      attackFx: attackFx ? {
        spriteCenterY: Math.round(GROUND_Y - ATTACK_SPRITE_LIFT),
        size: ATTACK_SPRITE_SIZE
      } : null,
      slashFx: slashFx ? {
        spriteCenterY: Math.round(GROUND_Y - SLASH_SPRITE_LIFT),
        anchorY: Math.round(GROUND_Y - 96)
      } : null
    };
  };
  window.__debugPetFlip = function () {
    return PET_SPRITE_FLIP;
  };
  // 调试用：立刻生成一个"其他玩家"虚影（可选指定模式）
  window.__debugSpawnMirage = function (mode) {
    spawnMirage(mode);
    return mirage ? mirage.mode : null;
  };
  window.__debugMirage = function () {
    if (!mirage) return null;
    return {
      mode: mirage.mode,
      t: Math.round(mirage.t * 100) / 100,
      life: Math.round(mirage.life * 100) / 100,
      worldX: Math.round(mirage.worldX),
      sx: Math.round(mirage.worldX - cameraX()),
      playerSx: Math.round(player.worldX - cameraX())
    };
  };
  window.__debugPetSprite = function () {
    if (!petAlive()) return null;
    const p = leadPet();
    const key = p.sprite || 'snake';
    const info = PET_SPRITES[key] || {};
    const anims = ['idle', 'walk', 'attack', 'hurt'].map(function (a) {
      const img = images['pet_' + key + '_' + a];
      return a + ':' + (img && img.naturalWidth ? (img.naturalWidth / PET_FRAME_SIZE) + '帧' : '缺失');
    });
    return {
      pet: p.name, key: key, ok: !!info.name, sheet: 'pet_' + key, anims: anims.join(' '),
      count: alivePets().length,
      slots: pets.map(function (x) { return x.name + '(等级' + (x.level || 1) + ')'; })
    };
  };
  // 调试用：把当前目标打成残血，测试"击杀后其余敌人照样还手"
  window.__debugBoostEnemyHp = function (mult) {
    if (!activeEnemy) return 0;
    const m = Math.max(1, mult || 5);
    activeEnemy.maxHp = Math.round(activeEnemy.maxHp * m);
    activeEnemy.hp = activeEnemy.maxHp;
    updateBattleHud();
    return activeEnemy.maxHp;
  };
  window.__debugHurtPrimary = function (hp) {
    if (!activeEnemy) return false;
    activeEnemy.hp = Math.max(1, Math.round(hp || 1));
    updateBattleHud();
    return activeEnemy.hp;
  };
  // 调试用：把玩家生命设成最大生命的百分之多少（测「处决」的低血判定）
  window.__debugSetPlayerHp = function (pct) {
    const p = clamp(Number(pct) || 100, 1, 100);
    combat.hp = Math.max(1, Math.round(combat.maxHp * p / 100));
    updateBattleHud();
    return {
      hp: combat.hp, maxHp: combat.maxHp,
      pct: Math.round(combat.hp / combat.maxHp * 100),
      lowLife: playerLowLife()
    };
  };
  // 调试用：把玩家魔力补到最大值的百分之多少（连放技能采样时用）
  window.__debugSetMp = function (pct) {
    const max = derived.mpUsable || combat.maxMp || 0;
    combat.mp = Math.max(0, Math.round(max * clamp(Number(pct) || 100, 0, 100) / 100));
    updateBattleHud();
    return { mp: Math.round(combat.mp), max: Math.round(max) };
  };
  window.__debugScales = function () {
    return {
      cave: cave ? cave.def.id : null,
      player: playerFx.scale * caveScale(),
      enemyMode: caveScale(),
      enemyTier: activeEnemy ? enemyDrawScale(activeEnemy) : null,
      enemy: activeEnemy ? enemyDrawScale(activeEnemy) * caveScale() : null
    };
  };
  window.__debugGroupInfo = function () {
    return {
      primary: activeEnemy ? { x: activeEnemy.x, tier: activeEnemy.tier } : null,
      helpers: battleGroup.filter(function (e) { return e !== activeEnemy; }).map(function (e) {
        return { x: e.x, moveToX: e.moveToX, tier: e.tier };
      })
    };
  };
  // 调试用：强制把附近敌人拉进当前战斗
  window.__debugForceJoin = function () {
    if (!cave || !activeEnemy) return false;
    const def = cave.def;
    const tier = def.tiers[randInt(0, def.tiers.length - 1)];
    addCaveHelper(tier, def);
    setBattleLog('附近的敌人加入了战斗！（多对一）');
    updateBattleHud();
    return true;
  };

  // 地面上的波纹（画在角色下方，像从脚边漾开）
  function drawBloodWavesGround() {
    if (!bloodWaves.length) return;
    const cx = player.worldX - cameraX() - 6;
    const gy = GROUND_Y + 6;
    bloodWaves.forEach(function (w) {
      if (w.t < 0) return;
      const s = bloodWaveProgress(w);
      const r = Math.max(1, s.r);
      const a = s.fade;
      if (a <= 0.01) return;

      ctx.save();
      ctx.translate(cx, gy);
      ctx.scale(1, 0.34); // 压扁成地面上的涟漪

      // 中心深、外围淡的血色径向渐变
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
      g.addColorStop(0, 'rgba(138, 4, 10, ' + (0.52 * a).toFixed(3) + ')');
      g.addColorStop(0.4, 'rgba(186, 18, 24, ' + (0.3 * a).toFixed(3) + ')');
      g.addColorStop(0.74, 'rgba(238, 52, 58, ' + (0.14 * a).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(255, 96, 96, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fill();

      // 两道波纹前沿，增强“荡漾”的层次
      ctx.strokeStyle = 'rgba(255, 86, 86, ' + (0.38 * a).toFixed(3) + ')';
      ctx.lineWidth = 2.6;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.97, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(255, 152, 152, ' + (0.2 * a).toFixed(3) + ')';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(0, 0, r * 0.7, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    });
  }

  // 身周的血色光晕（画在角色上方，让血气裹住身体）
  function drawBloodWavesAura() {
    if (!bloodWaves.length) return;
    const cx = player.worldX - cameraX() - 6;
    const hy = GROUND_Y - 56;
    bloodWaves.forEach(function (w) {
      if (w.t < 0) return;
      const s = bloodWaveProgress(w);
      const a = s.fade;
      if (a <= 0.01) return;
      const r = 30 + s.r * 0.34;
      const g = ctx.createRadialGradient(cx, hy, 3, cx, hy, r);
      g.addColorStop(0, 'rgba(112, 0, 6, ' + (0.4 * a).toFixed(3) + ')');
      g.addColorStop(0.5, 'rgba(178, 16, 22, ' + (0.16 * a).toFixed(3) + ')');
      g.addColorStop(1, 'rgba(255, 80, 80, 0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(cx, hy, r, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  // 宠物：跟随玩家的简易小动物（Canvas 自绘，免费无版权问题）
  function drawPet() {
    if (!petAlive()) return;
    // 按列表顺序（等级高的在前）排在玩家身后
    alivePets().forEach(function (p, i) {
      drawOnePet(p, i);
    });
  }

  function drawOnePet(p, slot) {
    const sx = petScreenX(slot);
    const shake = shakeOffset(p.hurtT);
    const bob = Math.sin(p.walkT * 6) * 2;
    const py = GROUND_Y + shake.y;

    // 影子
    ctx.fillStyle = 'rgba(55, 28, 8, .32)';
    ctx.beginPath();
    ctx.ellipse(sx + shake.x, py + 4, PET_DRAW_SIZE * 0.42, 6, 0, 0, Math.PI * 2);
    ctx.fill();

    // 有素材就用 itch 的沙漠生物精灵图（蝎子 / 鬣狗 / 毒蛇 / 猎鹰）
    if (drawPetSprite(p, sx + shake.x, py)) {
      drawPetHpBar(p, sx, shake);
      return;
    }

    // 兜底：素材没加载出来时用手绘小动物
    ctx.save();
    ctx.translate(sx + shake.x, py + bob);

    // 尾巴
    ctx.strokeStyle = '#a8743f';
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(-18, -14);
    ctx.quadraticCurveTo(-34, -20, -30, -32);
    ctx.stroke();

    // 身体
    ctx.fillStyle = '#c98f54';
    ctx.beginPath();
    ctx.ellipse(0, -20, 25, 18, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#f2c58c';
    ctx.beginPath();
    ctx.ellipse(-2, -26, 16, 12, 0, 0, Math.PI * 2);
    ctx.fill();

    // 头与耳朵
    ctx.fillStyle = '#c98f54';
    ctx.beginPath();
    ctx.moveTo(14, -60);
    ctx.lineTo(24, -74);
    ctx.lineTo(31, -56);
    ctx.closePath();
    ctx.moveTo(30, -62);
    ctx.lineTo(42, -68);
    ctx.lineTo(43, -51);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#e8b078';
    ctx.beginPath();
    ctx.arc(28, -48, 15, 0, Math.PI * 2);
    ctx.fill();

    // 眼睛
    ctx.fillStyle = '#2a1406';
    ctx.beginPath();
    ctx.arc(32, -51, 2.4, 0, Math.PI * 2);
    ctx.arc(37, -49, 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // 宠物血条（保留 35% 生命的反馈）
    drawPetHpBar(p, sx, shake);
  }

  // 宠物精灵图：受伤 → 攻击 → 行走 → 待机
  function petAnimKey(p) {
    const key = p.sprite || 'snake';
    const info = PET_SPRITES[key] || PET_SPRITES.snake;
    const sheetOf = function (anim) {
      const img = images['pet_' + key + '_' + anim];
      return img && img.complete && img.naturalWidth > 0 ? { img: img, frames: info[anim] || 4 } : null;
    };
    if (p.hurtT > 0.12) {
      const s = sheetOf('hurt');
      if (s) return s;
    }
    if (p.attackT > 0) {
      const s = sheetOf('attack');
      if (s) return s;
    }
    if (gameState === 'walk' && input.moving) {
      const s = sheetOf('walk');
      if (s) return s;
    }
    return sheetOf('idle') || sheetOf('walk') || sheetOf('attack');
  }

  function drawPetSprite(p, drawX, groundY) {
    const sheet = petAnimKey(p);
    if (!sheet) return false;
    const frames = sheet.frames;
    const fps = 8;
    const idx = Math.floor((p.animT || 0) * fps) % frames;
    const size = PET_DRAW_SIZE;
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    if (PET_SPRITE_FLIP) {
      ctx.translate(drawX, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(
        sheet.img,
        idx * PET_FRAME_SIZE, 0, PET_FRAME_SIZE, PET_FRAME_SIZE,
        -size / 2, groundY - size + 6, size, size
      );
    } else {
      ctx.drawImage(
        sheet.img,
        idx * PET_FRAME_SIZE, 0, PET_FRAME_SIZE, PET_FRAME_SIZE,
        drawX - size / 2, groundY - size + 6, size, size
      );
    }
    ctx.restore();
    return true;
  }

  function drawPetHpBar(p, sx, shake) {
    if (gameState === 'battle' || p.hp < p.maxHp) {
      const bw = 48;
      const bx = sx + shake.x - bw / 2;
      const by = GROUND_Y - 86;
      const pct = clamp(p.hp / p.maxHp, 0, 1);
      ctx.fillStyle = 'rgba(15, 6, 2, .72)';
      roundRect(bx - 1, by - 1, bw + 2, 8, 4);
      ctx.fill();
      ctx.fillStyle = pct > 0.4 ? '#9adf8c' : '#e27a4a';
      if (pct > 0.01) {
        roundRect(bx, by, Math.max(4, Math.round(bw * pct)), 6, 3);
        ctx.fill();
      }
    }
  }

  // “召唤普通敌人”的临时召唤物
  function drawAlly() {
    if (!ally || ally.turns <= 0) return;
    const sx = player.worldX - cameraX() - 112;
    const aura = ctx.createRadialGradient(sx, GROUND_Y - 46, 6, sx, GROUND_Y - 46, 66);
    aura.addColorStop(0, 'rgba(120, 255, 170, .32)');
    aura.addColorStop(1, 'rgba(120, 255, 170, 0)');
    ctx.fillStyle = aura;
    ctx.beginPath();
    ctx.arc(sx, GROUND_Y - 46, 66, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(50, 28, 8, .3)';
    ctx.beginPath();
    ctx.ellipse(sx - 6, GROUND_Y + 4, 34, 7, 0, 0, Math.PI * 2);
    ctx.fill();
    // 召唤物用敌人美术（面朝右，和玩家一起打左边的敌人）
    const allyImg = images[enemySpriteDef({ sprite: ally.sprite }).key + '_1'];
    if (!drawEnemySprite(allyImg && allyImg.complete ? allyImg : null, sx, GROUND_Y, 0.62, true, 0,
      enemySpriteDef({ sprite: ally.sprite }))) {
      drawCharacterCrop(images.enemyIdle, sx, GROUND_Y, false, 0, 0.62);
    }
    ctx.save();
    ctx.textAlign = 'center';
    ctx.font = 'bold 13px "Microsoft YaHei", sans-serif';
    ctx.lineWidth = 4;
    ctx.strokeStyle = 'rgba(20, 40, 20, .9)';
    const label = (ally.name || '召唤物') + ' x' + ally.turns;
    ctx.strokeText(label, sx, GROUND_Y - 96);
    ctx.fillStyle = '#b6ffcf';
    ctx.fillText(label, sx, GROUND_Y - 96);
    ctx.restore();
  }

  function drawEnemy() {
    if (!activeEnemy) return;
    if (debugHideEnemies) return;
    const sx = activeEnemy.x - cameraX();
    if (sx < -180 || sx > W + 180) return;
    const shake = shakeOffset(activeEnemy.hurtT);

    // 精英光环
    if (activeEnemy.elite) {
      const rr = (activeEnemy.legend ? 240 : 120) * caveScale();
      const ag = ctx.createRadialGradient(sx, GROUND_Y - 70, 10, sx, GROUND_Y - 70, rr);
      ag.addColorStop(0, activeEnemy.legend ? 'rgba(255, 120, 220, .5)' : 'rgba(190, 110, 255, .38)');
      ag.addColorStop(1, activeEnemy.legend ? 'rgba(255, 120, 220, 0)' : 'rgba(190, 110, 255, 0)');
      ctx.fillStyle = ag;
      ctx.beginPath();
      ctx.arc(sx, GROUND_Y - 70, rr, 0, Math.PI * 2);
      ctx.fill();
    } else if (activeEnemy.tier === 'magic') {
      const rrM = 92 * caveScale();
      const mg = ctx.createRadialGradient(sx, GROUND_Y - 70, 6, sx, GROUND_Y - 70, rrM);
      mg.addColorStop(0, 'rgba(90, 170, 255, .32)');
      mg.addColorStop(1, 'rgba(90, 170, 255, 0)');
      ctx.fillStyle = mg;
      ctx.beginPath();
      ctx.arc(sx, GROUND_Y - 70, rrM, 0, Math.PI * 2);
      ctx.fill();
    }

    // 走路状态敌人左右巡逻；战斗时停下并面朝玩家（玩家在左侧）
    const walkBob = gameState === 'walk' ? Math.sin(activeEnemy.walkT * 4.5) * 1.5 : 0;
    const scale = enemyDrawScale(activeEnemy) * caveScale();
    ctx.fillStyle = 'rgba(58, 30, 8, .34)';
    ctx.beginPath();
    ctx.ellipse(sx - 12 * scale, GROUND_Y + 5, 56 * scale, 9 * scale, -0.06, 0, Math.PI * 2);
    ctx.fill();

  // 传奇 BOSS 走动时脚下扬尘
    if (activeEnemy.legend && gameState === 'walk') {
      for (let i = 0; i < 3; i++) {
        const dp = (activeEnemy.walkT * 0.5 + i * 0.33) % 1;
        const dx = sx - 70 * scale + dp * 150 * scale;
        const r = 14 + dp * 40;
        ctx.fillStyle = 'rgba(198, 158, 112, ' + (0.3 * (1 - dp)).toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(dx, GROUND_Y + 6, r * 0.35, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // 世界BOSS：直接用专属立绘（单张图，靠呼吸起伏 / 缓慢摇摆显得"活"）
    const bossArt = activeEnemy.wbBossArt ? images[WB_BOSS_ART_KEY] : null;
    if (bossArt && bossArt.complete && bossArt.naturalWidth > 0) {
      drawWorldBossArt(bossArt, sx + shake.x, GROUND_Y + shake.y, scale);
    } else {
      // 新美术：按类型取帧（只有移动帧 → 赶路按 8fps、战斗站定慢速循环）
      const spriteImg = enemyFrameImage(activeEnemy, gameState === 'walk' ? ENEMY_WALK_FPS : ENEMY_STAND_FPS);
      const faceRight = gameState === 'walk' ? activeEnemy.dir > 0 : false;
      if (!drawEnemySprite(spriteImg, sx + shake.x, GROUND_Y + shake.y, scale, faceRight, walkBob,
        enemySpriteDef(activeEnemy))) {
      // 兜底：新美术没加载出来时用旧的 Kenney 敌人，避免敌人"消失"
      const oldFrame = gameState === 'walk'
        ? (Math.floor(activeEnemy.walkT) % 2 === 0 ? images.enemyWalkA : images.enemyWalkB)
        : images.enemyIdle;
        drawCharacterCrop(oldFrame, sx + shake.x, GROUND_Y + shake.y, true, walkBob, scale);
      }
    }
    drawHurtSlash(sx + shake.x, GROUND_Y + shake.y, activeEnemy.hurtT);
    drawEnemyStatus(sx);

    if (gameState === 'walk') {
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ff3f2c';
      ctx.font = 'bold 28px Arial, sans-serif';
      ctx.fillText('!', sx, GROUND_Y - 256 - Math.round((scale - 1) * 150));
    }
  }

  // 多对一：画出同场战斗里其余的敌人（带小血条）
  function drawBattleGroup() {
    if (battleGroup.length < 2) return;
    battleGroup.forEach(function (e) {
      if (e === activeEnemy || !aliveEnemy(e)) return;
      const sx = e.x - cameraX();
      if (sx < -180 || sx > W + 180) return;
      const scale = enemyDrawScale(e) * 0.85 * caveScale();

      ctx.fillStyle = 'rgba(58, 30, 8, .3)';
      ctx.beginPath();
      ctx.ellipse(sx - 10 * scale, GROUND_Y + 5, 50 * scale, 8 * scale, 0, 0, Math.PI * 2);
      ctx.fill();

      // 同伴敌人也用它自己那套美术（面朝左 → 面对玩家/主目标）
      const helperImg = enemyFrameImage(e, ENEMY_STAND_FPS);
      if (!drawEnemySprite(helperImg, sx, GROUND_Y, scale, false, 0, enemySpriteDef(e))) {
        drawCharacterCrop(images.enemyIdle, sx, GROUND_Y, true, 0, scale);
      }

      // 小血条
      const w = 86;
      const h = 8;
      const bx = sx - w / 2;
      const by = GROUND_Y - 162 * scale - 26;
      const pct = clamp(e.hp / e.maxHp, 0, 1);
      ctx.fillStyle = 'rgba(16, 5, 1, .7)';
      roundRect(bx - 1, by - 1, w + 2, h + 2, 5);
      ctx.fill();
      ctx.fillStyle = 'rgba(84, 22, 8, .92)';
      roundRect(bx, by, w, h, 4);
      ctx.fill();
      if (pct > 0.02) {
        ctx.fillStyle = e.legend ? '#ff7fb8' : e.elite ? '#b479ff' : e.tier === 'magic' ? '#5fa8ff' : '#6fd08a';
        roundRect(bx, by, Math.max(4, Math.round(w * pct)), h, 4);
        ctx.fill();
      }
      ctx.textAlign = 'center';
      ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(24, 6, 2, .9)';
      ctx.strokeText(enemyLabel(e, false) + ' · Lv.' + e.level, sx, by - 5);
      ctx.fillStyle = '#ffd9bd';
      ctx.fillText(enemyLabel(e, false) + ' · Lv.' + e.level, sx, by - 5);
    });

    // 围攻提示
    ctx.textAlign = 'center';
    ctx.font = 'bold 15px "Microsoft YaHei", sans-serif';
    ctx.lineWidth = 5;
    ctx.strokeStyle = 'rgba(40, 4, 4, .92)';
    const label = '多对一：被 ' + battleAlive().length + ' 个敌人围攻';
    ctx.strokeText(label, W / 2, 96);
    ctx.fillStyle = '#ff9a8a';
    ctx.fillText(label, W / 2, 96);
  }

  // ---------- 掉落光柱：装备按品级发光（普通/魔法/稀有/传奇 → 白/蓝/金/粉）----------
  const DROP_PILLAR_TIER = { common: 1, magic: 2, rare: 3, legend: 4 };

  function hexToRgba(hex, alpha) {
    let h = String(hex || '').replace('#', '').trim();
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const n = parseInt(h || 'ffffff', 16);
    if (isNaN(n)) return 'rgba(255,255,255,' + alpha + ')';
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + alpha + ')';
  }

  // 这堆掉落里最好的那件装备（决定光柱颜色 / 高度）
  function corpseTopRarity() {
    if (!corpse || !corpse.drops) return null;
    let best = null;
    corpse.drops.forEach(function (d) {
      if (!d || d.type !== 'equipment') return;
      const tier = DROP_PILLAR_TIER[d.rarity] || 1;
      if (!best || tier > best.tier) {
        best = { tier: tier, color: d.rarityColor || '#d8d3c2', name: d.rarityName || '装备' };
      }
    });
    return best;
  }

  // 颜色混合（往白里靠，做亮芯）
  function mixHex(hex, target, k) {
    let h1 = String(hex || '').replace('#', '');
    let h2 = String(target || '').replace('#', '');
    if (h1.length === 3) h1 = h1[0] + h1[0] + h1[1] + h1[1] + h1[2] + h1[2];
    if (h2.length === 3) h2 = h2[0] + h2[0] + h2[1] + h2[1] + h2[2] + h2[2];
    const n1 = parseInt(h1, 16), n2 = parseInt(h2, 16);
    if (isNaN(n1) || isNaN(n2)) return hex;
    const mix = function (a, b) { return Math.round(a + (b - a) * k); };
    const r = mix((n1 >> 16) & 255, (n2 >> 16) & 255);
    const g2 = mix((n1 >> 8) & 255, (n2 >> 8) & 255);
    const b = mix(n1 & 255, n2 & 255);
    return '#' + ((1 << 24) + (r << 16) + (g2 << 8) + b).toString(16).slice(1);
  }

  // 光柱贴图：一次性预渲染（三层柱体 + 地面光池都带模糊），运行期只 drawImage。
  // 硬边梯形看着生硬，模糊过的体积光才像暗黑 4 那种"一束柔光"。
  const PILLAR_SPRITE = { w: 210, h: 440, ground: 410 };
  const pillarSpriteCache = {};
  const pillarDotCache = {};

  function pillarBeamHeight(tier) { return 150 + tier * 62; }

  function pillarSprite(color, tier) {
    const key = color + '|' + tier;
    if (pillarSpriteCache[key]) return pillarSpriteCache[key];
    const cv = document.createElement('canvas');
    cv.width = PILLAR_SPRITE.w;
    cv.height = PILLAR_SPRITE.h;
    const g = cv.getContext('2d');
    const cx = PILLAR_SPRITE.w / 2;
    const gy = PILLAR_SPRITE.ground;
    const t = clamp(tier || 1, 1, 4);
    const h = pillarBeamHeight(t);
    const half = 13 + t * 4.5;                 // 柱脚半宽
    const bright = mixHex(color, '#ffffff', 0.55);

    // ① 地面光池（柔和的椭圆光晕）
    const pool = g.createRadialGradient(cx, gy, 1, cx, gy, half * 3.2);
    pool.addColorStop(0, hexToRgba(color, 0.42));
    pool.addColorStop(0.45, hexToRgba(color, 0.14));
    pool.addColorStop(1, hexToRgba(color, 0));
    g.save();
    g.globalCompositeOperation = 'lighter';
    g.filter = 'blur(6px)';
    g.fillStyle = pool;
    g.beginPath();
    g.ellipse(cx, gy, half * 3.2, half * 1.15, 0, 0, Math.PI * 2);
    g.fill();
    g.restore();

    // ② 三层柱体：外柔光 → 中层 → 近白亮芯（越往里越窄越亮）
    const layers = [
      { blur: 18, widen: 1.95, alpha: 0.30, color: color, topCut: 0 },
      { blur: 8, widen: 1.15, alpha: 0.46, color: color, topCut: 0.07 },
      { blur: 3.5, widen: 0.5, alpha: 0.78, color: bright, topCut: 0.24 }
    ];
    layers.forEach(function (L) {
      const topY = gy - h * (1 - L.topCut);
      const grad = g.createLinearGradient(0, topY, 0, gy + 4);
      grad.addColorStop(0, hexToRgba(L.color, 0));
      grad.addColorStop(0.3, hexToRgba(L.color, L.alpha * 0.22));
      grad.addColorStop(0.7, hexToRgba(L.color, L.alpha * 0.62));
      grad.addColorStop(1, hexToRgba(L.color, L.alpha));
      const halfTop = Math.max(1.6, half * 0.16 * L.widen);
      const halfBot = half * L.widen;
      g.save();
      g.globalCompositeOperation = 'lighter';
      g.filter = 'blur(' + L.blur + 'px)';
      g.fillStyle = grad;
      g.beginPath();
      g.moveTo(cx - halfTop, topY);
      g.bezierCurveTo(cx - halfTop * 1.7, topY + h * 0.34,
        cx - halfBot * 0.86, gy - h * 0.32, cx - halfBot, gy + 3);
      g.lineTo(cx + halfBot, gy + 3);
      g.bezierCurveTo(cx + halfBot * 0.86, gy - h * 0.32,
        cx + halfTop * 1.7, topY + h * 0.34, cx + halfTop, topY);
      g.closePath();
      g.fill();
      g.restore();
    });
    pillarSpriteCache[key] = cv;
    return cv;
  }

  // 上飘的光点：柔和小圆点（径向渐变，不是硬边圆）
  function pillarDot(color) {
    if (pillarDotCache[color]) return pillarDotCache[color];
    const s = 36;
    const cv = document.createElement('canvas');
    cv.width = s; cv.height = s;
    const g = cv.getContext('2d');
    const gr = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    gr.addColorStop(0, hexToRgba('#ffffff', 0.9));
    gr.addColorStop(0.32, hexToRgba(color, 0.6));
    gr.addColorStop(1, hexToRgba(color, 0));
    g.fillStyle = gr;
    g.fillRect(0, 0, s, s);
    pillarDotCache[color] = cv;
    return cv;
  }

  function drawDropPillar(sx, best) {
    const now = player.animT;
    const spr = pillarSprite(best.color, best.tier);
    const dot = pillarDot(best.color);
    // 呼吸要轻，别一闪一闪（暗黑 4 的光柱几乎是稳定的，只微微起伏）
    const pulse = 0.92 + Math.sin(now * 1.35) * 0.06 + Math.sin(now * 3.1) * 0.015;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = pulse;
    ctx.drawImage(spr, sx - PILLAR_SPRITE.w / 2, GROUND_Y + 12 - PILLAR_SPRITE.ground,
      PILLAR_SPRITE.w, PILLAR_SPRITE.h);
    // 上飘的光点：中段最亮，两端淡出
    const beamH = pillarBeamHeight(best.tier);
    for (let i = 0; i < 6; i++) {
      const t = (now * 0.3 + i / 6) % 1;
      const px = sx + Math.sin(now * 0.7 + i * 2.2) * (8 + best.tier * 3);
      const py = GROUND_Y + 2 - t * beamH;
      const size = 9 + (1 - t) * 9;
      ctx.globalAlpha = Math.sin(Math.PI * t) * 0.5;
      ctx.drawImage(dot, px - size / 2, py - size / 2, size, size);
    }
    ctx.restore();
  }

  function drawCorpse() {
    if (!corpse) return;
    const sx = corpse.worldX - cameraX();
    if (sx < -180 || sx > W + 180) return;

    // 掉落物里有装备 → 按装备品级在尸体上打一根光柱（只有金币 / 材料就不打，走默认样式）
    const bestDrop = corpseTopRarity();
    if (bestDrop) drawDropPillar(sx, bestDrop);

    // 尸体影子
    ctx.fillStyle = 'rgba(34, 17, 6, .5)';
    ctx.beginPath();
    ctx.ellipse(sx + 2, GROUND_Y + 9, 76, 13, 0, 0, Math.PI * 2);
    ctx.fill();

    // 压扁成“倒地”的尸体轮廓（沿用敌人自己的那套美术）
    const corpseDef = enemySpriteDef({ sprite: corpse.sprite });
    const img = (function () {
      const im = images[corpseDef.key + '_1'];
      return im && im.complete && im.naturalWidth > 0 ? im : images.enemyIdle;
    })();
    ctx.save();
    ctx.translate(sx, GROUND_Y);
    ctx.scale(1.02, 0.34);
    ctx.globalAlpha = 0.88;
    if (img === images.enemyIdle) {
      ctx.drawImage(img, 42, 48, 176, 208, -74, -170, 148, 178);
    } else {
      // 新素材：整帧贴，锚点对齐（躺下时以锚点为中心稍微铺开）
      ctx.drawImage(img, -ENEMY_FRAME.w / 2, -(ENEMY_FRAME.h - ENEMY_FRAME.basePad) - 6,
        ENEMY_FRAME.w, ENEMY_FRAME.h);
    }
    ctx.restore();

    // 暗色血迹与尘土
    ctx.fillStyle = 'rgba(92, 12, 8, .24)';
    ctx.beginPath();
    ctx.ellipse(sx - 4, GROUND_Y + 2, 62, 16, 0, 0, Math.PI * 2);
    ctx.fill();

    if (!corpse.searched) {
      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = 'bold 17px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(28, 8, 1, .92)';
      ctx.strokeText('尸体 · 点击搜索', sx, GROUND_Y - 24);
      ctx.fillStyle = '#ffe9c0';
      ctx.fillText('尸体 · 点击搜索', sx, GROUND_Y - 24);
      ctx.font = 'bold 24px Arial, sans-serif';
      ctx.fillStyle = '#9fe2ff';
      ctx.fillText('?', sx - 126, GROUND_Y - 42);
      ctx.restore();
    }
  }

  // ---------- 遗迹洞府：路面入口 + 洞内环境 ----------
  // 入口美术：用户提供 Build1.png（已裁掉透明边、缩到 560×300，见 tools/make_cave_asset.cjs）
  const CAVE_IMG_RATIO = 560 / 388; // 兜底宽高比（素材没加载完时用）
  const CAVE_IMG_H = 240;           // 画到场景里的高度（宽度按素材实际比例算）

  // 宽高比直接读素材，换图不用再改代码
  function caveImgRatio() {
    const im = images.caveEntrance;
    if (im && im.complete && im.naturalWidth > 0) return im.naturalWidth / im.naturalHeight;
    return CAVE_IMG_RATIO;
  }

  function drawCaveEntrances() {
    if (cave) return;
    caveEntrances.forEach(function (c) {
      if (c.used) return;
      const sx = c.worldX - cameraX();
      const dh = CAVE_IMG_H;
      const dw = dh * caveImgRatio();
      if (sx < -dw || sx > W + dw) return;
      const def = c.def;
      const baseY = GROUND_Y + 10;   // 底座稍微压在路面下沿，像是"长"在路上
      const topY = baseY - dh;
      const near = Math.abs(sx - (player.worldX - cameraX())) < 320;
      const img = images.caveEntrance;

      ctx.save();

      // ① 地面投影
      ctx.fillStyle = 'rgba(30, 22, 16, .5)';
      ctx.beginPath();
      ctx.ellipse(sx - 6, baseY + 2, dw * 0.42, 16, 0, 0, Math.PI * 2);
      ctx.fill();

      // ② 阶级光晕（画在建筑后面）
      const glowY = baseY - dh * 0.46;
      const glowR = dw * 0.62;
      const glow = ctx.createRadialGradient(sx, glowY, 8, sx, glowY, glowR);
      glow.addColorStop(0, def.glow);
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(sx, glowY, glowR, 0, Math.PI * 2);
      ctx.fill();

      if (img && img.complete && img.naturalWidth > 0) {
        // ③ 洞府建筑本体
        ctx.drawImage(img, sx - dw / 2, topY, dw, dh);
        // ④ 阶级色光：从洞口往外的一层淡色，绿/蓝/金三种洞府还是能一眼分开
        const tint = ctx.createRadialGradient(sx, baseY - dh * 0.4, 6, sx, baseY - dh * 0.4, dw * 0.4);
        tint.addColorStop(0, def.color);
        tint.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalCompositeOperation = 'lighter';
        ctx.globalAlpha = 0.26;
        ctx.fillStyle = tint;
        ctx.beginPath();
        ctx.arc(sx, baseY - dh * 0.4, dw * 0.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 1;
      } else {
        // 兜底：素材没加载出来时还是画原来的石洞口
        ctx.fillStyle = '#4a3a2c';
        ctx.beginPath();
        ctx.moveTo(sx - 120, baseY);
        ctx.quadraticCurveTo(sx - 110, baseY - 210, sx, baseY - 235);
        ctx.quadraticCurveTo(sx + 110, baseY - 210, sx + 120, baseY);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = '#120c08';
        ctx.beginPath();
        ctx.moveTo(sx - 78, baseY);
        ctx.quadraticCurveTo(sx - 70, baseY - 160, sx, baseY - 182);
        ctx.quadraticCurveTo(sx + 70, baseY - 160, sx + 78, baseY);
        ctx.closePath();
        ctx.fill();
      }

      // ⑤ 名牌
      ctx.textAlign = 'center';
      ctx.font = 'bold 16px "Microsoft YaHei", sans-serif';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(20, 10, 2, .92)';
      const label = def.name + (def.token ? '（需令牌）' : '');
      ctx.strokeText(label, sx, topY - 26);
      ctx.fillStyle = def.color;
      ctx.fillText(label, sx, topY - 26);

      ctx.font = 'bold 12px "Microsoft YaHei", sans-serif';
      const sub = def.token
        ? (caveToken > 0 ? '点击进入 · 令牌 ' + caveToken + ' 枚' : '需要金色洞府令牌')
        : '点击进入 · 随时可离开';
      ctx.strokeText(sub, sx, topY - 8);
      ctx.fillStyle = 'rgba(255, 240, 214, .88)';
      ctx.fillText(sub, sx, topY - 8);

      if (near) {
        ctx.font = 'bold 30px Arial, sans-serif';
        ctx.fillStyle = def.color;
        ctx.fillText('▼', sx, topY - 44 + Math.sin(performance.now() / 260) * 4);
      }
      ctx.restore();
    });
  }

  // 洞府内：压暗画面 + 岩壁剪影，营造洞窟氛围
  function drawCaveInterior() {
    if (!cave) return;
    const def = cave.def;
    ctx.save();
    ctx.fillStyle = 'rgba(8, 6, 10, .62)';
    ctx.fillRect(-20, -20, W + 40, H + 40);

    ctx.fillStyle = 'rgba(10, 8, 14, .85)';
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(W, 0);
    ctx.lineTo(W, 150);
    for (let x = W; x >= 0; x -= 60) {
      ctx.lineTo(x, 110 + Math.sin(x / 90) * 42 + Math.sin(x / 31) * 12);
    }
    ctx.closePath();
    ctx.fill();

    // 洞府颜色光带
    const g = ctx.createLinearGradient(0, GROUND_Y, 0, GROUND_Y - 420);
    g.addColorStop(0, def.glow);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.globalAlpha = 0.25;
    ctx.fillStyle = g;
    ctx.fillRect(0, GROUND_Y - 420, W, 420);
    ctx.restore();
  }

  // 首次游戏的宝箱
  function drawChest() {
    if (!chest) return;
    const sx = chest.worldX - cameraX();
    if (sx < -200 || sx > W + 200) return;
    const baseY = GROUND_Y + 6;
    const hasLoot = !chest.weaponTaken || !chest.gemTaken;

    ctx.fillStyle = 'rgba(40, 20, 6, .42)';
    ctx.beginPath();
    ctx.ellipse(sx - 8, baseY + 4, 74, 12, 0, 0, Math.PI * 2);
    ctx.fill();

    // 箱体
    ctx.save();
    ctx.fillStyle = '#8a5626';
    roundRect(sx - 58, baseY - 52, 116, 54, 8);
    ctx.fill();
    ctx.fillStyle = '#6d3f19';
    roundRect(sx - 58, baseY - 22, 116, 24, 7);
    ctx.fill();

    // 金属包边
    ctx.fillStyle = '#e0b25e';
    ctx.fillRect(sx - 44, baseY - 52, 9, 54);
    ctx.fillRect(sx - 4, baseY - 52, 9, 54);
    ctx.fillRect(sx + 35, baseY - 52, 9, 54);
    ctx.fillStyle = '#b98a3e';
    ctx.fillRect(sx - 58, baseY - 6, 116, 7);

    // 箱盖（打开时向后翻起）
    ctx.translate(sx, baseY - 52);
    if (chest.opened) ctx.rotate(-0.62);
    ctx.fillStyle = '#9a6330';
    roundRect(-58, -26, 116, 30, 8);
    ctx.fill();
    ctx.fillStyle = '#e0b25e';
    ctx.fillRect(-44, -26, 9, 30);
    ctx.fillRect(-4, -26, 9, 30);
    ctx.fillRect(35, -26, 9, 30);
    ctx.restore();

    // 锁扣
    ctx.fillStyle = '#ffd98a';
    roundRect(sx - 9, baseY - 30, 18, 22, 5);
    ctx.fill();

    // 未取完时闪烁的光点
    if (hasLoot) {
      for (let i = 0; i < 4; i++) {
        const a = 0.35 + 0.4 * Math.sin(weatherTime * 3 + i * 1.7);
        const gx = sx + Math.sin(weatherTime * 1.4 + i * 2.1) * 44;
        const gy = baseY - 40 - i * 16 + Math.cos(weatherTime * 2 + i) * 6;
        ctx.globalAlpha = Math.max(0.1, a);
        ctx.fillStyle = '#fff0b8';
        ctx.beginPath();
        ctx.arc(gx, gy, 2.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    ctx.save();
    ctx.textAlign = 'center';
    ctx.font = 'bold 17px "Microsoft YaHei", sans-serif';
    ctx.lineWidth = 5;
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(28, 8, 1, .92)';
    ctx.strokeText(hasLoot ? '宝箱 · 点击打开' : '空宝箱', sx, baseY - 86);
    ctx.fillStyle = hasLoot ? '#ffe9c0' : '#c9b391';
    ctx.fillText(hasLoot ? '宝箱 · 点击打开' : '空宝箱', sx, baseY - 86);
    ctx.restore();
  }

  // ---------- 主循环 ----------
  function draw() {
    ctx.clearRect(0, 0, W, H);
    // 世界BOSS战场：整套换成血沙场景（BOSS + 其他玩家虚影）
    if (worldBossRun) {
      drawWorldBossScene();
      return;
    }
    // intro：首页也在路上（镜头跟着角色），这样角色是"站在路边休息"而不是被推到屏幕外
    const cam = (gameState === 'walk' || gameState === 'battle' || gameState === 'dead' ||
      gameState === 'intro') ? cameraX() : 0;
    // 传奇BOSS在场时地面轻微震动（走动持续、落步/出手时更明显）
    const quake = quakeOffset();
    if (quake.x || quake.y) {
      ctx.save();
      ctx.translate(quake.x, quake.y);
    }
    drawBackground(cam);
    drawSandstorm();
    drawTornado(cam);
    drawDecor(cam);
    drawMilestones(cam);
    drawWeatherBack(cam);
    drawCans();
    drawChest();
    drawCaveEntrances();
    if (images.player_idle_1) {
      drawCorpse();
      drawEnemy();
      drawBattleGroup();
      drawBloodWavesGround();
      drawShockWavesGround();
      drawDefendFxBehind();   // 防御护盾：铺在角色后面（护盾裹住角色）
      drawPlayer();
      drawRunDust();          // 赶路扬尘：跟在脚后（画在角色之后，免得被脚下的影子盖住）
      drawDirtyOnPlayer();
      drawDefendFxFront();    // 角色前面再罩一层薄的：看起来被包住，又不遮住角色
      drawBloodWavesAura();
      drawPet();
      drawAlly();
      drawMirage();
    }
    drawWeatherFront(cam);
    drawCaveInterior();
    drawCaveEnterFx();
    // 洞府增幅特效：要画在「洞府内部暗色滤镜」之后，否则会被那层滤镜压得看不见
    drawCaveBuffGroundFx();   // 脚下的火焰 / 寒冰光环
    drawCaveBuffOverFx();     // 敌人手上的火焰
      drawAttackFx();
      drawDirtyDrops();
      drawAirSlashFx();
      drawSlashFx();
    drawYangClawFx();
    drawDamagePopups();
    if (quake.x || quake.y) ctx.restore();
  }

  // ---------- 主循环：把 dt 秒的世界推进走一遍（不含绘制）----------
  // 单独抽出来是为了"网页在后台也要继续跑"：前台由 requestAnimationFrame 驱动，
  // 后台 rAF 会被浏览器停掉，改由定时器 + 真实时间差继续推进同一套逻辑。
  function updateWorld(dt) {
    if (gameState === 'walk' || gameState === 'battle') battleLogClock += dt;
    // 玩家角色动作计时：待机呼吸一直在走，出手动作只在点击后播一次
    player.animT += dt;
    if (playerAtkT > 0) playerAtkT = Math.max(0, playerAtkT - dt);
    updateRestPose(dt);
    if (gameState === 'walk') update(dt);
    updateWeather(dt);
    updateLoading(dt);
    updateSlashFx(dt);
    updateAttackFx(dt);
    updateDefendFx(dt);
    updateAirSlashFx(dt);
    updateTurnTimer(dt);
    updatePlayerIdleTimer(dt);
    updateAutoAdventure(dt);
    updateCans(dt);
    updateCaveSpawn();
    updateCaveTransition(dt);
    updateMirage(dt);
    updateDirty(dt);
    updateRunDust(dt);
    updateOnline(dt);
    updateBloodWaves(dt);
    updateShockWaves(dt);
    updateYangClawFx(dt);
    updateEnemyQuake(dt);
    applyLifeRegen(dt);
    updateRestHeal(dt);
    updateDamageFx(dt);
    updatePeriodicSave(dt);   // 定期兜底存档（金币等随时变动的数值也一定会落盘）
    // 世界BOSS：刷新 / 消失循环 + 战场推进（虚影、倒计时、参与奖励）
    updateWorldBossCycle();
    updateWorldBossRun(dt);
    updateWorldBossLoot(dt);    // 击杀后：战场中央宝箱的倒计时 + 走过去开箱
    updateCaveEnemyBuffs(dt);   // 洞府敌人「烈焰光环」：每秒灼烧 + 其它增幅状态
    updateWbEnv(dt);
    updateWorldBossPanelClock(dt);
    // 战斗中定时校正「战斗日志」按钮位置（战斗信息面板会随文案变高变矮 + 入场动画）
    if (gameState === 'battle' && ui.btnLog && !ui.btnLog.classList.contains('hidden')) {
      logBtnClock += dt;
      if (logBtnClock >= 0.3) {
        logBtnClock = 0;
        layoutBattleLogBtn();
      }
    }
    // HUD 布局定期校正（进游戏后按钮才可见，尺寸变化也要跟着重排）
    hudLayoutClock += dt;
    if (hudLayoutClock >= 1) {
      hudLayoutClock = 0;
      layoutHud();
    }
    // 生命归零 → 阵亡（回到 0km 重新出发）
    // 世界BOSS战场例外：那里在 updateWorldBossRun 里就把玩家踢出战场了（见上），不走普通阵亡
    if (!deathActive && combat.hp <= 0 && gameState === 'battle' && !worldBossRun) triggerDeath();
  }

  const MAX_WORLD_STEP = 0.1;    // 单步最多推进 0.1 秒（保证物理/动画不跳帧）
  const MAX_WORLD_CATCHUP = 120; // 一次最多补 120 秒（防止断电式长时间挂后台后卡死）
  let worldClock = { last: performance.now(), catchupSeconds: 0, bgTicks: 0 };

  // 按真实秒数推进世界：自动拆成若干小步，后台补帧也走这里
  function advanceWorld(seconds) {
    let remain = Math.min(Math.max(0, seconds), MAX_WORLD_CATCHUP);
    if (seconds > MAX_WORLD_CATCHUP) worldClock.catchupSeconds += seconds - MAX_WORLD_CATCHUP;
    while (remain > 0.0001) {
      const step = Math.min(MAX_WORLD_STEP, remain);
      updateWorld(step);
      remain -= step;
    }
  }

  function frame(now) {
    requestAnimationFrame(frame);
    const dt = Math.max(0, (now - lastTime) / 1000);
    lastTime = now;
    advanceWorld(dt);
    draw();
  }

  // ---------- 后台继续运行 ----------
  // 切到别的标签页 / 最小化窗口时，浏览器会把 requestAnimationFrame 停掉（甚至会节流定时器）。
  // 这里用定时器按"真实时间差"继续推进世界，所以回到前台时进度不会凭空丢失。
  let bgTicker = null;
  function backgroundTick() {
    const now = performance.now();
    const dt = Math.max(0, (now - lastTime) / 1000);
    lastTime = now;
    worldClock.bgTicks += 1;
    if (dt > 0) advanceWorld(dt);
  }

  function syncPageVisibility() {
    if (document.hidden) {
      if (!bgTicker) bgTicker = setInterval(backgroundTick, 250);
    } else {
      if (bgTicker) {
        clearInterval(bgTicker);
        bgTicker = null;
      }
      // 回到前台：先把后台落下的时间补上，避免"切回来才发现停了几分钟"
      backgroundTick();
      lastTime = performance.now();
    }
  }
  document.addEventListener('visibilitychange', syncPageVisibility);
  window.addEventListener('pagehide', syncPageVisibility);
  window.addEventListener('pageshow', syncPageVisibility);
  window.addEventListener('focus', syncPageVisibility);

  // ---------- 按钮事件 ----------
  ui.btnLogin.addEventListener('click', doLogin);
  // 删档要走确认弹窗
  if (ui.btnWipe) ui.btnWipe.addEventListener('click', askWipeSaves);
  if (ui.wipeConfirm) ui.wipeConfirm.addEventListener('click', doWipeSaves);
  if (ui.wipeCancel) ui.wipeCancel.addEventListener('click', closeWipeConfirm);
  // 首页引路人对白：点它（或点「上前对话」）就打开账号窗口
  if (ui.introDialog) {
    ui.introDialog.addEventListener('click', function (e) {
      openLoginFromIntro();
      // 点开登录窗口的就是这一下，别让它冒泡到外层"点空白收起登录窗口"的监听
      e.stopPropagation();
    });
  }
  // 第一个任务：接受后关闭
  if (ui.questAccept) ui.questAccept.addEventListener('click', closeQuestPopup);
  if (ui.questPopup) {
    ui.questPopup.addEventListener('click', function (e) {
      if (e.target === ui.questPopup) closeQuestPopup();
    });
  }
  if (ui.wipePopup) {
    ui.wipePopup.addEventListener('click', function (e) {
      if (e.target === ui.wipePopup) closeWipeConfirm();   // 点空白处 = 取消
    });
  }
  [ui.loginUser, ui.loginPwd, ui.loginIp].forEach(function (el) {
    // 输入时不要触发游戏快捷键
    el.addEventListener('keydown', function (e) {
      e.stopPropagation();
      if (e.code === 'Enter') {
        e.preventDefault();
        doLogin();
      }
    });
    // 改动输入内容 → 取消"确认新建档案"状态
    el.addEventListener('input', resetLoginConfirm);
  });
  ui.start.addEventListener('click', beginStartGame);

  // ---------- HUD 左侧功能列（养成 / 交易）----------
  // 「养成 / 交易」现在是分组标签，下面的功能按钮全部直接可见（带图标 + 文字），
  // 所以这里不需要展开 / 收起逻辑，只需要在窗口尺寸变化时重新排版（见 layoutHud）。
  ui.btnMove.addEventListener('click', function () {
    // 世界BOSS击杀后的开箱阶段也允许走动（去中央宝箱那边）
    if ((gameState !== 'walk' && !worldBossLootActive()) || ui.btnMove.disabled) return;
    input.moving = !input.moving;
    // 开始赶路 = 顺手把「第一个任务」的任务卡收掉（不挡着看路）
    if (input.moving && ui.questPopup && !ui.questPopup.classList.contains('hidden')) closeQuestPopup();
    syncMoveButton();
  });
  ui.btnFlask.addEventListener('click', useFlask);

  ui.btnAttack.addEventListener('click', function () { doPlayerAction('attack'); });
  ui.btnSpell.addEventListener('click', function () { doPlayerAction('spell'); });
  ui.btnDefend.addEventListener('click', function () { doPlayerAction('defend'); });
  ui.spellClose.addEventListener('click', closeSpellPanel);
  ui.btnWorldBoss.addEventListener('click', function () { openWorldBossPanel(); });
  ui.wbClose.addEventListener('click', closeWorldBossPanel);
  ui.wbHelpBtn.addEventListener('click', function () { toggleWorldBossHelp(); });
  ui.wbHelpClose.addEventListener('click', function () { toggleWorldBossHelp(false); });
  ui.wbPopup.addEventListener('click', function (e) {
    if (e.target === ui.wbPopup) closeWorldBossPanel();
  });
  ui.wbLeave.addEventListener('click', function () { leaveWorldBoss('你撤离了世界BOSS战场。'); });
  ui.wbRewardClose.addEventListener('click', closeWorldBossRewardChests);
  // 技能列表滚动时卡片位置会变，先收起悬停提示，避免提示停在旧位置
  ui.spellList.addEventListener('scroll', hideSkillTip);
  ui.spellPopup.addEventListener('click', function (e) {
    if (e.target === ui.spellPopup) closeSpellPanel();
  });
  ui.lootClose.addEventListener('click', closeLootPanel);
  ui.lootPopup.addEventListener('click', function (e) {
    if (e.target === ui.lootPopup) closeLootPanel();
  });
  ui.btnGear.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openGearPanel();
  });
  ui.gearClose.addEventListener('click', closeGearPanel);
  ui.gearPopup.addEventListener('click', function (e) {
    if (e.target === ui.gearPopup) closeGearPanel();
  });
  // 装备洗练
  ui.btnReforge.addEventListener('click', doReforge);
  ui.reforgeClose.addEventListener('click', closeReforge);
  ui.reforgePopup.addEventListener('click', function (e) {
    if (e.target === ui.reforgePopup) closeReforge();
  });
  ui.btnShop.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openShop();
  });
  ui.btnSign.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) signIn();
  });
  ui.btnAuto.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) toggleAutoAdventure();
  });
  ui.btnElite.addEventListener('click', tryEliteChallenge);
  if (ui.caveLeave) ui.caveLeave.addEventListener('click', leaveCave);
  ui.btnBreak.addEventListener('click', breakthrough);
  // 赞助 / 自愿捐赠
  if (ui.btnDonate) ui.btnDonate.addEventListener('click', openDonate);
  if (ui.donateClose) ui.donateClose.addEventListener('click', closeDonate);
  if (ui.donatePopup) {
    ui.donatePopup.addEventListener('click', function (e) {
      if (e.target === ui.donatePopup) closeDonate();   // 点空白关闭
    });
  }
  if (ui.btnOnlineClaim) ui.btnOnlineClaim.addEventListener('click', function () { claimOnlineReward(); });
  // 实名绑定窗口
  if (ui.identitySubmit) ui.identitySubmit.addEventListener('click', function () { submitIdentity(); });
  if (ui.identityCancel) ui.identityCancel.addEventListener('click', closeIdentityPanel);
  // 修改昵称
  if (ui.btnEditNick) ui.btnEditNick.addEventListener('click', function (e) { e.stopPropagation(); openNickPanel(); });
  if (ui.nickSubmit) ui.nickSubmit.addEventListener('click', function () { submitNickname(); });
  if (ui.nickCancel) ui.nickCancel.addEventListener('click', closeNickPanel);
  if (ui.nickInput) {
    ui.nickInput.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitNickname(); }
    });
  }
  [ui.idRealName, ui.idCardNo].forEach(function (el) {
    if (!el) return;
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitIdentity(); }
    });
  });
  ui.btnExchange.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openExchange();
  });
  ui.exchangeClose.addEventListener('click', closeExchange);
  ui.exchangePopup.addEventListener('click', function (e) {
    if (e.target === ui.exchangePopup) closeExchange();
  });
  ui.btnLog.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openBattleLog();
  });
  ui.logClose.addEventListener('click', closeBattleLog);
  // 点聊天窗右上角状态：手动重连世界频道
  if (ui.chatStatus) {
    ui.chatStatus.style.cursor = 'pointer';
    ui.chatStatus.addEventListener('click', reconnectChat);
  }

  // 聊天窗口（本地模式）
  ui.chatForm.addEventListener('submit', function (e) {
    e.preventDefault();
    sendChatMessage();
  });
  ui.chatToggle.addEventListener('click', toggleChatCollapse);
  ui.chatInput.addEventListener('keydown', function (e) {
    e.stopPropagation(); // 输入时不要触发游戏快捷键（例如标题页回车）
  });

  // 图鉴 / 抽卡
  ui.btnCodex.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openCodex();
  });
  ui.btnGacha.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openGacha();
  });
  ui.gachaOne.addEventListener('click', function () { doGacha(1); });
  ui.gachaTen.addEventListener('click', function () { doGacha(10); });
  ui.codexClose.addEventListener('click', closeCodex);
  ui.codexPopup.addEventListener('click', function (e) {
    if (e.target === ui.codexPopup) closeCodex();
  });
  // 鼠标滚轮翻藏品时格子会移动，提示跟着关掉，避免“粘”在旧位置
  ui.codexGrid.addEventListener('scroll', hideCodexTip);
  ui.gachaClose.addEventListener('click', closeGacha);
  ui.gachaPopup.addEventListener('click', function (e) {
    if (e.target === ui.gachaPopup) closeGacha();
  });
  ui.btnChar.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openCharPanel();
  });
  ui.btnSkill.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openSkillPanel();
  });
  ui.btnPet.addEventListener('click', function () {
    if (ui.title.classList.contains('hidden')) openPetPanel();
  });
  ui.petClose.addEventListener('click', closePetPanel);
  ui.petPopup.addEventListener('click', function (e) {
    if (e.target === ui.petPopup) closePetPanel();
  });
  ui.skillClose.addEventListener('click', closeSkillPanel);
  ui.skillPopup.addEventListener('click', function (e) {
    if (e.target === ui.skillPopup) closeSkillPanel();
  });
  ui.charClose.addEventListener('click', closeCharPanel);
  ui.charPopup.addEventListener('click', function (e) {
    // 角色窗口是最上层模态窗口：点空白处不会关闭，必须先点「关闭」才能操作其它按钮
    if (e.target === ui.charPopup) {
      showToast('角色窗口在最上层，点「关闭」后才能操作其它按钮。');
    }
  });
  ui.shopClose.addEventListener('click', closeShop);
  ui.shopPopup.addEventListener('click', function (e) {
    if (e.target === ui.shopPopup) closeShop();
  });
  ui.chestClose.addEventListener('click', closeChestPanel);
  ui.reviveBtn.addEventListener('click', reviveAtStart);
  ui.chestPopup.addEventListener('click', function (e) {
    if (e.target === ui.chestPopup) closeChestPanel();
  });
  ui.gemClose.addEventListener('click', closeGemPanel);
  ui.gemPopup.addEventListener('click', function (e) {
    if (e.target === ui.gemPopup) closeGemPanel();
  });
  ui.socketClose.addEventListener('click', closeSocketPanel);
  ui.socketPopup.addEventListener('click', function (e) {
    if (e.target === ui.socketPopup) closeSocketPanel();
  });
  // 点击画布上的尸体进行搜索
  canvas.addEventListener('click', function (e) {
    // 首页：点角色才弹出登录窗口
    if (gameState === 'intro') {
      const rect = canvas.getBoundingClientRect();
      const sx = (e.clientX - rect.left) * (canvas.width / rect.width);
      const sy = (e.clientY - rect.top) * (canvas.height / rect.height);
      if (hitPlayerAt(sx, sy)) {
        openLoginFromIntro();
        e.stopPropagation();   // 顺手点开的那一下，别让外层"点空白关闭"的监听又把它收掉
      }
      return;
    }
    // 世界BOSS击杀后的开箱阶段：点中央宝箱开箱；地上的战利品展示点了只给提示
    if (worldBossRun) {
      const rect0 = canvas.getBoundingClientRect();
      const cx = (e.clientX - rect0.left) * (canvas.width / rect0.width);
      const cy = (e.clientY - rect0.top) * (canvas.height / rect0.height);
      const chest = worldBossLootChestRect();
      if (chest && worldBossLootActive() &&
          cx >= chest.x - 14 && cx <= chest.x + chest.w + 14 &&
          cy >= chest.y - 14 && cy <= chest.groundY + 30) {
        const playerSx = player.worldX - cameraX();
        if (Math.abs(playerSx - chest.cx) > WB_LOOT_REACH) {
          showToast('先走近中央的宝箱，再点它开启。');
        } else {
          openWorldBossLootChest(false);
        }
        return;
      }
      const hitGift = (worldBossRun.gifts || []).some(function (g) {
        const r = worldBossGiftRect(g);
        return cx >= r.x - 6 && cx <= r.x + r.w + 6 && cy >= r.y - 6 && cy <= r.y + r.h + 10;
      });
      if (hitGift) { showToast(WB_GIFT_NOTE); return; }
      if (worldBossLootActive()) return;   // 开箱阶段不做别的
    }
    if (gameState !== 'walk') return;
    const rect = canvas.getBoundingClientRect();
    const sx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const sy = (e.clientY - rect.top) * (canvas.height / rect.height);
    // 优先检查易拉罐：点击可以踢飞
    for (let i = 0; i < cans.length; i++) {
      const c = cans[i];
      if (c.kicked) continue;
      const cxs = c.worldX - cameraX();
      if (Math.abs(sx - cxs) < 52 && Math.abs(sy - c.y) < 48) {
        kickCan(c);
        return;
      }
    }
    // 优先检查宝箱
    if (chest && (!chest.weaponTaken || !chest.gemTaken)) {
      const cxs = chest.worldX - cameraX();
      if (Math.abs(sx - cxs) < 95 && sy > GROUND_Y - 160 && sy < H) {
        openChestPanel();
        return;
      }
    }
    // 尸体（精确目标，有才判定；没有尸体不能直接 return，否则会挡掉后面的洞府点击）
    if (corpse && !corpse.searched) {
      const csx = corpse.worldX - cameraX();
      if (Math.abs(sx - csx) < 115 && sy > GROUND_Y - 300 && sy < H) {
        searchCorpse();
        return;
      }
    }
    // 最后才是面积最大的洞府入口（避免挡住尸体 / 易拉罐的点击）
    const caveHit = caveEntranceAt(sx, sy);
    if (caveHit && !cave) clickCaveEntrance(caveHit);
  });

  // 登录窗口：点窗口之外的空白处（画面里的暗底、画面外的黑色留白）都收起窗口。
  // 面板内部的输入框 / 按钮 / 关闭按钮照常操作，不受影响。
  (function bindLoginBackdropClose() {
    const stageEl = document.getElementById('stage');
    if (!stageEl) return;
    stageEl.addEventListener('click', function (e) {
      if (!ui.loginScreen || ui.loginScreen.classList.contains('hidden')) return;
      const t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('#loginScreen .panel')) return;                     // 登录面板内部
      if (t.closest('.popupOverlay')) return;                           // 任何弹窗内部（删档确认、商店…）
      if (ui.introDialog && ui.introDialog.contains(t)) return;         // 首页引路人对白
      if (ui.btnDonate && ui.btnDonate.contains(t)) return;             // 赞助按钮
      if (ui.beianBar && ui.beianBar.contains(t)) return;               // 底部备案链接
      closeLoginFromIntro();
    });
  })();

  // 开始画面也允许用回车进入
  window.addEventListener('keydown', function (e) {
    if (e.code === 'Enter') {
      if (ui.loginScreen && !ui.loginScreen.classList.contains('hidden')) { doLogin(); return; }
      if (gameState === 'title') beginStartGame();
    }
    // 角色窗口：Esc 等价于点「关闭」
    if (e.code === 'Escape' && !ui.charPopup.classList.contains('hidden')) closeCharPanel();
  });

  // ---------- 启动 ----------
  initWeather();
  loadSave();
  updateCharButton();
  preloadPlayerRestFrames(); // 休息姿态：软加载，缺图也不影响进游戏
  preloadWorldBossInfoImages(); // 世界BOSS 资料图：悬停时才显示，先预取
  // 先显示登录页；登录成功后再露出标题页，点「开始游戏」才开始加载资源
  // 服务器地址：优先用上次登录用过的，其次用当前页面同源（nginx 反代 /api、/ws）
  try {
    const remembered = window.localStorage.getItem(NET_SERVER_KEY);
    ui.loginIp.value = remembered || location.host || '127.0.0.1:8787';
  } catch (e) {
    ui.loginIp.value = location.host || '127.0.0.1:8787';
  }
  if (skipLoginRequested()) {
    account.name = '测试账号';
    account.ip = ui.loginIp.value;
    account.hash = '';     // 测试直连：继续用本地默认存档（不影响账号体系）
    account.isNew = false;
    ui.loginScreen.classList.add('hidden');
    ui.title.classList.remove('hidden');
    if (ui.titleUser) ui.titleUser.textContent = '当前账号：' + account.name + '　·　服务器：' + account.ip + '　·　测试直连（本地默认档案）';
    gameState = 'title';
  } else {
    // 首页：先看到角色在路上休息，点角色才弹出登录窗口
    ui.loginScreen.classList.add('hidden');
    ui.title.classList.add('hidden');
    gameState = 'intro';
    enterIntroIdle();
  }
  updateDonateButton();
  syncPageVisibility();   // 页面一开始就处于后台标签页时，直接启用后台推进
  layoutHud();            // 首次进入就按当前窗口尺寸排好 HUD

  // 关页面 / 切到后台：把最新状态立刻推给服务器（避免丢进度、丢金币）
  window.addEventListener('pagehide', flushOnPageHide);
  window.addEventListener('beforeunload', flushOnPageHide);
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) flushOnPageHide();
  });

  requestAnimationFrame(frame);
})();
